import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import DeliveryJourneySidebar from '../components/DeliveryJourneySidebar';
import { PurposeIcon, FlowIcon, OutcomeIcon, TipsIcon } from '../components/HowToIcons';
import { CANVAS_API, UPLOAD_API } from '../config';
import { mergeSectionPreservingMeta, countDeepDiveSections } from '../utils/mergeCanvasUpload';
import SplitSlider from '../components/SplitSlider';
import EvaluationModal from '../components/EvaluationModal';
import useEvaluationFlow from '../hooks/useEvaluationFlow';
import { track } from '../utils/analytics';
import { formatStrategyContext } from './StrategyCanvas';

const STORAGE_KEY_STRATEGY = 'guruone_strategy';
const STORAGE_KEY_OM = 'guruone_operating_model';

// ── Shared dialogue rules ─────────────────────────────────────────
// Injected into every followup prompt across the OM dialogues. Mirrors
// the rules added to Direction Guru's followup prompt — consistent
// behaviour across both canvases. Kept as a single constant so the
// rules don't drift per dialogue.
const DIALOGUE_RULES_BLOCK = `
CRITICAL RULES — read these before every response:

ONE QUESTION PER TURN — strict.
Ask exactly one focused question and wait for the answer. Do not chain
questions with "and" or "—". Do not use **Q1:** / **Q2:** markers — they
will render as raw text. If you have two things to explore, pick the
one most worth asking first.

TRANSITION STYLE — neutral, not evaluative.
When you've gathered enough on one topic and are moving to the next,
NEVER open with negation/critique framing ("That's solid. But…",
"Good — however…", "Useful — but I'm missing…"). The leader hasn't
fallen short; you're moving on. Use neutral connectors:
  "Good. Now let me explore X with you."
  "That's enough on that for now. Next…"
  "OK — moving to X."
Only use "but" when genuinely pushing back on a specific weak answer.

PROBE STYLE — open, not forced-choice.
When probing for depth, prefer open-ended forms ("what specifically…",
"say more about…", "what made that the case…") over forced multiple
choice ("was it A, B, C — or something else?"). Forced-choice flattens
the answer. Use multiple-choice only when the dimension is genuinely
discrete (e.g. confirming a specific option label).

READY_TO_SUMMARISE rule — strict separation.
Only emit READY_TO_SUMMARISE when you have NO MORE QUESTIONS to ask.
If you still have a question — even a small clarifier — ASK IT instead
of summarising. The summarise token must NOT coexist with a question
mark in the same turn. When you're truly done, respond with ONLY the
literal token READY_TO_SUMMARISE on its own line. If you include
READY_TO_SUMMARISE AND a question, the system will treat the question
as the real intent and ignore the summarise signal — wasting a turn.
`;

// When the model emits READY_TO_SUMMARISE alongside a question in the
// same turn, defer the wrap-up: render the cleaned reply as a normal
// chat turn so the leader can answer the question. The model re-emits
// the signal next turn when it's truly done. Hard cap forces wrap-up
// regardless to avoid infinite "one more question" loops.
// Returns the cleaned reply text to render as a normal turn, OR null
// if the caller should proceed with wrap-up.
function maybeDeferSummariseForPendingQuestion(reply, questionCount, hardCap) {
  const cleanReply = reply.replace(/READY_TO_SUMMARISE/g, '').trim();
  if (cleanReply.includes('?') && questionCount < hardCap) {
    return cleanReply;
  }
  return null;
}

// ── Direction Guru completion percentage (mirrors calcStrategyPct in Dashboard.js)
// Used to gate the "Generate future state" toolbar button (>= 50% unlocks).
function calcDirectionPct() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_STRATEGY);
    if (!raw) return 0;
    const all = JSON.parse(raw);
    const data = Object.values(all).find(v => typeof v === 'object' && v !== null) || {};
    const KEYS = ['founder_purpose','values','mission','vision','value_proposition',
      'customers','competitors','differentiators','go_to_market','revenue_model',
      'cost_model','current_challenges'];
    const filled = KEYS.filter(k => {
      const s = data[k];
      if (!s) return false;
      if (s.entries) return s.entries.some(e => Object.values(e).some(v => v));
      return Object.values(s).some(v => v && (Array.isArray(v) ? v.length > 0 : String(v).trim().length > 0));
    }).length;
    return Math.round((filled / KEYS.length) * 100);
  } catch { return 0; }
}

// ── Strategy unlock check ─────────────────────────────────────────
function checkStrategyUnlock() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_STRATEGY);
    if (!raw) return { unlocked: false, missing: ['Vision','Customers','Go to Market','Revenue Model','Cost Model','Key Objectives'] };
    const all = JSON.parse(raw);
    const data = Object.values(all).find(v => typeof v === 'object' && v !== null) || {};
    const required = [
      { key: 'vision', label: 'Vision' },
      { key: 'customers', label: 'Customers' },
      { key: 'go_to_market', label: 'Go to Market' },
      { key: 'revenue_model', label: 'Revenue Model' },
      { key: 'cost_model', label: 'Cost Model' },
      { key: 'key_objectives', label: 'Key Objectives' },
    ];
    const missing = required.filter(r => {
      const s = data[r.key];
      if (!s) return true;
      if (s.entries) return s.entries.length === 0 || !s.entries.some(e => Object.values(e).some(v => v));
      return Object.values(s).every(v => !v || (Array.isArray(v) && v.length === 0) || String(v).trim() === '');
    }).map(r => r.label);
    return { unlocked: missing.length === 0, missing };
  } catch { return { unlocked: false, missing: ['Vision','Customers','Go to Market','Revenue Model','Cost Model','Key Objectives'] }; }
}

// ── people_roles legacy field migration ──────────────────────────
// One-time read-time migration: 'spans_of_control' was renamed to
// 'decision_rights'. Promote the old value when reading section data so
// users with existing answers don't lose them silently after the rename.
function migratePeopleRolesData(data) {
  if (!data || typeof data !== 'object') return data;
  const next = { ...data };
  if (next.current && next.current.spans_of_control && !next.current.decision_rights) {
    next.current = { ...next.current, decision_rights: next.current.spans_of_control };
  }
  return next;
}

// ── location_infrastructure legacy field migration ───────────────
// One-time read-time migration: 'geographic_footprint' (single-line text)
// was replaced by the broader 'site_locations' textarea. Promote the old
// value when reading section data so users with existing answers don't
// lose them silently after the rename.
function migrateLocationData(data) {
  if (!data || typeof data !== 'object') return data;
  const next = { ...data };
  if (next.current && next.current.geographic_footprint && !next.current.site_locations) {
    next.current = { ...next.current, site_locations: next.current.geographic_footprint };
  }
  return next;
}

// ── suppliers_partners legacy field migration ────────────────────
// One-time read-time migration: 'outsource_pct' was a free-text field
// (e.g. "30%", "approximately a third"); it's now a percentage slider
// expecting a number 0-100. Pull the first integer out of the legacy
// string so existing answers aren't silently lost. Non-numeric strings
// are left untouched (the slider falls back to 0).
function migrateSuppliersData(data) {
  if (!data || typeof data !== 'object') return data;
  const next = { ...data };
  const raw = next.current?.outsource_pct;
  if (raw != null && typeof raw !== 'number') {
    const match = String(raw).match(/(\d+)/);
    if (match) {
      const n = Math.min(100, Math.max(0, parseInt(match[1], 10)));
      next.current = { ...next.current, outsource_pct: n };
    }
  }
  return next;
}

// ── apps_technology + data_flow_security → it_data_security ──────
// Consolidation migration. Unlike the other migrations on this file,
// which operate on a single section's data shape, this one operates
// on the top-level omData object — apps_technology and
// data_flow_security were two separate sections, both replaced by
// the new combined it_data_security section.
//
// Behaviour: if the new key doesn't exist but either of the old keys
// does, shallow-merge the old current/future objects onto a new
// it_data_security entry. Old keys are preserved (left in place) so
// nothing is destroyed — they're simply unreferenced by OM_SECTIONS
// from now on. Their values won't render in the new UI because the
// field schema changed entirely; this migration's purpose is data
// preservation and to seed the new section so it appears in the
// canvas overview rather than starting empty.
function migrateITConsolidation(omData) {
  if (!omData || typeof omData !== 'object') return omData;
  if (omData.it_data_security) return omData;
  if (!omData.apps_technology && !omData.data_flow_security) return omData;
  const apps = omData.apps_technology || {};
  const security = omData.data_flow_security || {};
  return {
    ...omData,
    it_data_security: {
      current: { ...(apps.current || {}), ...(security.current || {}) },
      future: { ...(apps.future || {}), ...(security.future || {}) },
    },
  };
}

// ── finance_commercial legacy field migration ────────────────────
// One-time read-time migration: 'gross_margin' was a free-text field
// (e.g. "65%"); it's now a percentage_slider expecting a number 0-100.
// Pull the first integer out of the legacy string. Same pattern as
// outsource_pct (suppliers) and retention_rate (customer). Other
// replaced legacy fields (annual_revenue, monthly_fixed_costs,
// pricing_model, revenue_target, margin_target, pricing_changes) have
// no clean mapping into the new schema — they stay on the saved
// object as dormant data so nothing is lost, just not surfaced.
function migrateFinanceData(data) {
  if (!data || typeof data !== 'object') return data;
  const next = { ...data };
  const raw = next.current?.gross_margin;
  if (raw != null && typeof raw !== 'number') {
    const match = String(raw).match(/(\d+)/);
    if (match) {
      const n = Math.min(100, Math.max(0, parseInt(match[1], 10)));
      next.current = { ...next.current, gross_margin: n };
    }
  }
  return next;
}

// ── process_ways_of_work legacy field migration ──────────────────
// One-time read-time migration: the previous schema used chip-only
// fields (delivery_type, repeatability, bottleneck, process_improvements,
// systemisation_level). The new schema is broader. Promote the two
// values that map cleanly: bottleneck → key_bottlenecks (chip → text)
// and process_improvements → priority_process_improvements (textarea
// rename). Other legacy chip values stay on the saved object as
// dormant data so nothing is lost, just not surfaced in the new UI.
function migrateProcessData(data) {
  if (!data || typeof data !== 'object') return data;
  const next = { ...data };
  if (next.current && next.current.bottleneck && !next.current.key_bottlenecks) {
    next.current = { ...next.current, key_bottlenecks: next.current.bottleneck };
  }
  if (next.future && next.future.process_improvements && !next.future.priority_process_improvements) {
    next.future = { ...next.future, priority_process_improvements: next.future.process_improvements };
  }
  return next;
}

// ── customer_interactions legacy field migration ─────────────────
// One-time read-time migration: 'retention_rate' was a free-text field
// (e.g. "80% year-on-year"); it's now a percentage slider expecting a
// number 0-100. Pull the first integer out of the legacy string. Same
// pattern as migrateSuppliersData for outsource_pct.
function migrateCustomerData(data) {
  if (!data || typeof data !== 'object') return data;
  const next = { ...data };
  const raw = next.current?.retention_rate;
  if (raw != null && typeof raw !== 'number') {
    const match = String(raw).match(/(\d+)/);
    if (match) {
      const n = Math.min(100, Math.max(0, parseInt(match[1], 10)));
      next.current = { ...next.current, retention_rate: n };
    }
  }
  return next;
}

// ── Dialogue evaluation modal wrapper ──────────────────────────────
// Wraps the shared EvaluationModal so each OM dialogue can mount it
// with one element instead of repeating the 6 callback wires. Each
// dialogue passes onCommitWithEval (gets the evaluation, returns the
// payload to save) and onRefine (clears the modal, sends leader back
// to chat). Cancel / clear behaviour is uniform.
function DialogueEvalModal({ sectionMeta, evalFlow, onCommitWithEval, onRefine }) {
  if (!evalFlow.evalState || !sectionMeta) return null;
  return (
    <EvaluationModal
      section={{ title: sectionMeta.title, icon: sectionMeta.icon, colour: sectionMeta.colour }}
      evalState={evalFlow.evalState}
      onContinue={() => {
        const evaluation = evalFlow.evalState?.evaluation || null;
        onCommitWithEval(evaluation);
        evalFlow.clear();
      }}
      onCancel={() => evalFlow.clear()}
      onRefine={() => {
        evalFlow.clear();
        if (onRefine) onRefine();
      }}
      refineLabel="Refine — back to dialogue"
    />
  );
}

// ── Direction context for Design dialogues ──────────────────────────
// Every Design dialogue should start a conversation with the
// incremental knowledge of whatever the leader has captured upstream
// on the Direction canvas — regardless of the order they completed
// the sections in. Glenn's wording: "Marketing & Branding first deep
// dive question is 'are you primarily selling a product or service?'
// — and like all other cards it's not remembering I'm pre-revenue."
//
// readDirectionCanvas reads the user-scoped Direction blob; the block
// builder emits a compact briefing that gets injected at the top of
// every dialogue's opening + followup prompts. Empty when nothing has
// been captured yet.
const ACQ_MOTION_LABELS_OM = {
  direct_outreach: 'Direct outreach',
  inbound_content: 'Inbound & content',
  partner_referral: 'Partner & referral',
  self_serve: 'Self-serve & online'
};

function readDirectionCanvas() {
  try {
    const user = JSON.parse(localStorage.getItem('guruone_user') || '{}');
    const userKey = user?.id ? `user_${user.id}` : 'default';
    const raw = localStorage.getItem('guruone_strategy');
    if (!raw) return null;
    const all = JSON.parse(raw);
    const scoped = (all[userKey] && typeof all[userKey] === 'object') ? all[userKey] : null;
    return scoped;
  } catch { return null; }
}

function buildDirectionContextBlock(d) {
  if (!d || typeof d !== 'object') return '';
  const lines = [];

  // Vision — stage flag is the single most important signal across
  // every Design conversation. Pre-revenue vs growth vs established
  // changes what questions even make sense to ask.
  if (d.vision?.vision_stage) lines.push(`- Business stage: ${d.vision.vision_stage}`);
  if (d.vision?.vision_statement) lines.push(`- Vision: ${String(d.vision.vision_statement).slice(0, 180)}`);
  if (d.vision?.target_detail) {
    const yrs = d.vision.time_horizon_years ? ` in ${d.vision.time_horizon_years} years` : '';
    lines.push(`- Vision target: ${d.vision.target_detail}${yrs}`);
  } else if (d.vision?.target_type) {
    lines.push(`- Vision target type: ${d.vision.target_type}`);
  }

  // Mission
  if (Array.isArray(d.mission?.core_objective_themes) && d.mission.core_objective_themes.length) {
    lines.push(`- Mission themes: ${d.mission.core_objective_themes.join(', ')}`);
  }
  if (d.mission?.primary_beneficiary) lines.push(`- Primary beneficiary: ${d.mission.primary_beneficiary}`);

  // Value proposition
  if (d.value_proposition?.primary_problem_solved) lines.push(`- Problem solved: ${String(d.value_proposition.primary_problem_solved).slice(0, 180)}`);
  if (d.value_proposition?.target_customer_type) lines.push(`- Target customer type: ${String(d.value_proposition.target_customer_type).slice(0, 140)}`);
  if (d.value_proposition?.market_positioning) lines.push(`- Market positioning: ${d.value_proposition.market_positioning}`);
  if (d.value_proposition?.price_positioning) lines.push(`- Price positioning: ${d.value_proposition.price_positioning}`);

  // Values (just the themes)
  if (Array.isArray(d.values?.entries) && d.values.entries.length) {
    const vs = d.values.entries.map(v => v.value_theme).filter(Boolean);
    if (vs.length) lines.push(`- Values: ${vs.join(', ')}`);
  }

  // Customer segments — name + revenue % each
  if (Array.isArray(d.customers?.entries) && d.customers.entries.length) {
    const segs = d.customers.entries
      .filter(c => c && c.segment_name)
      .map(c => `${c.segment_name}${c.revenue_pct ? ` (${c.revenue_pct})` : ''}${c.strategic_importance ? ` [${c.strategic_importance}]` : ''}`);
    if (segs.length) lines.push(`- Customer segments: ${segs.join('; ')}`);
  }

  // Competitors
  if (Array.isArray(d.competitors?.alternative_types) && d.competitors.alternative_types.length) {
    lines.push(`- Competitor types: ${d.competitors.alternative_types.join(', ')}`);
  }
  if (d.competitors?.key_examples) lines.push(`- Named competitors: ${String(d.competitors.key_examples).slice(0, 140)}`);

  // Differentiators
  if (Array.isArray(d.differentiators?.entries) && d.differentiators.entries.length) {
    const diffs = d.differentiators.entries.map(de => de.differentiator).filter(Boolean);
    if (diffs.length) lines.push(`- Differentiators: ${diffs.join('; ')}`);
  }

  // GTM
  if (d.go_to_market?.primary_motion) lines.push(`- Primary GTM motion: ${d.go_to_market.primary_motion}`);
  if (d.go_to_market?.sales_approach) lines.push(`- Sales approach: ${d.go_to_market.sales_approach}`);
  if (d.go_to_market?.avg_sales_cycle) lines.push(`- Sales cycle: ${d.go_to_market.avg_sales_cycle}`);
  if (d.go_to_market?.customer_acquisition_cost) lines.push(`- CAC: ${d.go_to_market.customer_acquisition_cost}`);
  if (d.go_to_market?.retention_mechanism) lines.push(`- Retention: ${d.go_to_market.retention_mechanism}`);
  if (d.go_to_market?.acquisition_motion_blend?.splits) {
    const splits = d.go_to_market.acquisition_motion_blend.splits;
    const active = Array.isArray(d.go_to_market.acquisition_motion_blend.active)
      ? d.go_to_market.acquisition_motion_blend.active : Object.keys(splits);
    const mix = active.map(k => `${ACQ_MOTION_LABELS_OM[k] || k} ${splits[k] || 0}%`).join(' / ');
    if (mix) lines.push(`- Acquisition mix: ${mix}`);
  }

  // Revenue model
  if (Array.isArray(d.revenue_model?.business_type) && d.revenue_model.business_type.length) {
    lines.push(`- Business type: ${d.revenue_model.business_type.join(', ')}`);
  }
  if (d.revenue_model?.primary_revenue_type) lines.push(`- Primary revenue type: ${d.revenue_model.primary_revenue_type}`);
  if (d.revenue_model?.typical_deal_size) lines.push(`- Typical deal size: ${d.revenue_model.typical_deal_size}`);
  if (d.revenue_model?.gross_margin) lines.push(`- Gross margin: ${d.revenue_model.gross_margin}`);

  // Cost model
  if (d.cost_model?.monthly_fixed_costs) lines.push(`- Monthly fixed costs: ${d.cost_model.monthly_fixed_costs}`);
  if (d.cost_model?.fixed_variable_ratio) lines.push(`- Cost shape: ${d.cost_model.fixed_variable_ratio}`);

  // Current challenges
  if (Array.isArray(d.current_challenges?.entries) && d.current_challenges.entries.length) {
    const chals = d.current_challenges.entries.map(c => c.challenge).filter(Boolean);
    if (chals.length) lines.push(`- Active challenges: ${chals.slice(0, 4).join('; ')}`);
  }

  if (lines.length === 0) return '';

  return `\n\nDIRECTION CANVAS — INCREMENTAL CONTEXT FROM PRECEDING CARDS:\nThe leader has already captured the following on their Direction canvas. Build on it. Do NOT open with questions whose answer is already here. If the conversation surfaces something that contradicts these facts, ask — but otherwise treat them as known.\n${lines.join('\n')}\n`;
}

// ── Section definitions ───────────────────────────────────────────
const OM_SECTIONS = [
  {
    key: 'people_roles', title: 'People, Roles & Structure', icon: '👥', row: 1, colour: '#185FA5',
    type: 'org_builder',
    group: 'people_portfolio', colSpan: 6,
    description: 'Org structure by layer and function — current headcount, capability gaps, and future hiring plan',
    current_fields: [
      { key: 'role_definition_status', label: 'Role definitions', type: 'chips', options: ['Fully defined — all roles documented', 'Defined for some roles', 'Ad hoc — roles understood but not documented'], multi: false },
      { key: 'responsibilities_documented', label: 'Key responsibilities / accountabilities', type: 'chips', options: ['Documented for all roles', 'Documented for some roles', 'Not yet documented'], multi: false },
      { key: 'decision_rights', label: 'Decision rights', type: 'chips', options: ['Formalised and documented', 'Informally understood', 'Not yet defined'], multi: false,
        explainer: 'Decision rights define who is authorised to make which decisions, at which level. Formalising this reduces the business leader bottleneck and gives the team clarity on when to act and when to escalate.' },
      { key: 'key_gaps', label: 'Current capability gaps', type: 'textarea', placeholder: 'Where do you lack capacity or skills right now?' },
      { key: 'team_challenges', label: 'Current challenges — people & structure', type: 'textarea', placeholder: 'What is your biggest people or structure challenge right now?' },
      // The "Business leader time allocation" slider (FounderTimeSlider)
      // is rendered separately above for org_builder sections, so we
      // don't duplicate it here. (Was previously a redundant 'split'
      // field labelled "How do you split your time?".)
    ],
    future_fields: [
      { key: 'hiring_priorities', label: 'Key hiring priorities', type: 'textarea', placeholder: 'Who do you need to hire and when?' },
      { key: 'founder_role', label: 'Business leader role shift', type: 'textarea', placeholder: 'How does your role need to change?' },
      { key: 'structural_changes', label: 'Structural changes needed', type: 'textarea', placeholder: 'What needs to change about how the business is organised?' },
    ],
  },
  {
    key: 'culture_leadership', title: 'Culture & Leadership', icon: '🌱', row: 1, colour: '#7C3AED',
    type: 'culture_dialogue',
    group: 'foundations', colSpan: 3,
    description: 'How the business actually behaves and how leaders show up — characterised through dialogue',
    current_fields: [
      // Culture fields (populated via Culture dialogue or quick entry)
      { key: 'decision_making', label: 'Decision-making style', type: 'chips', options: ['Founder decides most things', 'Small leadership team decides', 'Consensus-driven', 'Delegated to functions'], multi: false },
      { key: 'transparency', label: 'Information transparency', type: 'chips', options: ['Open book — team knows everything', 'Selective — key info shared', 'Need-to-know — minimal sharing'], multi: false },
      { key: 'team_empowerment', label: 'Team empowerment', type: 'chips', options: ['High — people act autonomously', 'Moderate — some autonomy', 'Low — most things go through business leader'], multi: false },
      { key: 'what_celebrated', label: 'What gets celebrated', type: 'textarea', placeholder: 'What behaviours, results or attitudes get noticed and praised?' },
      { key: 'what_not_tolerated', label: 'What is not tolerated', type: 'textarea', placeholder: 'What would quickly get someone into trouble?' },
      { key: 'culture_characterisation', label: 'Culture characterisation', type: 'textarea', placeholder: 'A short honest description of how this business actually operates culturally' },
      { key: 'culture_evidence', label: 'Key culture evidence point', type: 'textarea', placeholder: 'One specific example that best illustrates this culture in practice' },
      { key: 'culture_challenges', label: 'Current challenges — culture & leadership', type: 'textarea', placeholder: 'What is your biggest culture or leadership challenge right now?' },
      { key: 'working_norms_style', label: 'How working norms are set', type: 'chips', options: ['Set centrally by leadership', 'Set by each team manager', 'Self-managed teams', 'Structured methodology (Agile, OKRs, EOS)', 'No agreed way of working'], multi: false },
      // Leadership fields (populated via Leadership dialogue or quick entry)
      { key: 'leadership_visibility', label: 'Leadership visibility', type: 'chips', options: ['Very visible — daily presence', 'Moderate — regular structured check-ins', 'Hands-off — team runs itself'], multi: false },
      { key: 'empowerment_style', label: 'Empowerment style', type: 'chips', options: ['High — team makes most decisions', 'Moderate — shared decision-making', 'Low — leader decides most things'], multi: false },
      { key: 'communication_pattern', label: 'Communication pattern', type: 'chips', options: ['Consistent and structured', 'Regular but informal', 'Ad hoc — as needed'], multi: false },
      { key: 'leadership_development', label: 'Building leadership capacity', type: 'chips', options: ['Actively developing next tier', 'Some development happening', 'Not yet a focus'], multi: false },
      { key: 'leadership_characterisation', label: 'Leadership characterisation', type: 'textarea', placeholder: 'How leadership actually shows up in this business — observable patterns, not aspirations' },
      { key: 'leadership_evidence', label: 'Key leadership evidence point', type: 'textarea', placeholder: 'One specific example that best illustrates the leadership style' },
    ],
    future_fields: [
      { key: 'cultural_shift', label: 'Cultural shift needed', type: 'textarea', placeholder: 'What needs to change about how you work together?' },
      { key: 'future_empowerment', label: 'Target empowerment level', type: 'chips', options: ['More autonomy for team', 'Stay the same', 'More structure needed'], multi: false },
      { key: 'leadership_development_plan', label: 'Leadership development plan', type: 'textarea', placeholder: 'Who needs to grow into what, and what does that require?' },
    ],
  },
  {
    key: 'location_infrastructure', title: 'Location & Infrastructure', icon: '🏢', row: 1, colour: '#9B6B3A',
    type: 'location_dialogue',
    group: 'foundations', colSpan: 3,
    description: 'Working arrangements, geographic footprint and physical constraints',
    current_fields: [
      { key: 'working_arrangement', label: 'Working arrangement', type: 'chips', options: ['Office-based','Hybrid','Remote','Multi-site'], multi: false },
      { key: 'site_locations', label: 'Sites and working arrangements', type: 'textarea', placeholder: 'Describe every location — office, depot, hub or home-working cluster — and the arrangement at each' },
      { key: 'customer_locations', label: 'Customer locations and proximity needs', type: 'textarea', placeholder: 'Where are your customers, and does physical proximity matter for your business model?' },
      { key: 'infrastructure_constraints', label: 'Infrastructure constraints', type: 'textarea', placeholder: 'What limits you physically?' },
      { key: 'location_challenges', label: 'Current challenges — location & infrastructure', type: 'textarea', placeholder: 'What is your biggest location or infrastructure challenge right now?' },
    ],
    future_fields: [
      { key: 'target_locations', label: 'Target locations', type: 'textarea', placeholder: 'Where will you operate?' },
      { key: 'property_strategy', label: 'Property strategy', type: 'chips', options: ['Expand current','New office','More remote','Co-working','None needed'], multi: false },
      { key: 'tech_infrastructure_requirements', label: 'Technical infrastructure requirements', type: 'textarea', placeholder: 'What connectivity, security or systems capability does any future location need to support? (Detail covered in Apps & Technology and Data sections)' },
    ],
  },
  {
    key: 'suppliers_partners', title: 'Suppliers & Partners', icon: '🤝', row: 1, colour: '#1D9E75',
    type: 'suppliers_dialogue',
    group: 'operations', colSpan: 3,
    description: 'Key dependencies, outsourcing and partnership strategy',
    current_fields: [
      { key: 'key_partners', label: 'Key external partners', type: 'textarea', placeholder: 'Who do you depend on to deliver?' },
      { key: 'outsource_pct', label: '% of delivery outsourced to external partners', type: 'percentage_slider' },
      { key: 'dependency_risk', label: 'Critical dependency risk', type: 'chips', options: ['Low','Medium','High'], multi: false },
      { key: 'contract_status', label: 'Contract and documentation status', type: 'chips', options: ['Documented and current', 'Partially documented', 'Undocumented or at risk'], multi: false },
      { key: 'strategic_partnerships', label: 'Strategic partnership opportunities', type: 'textarea', placeholder: 'Existing or potential referral, complementary service, or white-label relationships' },
      { key: 'make_vs_buy_flagged', label: 'Make vs buy decisions flagged for Decision Guru', type: 'textarea', placeholder: 'Any significant insource/outsource decisions worth a structured analysis' },
      { key: 'supplier_challenges', label: 'Current challenges — suppliers & partners', type: 'textarea', placeholder: 'What is your biggest supplier or partner challenge right now?' },
    ],
    future_fields: [
      { key: 'partner_strategy', label: 'Partnership strategy', type: 'textarea', placeholder: 'Which relationships to build, deepen or reduce?' },
      { key: 'insource_outsource', label: 'Insource vs outsource shift', type: 'chips', options: ['Bring more in-house','Outsource more','Stay the same'], multi: false },
    ],
  },
  {
    key: 'customer_interactions', title: 'Customer Interactions', icon: '💬', row: 1, colour: '#C0556E',
    type: 'customer_dialogue',
    group: 'operations', colSpan: 3,
    description: 'Relationship type, touch points and retention',
    current_fields: [
      { key: 'relationship_type', label: 'Relationship type', type: 'chips', options: ['Transactional','Ongoing retainer','Long-term partnership','Project-based'], multi: false },
      { key: 'engagement_style', label: 'Engagement style', type: 'chips', options: ['Proactive', 'Mostly proactive', 'Reactive', 'Mixed'], multi: false },
      { key: 'touch_points', label: 'Key touchpoints', type: 'textarea', placeholder: 'How do you interact with customers?' },
      { key: 'issue_handling', label: 'Issue and complaint handling', type: 'chips', options: ['Strong — customers feel well supported', 'Inconsistent', 'Reactive only', 'Not yet formalised'], multi: false },
      { key: 'retention_rate', label: 'Year-on-year retention rate', type: 'percentage_slider' },
      { key: 'churn_reasons', label: 'Primary churn reasons', type: 'textarea', placeholder: 'What patterns do you see when customers leave?' },
      { key: 'automation_opportunity', label: 'Automation opportunity', type: 'textarea', placeholder: 'Which customer interactions could be systematised without losing what is genuinely distinctive?' },
      { key: 'customer_challenges', label: 'Current challenges — customer interactions', type: 'textarea', placeholder: 'What is your biggest customer interaction or retention challenge right now?' },
    ],
    future_fields: [
      { key: 'cx_improvements', label: 'Customer experience improvements', type: 'textarea', placeholder: 'What needs to get better?' },
      { key: 'target_relationship', label: 'Target relationship model', type: 'chips', options: ['More transactional','Deeper partnerships','Self-serve','Community'], multi: false },
    ],
  },
  {
    key: 'process_ways_of_work', title: 'Process & Ways of Work', icon: '⚙️', row: 2, colour: '#0891B2',
    type: 'process_dialogue',
    group: 'operations', colSpan: 3,
    description: 'How the business delivers — process ownership, supporting systems, bottlenecks, and AI opportunity',
    current_fields: [
      { key: 'delivery_process_description', label: 'Delivery process', type: 'textarea', placeholder: 'How work gets delivered end to end — key steps and who owns each' },
      { key: 'documentation_status', label: 'Documentation status', type: 'chips', options: ['Fully documented', 'Partially documented', 'Mostly undocumented'], multi: false },
      { key: 'step_ownership_clarity', label: 'Step ownership', type: 'chips', options: ['Explicit — each step has a named owner', 'Broadly understood but informal', 'Unclear or overlapping'], multi: false },
      { key: 'key_delivery_systems', label: 'Key delivery systems', type: 'textarea', placeholder: 'Tools that touch the delivery process and how they connect' },
      { key: 'systems_integration', label: 'Systems integration', type: 'chips', options: ['Well integrated', 'Partial integration — some manual handoffs', 'Largely manual — systems don\'t talk to each other'], multi: false },
      { key: 'skill_judgment_dependency', label: 'Skill and judgment dependency', type: 'chips', options: ['High — most steps require experience', 'Mixed', 'Low — largely procedural'], multi: false },
      { key: 'key_bottlenecks', label: 'Key bottlenecks', type: 'textarea', placeholder: 'The specific steps, people, or handoffs that create constraint' },
      { key: 'quality_control_method', label: 'Quality control', type: 'chips', options: ['Formal QA process', 'Senior review on every job', 'Spot-check', 'Customer feedback only', 'None'], multi: false },
      { key: 'ai_automation_in_use', label: 'AI and automation', type: 'textarea', placeholder: 'What\'s already being used — or specific opportunities identified' },
      { key: 'process_challenges', label: 'Current challenges — process & ways of work', type: 'textarea', placeholder: 'What is your single biggest process or delivery challenge right now?' },
      { key: 'automation_maturity', label: 'Automation maturity', type: 'chips', options: ['Highly manual', 'Partially automated', 'Mostly automated', 'Deliberately automated'], multi: false },
    ],
    future_fields: [
      { key: 'priority_process_improvements', label: 'Priority process improvements', type: 'textarea', placeholder: 'The one or two changes with the highest impact' },
      { key: 'target_delivery_model', label: 'Target delivery model', type: 'textarea', placeholder: 'What delivery looks like when the business is operating at scale' },
      { key: 'systems_integration_changes', label: 'Systems and integration changes', type: 'textarea', placeholder: 'Integration improvements or new tools needed' },
      { key: 'ai_automation_roadmap', label: 'AI and automation roadmap', type: 'textarea', placeholder: 'Specific AI or automation changes planned or under consideration' },
    ],
  },
  {
    key: 'governance_controls', title: 'Governance & Review Rhythm', icon: '🔄', row: 2, colour: '#4F46E5',
    type: 'governance_dialogue',
    group: 'foundations', colSpan: 3,
    description: 'How the business runs itself — meeting discipline, decision-making, follow-through, and external challenge',
    current_fields: [
      { key: 'meeting_cadence', label: 'What meets and how often', type: 'textarea', placeholder: 'What meetings happen, how often, who attends, and what each is for' },
      { key: 'meeting_quality', label: 'How meetings run', type: 'chips', options: ['Structured agenda — decisions and actions focused', 'Mix of updates and decisions', 'Mostly updates', 'Ad hoc'], multi: false },
      { key: 'action_capture', label: 'Action capture and follow-through', type: 'chips', options: ['Captured and reviewed every meeting', 'Captured but not always reviewed', 'Not formally captured'], multi: false },
      { key: 'financial_review_frequency', label: 'Financial review frequency', type: 'chips', options: ['Weekly', 'Monthly', 'Quarterly', 'Ad hoc'], multi: false },
      { key: 'decision_making', label: 'How significant decisions get made', type: 'chips', options: ['Formal leadership forum', 'Informal group', 'Founder only'], multi: false },
      { key: 'external_oversight', label: 'External oversight and challenge', type: 'chips', options: ['Active board or advisor — genuinely challenging', 'Available but largely passive', 'None'], multi: false },
      { key: 'governance_challenges', label: 'Current challenges — governance & review', type: 'textarea', placeholder: 'What is the biggest gap in how the business currently runs itself?' },
    ],
    future_fields: [
      { key: 'target_meeting_rhythm', label: 'Target meeting rhythm', type: 'textarea', placeholder: 'What needs to meet, how often, and with what focus' },
      { key: 'discipline_improvements', label: 'Discipline improvements', type: 'textarea', placeholder: 'The specific habits or structures that need to change' },
      { key: 'external_oversight_changes', label: 'External oversight changes', type: 'textarea', placeholder: 'What external challenge or accountability the business needs as it grows' },
    ],
  },
  {
    key: 'finance_commercial', title: 'Finance & Commercial', icon: '💰', row: 2, colour: '#059669',
    type: 'finance_dialogue',
    group: 'operations', colSpan: 3,
    description: 'Revenue sources, costs, margin, and cash position — the financial shape of the business',
    current_fields: [
      { key: 'revenue_streams', label: 'Where revenue comes from', type: 'textarea', placeholder: 'The different ways the business makes money and roughly how much each contributes' },
      { key: 'revenue_model', label: 'Revenue model', type: 'chips', options: ['Recurring retainer', 'Project-based', 'Transactional', 'Mixed'], multi: false },
      { key: 'revenue_concentration', label: 'Revenue concentration', type: 'chips', options: ['Well spread', 'Some concentration', 'High concentration — one source >30%'], multi: false },
      { key: 'gross_margin', label: 'Gross margin — what the business keeps after direct delivery costs', type: 'percentage_slider' },
      { key: 'pricing_approach', label: 'Pricing approach', type: 'chips', options: ['Based on our costs', 'Based on market rates', 'Based on the value we deliver', 'Mixed'], multi: false,
        explainer: 'Pricing model is explored in depth in the Business Development section. Record the current approach here.' },
      { key: 'main_costs', label: 'Main costs', type: 'textarea', placeholder: 'The three or four largest costs — and whether they stay fixed or move with revenue' },
      { key: 'cash_position', label: 'Cash position day to day', type: 'chips', options: ['Generally comfortable', 'Manageable', 'Often tight — waiting on invoices'], multi: false },
      { key: 'financial_decision_data', label: 'How financial decisions get made', type: 'chips', options: ['Regular management accounts', 'Periodic review', 'Mainly experience-based'], multi: false },
      { key: 'finance_challenges', label: 'Current challenges — finance & commercial', type: 'textarea', placeholder: 'What is your biggest financial or commercial challenge right now?' },
      { key: 'revenue_by_segment', label: 'Revenue by segment or market', type: 'split',
        options: [],
        allow_custom: true,
        allowAbsolute: true },
      { key: 'growth_risk_appetite', label: 'Growth risk appetite', type: 'chips', options: ['Conservative — steady profitable growth', 'Moderate — willing to invest and take some risk', 'Ambitious — growth is the priority'], multi: false },
    ],
    future_fields: [
      { key: 'commercial_model_changes', label: 'Commercial model changes', type: 'textarea', placeholder: 'What needs to change about how the business makes money' },
      { key: 'margin_improvements', label: 'Ways to keep more from each pound of revenue', type: 'textarea', placeholder: 'Revenue-side, cost-side, or model changes' },
      { key: 'income_diversification', label: 'Income diversification plan', type: 'textarea', placeholder: 'How to spread income across more sources — if concentration is a risk' },
      { key: 'funding_appetite', label: 'Growth funding approach', type: 'chips', options: ['Self-funding from cash flow', 'Exploring equity investment', 'Exploring debt or grants', 'Mix of sources', 'Not yet decided'], multi: false },
    ],
  },
  {
    key: 'it_data_security', title: 'IT & Data Security', icon: '🔒', row: 2, colour: '#0F766E',
    type: 'it_dialogue',
    group: 'foundations', colSpan: 3,
    description: 'Tech estate, bespoke solutions, data organisation, compliance, security, and technology as a competitive lever',
    current_fields: [
      { key: 'tech_estate', label: 'Main systems and tools', type: 'textarea', placeholder: 'Key systems beyond delivery tools — finance, HR, communication, file storage, CRM' },
      { key: 'bespoke_solutions', label: 'Bespoke solutions and workarounds', type: 'textarea', placeholder: 'Spreadsheets, automations, or informal tools the business depends on — and who understands them' },
      { key: 'ai_data_risk', label: 'AI tool data risk', type: 'chips', options: ['Aware and managed', 'Not yet considered', 'No AI tools in use'], multi: false,
        explainer: 'Consumer AI tools process the files and data they receive. If client or sensitive data is going through tools like ChatGPT, this is worth reviewing.' },
      { key: 'file_organisation', label: 'Data and file organisation', type: 'chips', options: ['Consistent structure — anyone could find things', 'Partially organised', 'Each person has their own system'], multi: false },
      { key: 'data_categories', label: 'Data the business holds', type: 'chips', options: ['Customer contact data', 'Payment or financial data', 'Employee records', 'Commercially sensitive client data', 'Health or special category data', 'Operational data'], multi: true },
      { key: 'gdpr_posture', label: 'GDPR and compliance posture', type: 'chips', options: ['Current privacy policy and consent in place', 'Partially addressed', 'Not yet addressed'], multi: false },
      { key: 'security_basics', label: 'Security basics', type: 'chips', options: ['Strong controls — devices protected, access managed', 'Basic controls in place', 'Limited controls'], multi: false },
      { key: 'backup_recovery', label: 'Backup and recovery', type: 'chips', options: ['Tested and reliable', 'In place but untested', 'Not in place'], multi: false },
      { key: 'horizon_scanning', label: 'Technology horizon scanning', type: 'chips', options: ['Active — deliberate scanning and experimentation', 'Occasional', 'Reactive only'], multi: false },
      { key: 'it_challenges', label: 'Current challenges — IT & data security', type: 'textarea', placeholder: 'Biggest concern around data, technology, or security right now' },
    ],
    future_fields: [
      { key: 'compliance_security_priorities', label: 'Compliance and security priorities', type: 'textarea', placeholder: 'What needs to be addressed first and why' },
      { key: 'bespoke_remediation', label: 'Bespoke solution remediation', type: 'textarea', placeholder: 'Informal tools to replace, document, or de-risk' },
      { key: 'data_organisation_improvements', label: 'Data and file organisation improvements', type: 'textarea', placeholder: 'What needs to change about how data and files are managed' },
      { key: 'technology_opportunities', label: 'Technology opportunities to explore', type: 'textarea', placeholder: 'Tools or capabilities that could create competitive advantage' },
    ],
  },
  {
    key: 'portfolio', title: 'Product & Service Portfolio', icon: '🗂️', row: 2, colour: '#B45309',
    type: 'portfolio_dialogue',
    group: 'people_portfolio', colSpan: 6,
    description: 'What the business offers, how the portfolio performs, and whether to go deeper or broader',
    current_fields: [
      { key: 'portfolio_description', label: 'What the business offers', type: 'textarea', placeholder: 'Products, services, and offerings — what each is, who it\'s for, and what it involves' },
      { key: 'portfolio_size', label: 'Portfolio size', type: 'chips', options: ['Single core offering', 'Small portfolio — 2 to 5 offerings', 'Broader portfolio — 6 or more'], multi: false },
      { key: 'portfolio_health', label: 'Portfolio health', type: 'chips', options: ['Strong across the board', 'Mixed — some performing well, some under pressure', 'Under pressure overall'], multi: false },
      { key: 'margin_profile', label: 'Margin profile', type: 'chips', options: ['High margin offerings dominant', 'Mixed margin profile', 'Low margin — volume dependent'], multi: false },
      { key: 'portfolio_focus', label: 'Portfolio focus', type: 'chips', options: ['Tightly focused — clear specialism', 'Moderately focused', 'Broad — wide range of offerings'], multi: false },
      { key: 'offering_development_model', label: 'How new offerings are developed', type: 'chips', options: ['Structured and deliberate', 'Ad hoc — as opportunities arise', 'Reactive to client requests'], multi: false },
      { key: 'offerings_to_retire', label: 'Offerings to consider retiring', type: 'textarea', placeholder: 'What is being kept for the wrong reasons' },
      { key: 'portfolio_challenges', label: 'Current challenges — product & service portfolio', type: 'textarea',
        placeholder: 'Biggest portfolio challenge right now',
        explainer: 'Portfolio-specific challenges (not GTM, positioning or pricing): cannibalisation between offerings, an underperformer kept for the wrong reasons, margin distortion from one offering, breadth outrunning team capability, buyer confusion, saturation in the core, a known adjacency gap, or build-vs-buy stuck.' },
      { key: 'profitability_by_offering', label: 'Profitability by product or service', type: 'rated_list',
        rating_options: ['High margin', 'Medium margin', 'Low margin', 'Loss-making', "Don't know"] },
    ],
    future_fields: [
      { key: 'strategic_direction', label: 'Strategic direction', type: 'chips', options: ['Go deeper — strengthen the specialism', 'Broaden deliberately — add adjacent offerings', 'Both — different parts of the portfolio need different approaches'], multi: false },
      { key: 'new_offerings', label: 'New offerings in development or planned', type: 'textarea', placeholder: 'What is being developed and why' },
      { key: 'portfolio_rationalisation', label: 'Portfolio rationalisation', type: 'textarea', placeholder: 'What to exit or wind down and why' },
      { key: 'portfolio_adjacencies', label: 'Portfolio adjacencies', type: 'textarea', placeholder: 'Natural extensions the business is well placed to develop' },
      { key: 'build_buy_partner', label: 'Route to filling portfolio gaps', type: 'chips', options: ['Build internally', 'Acquire', 'Partner with another business', 'Mix of routes'], multi: false },
      { key: 'future_portfolio_vision', label: 'Future portfolio vision', type: 'textarea', placeholder: 'What the overall portfolio looks like at target scale' },
    ],
  },
  {
    key: 'business_development', title: 'Business Development', icon: '🤝', row: 2, colour: '#DC2626',
    type: 'bd_dialogue',
    group: 'revenue_generation', colSpan: 6,
    description: 'How the business converts opportunities into income — pipeline, qualification, pricing discipline, and referral activation',
    current_fields: [
      { key: 'sales_model', label: 'Sales model', type: 'chips', options: ['Transactional — short cycle, set pricing', 'Consultative — longer cycle, bespoke proposals', 'Mixed'], multi: false,
        explainer: 'Transactional: customers decide quickly, pricing is set or near-set, volume-based. Consultative: longer process, proposals, relationship-dependent, high average value.' },
      { key: 'conversion_rate', label: 'Conversion rate (transactional)', type: 'textarea', placeholder: 'Proportion of prospects or visitors who buy — known figure or honest estimate' },
      { key: 'avg_transaction_value', label: 'Average transaction value (transactional)', type: 'textarea', placeholder: 'Typical value of a single transaction and whether it varies significantly' },
      { key: 'repeat_purchase_approach', label: 'Repeat purchase approach (transactional)', type: 'chips', options: ['Systematic — loyalty or incentive programme in place', 'Occasional reminders or follow-up', 'Passive — customer-led'], multi: false },
      { key: 'pipeline_definition', label: 'Pipeline definition (consultative)', type: 'chips', options: ['Defined pipeline with clear stages', 'Informal pipeline', 'No defined pipeline'], multi: false },
      { key: 'qualification_discipline', label: 'Qualification discipline (consultative)', type: 'chips', options: ['Strong — clear criteria for what to pursue', 'Some qualification', 'Most opportunities are pursued'], multi: false },
      { key: 'pricing_commercial', label: 'Pricing in the commercial conversation (consultative)', type: 'chips', options: ['Consistent approach — held under pressure', 'Varies by opportunity', 'Often discounted'], multi: false },
      { key: 'win_rate', label: 'Win rate (consultative)', type: 'textarea', placeholder: 'Proportion of pursued opportunities that convert — and whether wins and losses are reviewed' },
      { key: 'existing_client_expansion', label: 'Existing client expansion', type: 'chips', options: ['Systematic — actively pursued', 'Occasional', 'Reactive — client-led'], multi: false },
      { key: 'referral_activation', label: 'Referral activation', type: 'chips', options: ['Systematic — asked for, tracked, and incentivised', 'Occasional', 'Passive — happens when it happens'], multi: false },
      { key: 'founder_dependency_bd', label: 'Business leader dependency in BD', type: 'chips', options: ['Founder does most BD', 'Split between founder and team', 'Team-led with founder support'], multi: false },
      { key: 'crm_pipeline_mgmt', label: 'CRM and pipeline management', type: 'chips', options: ['CRM used consistently', 'CRM used inconsistently', 'No CRM — managed manually'], multi: false },
      { key: 'bd_challenges', label: 'Current challenges — business development', type: 'textarea', placeholder: 'The single biggest BD or income generation challenge right now' },
    ],
    future_fields: [
      { key: 'target_bd_model', label: 'Target BD model', type: 'textarea', placeholder: 'What the BD function looks like when working properly' },
      { key: 'bd_process_improvements', label: 'Process improvements', type: 'textarea', placeholder: 'The specific disciplines that need to change' },
      { key: 'bd_team_capability', label: 'Team and capability', type: 'textarea', placeholder: 'First BD hire, transition from founder-led selling, incentive approach' },
      { key: 'referral_expansion_approach', label: 'Referral and expansion approach', type: 'textarea', placeholder: 'Systematic approaches to referral activation and existing client growth' },
    ],
  },
  {
    key: 'marketing_brand', title: 'Marketing & Brand', icon: '📣', row: 2, colour: '#9D174D',
    type: 'marketing_dialogue',
    group: 'revenue_generation', colSpan: 6,
    description: 'Brand, channels, credibility, and demand generation — calibrated to the type of business and how it competes',
    current_fields: [
      { key: 'business_marketing_context', label: 'Business type', type: 'chips', options: ['B2B product', 'B2C product', 'B2B service', 'B2C service', 'Mixed'], multi: false },
      { key: 'competitive_position', label: 'Competitive position', type: 'chips', options: ['Primarily competing on price', 'Primarily competing on quality or expertise', 'Mixed'], multi: false },
      { key: 'brand_description', label: 'Brand and position', type: 'textarea', placeholder: 'How the business presents itself, what it stands for, and whether that is distinctive' },
      { key: 'brand_consistency', label: 'Brand consistency', type: 'chips', options: ['Consistent and distinctive across all touchpoints', 'Broadly consistent', 'Inconsistent or generic'], multi: false },
      { key: 'brand_alignment', label: 'Brand alignment with direction', type: 'chips', options: ['Strong — reflects where the business is going', 'Partial', 'Weak — left behind'], multi: false },
      { key: 'active_channels', label: 'Active marketing channels', type: 'chips', options: ['Website and SEO', 'Social media', 'Content and thought leadership', 'Email', 'Paid advertising', 'Reviews and social proof', 'Platform and marketplace presence', 'Events and networking', 'PR', 'Partnerships'], multi: true },
      { key: 'channel_effectiveness', label: 'Channel effectiveness', type: 'textarea', placeholder: 'What is working and what is not — and on what basis' },
      { key: 'credibility_signals', label: 'Credibility signals', type: 'chips', options: ['Strong — case studies, testimonials, awards visible', 'Some', 'Weak or absent'], multi: false,
        explainer: 'Credibility signals are what a prospect sees before they have met you — case studies, reviews, accreditations, awards, client logos. For quality competitors these are often the most important marketing lever.' },
      { key: 'marketing_measurement', label: 'Marketing measurement', type: 'chips', options: ['Measured regularly — clear view of what works', 'Some measurement', 'Not measured'], multi: false },
      { key: 'marketing_capability', label: 'Marketing capability', type: 'chips', options: ['Dedicated internal resource', 'Shared responsibility', 'Founder-led', 'Agency or freelancer', 'Mix'], multi: false },
      { key: 'marketing_budget', label: 'Marketing budget', type: 'chips', options: ['Defined budget', 'Ad hoc spend', 'Minimal or none'], multi: false },
      { key: 'ai_marketing', label: 'AI in marketing', type: 'textarea', placeholder: 'Tools currently in use or specific opportunities identified' },
      { key: 'bd_handoff', label: 'Marketing to BD handoff', type: 'chips', options: ['Explicit process — leads handled consistently', 'Broadly understood', 'Ad hoc'], multi: false },
      { key: 'marketing_challenges', label: 'Current challenges — marketing & brand', type: 'textarea', placeholder: 'The biggest marketing or brand challenge right now' },
    ],
    future_fields: [
      { key: 'brand_development', label: 'Brand development', type: 'textarea', placeholder: 'What needs to change about how the business presents itself' },
      { key: 'target_channel_mix', label: 'Target channel mix', type: 'textarea', placeholder: 'The one or two channels to invest in and why — calibrated to business type' },
      { key: 'credibility_building_plan', label: 'Credibility building plan', type: 'textarea', placeholder: 'How the business will build and display credibility signals — for quality and expertise competitors' },
      { key: 'marketing_capability_changes', label: 'Marketing capability changes', type: 'textarea', placeholder: 'Who does marketing and how that changes' },
      { key: 'marketing_investment', label: 'Marketing investment', type: 'textarea', placeholder: 'Budget and resource needed to execute the marketing strategy' },
      { key: 'ai_marketing_roadmap', label: 'AI marketing roadmap', type: 'textarea', placeholder: 'Specific AI tools or approaches planned' },
    ],
  },
];

// ── Completeness check ────────────────────────────────────────────
function isSectionFilled(sectionData, fields) {
  if (!sectionData) return false;
  // Org builder: check if any roles have been added
  if (sectionData.org) {
    return ['leadership', 'management', 'team'].some(layer => {
      const layerData = sectionData.org[layer];
      if (!layerData) return false;
      return Object.values(layerData).some(roles => Array.isArray(roles) && roles.some(r => r && r.trim()));
    });
  }
  // Culture dialogue: check for characterisation or key chips
  if (sectionData.culture_characterisation || sectionData.cultural_style || sectionData.decision_making) return true;
  return fields.some(f => {
    const v = sectionData[f.key];
    return v && (Array.isArray(v) ? v.length > 0 : String(v).trim().length > 0);
  });
}

// Get roles from an org object for a given layer + function index
function getOrgRoles(org, layer, fi) {
  return org?.[layer]?.[fi] || [];
}

// ── Org Builder ───────────────────────────────────────────────────
const ORG_LAYERS = [
  { key: 'leadership', label: 'Leadership', chipBg: '#E6F1FB', chipBorder: '#B5D4F4', chipText: '#0C447C', rowBg: '#f0f6fd' },
  { key: 'management', label: 'Management', chipBg: '#EEEDFE', chipBorder: '#CECBF6', chipText: '#3C3489', rowBg: '#f7f7fe' },
  { key: 'team',       label: 'Team',       chipBg: '#E1F5EE', chipBorder: '#9FE1CB', chipText: '#085041', rowBg: '#f0fdf8' },
];
const DEFAULT_ORG_FUNCTIONS = ['Sales', 'Delivery', 'Operations', 'Finance'];
const DEFAULT_ORG = {
  founder_title: 'Founder / MD',
  functions: [...DEFAULT_ORG_FUNCTIONS],
  leadership: {}, management: {}, team: {},
  advisors: [],
};

// ── Business leader time slider ────────────────────────────────────────────
// Uses the shared SplitSlider component — same drag-and-drop bar,
// per-segment inputs and chip toggle pattern as DecisionGuru.
const FOUNDER_TIME_OPTIONS = [
  { key: 'delivery', label: 'Delivery / client work', colour: '#1D9E75' },
  { key: 'sales',    label: 'Sales & business dev',   colour: '#185FA5' },
  { key: 'strategy', label: 'Strategy & planning',    colour: '#7C3AED' },
  { key: 'people',   label: 'People & leadership',    colour: '#C0556E' },
  { key: 'finance',  label: 'Finance & admin',        colour: '#BA7517' },
  { key: 'other',    label: 'Other',                  colour: '#888780' },
];

function FounderTimeSlider({ value, onChange }) {
  return (
    <SplitSlider
      label="Business leader time allocation"
      options={FOUNDER_TIME_OPTIONS}
      value={value}
      onChange={onChange}
      editableKeys={['other']}
    />
  );
}

function OrgBuilder({ value, onChange, currentOrg, label }) {
  const [adding, setAdding] = useState(null);
  const [addInput, setAddInput] = useState('');
  const [addingFn, setAddingFn] = useState(false);
  const [fnInput, setFnInput] = useState('');
  const [editingFounder, setEditingFounder] = useState(false);
  const [founderInput, setFounderInput] = useState('');
  const [addingAdvisor, setAddingAdvisor] = useState(false);
  const [advisorInput, setAdvisorInput] = useState('');

  const org = value && value.functions ? value : { ...DEFAULT_ORG };
  const functions = org.functions;
  const founderTitle = org.founder_title || 'Founder / MD';
  const advisors = org.advisors || [];

  const update = (next) => onChange(next);
  const getRoles = (layer, fi) => org[layer]?.[fi] || [];

  const addRole = (layer, fi, name) => {
    const roles = [...getRoles(layer, fi), name];
    update({ ...org, [layer]: { ...org[layer], [fi]: roles } });
  };

  const removeRole = (layer, fi, ri) => {
    const roles = getRoles(layer, fi).filter((_, i) => i !== ri);
    update({ ...org, [layer]: { ...org[layer], [fi]: roles } });
  };

  const addFunction = (name) => update({ ...org, functions: [...functions, name] });

  const removeFunction = (fi) => {
    const newFns = functions.filter((_, i) => i !== fi);
    const newOrg = { ...org, functions: newFns };
    ORG_LAYERS.forEach(l => {
      const rebuilt = {};
      newFns.forEach((_, newFi) => {
        const oldFi = newFi < fi ? newFi : newFi + 1;
        rebuilt[newFi] = org[l.key]?.[oldFi] || [];
      });
      newOrg[l.key] = rebuilt;
    });
    update(newOrg);
  };

  const addAdvisor = (name) => update({ ...org, advisors: [...advisors, name] });
  const removeAdvisor = (i) => update({ ...org, advisors: advisors.filter((_, idx) => idx !== i) });

  // Stats
  const totalRoles = ORG_LAYERS.reduce((t, l) =>
    t + functions.reduce((tt, _, fi) => tt + getRoles(l.key, fi).filter(r => r && r.trim()).length, 0), 0);
  const layerCounts = ORG_LAYERS.map(l =>
    functions.reduce((t, _, fi) => t + getRoles(l.key, fi).filter(r => r && r.trim()).length, 0));

  return (
    <div>
      {label && (
        <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#185FA5', marginBottom: '1rem' }}>
          {label}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {[
          { val: totalRoles + 1, label: 'Total roles', colour: '#1a1a2e' },
          ...ORG_LAYERS.map((l, i) => ({ val: layerCounts[i], label: l.label, colour: l.chipText })),
          { val: functions.length, label: 'Functions', colour: '#888' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#f7f7f9', borderRadius: 8, padding: '0.4rem 0.75rem' }}>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: s.colour, lineHeight: 1 }}>{s.val}</div>
            <div style={{ fontSize: '0.58rem', color: '#aaa', marginTop: 1 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Business leader / CEO row — spans full width above the grid */}
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: `${72 + functions.length * 110}px` }}>

          {/* Business leader bar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#1a1a2e', borderRadius: '10px 10px 0 0',
            padding: '0.5rem 1rem', gap: '0.5rem',
          }}>
            {editingFounder ? (
              <>
                <input autoFocus value={founderInput}
                  onChange={e => setFounderInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { update({ ...org, founder_title: founderInput.trim() || founderTitle }); setEditingFounder(false); }
                    if (e.key === 'Escape') setEditingFounder(false);
                  }}
                  onBlur={() => { update({ ...org, founder_title: founderInput.trim() || founderTitle }); setEditingFounder(false); }}
                  style={{ fontSize: '0.82rem', fontWeight: 700, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '0.25rem 0.6rem', color: 'white', fontFamily: 'inherit', textAlign: 'center', width: 220 }}
                />
              </>
            ) : (
              <>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'white', letterSpacing: '0.02em' }}>{founderTitle}</span>
                <button onClick={() => { setFounderInput(founderTitle); setEditingFounder(true); }}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem', cursor: 'pointer', padding: '0 0.25rem', width: 'auto', lineHeight: 1 }}
                  title="Edit title">✎</button>
              </>
            )}
          </div>

          {/* Main grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: `72px repeat(${functions.length}, minmax(110px, 1fr))`,
            border: '1px solid #f0f0f0', borderTop: 'none',
            borderRadius: '0 0 10px 10px', overflow: 'hidden',
          }}>
            {/* Function header row */}
            <div style={{ background: '#f7f7f9', padding: '0.5rem', borderBottom: '1px solid #f0f0f0' }} />
            {functions.map((fn, fi) => (
              <div key={fi} style={{ background: '#f7f7f9', padding: '0.45rem 0.5rem', borderBottom: '1px solid #f0f0f0', borderLeft: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#1a1a2e', textAlign: 'center', lineHeight: 1.2 }}>{fn}</span>
                {functions.length > 1 && (
                  <button onClick={() => removeFunction(fi)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: '0.7rem', padding: 0, width: 'auto', lineHeight: 1, flexShrink: 0 }}
                    title={`Remove ${fn}`}>×</button>
                )}
              </div>
            ))}

            {/* Layer rows */}
            {ORG_LAYERS.map((layer, li) => {
              const isLastRow = li === ORG_LAYERS.length - 1;
              return (
                <React.Fragment key={layer.key}>
                  <div style={{ background: layer.rowBg, padding: '0.6rem 0.4rem', borderBottom: isLastRow ? 'none' : '1px solid #f0f0f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                    <span style={{ fontSize: '0.62rem', fontWeight: 700, color: layer.chipText, textAlign: 'center', lineHeight: 1.3, writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: '0.04em' }}>
                      {layer.label}
                    </span>
                  </div>
                  {functions.map((_, fi) => {
                    const roles = getRoles(layer.key, fi);
                    const isAdding = adding?.layer === layer.key && adding?.fi === fi;
                    return (
                      <div key={fi} style={{ padding: '0.5rem', minHeight: 68, borderLeft: '1px solid #f0f0f0', borderBottom: isLastRow ? 'none' : '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 3, background: 'white' }}>
                        {roles.filter(r => r && r.trim()).map((role, ri) => {
                          const isNew = currentOrg && !getOrgRoles(currentOrg, layer.key, fi).includes(role);
                          return (
                            <div key={ri} style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '2px 4px 2px 7px', borderRadius: 20, fontSize: '0.68rem', background: isNew ? '#FEF3C7' : layer.chipBg, border: `0.5px solid ${isNew ? '#FCD34D' : layer.chipBorder}`, color: isNew ? '#92400E' : layer.chipText, lineHeight: 1.3 }}>
                              <span style={{ flex: 1 }}>{role}</span>
                              <button onClick={() => removeRole(layer.key, fi, ri)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '0.7rem', padding: 0, width: 'auto', opacity: 0.5, lineHeight: 1 }}>×</button>
                            </div>
                          );
                        })}
                        {isAdding ? (
                          <input autoFocus value={addInput} onChange={e => setAddInput(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && addInput.trim()) { addRole(layer.key, fi, addInput.trim()); setAddInput(''); setAdding(null); }
                              if (e.key === 'Escape') { setAdding(null); setAddInput(''); }
                            }}
                            onBlur={() => { if (addInput.trim()) addRole(layer.key, fi, addInput.trim()); setAddInput(''); setAdding(null); }}
                            placeholder="Role title…"
                            style={{ fontSize: '0.68rem', padding: '2px 5px', borderRadius: 5, border: '1px solid #ddd', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' }}
                          />
                        ) : (
                          <button onClick={() => setAdding({ layer: layer.key, fi })}
                            style={{ fontSize: '0.62rem', color: '#ccc', background: 'none', border: '0.5px dashed #e0e0e0', borderRadius: 4, padding: '2px 5px', cursor: 'pointer', width: 'fit-content', fontFamily: 'inherit' }}>
                            + add
                          </button>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>

          {/* Advisors / Board row */}
          <div style={{ marginTop: '0.75rem', border: '1px solid #f0f0f0', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ background: '#f7f7f9', padding: '0.4rem 0.75rem', borderBottom: advisors.length > 0 ? '1px solid #f0f0f0' : 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#555', flex: 1 }}>Advisors &amp; Board</span>
              <span style={{ fontSize: '0.6rem', color: '#aaa' }}>Non-executive, advisory or governance roles</span>
            </div>
            {advisors.length > 0 && (
              <div style={{ padding: '0.5rem 0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem', background: 'white' }}>
                {advisors.map((a, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 6px 2px 9px', borderRadius: 20, fontSize: '0.68rem', background: '#F1EFE8', border: '0.5px solid #D3D1C7', color: '#444441' }}>
                    <span>{a}</span>
                    <button onClick={() => removeAdvisor(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '0.7rem', padding: 0, width: 'auto', opacity: 0.5, lineHeight: 1 }}>×</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ padding: '0.4rem 0.75rem', background: advisors.length > 0 ? '#fafafa' : 'white', borderTop: advisors.length > 0 ? '1px solid #f0f0f0' : 'none' }}>
              {addingAdvisor ? (
                <input autoFocus value={advisorInput} onChange={e => setAdvisorInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && advisorInput.trim()) { addAdvisor(advisorInput.trim()); setAdvisorInput(''); setAddingAdvisor(false); }
                    if (e.key === 'Escape') { setAddingAdvisor(false); setAdvisorInput(''); }
                  }}
                  onBlur={() => { if (advisorInput.trim()) addAdvisor(advisorInput.trim()); setAdvisorInput(''); setAddingAdvisor(false); }}
                  placeholder="e.g. Non-Executive Director, Industry Advisor, Investor…"
                  style={{ fontSize: '0.72rem', padding: '0.25rem 0.5rem', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' }}
                />
              ) : (
                <button onClick={() => setAddingAdvisor(true)}
                  style={{ fontSize: '0.68rem', color: '#888', background: 'none', border: '0.5px dashed #d0d0d0', borderRadius: 4, padding: '2px 7px', cursor: 'pointer', width: 'fit-content', fontFamily: 'inherit' }}>
                  + Add advisor or board member
                </button>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Add function */}
      <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {addingFn ? (
          <>
            <input autoFocus value={fnInput} onChange={e => setFnInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && fnInput.trim()) { addFunction(fnInput.trim()); setFnInput(''); setAddingFn(false); }
                if (e.key === 'Escape') { setAddingFn(false); setFnInput(''); }
              }}
              onBlur={() => { if (fnInput.trim()) addFunction(fnInput.trim()); setFnInput(''); setAddingFn(false); }}
              placeholder="Function name…"
              style={{ fontSize: '0.78rem', padding: '0.35rem 0.6rem', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', width: 180 }}
            />
            <button onClick={() => { setAddingFn(false); setFnInput(''); }}
              style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.78rem', width: 'auto' }}>Cancel</button>
          </>
        ) : (
          <button onClick={() => setAddingFn(true)}
            style={{ fontSize: '0.75rem', color: '#185FA5', background: 'none', border: '1px solid #185FA520', borderRadius: 6, padding: '0.3rem 0.7rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            + Add function
          </button>
        )}
        {currentOrg && (
          <span style={{ fontSize: '0.62rem', color: '#aaa', marginLeft: '0.25rem' }}>Amber = new in future state</span>
        )}
      </div>
    </div>
  );
}

// ── Culture & Leadership dialogue guidance ────────────────────────
// Mirrors the SECTION_GUIDANCE pattern from StrategyCanvas — each
// dialogue has an objective, probe guidance, archetype and output spec.
// These shape both the opening and followup system prompts.

const DIALOGUE_GUIDANCE = {
  culture: {
    colour: '#7C3AED',
    bgColour: '#F5F3FF',
    borderColour: '#C4B5FD',
    title: 'Culture dialogue',
    characterisationKey: 'culture_characterisation',
    transcriptKey: '_culture_transcript',
    distinction: 'Culture is behaviour, not aspiration — what colleagues and customers would describe if asked how this business actually works. This is distinct from Values (what you believe) and from Leadership (how leaders specifically show up).',
    objective: 'Surface an honest, specific characterisation of how this business actually operates day-to-day. Not labels or aspirations — real behavioural patterns with evidence.',
    why_it_matters: 'Culture is the invisible operating system. Understanding it honestly is essential for identifying what to preserve, what to change, and what is blocking scale.',
    good_answer_looks_like: 'Specific, observable examples with frequency or format. Not "we\'re transparent" but "revenue numbers go to the whole team in a monthly all-hands." Not "we move fast" but "we typically go from idea to decision in a week."',
    probe_deeper_on: 'Any answer using a generic adjective without a concrete example — ask "Can you give me a recent specific example of that?" If an answer reads as aspirational: "Is that how it is now, or how you\'d like it to be?" If an answer sounds too comfortable: "What would a sceptic say about that?"',
    themes: [
      'start with shadow — who didn\'t fit and why (surfaces real culture faster than positive questions)',
      'what gets celebrated or noticed — formally and informally',
      'how decisions actually get made day-to-day (not the theory, the practice)',
      'information flow and transparency — what the team actually knows',
      'pace, pressure and atmosphere — what it actually feels like to work here',
    ],
    opening: 'Think about someone who worked here and didn\'t last — not because they lacked skill, but because they didn\'t fit. What was it about how they operated that created the problem?',
    extract_instruction: `Extract culture data. Return ONLY valid JSON starting with {:
{
  "decision_making": one of: "Founder decides most things" | "Small leadership team decides" | "Consensus-driven" | "Delegated to functions",
  "transparency": one of: "Open book — team knows everything" | "Selective — key info shared" | "Need-to-know — minimal sharing",
  "team_empowerment": one of: "High — people act autonomously" | "Moderate — some autonomy" | "Low — most things go through business leader",
  "what_celebrated": string — specific examples of what gets noticed and praised,
  "what_not_tolerated": string — specific examples of what would get someone into trouble,
  "culture_evidence": string — the single most illustrative example of this culture in practice,
  "culture_challenges": string — the business leader's biggest culture or leadership challenge right now, in their own words. Omit if not discussed,
  "culture_characterisation": string — 3-5 sentence honest paragraph in third person. Specific and grounded. Capture the actual texture of how this business operates. Not flattering.
}
Omit any field not clearly evidenced by the conversation.`,
  },
  leadership: {
    colour: '#185FA5',
    bgColour: '#EBF4FB',
    borderColour: '#B5D4F4',
    title: 'Leadership dialogue',
    characterisationKey: 'leadership_characterisation',
    transcriptKey: '_leadership_transcript',
    distinction: 'Leadership characterisation is about how those in leadership positions actually show up — their visibility, consistency, communication patterns and whether they are building leadership capacity around them. Distinct from Culture (which describes the whole business) and from Values (principles).',
    objective: 'Characterise how leadership actually shows up in the business — not the theory, but what the team experiences day to day.',
    why_it_matters: 'Leadership behaviour sets the ceiling for culture. You cannot build a culture that exceeds what leadership models. Understanding how leadership currently shows up is essential for knowing what needs to change.',
    good_answer_looks_like: 'Specific frequency and format. Not "I\'m very visible" but "I do a 10-minute check-in with the full team every Monday." Not "we empower the team" but "our project managers can approve spend up to £5k without coming to me."',
    probe_deeper_on: '"I\'m hands-on" (hands-on doing what exactly?), "We empower the team" (what is the biggest decision a team member made without you recently?), "I\'m very accessible" (what does that actually look like in practice?).',
    themes: [
      'decision pattern — when the business leader is unavailable, who decides and on what basis',
      'leadership visibility and presence — the actual pattern, not the aspiration',
      'empowerment — what can people genuinely decide without leadership approval',
      'communication consistency — how the team knows what matters and what\'s changed',
      'leadership development — who is growing into more responsibility and what\'s being done about it',
    ],
    opening: 'Walk me through a typical week — when and how do you actually interact with your team? I want the real pattern, not the version you aspire to.',
    extract_instruction: `Extract leadership data. Return ONLY valid JSON starting with {:
{
  "leadership_visibility": one of: "Very visible — daily presence" | "Moderate — regular structured check-ins" | "Hands-off — team runs itself",
  "empowerment_style": one of: "High — team makes most decisions" | "Moderate — shared decision-making" | "Low — leader decides most things",
  "communication_pattern": one of: "Consistent and structured" | "Regular but informal" | "Ad hoc — as needed",
  "leadership_development": one of: "Actively developing next tier" | "Some development happening" | "Not yet a focus",
  "leadership_evidence": string — the single most illustrative example of the leadership style in practice,
  "leadership_characterisation": string — 3-5 sentence honest paragraph in third person. Observable and specific. How leadership shows up, not how it intends to.
}
Omit any field not clearly evidenced by the conversation.`,
  },
};

// ── Single dialogue (reusable) ────────────────────────────────────
// Handles one dialogue thread (culture OR leadership) parameterised
// by a guidance object. Follows the ExploreEditor pattern from
// StrategyCanvas: open → chat → extract → review → save.

function SingleDialogue({ guidance, currentData, onChange, isFutureState, sectionKey }) {
  const priorTranscript = currentData?.[guidance.transcriptKey];
  const hasPrior = !!(priorTranscript?.messages?.length > 0);
  const hasCharacterisation = !!(currentData?.[guidance.characterisationKey]);
  // entry_choice if prior data, intro if first time, chatting never starts directly
  const initialPhase = (hasPrior || hasCharacterisation) ? 'entry_choice' : 'intro';

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState(initialPhase);
  const [editedData, setEditedData] = useState(null);
  const chatContainerRef = useRef(null);
  const questionCount = useRef(0);
  const directionContextBlock = buildDirectionContextBlock(readDirectionCanvas());

  // Evaluation flow — SingleDialogue is used for culture + leadership
  // inside the culture_leadership section. Both halves trigger their
  // own eval modal on save (the latest one wins).
  const sectionMeta = OM_SECTIONS.find(s => s.key === sectionKey);
  const evalFlow = useEvaluationFlow({
    sectionKey,
    sectionTitle: sectionMeta?.title || sectionKey,
    sectionFields: sectionMeta?.current_fields || [],
    repeatable: false,
  });

  useEffect(() => {
    // Don't auto-start — the intro phase handles the start button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  function buildOpeningPrompt() {
    const stateContext = isFutureState
      ? `This is about the FUTURE STATE — what the business leader wants to build. All questions should be aspirational and forward-looking. Use future tense throughout: "What do you want...", "Where are you heading...", "What will success look like...". Do not ask about the current reality.`
      : `This is about the CURRENT STATE — how the business actually operates today. Be specific and grounded. Push back on aspirational answers and ask what is actually happening now.`;
    const openingQ = isFutureState
      ? (guidance.title === 'Culture dialogue'
          ? `When you picture this business in three years — how decisions get made, how the team interacts, the atmosphere day to day — what's the most important thing that would feel different from today?`
          : `In the business you're building — how do you want leadership to show up differently from how it does now? What's the most important shift you're aiming for?`)
      : guidance.opening;

    return `You are GuruOne, a strategic advisor helping a business leader characterise their business ${guidance.title.replace(' dialogue', '').toLowerCase()}.${directionContextBlock}

${stateContext}

OBJECTIVE
${guidance.objective}

WHY THIS MATTERS
${guidance.why_it_matters}

WHAT A GOOD ANSWER LOOKS LIKE
${isFutureState ? 'Specific aspirations with a concrete picture — not "more empowered" but "team members will be able to sign off decisions up to £10k without approval." Not "better communication" but "we\'ll have a weekly all-hands where strategy progress is shared openly."' : guidance.good_answer_looks_like}

SURFACE-LEVEL ANSWERS TO PROBE DEEPER ON
${isFutureState ? 'Vague aspirations without specifics. Ask: "What would that actually look like in practice?" or "How will you know you\'ve achieved that?" Push for concrete pictures of the future, not adjectives.' : guidance.probe_deeper_on}

THEMES TO COVER (in roughly this order)
${guidance.themes.map((t, i) => `${i + 1}. ${isFutureState ? `Future: ${t}` : t}`).join('\n')}

CRITICAL RULES:
1. Ask ONE warm, direct opening question — specific, not generic.
2. Wait for the answer before asking the next question.
3. If an answer is vague, probe for a concrete picture before moving on.
4. Be concise — 2–3 sentences of framing then the question.
5. ${isFutureState ? 'Keep all framing aspirational and future-tense throughout.' : 'Keep all questions grounded in current reality, not aspirations.'}

Opening question: ${openingQ}`;
  }

  function buildFollowupPrompt() {
    return `You are GuruOne, continuing a ${guidance.title.replace(' dialogue', '').toLowerCase()} ${isFutureState ? 'future state ' : ''}characterisation conversation.${directionContextBlock}

${isFutureState ? 'IMPORTANT: This is about the FUTURE STATE the business leader wants to build. Keep all questions aspirational and future-tense. Do not ask about current reality. Ask about what they want, where they are heading, what success looks like.' : 'IMPORTANT: This is about the CURRENT STATE. Keep all questions grounded in what is actually happening now. Push back on aspirational answers.'}

SECTION OBJECTIVE
${guidance.objective}

THEMES TO COVER
${guidance.themes.map((t, i) => `${i + 1}. ${t}`).join('\n')}

THINGS WORTH PROBING DEEPER ON
${isFutureState ? 'Vague aspirations without specifics. Ask: "What would that look like in practice?" or "How will you know when you\'ve achieved that?" Push for concrete pictures.' : guidance.probe_deeper_on}

COVERAGE CHECK — before every response:
Review the conversation and note which themes have been addressed. If any theme has no answer, ask about it next.
Phase 1 (Breadth): If uncovered themes remain, ask about them.
Phase 2 (Depth): Once all themes have initial answers, probe for specifics and concrete pictures.

You have asked ${questionCount.current} question(s) so far.

When every theme has a substantive answer, emit READY_TO_SUMMARISE on its own line.

Rules:
- ONE question per turn. Two only when genuinely independent.
- Keep responses to 2–4 sentences plus the question.
- ${isFutureState ? 'Use future tense throughout — "will", "want to", "aiming for".' : 'Ground every question in present reality.'}`;
  }

  async function startConversation() {
    setLoading(true);
    setPhase('chatting');
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: [{ role: 'user', content: `Start the ${guidance.title.toLowerCase()}.` }],
        context: buildOpeningPrompt(),
      });
      setMessages([{ role: 'assistant', content: res.data.reply }]);
      questionCount.current = 1;
    } catch {
      setMessages([{ role: 'assistant', content: guidance.opening }]);
      questionCount.current = 1;
    }
    setLoading(false);
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      questionCount.current += 1;
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: newMessages,
        context: buildFollowupPrompt() + DIALOGUE_RULES_BLOCK,
      });
      const reply = res.data.reply || '';
      const isReady = reply.includes('READY_TO_SUMMARISE');

      const deferredQuestion = isReady ? maybeDeferSummariseForPendingQuestion(reply, questionCount.current, 8) : null;

      if (deferredQuestion) {

        setMessages(prev => [...prev, { role: 'assistant', content: deferredQuestion }]);

      } else if (isReady || questionCount.current >= 8) {
        track('design_conversation_completed', { section: 'culture_leadership' });
        const clean = reply.replace(/READY_TO_SUMMARISE/g, '').trim();
        const convo = clean ? [...newMessages, { role: 'assistant', content: clean }] : newMessages;
        if (clean) setMessages(prev => [...prev, { role: 'assistant', content: clean }]);
        await extractData(convo);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong — please try again.' }]);
    }
    setLoading(false);
  }

  async function extractData(convo) {
    setMessages(prev => [...prev, { role: 'assistant', content: "Good — I have enough to work with. Let me synthesise what you've shared into a characterisation. You'll review and edit everything before saving." }]);
    setPhase('extracting');

    const founderAnswers = convo.filter(m => m.role === 'user').map((m, i) => `Answer ${i + 1}: ${m.content}`).join('\n\n');
    const condensed = [{ role: 'user', content: `Here are the business leader's answers:\n\n${founderAnswers}\n\nExtract the structured data now.` }];

    const extractInstruction = isFutureState
      ? guidance.extract_instruction.replace(
          /honest paragraph.*?operates\./,
          'aspirational paragraph describing the culture this business leader wants to build. Future tense. Specific and concrete — not "more collaborative" but what that will actually look like.'
        ).replace(
          /how leadership shows up in this business.*?specific\./,
          'aspirational paragraph describing the leadership style this business leader wants to embody. Future tense. Observable and specific.'
        )
      : guidance.extract_instruction;

    try {
      const res = await axios.post(`${CANVAS_API}/chat`, { messages: condensed, context: extractInstruction });
      const raw = res.data.reply || '';
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1 && e !== -1) {
        const parsed = JSON.parse(raw.slice(s, e + 1));
        setEditedData(parsed);
        setPhase('reviewing');
        return;
      }
    } catch {}
    setPhase('entry_choice');
    setMessages(prev => [...prev, { role: 'assistant', content: "I wasn't able to extract automatically this time. Use Quick entry to fill in the fields directly." }]);
  }

  function handleSave() {
    // Future state and "no editedData" both bypass eval and commit
    // directly — eval is current-state only and needs a payload.
    const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
    if (isFutureState || !editedData) {
      onChange({ ...currentData, ...editedData, [guidance.transcriptKey]: transcript });
      setPhase('saved');
      return;
    }
    evalFlow.runEvaluation({ fields: editedData, transcript });
  }

  const colour = guidance.colour;

  if (phase === 'intro') {
    return (
      <div style={{ background: guidance.bgColour, border: `1px solid ${guidance.borderColour}`, borderRadius: 10, padding: '1.25rem 1.5rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: guidance.colour, marginBottom: '0.75rem' }}>
          What this conversation does
        </div>
        <p style={{ fontSize: '0.82rem', color: '#333', lineHeight: 1.6, margin: '0 0 0.75rem' }}>
          {guidance.objective}
        </p>
        <p style={{ fontSize: '0.75rem', color: '#666', lineHeight: 1.55, margin: '0 0 1rem' }}>
          {guidance.why_it_matters}
        </p>
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, color: guidance.colour, marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Themes we'll explore
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            {guidance.themes.map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.75rem', color: '#555' }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: guidance.colour, flexShrink: 0, marginTop: 5 }} />
                {t}
              </div>
            ))}
          </div>
        </div>
        <div style={{ fontSize: '0.68rem', color: '#888', lineHeight: 1.5, marginBottom: '1.25rem', borderTop: `1px solid ${guidance.borderColour}`, paddingTop: '0.75rem' }}>
          <strong>How it works:</strong> GuruOne will ask one question at a time and probe for specific examples — not generic labels. At the end it synthesises a characterisation paragraph that you review and edit before saving. Typically 6–8 exchanges.
        </div>
        <button onClick={() => { setPhase('chatting'); startConversation(); }}
          style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: guidance.colour, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
          Start {guidance.title.toLowerCase()} →
        </button>
      </div>
    );
  }

  if (phase === 'entry_choice') {
    const charText = currentData?.[guidance.characterisationKey];
    return (
      <div>
        {charText && (
          <div style={{ background: guidance.bgColour, border: `1px solid ${guidance.borderColour}`, borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.4rem' }}>Characterisation</div>
            <p style={{ fontSize: '0.82rem', color: colour === '#7C3AED' ? '#3C3489' : '#0C447C', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>"{charText}"</p>
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {hasPrior ? (
            <>
              <button onClick={() => { setMessages(priorTranscript.messages || []); questionCount.current = priorTranscript.questionCount || 0; setPhase('chatting'); }}
                style={{ padding: '0.5rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
                Continue conversation
              </button>
              <button onClick={() => setPhase('intro')}
                style={{ padding: '0.5rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${colour}`, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
                Start fresh
              </button>
            </>
          ) : (
            <button onClick={() => { setPhase('chatting'); startConversation(); }}
              style={{ padding: '0.5rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              {charText ? 'Refine through dialogue' : 'Start dialogue'}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'saved') {
    const charText = currentData?.[guidance.characterisationKey];
    return (
      <div>
        <div style={{ background: guidance.bgColour, border: `1px solid ${guidance.borderColour}`, borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.4rem' }}>
            Characterisation — saved
          </div>
          {charText && <p style={{ fontSize: '0.82rem', color: colour === '#7C3AED' ? '#3C3489' : '#0C447C', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>"{charText}"</p>}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={() => setPhase('intro')}
            style={{ background: 'none', border: 'none', color: colour, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', padding: '0.5rem 0', width: 'auto' }}>
            Refine characterisation →
          </button>
          {hasPrior && (
            <button onClick={() => { setMessages(priorTranscript.messages || []); questionCount.current = priorTranscript.questionCount || 0; setPhase('chatting'); }}
              style={{ padding: '0.5rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${colour}`, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Continue conversation
            </button>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'extracting') {
    return (
      <div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {messages.slice(-2).map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '0.6rem 0.9rem', borderRadius: 10, background: m.role === 'user' ? colour : guidance.bgColour, color: m.role === 'user' ? 'white' : (colour === '#7C3AED' ? '#3C3489' : '#0C447C'), fontSize: '0.8rem', lineHeight: 1.5 }}>{m.content}</div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: colour, fontSize: '0.8rem' }}>
          <div style={{ width: 14, height: 14, border: `1.5px solid ${guidance.borderColour}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          Synthesising your characterisation…
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  if (phase === 'reviewing' && editedData) {
    const charField = guidance.title === 'Culture dialogue' ? 'culture_characterisation' : 'leadership_characterisation';
    const evidenceField = guidance.title === 'Culture dialogue' ? 'culture_evidence' : 'leadership_evidence';
    const chipFields = guidance.title === 'Culture dialogue'
      ? [{ key: 'decision_making', options: ['Founder decides most things','Small leadership team decides','Consensus-driven','Delegated to functions'], label: 'Decision-making' },
         { key: 'transparency', options: ['Open book — team knows everything','Selective — key info shared','Need-to-know — minimal sharing'], label: 'Transparency' },
         { key: 'team_empowerment', options: ['High — people act autonomously','Moderate — some autonomy','Low — most things go through business leader'], label: 'Empowerment' }]
      : [{ key: 'leadership_visibility', options: ['Very visible — daily presence','Moderate — regular structured check-ins','Hands-off — team runs itself'], label: 'Visibility' },
         { key: 'empowerment_style', options: ['High — team makes most decisions','Moderate — shared decision-making','Low — leader decides most things'], label: 'Style' },
         { key: 'communication_pattern', options: ['Consistent and structured','Regular but informal','Ad hoc — as needed'], label: 'Communication' },
         { key: 'leadership_development', options: ['Actively developing next tier','Some development happening','Not yet a focus'], label: 'Development' }];

    return (
      <div>
        <DialogueEvalModal
          sectionMeta={sectionMeta}
          evalFlow={evalFlow}
          onCommitWithEval={(evaluation) => {
            const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
            const payload = { ...currentData, ...editedData, [guidance.transcriptKey]: transcript };
            if (evaluation) payload._evaluation = evaluation;
            onChange(payload);
            setPhase('saved');
          }}
          onRefine={() => setPhase('chatting')}
        />
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colour, marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Review &amp; edit</div>
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 3 }}>Characterisation</div>
          <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: 5 }}>AI-drafted from your conversation — edit until it precisely captures reality</div>
          <textarea value={editedData[charField] || ''} onChange={e => setEditedData(p => ({ ...p, [charField]: e.target.value }))} rows={4}
            style={{ width: '100%', fontSize: '0.82rem', padding: '0.65rem 0.8rem', borderRadius: 8, border: `1.5px solid ${guidance.borderColour}`, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
          {chipFields.map(f => (
            <div key={f.key}>
              <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.35rem' }}>{f.label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                {f.options.map(opt => {
                  const active = editedData[f.key] === opt;
                  return (
                    <button key={opt} onClick={() => setEditedData(p => ({ ...p, [f.key]: active ? '' : opt }))}
                      style={{ padding: '0.2rem 0.55rem', borderRadius: 20, fontSize: '0.68rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? guidance.bgColour : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto', transition: 'all 0.1s' }}>
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {[{ key: evidenceField, label: 'Key evidence point', placeholder: 'One specific example that best illustrates this' },
          ...(guidance.title === 'Culture dialogue'
            ? [{ key: 'what_celebrated', label: 'What gets celebrated', placeholder: '' }, { key: 'what_not_tolerated', label: 'What is not tolerated', placeholder: '' }, { key: 'culture_challenges', label: 'Current challenges', placeholder: 'Biggest culture or leadership challenge right now' }]
            : [])].map(f => (
          <div key={f.key} style={{ marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 3 }}>{f.label}</div>
            <textarea value={editedData[f.key] || ''} onChange={e => setEditedData(p => ({ ...p, [f.key]: e.target.value }))} rows={2} placeholder={f.placeholder}
              style={{ width: '100%', fontSize: '0.78rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
        ))}
        <div style={{ display: 'flex', gap: '0.6rem' }}>
          <button onClick={handleSave} style={{ padding: '0.6rem 1.25rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>Save characterisation</button>
          <button onClick={() => setPhase('chatting')} style={{ padding: '0.6rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${guidance.borderColour}`, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>Continue conversation</button>
        </div>
      </div>
    );
  }

  // Chatting phase
  return (
    <div>
      <div ref={chatContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '0.85rem', maxHeight: 320, overflowY: 'auto', paddingRight: '0.2rem' }}>
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          return (
            <div key={i} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '88%', padding: '0.6rem 0.85rem', borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px', background: isUser ? colour : guidance.bgColour, color: isUser ? 'white' : (colour === '#7C3AED' ? '#3C3489' : '#0C447C'), fontSize: '0.82rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              {m.content}
            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: colour, fontStyle: 'italic' }}>
            <div style={{ width: 11, height: 11, border: `1.5px solid ${guidance.borderColour}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            GuruOne is thinking…
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type your answer… (Enter to send)"
          disabled={loading} rows={2}
          style={{ flex: 1, fontSize: '0.82rem', padding: '0.6rem 0.8rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          style={{ padding: '0.6rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
          Send
        </button>
      </div>
      <div style={{ marginTop: '0.5rem', fontSize: '0.6rem', color: '#bbb' }}>
        Covers: {guidance.themes.join(' · ')}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ── Culture Dialogue (dual wrapper) ───────────────────────────────
// Shows a distinction callout and two sub-dialogues: Culture + Leadership.

function CultureDialogue({ currentData, onChange, isFutureState, sectionKey = 'culture_leadership' }) {
  return (
    <div>
      {/* Values distinction callout */}
      <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '0.65rem 0.9rem', marginBottom: '1.5rem' }}>
        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#92400E', marginBottom: '0.25rem' }}>How this differs from Values</div>
        <div style={{ fontSize: '0.68rem', color: '#78350F', lineHeight: 1.55 }}>
          <strong>Values</strong> = what you believe and stand for — principles. Captured in Direction Guru.{' '}
          <strong>Culture</strong> = how those values actually manifest in day-to-day behaviour.{' '}
          <strong>Leadership</strong> = how those in leadership positions specifically show up, communicate and empower.
        </div>
      </div>

      {/* Culture dialogue — always visible */}
      <div style={{ marginBottom: '1.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem', paddingBottom: '0.6rem', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ width: 8, height: 28, borderRadius: 4, background: DIALOGUE_GUIDANCE.culture.colour, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>{DIALOGUE_GUIDANCE.culture.title}</div>
            <div style={{ fontSize: '0.65rem', color: '#888', lineHeight: 1.4 }}>{DIALOGUE_GUIDANCE.culture.distinction}</div>
          </div>
          {currentData?.[DIALOGUE_GUIDANCE.culture.characterisationKey] && (
            <span style={{ marginLeft: 'auto', fontSize: '0.62rem', background: '#E1F5EE', color: '#0F6E56', padding: '2px 8px', borderRadius: 20, fontWeight: 600, flexShrink: 0 }}>✓ Done</span>
          )}
        </div>
        <SingleDialogue
          guidance={DIALOGUE_GUIDANCE.culture}
          currentData={currentData}
          onChange={onChange}
          isFutureState={isFutureState}
          sectionKey={sectionKey}
        />
      </div>

      {/* Leadership dialogue — always visible */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem', paddingBottom: '0.6rem', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ width: 8, height: 28, borderRadius: 4, background: DIALOGUE_GUIDANCE.leadership.colour, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>{DIALOGUE_GUIDANCE.leadership.title}</div>
            <div style={{ fontSize: '0.65rem', color: '#888', lineHeight: 1.4 }}>{DIALOGUE_GUIDANCE.leadership.distinction}</div>
          </div>
          {currentData?.[DIALOGUE_GUIDANCE.leadership.characterisationKey] && (
            <span style={{ marginLeft: 'auto', fontSize: '0.62rem', background: '#E1F5EE', color: '#0F6E56', padding: '2px 8px', borderRadius: 20, fontWeight: 600, flexShrink: 0 }}>✓ Done</span>
          )}
        </div>
        <SingleDialogue
          guidance={DIALOGUE_GUIDANCE.leadership}
          currentData={currentData}
          onChange={onChange}
          isFutureState={isFutureState}
          sectionKey={sectionKey}
        />
      </div>
    </div>
  );
}


// ── Field editor ──────────────────────────────────────────────────
function FieldGroup({ fields, data, onChange }) {
  return (
    <div>
      {fields.map(f => {
        const value = data[f.key] || '';
        const update = (v) => onChange({ ...data, [f.key]: v });
        if (f.type === 'chips') {
          const vals = Array.isArray(value) ? value : (value ? [value] : []);
          return (
            <div key={f.key} style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 6 }}>{f.label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {f.options.map(opt => {
                  const active = f.multi ? vals.includes(opt) : value === opt;
                  return (
                    <button key={opt} onClick={() => update(f.multi ? (active ? vals.filter(v => v !== opt) : [...vals, opt]) : (active ? '' : opt))}
                      style={{ padding: '0.3rem 0.75rem', borderRadius: 20, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? '1.5px solid #1D9E75' : '1.5px solid #e0e0e0', background: active ? '#E1F5EE' : 'white', color: active ? '#0F6E56' : '#555', width: 'auto', fontWeight: active ? 600 : 400, transition: 'all 0.12s' }}>
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }
        if (f.type === 'percentage_slider') return (
          <div key={f.key} style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{f.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input type="range" min="0" max="100" step="5"
                value={value || 0}
                onChange={e => update(Number(e.target.value))}
                style={{ flex: 1, cursor: 'pointer', accentColor: '#1D9E75' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                <input type="number" min="0" max="100"
                  value={value || 0}
                  onChange={e => update(Math.min(100, Math.max(0, Number(e.target.value))))}
                  style={{ width: 52, fontSize: '0.82rem', padding: '0.3rem 0.4rem', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', textAlign: 'center', boxSizing: 'border-box' }} />
                <span style={{ fontSize: '0.82rem', color: '#555' }}>%</span>
              </div>
            </div>
          </div>
        );
        if (f.type === 'textarea') return (
          <div key={f.key} style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{f.label}</div>
            <textarea value={value} onChange={e => update(e.target.value)} placeholder={f.placeholder || ''} rows={3}
              style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
        );
        return (
          <div key={f.key} style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{f.label}</div>
            <input type="text" value={value} onChange={e => update(e.target.value)} placeholder={f.placeholder || ''}
              style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', boxSizing: 'border-box' }} />
          </div>
        );
      })}
    </div>
  );
}

// ── PeopleDialogue ────────────────────────────────────────────────
// Standalone dialogue for the people_roles (org_builder) section.
// Mirrors the SingleDialogue pattern: phase machine, /api/canvas/chat
// endpoint (which auto-injects PRINCIPLES_PROMPT), READY_TO_SUMMARISE
// marker. Differences vs SingleDialogue: no challenger mode, structured
// extracted fields (not a single characterisation paragraph), and a
// Decision Guru handoff button surfaced after save when the LLM
// flagged a real decision via decision_guru_topic.
//
// Props: { currentData, onChange, isFutureState, hasFutureState }
//   currentData      — the data sub-object the dialogue edits (current or future)
//   onChange         — wrapped by parent to write back to the right slot
//   isFutureState    — false for current-state tab, true for future-state tab
//   hasFutureState   — when isFutureState is true, signals Mode B (review existing)
//                      vs Mode A (greenfield design)

const PEOPLE_DIALOGUE_COLOUR = '#185FA5';
const PEOPLE_DIALOGUE_BG = '#EBF4FB';
const PEOPLE_DIALOGUE_BORDER = '#B5D4F4';

const PEOPLE_FIELD_LABELS_CURRENT = {
  role_definition_status: 'Role definitions',
  responsibilities_documented: 'Responsibilities documented',
  decision_rights: 'Decision rights',
  key_gaps: 'Capability gaps',
  team_challenges: 'Current challenges',
};
const PEOPLE_FIELD_LABELS_FUTURE = {
  hiring_priorities: 'Priority hires',
  founder_role: 'Business leader role shift',
  structural_changes: 'Structural changes',
};
const PEOPLE_CHIP_OPTIONS = {
  role_definition_status: ['Fully defined — all roles documented', 'Defined for some roles', 'Ad hoc — roles understood but not documented'],
  responsibilities_documented: ['Documented for all roles', 'Documented for some roles', 'Not yet documented'],
  decision_rights: ['Formalised and documented', 'Informally understood', 'Not yet defined'],
};

// Derive a compact team-profile summary from the saved org chart so
// the LLM doesn't ask sole-operator-irrelevant questions ("what
// doesn't get done when you step back" / "who decides when you're
// out"). Reads currentData.org as built by the OrgBuilder above.
// Returns null when there's no usable org structure to read.
function summariseOrgProfile(org) {
  if (!org || typeof org !== 'object') return null;
  const functions = Array.isArray(org.functions) ? org.functions : [];
  // ORG_LAYERS would tell us the role keys but for a quick count we
  // walk every key on the org object and total nested arrays of roles.
  let roleCount = 0;
  for (const k of Object.keys(org)) {
    if (k === 'functions' || k === 'founder_title' || k === 'advisors') continue;
    const layer = org[k];
    if (layer && typeof layer === 'object') {
      for (const fi of Object.keys(layer)) {
        if (Array.isArray(layer[fi])) roleCount += layer[fi].length;
      }
    }
  }
  const advisorCount = Array.isArray(org.advisors) ? org.advisors.length : 0;
  const totalPeople = 1 + roleCount; // founder + filled roles
  const isSoleOperator = roleCount === 0;
  const isMicro = roleCount > 0 && totalPeople <= 5;
  const isSmall = totalPeople > 5 && totalPeople <= 15;
  // isScaling captured by the else branch when generating the size label
  let sizeLabel;
  if (isSoleOperator) sizeLabel = 'sole operator';
  else if (isMicro) sizeLabel = `micro team (${totalPeople} people)`;
  else if (isSmall) sizeLabel = `small team (${totalPeople} people)`;
  else sizeLabel = `${totalPeople}+ people`;
  return {
    sizeLabel,
    isSoleOperator,
    totalPeople,
    roleCount,
    advisorCount,
    functions,
  };
}

function buildOrgProfileBlock(profile) {
  if (!profile) return '';
  const lines = [`TEAM PROFILE (from the leader's saved org chart):`, `- Current size: ${profile.sizeLabel}`];
  if (profile.functions.length > 0) lines.push(`- Functions: ${profile.functions.join(', ')}`);
  if (profile.advisorCount > 0) lines.push(`- Advisors: ${profile.advisorCount}`);
  if (profile.isSoleOperator) {
    lines.push('');
    lines.push('IMPORTANT — SOLE OPERATOR ADAPTATION:');
    lines.push('This leader runs the business alone. Many standard people/structure questions are nonsensical for a sole operator. Skip questions about:');
    lines.push("- delegation, decision rights, what gets done when they step back, work only they can do");
    lines.push("- formalisation of others' roles, documentation of other people's responsibilities, team challenges with people");
    lines.push("Instead, lean into: what they personally are stretched on, what would they hire FIRST and why, what's blocking them from bringing someone in (cost / readiness / right person), how they currently handle holidays/illness, and what advisors or fractional help they already use. Treat them as an individual who has chosen this scale or is on the way to scaling — not as a leader of an absent team.");
  }
  return lines.join('\n');
}

function PeopleDialogue({ currentData, onChange, isFutureState, hasFutureState, sectionKey }) {
  const TRANSCRIPT_KEY = isFutureState ? '_people_future_transcript' : '_people_current_transcript';
  const priorTranscript = currentData?.[TRANSCRIPT_KEY];
  const hasPrior = !!(priorTranscript?.messages?.length > 0);
  const initialPhase = hasPrior ? 'entry_choice' : 'intro';
  const orgProfile = summariseOrgProfile(currentData?.org);
  const orgProfileBlock = buildOrgProfileBlock(orgProfile);

  const [phase, setPhase] = useState(initialPhase);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [decisionGuruTopic, setDecisionGuruTopic] = useState(null);
  const chatContainerRef = useRef(null);
  const questionCount = useRef(0);
  // Cross-card context — Direction snapshot. Injected into opening
  // and followup prompts so this dialogue doesn't re-ask the leader
  // anything already captured upstream (stage, revenue model,
  // customer segments, value proposition, etc.).
  const directionContextBlock = buildDirectionContextBlock(readDirectionCanvas());

  // Evaluation flow — kicks off /evaluate-section after the leader
  // confirms the extracted data. The DialogueEvalModal wrapper at
  // the top of the reviewing-phase return handles the modal lifecycle.
  const sectionMeta = OM_SECTIONS.find(s => s.key === sectionKey);
  const evalFlow = useEvaluationFlow({
    sectionKey,
    sectionTitle: sectionMeta?.title || sectionKey,
    sectionFields: sectionMeta?.current_fields || [],
    repeatable: false,
  });

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // ── Prompt builders ────────────────────────────────────────────
  // Note: the backend /chat endpoint injects PRINCIPLES_PROMPT into the
  // system prompt automatically — we don't need to repeat it here.

  function buildOpeningPrompt() {
    if (!isFutureState) {
      // Sole-operator path — different opener, different beats. The
      // standard role-formalisation / decision-rights / team-challenge
      // questions are nonsensical when there's no team to formalise.
      if (orgProfile?.isSoleOperator) {
        return `You are GuruOne Design Guru, helping a sole operator think through their people and structure situation.

${orgProfileBlock}

WHAT TO COVER (sole-operator version):
1. Stretch — where they personally are most stretched (capability or capacity)
2. First hire — who they'd hire first and why, if they could; what's holding them back (cost, readiness, finding the right person)
3. Coverage — how they currently handle holidays, illness, peak demand
4. External help — what advisors, fractional support, or freelancers they already use, and where that could grow

RULES:
- One question per turn. Short, direct, conversational. Plain prose, no markdown emphasis.
- Treat sole-operator status as a deliberate position, not a deficiency. Some leaders intend to stay solo; others are en route to scaling. Find out which.
- After 4–6 exchanges covering all four beats, emit READY_TO_SUMMARISE on its own line.
- If a real decision emerges (hire vs outsource, when to bring someone in), acknowledge the gravity but keep the dialogue moving — Decision Guru is offered at the end, not in the middle.

Open with: "You're running this on your own right now — that's a deliberate position for some leaders and a stepping stone for others. Where are you personally most stretched at the moment — is it capability (a skill you don't have) or capacity (just not enough hours)?"`;
      }

      return `You are GuruOne Design Guru, helping a business leader understand their people and structure.${directionContextBlock}

${orgProfileBlock}

The business leader has just built their org chart. Your job is to deepen that picture through conversation — covering role formalisation, decision rights, capability gaps, and their biggest people/structure challenge.

WHAT TO COVER IN ORDER:
1. Role formalisation — how clearly roles and responsibilities are defined in practice
2. Decision rights — who makes decisions when the business leader is unavailable
3. Capability gaps — where a people or skills constraint is limiting the business
4. Team challenges — the business leader's single biggest people or structure challenge right now

RULES:
- One question per turn. Short, direct, conversational. Plain prose, no markdown emphasis.
- After 4–6 exchanges covering all beats, emit READY_TO_SUMMARISE on its own line.
- If a genuine decision emerges (e.g. hire vs develop vs restructure) where the business leader is uncertain, acknowledge the gravity of it without pivoting away. Use phrasing like: "There's some real gravity to that — let's note it and come back to it. We can hand it to your virtual NED, Decision Guru, after we wrap this exercise." Then keep going with the dialogue. Do NOT route them away mid-conversation.

Open with: "You've mapped your team above — tell me about how clearly roles and responsibilities are defined in practice. Are they formally documented, or does everyone just know what they do?"`;
    }

    // Future state — Mode A (greenfield) vs Mode B (review existing)
    if (hasFutureState) {
      return `You are GuruOne reviewing an AI-generated future state with the business leader.

AI-GENERATED FUTURE STATE:
Priority hires: ${currentData?.hiring_priorities || '(not yet captured)'}
Business leader role shift: ${currentData?.founder_role || '(not yet captured)'}
Structural changes: ${currentData?.structural_changes || '(not yet captured)'}

Walk through each element. For each: present what was suggested, ask if it matches their thinking.
- If they agree: confirm and move on
- If they disagree: understand whether the AI misread the direction, misread current state, or whether the business leader is resisting something worth gently challenging
- If a real decision surfaces, flag the gravity but keep the dialogue moving — Decision Guru is offered at the end, not in the middle

After covering all three areas, emit READY_TO_SUMMARISE on its own line.

Open with one area at a time — do not paste all three suggestions into the opener. Start with: "I've put together a suggested future state for your people and structure. Let's start with priority hires — I've suggested: ${currentData?.hiring_priorities || '(no specific suggestion yet)'}. Does that match your thinking, or is something off?"`;
    }

    // Mode A: greenfield future-state design
    return `You are GuruOne Design Guru helping a business leader design their future people structure.

CONTEXT FROM CURRENT STATE:
Capability gaps: ${currentData?.key_gaps || '(not yet captured — the business leader may not have completed the current-state dialogue)'}
Team challenges: ${currentData?.team_challenges || '(not yet captured)'}

Work through three areas in order:
1. Priority hires — who, in what order, and why
2. Business leader role shift — how the business leader's role needs to evolve as the business grows
3. Structural changes — anything that needs to change about how the business is organised beyond individual hires

Rules: one area at a time, probe for specificity, name competing options when they arise.
If a real decision surfaces, flag the gravity but keep the dialogue moving — Decision Guru is offered at the end, not in the middle.
After covering all three, emit READY_TO_SUMMARISE on its own line.

Open with: "Based on what you've shared about your current structure and gaps, let's think about where this needs to go. What's the single biggest change that would most unblock your growth — a key hire, a restructure, or a shift in how you personally operate?"`;
  }

  function buildFollowupPrompt() {
    const stateContext = isFutureState
      ? 'This is about the FUTURE STATE the business leader wants to build. Keep all questions aspirational and forward-looking.'
      : 'This is about the CURRENT STATE — how the business actually operates today. Push back on aspirational answers and keep things grounded in reality.';

    const remainingBeats = (!isFutureState && orgProfile?.isSoleOperator)
      ? `Continue covering any sole-operator beats not yet addressed:
- Stretch — where they're personally most stretched (capability vs capacity)
- First hire — who they'd hire first and what's holding them back
- Coverage — how they handle holidays, illness, peak demand today
- External help — advisors / fractional support / freelancers and where that could grow

Do NOT ask about role formalisation, decision rights, what doesn't get done when they step back, or what only they can do. Those questions are not useful for a sole operator and read as the system not having listened.`
      : isFutureState
        ? `Continue covering any of these areas not yet addressed:
- Priority hires — who, in what order, why
- Business leader role shift — how the business leader's role needs to evolve
- Structural changes — anything that needs to change about how the business is organised`
        : `Continue covering any beats not yet addressed:
- Role formalisation
- Decision rights: "When a decision needs to be made and you're not there — who makes it, and on what basis?"
- Capability gaps: "Where does the business slow down because of a people or skills constraint?"
- Team challenges: "What's your single biggest people or structure challenge right now?"`;

    return `You are GuruOne helping a business leader map their people and structure. You have asked ${questionCount.current} question(s) so far.${directionContextBlock}

${stateContext}

${orgProfileBlock}

${remainingBeats}

After all areas are covered or after 6 exchanges total, emit READY_TO_SUMMARISE on its own line.

Rules: one question per turn, reference previous answers, accept honest incomplete answers. Plain prose, no markdown emphasis.
If a real decision is surfacing, flag the gravity but keep the dialogue moving — Decision Guru is offered at the end, not in the middle.`;
  }

  function buildExtractPrompt() {
    if (!isFutureState) {
      return `Based on this conversation, extract structured data about the business leader's people and structure.

Return ONLY valid JSON starting with {:
{
  "role_definition_status": "one of exactly: 'Fully defined — all roles documented' | 'Defined for some roles' | 'Ad hoc — roles understood but not documented'",
  "responsibilities_documented": "one of exactly: 'Documented for all roles' | 'Documented for some roles' | 'Not yet documented'",
  "decision_rights": "one of exactly: 'Formalised and documented' | 'Informally understood' | 'Not yet defined'",
  "key_gaps": "2–4 sentences summarising capability gaps described",
  "team_challenges": "2–3 sentences capturing the business leader's main people/structure challenge in their own words",
  "decision_guru_topic": "string or null — set ONLY if a genuine decision emerged in the conversation that warrants Decision Guru's structured analysis. Short topic phrase suitable as a Decision Guru challenge title (e.g. 'Whether to hire a Head of Sales now or develop internally'). Return null if no real decision surfaced."
}
Omit any field not clearly evidenced by the conversation, except decision_guru_topic which must always be present (set to null if not applicable).`;
    }
    return `Based on this conversation, extract the agreed future state for people and structure.

Return ONLY valid JSON starting with {:
{
  "hiring_priorities": "priority hires in order with brief rationale",
  "founder_role": "how the business leader's role needs to shift — specific and directional",
  "structural_changes": "structural changes needed beyond individual hires",
  "decision_guru_topic": "string or null — set ONLY if a genuine decision emerged that warrants Decision Guru. Short topic phrase suitable as a challenge title. Return null otherwise."
}
Omit any field not clearly evidenced by the conversation, except decision_guru_topic which must always be present.`;
  }

  // ── Conversation lifecycle ────────────────────────────────────
  async function startConversation() {
    setLoading(true);
    setPhase('chatting');
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: [{ role: 'user', content: 'Begin the dialogue.' }],
        context: buildOpeningPrompt(),
      });
      setMessages([{ role: 'assistant', content: res.data.reply }]);
      questionCount.current = 1;
    } catch {
      setMessages([{ role: 'assistant', content: isFutureState
        ? "Let's design the future state for your people and structure. What's the single biggest change that would most unblock your growth?"
        : "You've mapped your team above — tell me about how clearly roles and responsibilities are defined in practice." }]);
      questionCount.current = 1;
    }
    setLoading(false);
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);
    try {
      questionCount.current += 1;
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: newMessages,
        context: buildFollowupPrompt() + DIALOGUE_RULES_BLOCK,
      });
      const reply = res.data.reply || '';
      const isReady = reply.includes('READY_TO_SUMMARISE');
      const deferredQuestion = isReady ? maybeDeferSummariseForPendingQuestion(reply, questionCount.current, 8) : null;
      if (deferredQuestion) {
        setMessages(prev => [...prev, { role: 'assistant', content: deferredQuestion }]);
      } else if (isReady || questionCount.current >= 8) {
        track('design_conversation_completed', { section: sectionKey || 'people_roles' });
        const clean = reply.replace(/READY_TO_SUMMARISE/g, '').trim();
        const convo = clean ? [...newMessages, { role: 'assistant', content: clean }] : newMessages;
        if (clean) setMessages(prev => [...prev, { role: 'assistant', content: clean }]);
        await extractData(convo);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong — please try again.' }]);
    }
    setLoading(false);
  }

  async function extractData(convo) {
    setMessages(prev => [...prev, { role: 'assistant', content: "Good — I have enough to work with. Let me synthesise what you've shared. You'll review and edit everything before saving." }]);
    setPhase('extracting');
    const founderAnswers = convo.filter(m => m.role === 'user').map((m, i) => `Answer ${i + 1}: ${m.content}`).join('\n\n');
    const condensed = [{ role: 'user', content: `Here are the business leader's answers:\n\n${founderAnswers}\n\nExtract the structured data now.` }];
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, { messages: condensed, context: buildExtractPrompt() });
      const raw = res.data.reply || '';
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1 && e !== -1) {
        const parsed = JSON.parse(raw.slice(s, e + 1));
        // Pull decision_guru_topic out of the editable data — it's a routing
        // signal, not user-facing data to be edited or persisted.
        const { decision_guru_topic, ...editable } = parsed;
        setEditedData(editable);
        setDecisionGuruTopic(typeof decision_guru_topic === 'string' && decision_guru_topic.trim().length > 0 ? decision_guru_topic.trim() : null);
        setPhase('reviewing');
        return;
      }
    } catch { /* fall through */ }
    setPhase('entry_choice');
    setMessages(prev => [...prev, { role: 'assistant', content: "I wasn't able to extract automatically this time. Use Quick entry to fill in the fields directly." }]);
  }

  function handleSave() {
    if (!editedData) return;
    const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
    if (isFutureState) {
      // Future state has no eval flow; commit directly.
      onChange({ ...currentData, ...editedData, [TRANSCRIPT_KEY]: transcript });
      if (decisionGuruTopic) {
        try { localStorage.setItem('guruone_decision_prefill', JSON.stringify({ title: decisionGuruTopic })); } catch { /* non-fatal */ }
      }
      setPhase('saved');
      return;
    }
    evalFlow.runEvaluation({ fields: editedData, transcript });
  }

  function openDecisionGuru() {
    if (!decisionGuruTopic) return;
    try {
      localStorage.setItem('guruone_decision_prefill', JSON.stringify({ title: decisionGuruTopic }));
    } catch { /* non-fatal */ }
    track('decision_guru_routed', { source: 'design_canvas' });
    window.open('/decision', '_blank', 'noopener');
  }

  // ── Render ────────────────────────────────────────────────────
  const colour = PEOPLE_DIALOGUE_COLOUR;
  const bg = PEOPLE_DIALOGUE_BG;
  const border = PEOPLE_DIALOGUE_BORDER;

  if (phase === 'intro') {
    const introCopy = isFutureState
      ? (hasFutureState
          ? "I've put together a suggested future state for your people and structure based on the AI generation. Let's walk through it together — you can confirm what fits, push back on what doesn't, and we'll capture the agreed direction."
          : "Let's design the future state for your people and structure. We'll work through priority hires, how your role needs to shift, and any structural changes the business needs.")
      : "You've mapped your team above. Now let's deepen that picture through conversation — how clearly roles are defined, how decisions get made, where capability gaps are, and the biggest people or structure challenge you're facing.";
    const buttonLabel = isFutureState
      ? (hasFutureState ? 'Review the suggested future state →' : 'Design future state →')
      : 'Begin people exploration →';
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1.25rem 1.5rem', marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.75rem' }}>
          People &amp; structure dialogue
        </div>
        <p style={{ fontSize: '0.82rem', color: '#333', lineHeight: 1.6, margin: '0 0 1rem' }}>
          {introCopy}
        </p>
        <div style={{ fontSize: '0.68rem', color: '#888', lineHeight: 1.5, marginBottom: '1.25rem', borderTop: `1px solid ${border}`, paddingTop: '0.75rem' }}>
          <strong>How it works:</strong> GuruOne asks one question at a time and probes for specifics. At the end you review and edit a structured summary before saving. Typically 4–6 exchanges. If a real decision surfaces, you can hand it to Decision Guru after you finish.
        </div>
        <button onClick={startConversation}
          style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
          {buttonLabel}
        </button>
      </div>
    );
  }

  if (phase === 'entry_choice') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.4rem' }}>People dialogue — already started</div>
        <p style={{ fontSize: '0.78rem', color: '#0C447C', lineHeight: 1.55, margin: '0 0 0.85rem' }}>
          You have a saved conversation for this section. Continue where you left off, or start fresh.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={() => { setMessages(priorTranscript.messages || []); questionCount.current = priorTranscript.questionCount || 0; setPhase('chatting'); }}
            style={{ padding: '0.5rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Continue conversation
          </button>
          <button onClick={() => setPhase('intro')}
            style={{ padding: '0.5rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${colour}`, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Start fresh
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'extracting') {
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {messages.slice(-2).map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '0.6rem 0.9rem', borderRadius: 10, background: m.role === 'user' ? colour : bg, color: m.role === 'user' ? 'white' : '#0C447C', fontSize: '0.8rem', lineHeight: 1.5 }}>{m.content}</div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: colour, fontSize: '0.8rem' }}>
          <div style={{ width: 14, height: 14, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          Synthesising your answers…
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      </div>
    );
  }

  if (phase === 'reviewing' && editedData) {
    const fieldLabels = isFutureState ? PEOPLE_FIELD_LABELS_FUTURE : PEOPLE_FIELD_LABELS_CURRENT;
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <DialogueEvalModal
          sectionMeta={sectionMeta}
          evalFlow={evalFlow}
          onCommitWithEval={(evaluation) => {
            const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
            const payload = { ...currentData, ...editedData, [TRANSCRIPT_KEY]: transcript };
            if (evaluation) payload._evaluation = evaluation;
            if (decisionGuruTopic) {
              try { localStorage.setItem('guruone_decision_prefill', JSON.stringify({ title: decisionGuruTopic })); } catch { /* non-fatal */ }
            }
            onChange(payload);
            setPhase('saved');
          }}
          onRefine={() => setPhase('chatting')}
        />
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colour, marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Review &amp; edit</div>
        {Object.entries(fieldLabels).map(([key, label]) => {
          const value = editedData[key] || '';
          const chipOpts = PEOPLE_CHIP_OPTIONS[key];
          if (chipOpts) {
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>{label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {chipOpts.map(opt => {
                    const active = value === opt;
                    return (
                      <button key={opt} onClick={() => setEditedData(p => ({ ...p, [key]: active ? '' : opt }))}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? bg : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          return (
            <div key={key} style={{ marginBottom: '0.85rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
              <textarea value={value} onChange={e => setEditedData(p => ({ ...p, [key]: e.target.value }))} rows={3}
                style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: `1px solid ${border}`, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55 }} />
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.6rem 1.25rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save
          </button>
          <button onClick={() => setPhase('chatting')}
            style={{ padding: '0.6rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${border}`, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Continue conversation
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'saved') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>
            {isFutureState ? 'Future state captured' : 'Current state captured'}
          </div>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#444', margin: '0 0 0.85rem', lineHeight: 1.55 }}>
          Your answers are saved. You can edit any field above by reopening this section in Quick mode.
        </p>
        {decisionGuruTopic && (
          <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', background: 'white', border: `1px solid ${border}`, borderRadius: 8 }}>
            <div style={{ fontSize: '0.72rem', color: '#555', marginBottom: '0.5rem', lineHeight: 1.5 }}>
              A decision came up during this conversation that's worth thinking through more carefully:
            </div>
            <div style={{ fontSize: '0.82rem', color: '#1a1a2e', fontStyle: 'italic', marginBottom: '0.75rem', lineHeight: 1.5 }}>
              &ldquo;{decisionGuruTopic}&rdquo;
            </div>
            <button onClick={openDecisionGuru}
              style={{ padding: '0.55rem 1rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Take this to Decision Guru →
            </button>
          </div>
        )}
        <button onClick={() => setPhase('intro')}
          style={{ background: 'none', border: 'none', color: colour, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', padding: '0.5rem 0', width: 'auto', marginTop: '0.5rem' }}>
          Refine through another conversation →
        </button>
      </div>
    );
  }

  // Chatting phase
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div ref={chatContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '0.85rem', maxHeight: 320, overflowY: 'auto', paddingRight: '0.2rem' }}>
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          return (
            <div key={i} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '88%', padding: '0.6rem 0.85rem', borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px', background: isUser ? colour : bg, color: isUser ? 'white' : '#0C447C', fontSize: '0.82rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              {m.content}
            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: colour, fontStyle: 'italic' }}>
            <div style={{ width: 11, height: 11, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            GuruOne is thinking…
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type your answer… (Enter to send)"
          disabled={loading} rows={2}
          style={{ flex: 1, fontSize: '0.82rem', padding: '0.6rem 0.8rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          style={{ padding: '0.6rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
          Send
        </button>
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

// ── CustomerDialogue ──────────────────────────────────────────────
// Standalone dialogue for the customer_interactions section.
// Same phase machine as SuppliersDialogue/LocationDialogue — handles
// both current and future state from a single mount on the current
// tab via isFutureMode.
//
// Decision Guru routing: inline button under any assistant message
// containing "Decision Guru". Uses window.open('_blank') so the
// in-flight dialogue is not destroyed when the user clicks. No
// post-save handoff in this dialogue — there's no equivalent of
// suppliers' make_vs_buy_flagged extracted here.

const CUSTOMER_DIALOGUE_COLOUR = '#C0556E';
const CUSTOMER_DIALOGUE_BG = '#FBE9EE';
const CUSTOMER_DIALOGUE_BORDER = '#E5B0BC';

const CUSTOMER_CHIP_OPTIONS = {
  relationship_type: ['Transactional','Ongoing retainer','Long-term partnership','Project-based'],
  engagement_style: ['Proactive','Mostly proactive','Reactive','Mixed'],
  issue_handling: ['Strong — customers feel well supported','Inconsistent','Reactive only','Not yet formalised'],
  target_relationship: ['More transactional','Deeper partnerships','Self-serve','Community'],
};

const CUSTOMER_FIELD_LABELS_CURRENT = {
  relationship_type: 'Relationship type',
  engagement_style: 'Engagement style',
  touch_points: 'Key touchpoints',
  issue_handling: 'Issue and complaint handling',
  retention_rate: 'Retention rate',
  churn_reasons: 'Primary churn reasons',
  automation_opportunity: 'Automation opportunity',
  customer_challenges: 'Current challenges',
};

const CUSTOMER_FIELD_LABELS_FUTURE = {
  cx_improvements: 'Customer experience improvements',
  target_relationship: 'Target relationship model',
};

function CustomerDialogue({ currentData, futureData, onChange, onFutureChange, hasFutureState, sectionKey }) {
  const TRANSCRIPT_KEY_CURRENT = '_customer_current_transcript';
  const TRANSCRIPT_KEY_FUTURE = '_customer_future_transcript';

  const [phase, setPhase] = useState('intro');
  useEffect(() => {
    if (phase === 'extracting') track('design_conversation_completed', { section: sectionKey });
  }, [phase, sectionKey]);
  const [isFutureMode, setIsFutureMode] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const chatContainerRef = useRef(null);
  const questionCount = useRef(0);
  // Cross-card context — Direction snapshot. Injected into opening
  // and followup prompts so this dialogue doesn't re-ask the leader
  // anything already captured upstream (stage, revenue model,
  // customer segments, value proposition, etc.).
  const directionContextBlock = buildDirectionContextBlock(readDirectionCanvas());

  // Evaluation flow — kicks off /evaluate-section after the leader
  // confirms the extracted data. The DialogueEvalModal wrapper at
  // the top of the reviewing-phase return handles the modal lifecycle.
  const sectionMeta = OM_SECTIONS.find(s => s.key === sectionKey);
  const evalFlow = useEvaluationFlow({
    sectionKey,
    sectionTitle: sectionMeta?.title || sectionKey,
    sectionFields: sectionMeta?.current_fields || [],
    repeatable: false,
  });

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  function openDecisionGuruInline() {
    // Mid-conversation: open in new tab so the dialogue isn't lost.
    track('decision_guru_routed', { source: 'design_canvas' });
    window.open('/decision', '_blank', 'noopener');
  }

  // ── Prompt builders ─────────────────────────────────────────────
  // Note: the backend /chat endpoint injects PRINCIPLES_PROMPT into the
  // system prompt automatically — we don't need to repeat it here.

  function buildOpeningPrompt(future) {
    if (!future) {
      return `You are GuruOne Design Guru, helping a business leader map how their business interacts with and retains customers.${directionContextBlock}

WHAT TO COVER IN ORDER:
1. The customer's actual view — what customers say when they renew, and what patterns emerge when they leave
2. Relationship reality — how customers would honestly describe their relationship with the business, versus how the business leader describes it
3. Moments that matter — which key points in the relationship (onboarding, when things go wrong, renewal) are handled well and which are not
4. Proactive vs reactive — who typically makes the first move between interactions, and how customers know the business is thinking about them
5. Retention and churn — the actual year-on-year retention rate (push for a number, even an honest estimate), and the most common churn pattern
6. Scaling and automation — which parts of customer interaction genuinely require a senior person, and which parts are manual by default because no system exists. If the business leader conflates personal with manual: "Some business leaders find that systematising the routine interactions frees up more time for the conversations that are genuinely irreplaceable — have you thought about where that line sits for you?"
7. Current challenges — the biggest customer interaction or retention challenge right now

RULES:
- One question per turn
- Start with real customer language, not the business leader's interpretation
- Push for a retention number — "pretty good" is not a number
- Do not accept "we treat everyone as partners" without probing what customers actually experience
- After all beats covered, emit READY_TO_SUMMARISE on its own line

Open with: "When a customer renews with you — and you've had the chance to ask them why — what have they actually said? And when they leave, what reasons have you heard?"`;
    }
    if (hasFutureState) {
      return `You are GuruOne reviewing an AI-generated future customer interaction model with the business leader.

AI-GENERATED FUTURE STATE:
Customer experience improvements: ${futureData?.cx_improvements || '(no specific suggestion yet)'}
Target relationship model: ${futureData?.target_relationship || '(no specific suggestion yet)'}

Walk through each element. For each: present what was suggested, ask whether it matches their thinking.
Probe where the business leader agrees too easily with aspirational descriptions: "What specifically would change about how you operate if this were true — what would you start doing, or stop doing?"
If automation opportunity was identified in current state (${currentData?.automation_opportunity || 'none recorded'}), raise it if not yet discussed.
After reviewing all elements, emit READY_TO_SUMMARISE on its own line.

Open with: "I've put together a suggested future state for your customer interactions. Let me walk you through it — starting with customer experience improvements: ${futureData?.cx_improvements || '(no specific suggestion yet)'}. Does that match your thinking, or is something off?"`;
    }
    return `You are GuruOne helping a business leader design their future customer interaction model.

CURRENT STATE CONTEXT:
Relationship type: ${currentData?.relationship_type || 'not captured'}
Retention rate: ${currentData?.retention_rate ?? 'not captured'}%
Churn reasons: ${currentData?.churn_reasons || 'not captured'}
Automation opportunity: ${currentData?.automation_opportunity || 'not identified'}

Cover two areas:
1. Customer experience improvements — specific and operational. Not "better service" but what would actually change: what starts, what stops, what gets systematised. Probe: "What would have to be different operationally for that to be true?"
2. Target relationship model — whether the model itself needs to change, and what that requires in terms of people, process, and technology

For any customer experience improvement involving meaningful automation, process redesign, or investment: "That's worth thinking through properly — do you want to take it to Decision Guru for a structured analysis?"

After covering both areas, emit READY_TO_SUMMARISE on its own line.

Open with: "If you could change one thing about how customers experience your business — not what you say to them, but what they actually feel — what would it be?"`;
  }

  function buildFollowupPrompt() {
    if (!isFutureMode) {
      return `You are GuruOne mapping a business leader's customer interaction and retention picture. You have asked ${questionCount.current} questions.${directionContextBlock}

Continue covering remaining beats:
- Relationship reality — how customers would honestly describe it versus how the business leader does
- Moments that matter — onboarding, when things go wrong, renewal
- Proactive vs reactive engagement pattern
- Retention rate — push for an actual number
- Scaling and automation — where the line sits between genuinely personal and manually delivered by default
- Current challenges

Rules: one question per turn. Do not accept aspirational answers without probing the actual experience.
After all beats covered, emit READY_TO_SUMMARISE on its own line.`;
    }
    return `You are GuruOne helping a business leader design their future customer interaction model. You have asked ${questionCount.current} questions.

Continue covering any of the two areas not yet fully addressed:
1. Customer experience improvements — specific, operational changes (what starts, what stops, what gets systematised)
2. Target relationship model — whether the model itself needs to change, and what that implies for people, process, and technology

For any improvement involving meaningful automation, process redesign or investment, route to Decision Guru rather than designing it in detail here.

After both areas covered, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn.`;
  }

  function buildExtractPrompt() {
    if (!isFutureMode) {
      return `Based on this conversation, extract customer interaction and retention data.

Return ONLY valid JSON starting with {:
{
  "relationship_type": "one of exactly: 'Transactional' | 'Ongoing retainer' | 'Long-term partnership' | 'Project-based'",
  "engagement_style": "one of exactly: 'Proactive' | 'Mostly proactive' | 'Reactive' | 'Mixed'",
  "touch_points": "the two or three touchpoints that most directly drive satisfaction and renewal — not a comprehensive list",
  "issue_handling": "one of exactly: 'Strong — customers feel well supported' | 'Inconsistent' | 'Reactive only' | 'Not yet formalised'",
  "retention_rate": number between 0 and 100 representing estimated year-on-year retention percentage,
  "churn_reasons": "patterns observed when customers leave — specific if discussed",
  "automation_opportunity": "parts of customer interaction that could be systematised without losing what is genuinely distinctive. Omit if not discussed.",
  "customer_challenges": "biggest customer interaction or retention challenge in the business leader's own words"
}
Omit any field not clearly evidenced by the conversation.`;
    }
    return `Based on this conversation, extract the agreed future state for customer interactions.

Return ONLY valid JSON starting with {:
{
  "cx_improvements": "specific operational changes — what starts, what stops, what gets systematised",
  "target_relationship": "one of exactly: 'More transactional' | 'Deeper partnerships' | 'Self-serve' | 'Community'"
}
Omit any field not clearly evidenced by the conversation.`;
  }

  // ── Conversation lifecycle ───────────────────────────────────────
  async function startConversation(future) {
    setIsFutureMode(future);
    setMessages([]);
    setInput('');
    setEditedData(null);
    questionCount.current = 0;
    setLoading(true);
    setPhase('chatting');
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: [{ role: 'user', content: 'Begin the dialogue.' }],
        context: buildOpeningPrompt(future),
      });
      setMessages([{ role: 'assistant', content: res.data.reply }]);
      questionCount.current = 1;
    } catch {
      setMessages([{ role: 'assistant', content: future
        ? "If you could change one thing about how customers experience your business — not what you say to them, but what they actually feel — what would it be?"
        : "When a customer renews with you — and you've had the chance to ask them why — what have they actually said?" }]);
      questionCount.current = 1;
    }
    setLoading(false);
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);
    try {
      questionCount.current += 1;
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: newMessages,
        context: buildFollowupPrompt() + DIALOGUE_RULES_BLOCK,
      });
      const reply = res.data.reply || '';
      const isReady = reply.includes('READY_TO_SUMMARISE');
      const deferredQuestion = isReady ? maybeDeferSummariseForPendingQuestion(reply, questionCount.current, 9) : null;
      if (deferredQuestion) {
        setMessages(prev => [...prev, { role: 'assistant', content: deferredQuestion }]);
      } else if (isReady || questionCount.current >= 9) {
        const clean = reply.replace(/READY_TO_SUMMARISE/g, '').trim();
        const convo = clean ? [...newMessages, { role: 'assistant', content: clean }] : newMessages;
        if (clean) setMessages(prev => [...prev, { role: 'assistant', content: clean }]);
        await extractData(convo);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong — please try again.' }]);
    }
    setLoading(false);
  }

  async function extractData(convo) {
    setMessages(prev => [...prev, { role: 'assistant', content: "Good — I have enough to work with. Let me synthesise what you've shared. You'll review and edit everything before saving." }]);
    setPhase('extracting');
    const founderAnswers = convo.filter(m => m.role === 'user').map((m, i) => `Answer ${i + 1}: ${m.content}`).join('\n\n');
    const condensed = [{ role: 'user', content: `Here are the business leader's answers:\n\n${founderAnswers}\n\nExtract the structured data now.` }];
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, { messages: condensed, context: buildExtractPrompt() });
      const raw = res.data.reply || '';
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1 && e !== -1) {
        const parsed = JSON.parse(raw.slice(s, e + 1));
        setEditedData(parsed);
        setPhase('reviewing');
        return;
      }
    } catch { /* fall through */ }
    setPhase('intro');
    setMessages(prev => [...prev, { role: 'assistant', content: "I wasn't able to extract automatically this time. Use Quick entry to fill in the fields directly." }]);
  }

  function handleSave() {
    if (!editedData) return;
    const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
    if (isFutureMode) {
      onFutureChange({ ...futureData, ...editedData, [TRANSCRIPT_KEY_FUTURE]: transcript });
      setPhase('saved');
      return;
    }
    evalFlow.runEvaluation({ fields: editedData, transcript });
  }

  // ── Render ───────────────────────────────────────────────────────
  const colour = CUSTOMER_DIALOGUE_COLOUR;
  const bg = CUSTOMER_DIALOGUE_BG;
  const border = CUSTOMER_DIALOGUE_BORDER;

  if (phase === 'intro') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1.25rem 1.5rem', marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.75rem' }}>
          Customer interactions dialogue
        </div>
        <p style={{ fontSize: '0.82rem', color: '#333', lineHeight: 1.6, margin: '0 0 1rem' }}>
          We&apos;ll surface what customers actually say (not what you assume they think), how the relationship really feels at the moments that matter, your true retention rate and churn pattern, and where automation would free up time for the conversations that genuinely require a senior hand. We&apos;ll also flag any improvement big enough to belong in Decision Guru rather than be designed here.
        </p>
        <div style={{ fontSize: '0.68rem', color: '#888', lineHeight: 1.5, marginBottom: '1.25rem', borderTop: `1px solid ${border}`, paddingTop: '0.75rem' }}>
          <strong>How it works:</strong> GuruOne asks one question at a time and probes for specifics. At the end you review and edit a structured summary before saving. Typically 6–8 exchanges.
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button onClick={() => startConversation(false)}
            style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Explore current state →
          </button>
          {hasFutureState && (
            <button onClick={() => startConversation(true)}
              style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: 'white', color: colour, border: `1.5px solid ${colour}`, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Review future state →
            </button>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'extracting') {
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {messages.slice(-2).map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '0.6rem 0.9rem', borderRadius: 10, background: m.role === 'user' ? colour : bg, color: m.role === 'user' ? 'white' : '#7C2A3D', fontSize: '0.8rem', lineHeight: 1.5 }}>{m.content}</div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: colour, fontSize: '0.8rem' }}>
          <div style={{ width: 14, height: 14, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          Synthesising your answers…
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      </div>
    );
  }

  if (phase === 'reviewing' && editedData) {
    const fieldLabels = isFutureMode ? CUSTOMER_FIELD_LABELS_FUTURE : CUSTOMER_FIELD_LABELS_CURRENT;
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <DialogueEvalModal
          sectionMeta={sectionMeta}
          evalFlow={evalFlow}
          onCommitWithEval={(evaluation) => {
            const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
            const payload = { ...currentData, ...editedData, [TRANSCRIPT_KEY_CURRENT]: transcript };
            if (evaluation) payload._evaluation = evaluation;
            onChange(payload);
            setPhase('saved');
          }}
          onRefine={() => setPhase('chatting')}
        />
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colour, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Review &amp; edit — {isFutureMode ? 'future state' : 'current state'}
        </div>
        <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: '0.85rem', lineHeight: 1.5 }}>
          AI-drafted from your conversation — edit any field until it precisely matches reality before saving.
        </div>
        {Object.entries(fieldLabels).map(([key, label]) => {
          const rawValue = editedData[key];
          const chipOpts = CUSTOMER_CHIP_OPTIONS[key];
          if (chipOpts) {
            const value = rawValue || '';
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>{label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {chipOpts.map(opt => {
                    const active = value === opt;
                    return (
                      <button key={opt} onClick={() => setEditedData(p => ({ ...p, [key]: active ? '' : opt }))}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? bg : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          if (key === 'retention_rate') {
            const numericValue = typeof rawValue === 'number' ? rawValue : 0;
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input type="range" min="0" max="100" step="5"
                    value={numericValue}
                    onChange={e => setEditedData(p => ({ ...p, [key]: Number(e.target.value) }))}
                    style={{ flex: 1, cursor: 'pointer', accentColor: colour }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                    <input type="number" min="0" max="100"
                      value={numericValue}
                      onChange={e => setEditedData(p => ({ ...p, [key]: Math.min(100, Math.max(0, Number(e.target.value))) }))}
                      style={{ width: 52, fontSize: '0.82rem', padding: '0.3rem 0.4rem', borderRadius: 6, border: `1px solid ${border}`, fontFamily: 'inherit', textAlign: 'center', boxSizing: 'border-box' }} />
                    <span style={{ fontSize: '0.82rem', color: '#555' }}>%</span>
                  </div>
                </div>
              </div>
            );
          }
          const value = rawValue || '';
          return (
            <div key={key} style={{ marginBottom: '0.85rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
              <textarea value={value} onChange={e => setEditedData(p => ({ ...p, [key]: e.target.value }))} rows={3}
                style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: `1px solid ${border}`, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55 }} />
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.6rem 1.25rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save
          </button>
          <button onClick={() => setPhase('chatting')}
            style={{ padding: '0.6rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${border}`, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Continue conversation
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'saved') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>
            {isFutureMode ? 'Future state captured' : 'Current state captured'}
          </div>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#444', margin: '0 0 0.85rem', lineHeight: 1.55 }}>
          Your answers are saved. {isFutureMode
            ? 'Switch to the Future State tab to see them, or edit any field there in Quick mode.'
            : 'You can edit any field in Quick mode below, or run another conversation to refine.'}
        </p>
        <button onClick={() => setPhase('intro')}
          style={{ background: 'none', border: 'none', color: colour, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', padding: '0.5rem 0', width: 'auto' }}>
          Run another conversation →
        </button>
      </div>
    );
  }

  // Chatting phase
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.5rem' }}>
        {isFutureMode ? 'Future state — design / review' : 'Current state — explore'}
      </div>
      <div ref={chatContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '0.85rem', maxHeight: 320, overflowY: 'auto', paddingRight: '0.2rem' }}>
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          const showDgButton = !isUser && typeof m.content === 'string' && m.content.includes('Decision Guru');
          return (
            <div key={i} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '88%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '0.6rem 0.85rem', borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px', background: isUser ? colour : bg, color: isUser ? 'white' : '#7C2A3D', fontSize: '0.82rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                {m.content}
              </div>
              {showDgButton && (
                <button onClick={openDecisionGuruInline}
                  style={{ marginTop: '0.5rem', padding: '0.4rem 1rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto', alignSelf: 'flex-start' }}>
                  Take to Decision Guru →
                </button>
              )}
            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: colour, fontStyle: 'italic' }}>
            <div style={{ width: 11, height: 11, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            GuruOne is thinking…
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type your answer… (Enter to send)"
          disabled={loading} rows={2}
          style={{ flex: 1, fontSize: '0.82rem', padding: '0.6rem 0.8rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          style={{ padding: '0.6rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
          Send
        </button>
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

// ── SuppliersDialogue ─────────────────────────────────────────────
// Standalone dialogue for the suppliers_partners section.
// Same phase machine as LocationDialogue: handles both current and
// future state from a single mount on the current tab via isFutureMode.
//
// Decision Guru handoff:
//  - Inline (mid-conversation): a "Take to Decision Guru →" button
//    appears under any assistant message containing the phrase
//    "Decision Guru". Opens DG in a new tab via window.open so the
//    in-flight dialogue is not lost — diverging from the spec's
//    window.location.href which would hard-navigate and discard the
//    conversation.
//  - Post-save: when the LLM extracts a non-empty make_vs_buy_flagged,
//    the saved screen surfaces a handoff button that prefills DG via
//    localStorage 'guruone_decision_prefill' (mirrors PeopleDialogue).

const SUPPLIERS_DIALOGUE_COLOUR = '#1D9E75';
const SUPPLIERS_DIALOGUE_BG = '#E1F5EE';
const SUPPLIERS_DIALOGUE_BORDER = '#9FE1CB';

const SUPPLIERS_CHIP_OPTIONS = {
  dependency_risk: ['Low','Medium','High'],
  contract_status: ['Documented and current','Partially documented','Undocumented or at risk'],
  insource_outsource: ['Bring more in-house','Outsource more','Stay the same'],
};

const SUPPLIERS_FIELD_LABELS_CURRENT = {
  key_partners: 'Key external partners',
  outsource_pct: 'Outsourced proportion',
  dependency_risk: 'Dependency risk',
  contract_status: 'Contract status',
  strategic_partnerships: 'Strategic partnership opportunities',
  make_vs_buy_flagged: 'Make vs buy decisions flagged',
  supplier_challenges: 'Current challenges',
};

const SUPPLIERS_FIELD_LABELS_FUTURE = {
  partner_strategy: 'Partnership strategy',
  insource_outsource: 'Insource vs outsource direction',
};

function SuppliersDialogue({ currentData, futureData, onChange, onFutureChange, hasFutureState, sectionKey }) {
  const TRANSCRIPT_KEY_CURRENT = '_suppliers_current_transcript';
  const TRANSCRIPT_KEY_FUTURE = '_suppliers_future_transcript';

  const [phase, setPhase] = useState('intro');
  useEffect(() => {
    if (phase === 'extracting') track('design_conversation_completed', { section: sectionKey });
  }, [phase, sectionKey]);
  const [isFutureMode, setIsFutureMode] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [savedMakeVsBuy, setSavedMakeVsBuy] = useState(null);
  const chatContainerRef = useRef(null);
  const questionCount = useRef(0);
  // Cross-card context — Direction snapshot. Injected into opening
  // and followup prompts so this dialogue doesn't re-ask the leader
  // anything already captured upstream (stage, revenue model,
  // customer segments, value proposition, etc.).
  const directionContextBlock = buildDirectionContextBlock(readDirectionCanvas());

  // Evaluation flow — kicks off /evaluate-section after the leader
  // confirms the extracted data. The DialogueEvalModal wrapper at
  // the top of the reviewing-phase return handles the modal lifecycle.
  const sectionMeta = OM_SECTIONS.find(s => s.key === sectionKey);
  const evalFlow = useEvaluationFlow({
    sectionKey,
    sectionTitle: sectionMeta?.title || sectionKey,
    sectionFields: sectionMeta?.current_fields || [],
    repeatable: false,
  });

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // ── Decision Guru handoff helpers ───────────────────────────────
  function openDecisionGuruInline() {
    // Mid-conversation: open in new tab so the dialogue isn't lost.
    // No prefill at this point — extraction hasn't happened yet.
    track('decision_guru_routed', { source: 'design_canvas' });
    window.open('/decision', '_blank', 'noopener');
  }

  function openDecisionGuruWithTopic(topic) {
    if (topic) {
      try {
        localStorage.setItem('guruone_decision_prefill', JSON.stringify({ title: topic }));
      } catch { /* non-fatal */ }
    }
    track('decision_guru_routed', { source: 'design_canvas' });
    window.open('/decision', '_blank', 'noopener');
  }

  // ── Prompt builders ─────────────────────────────────────────────
  // Note: the backend /chat endpoint injects PRINCIPLES_PROMPT into the
  // system prompt automatically — we don't need to repeat it here.

  function buildOpeningPrompt(future) {
    if (!future) {
      return `You are GuruOne Design Guru, helping a business leader map their supplier and partner picture.${directionContextBlock}

WHAT TO COVER IN ORDER:
1. Critical dependencies — the few suppliers or partners whose absence would halt delivery or damage a client relationship within a week
2. Dependency depth — for each critical partner: is the relationship documented, what are the terms, what is the leverage
3. Outsourcing picture — what proportion of delivery depends on external partners, and whether that is deliberate
4. Cost and concentration risk — spend as proportion of revenue or cost base, where risk is highest
5. Strategic partnerships — referral relationships, complementary service providers, white-label arrangements. Surface whether opportunities exist. Do not design partnerships in this conversation.
6. Current challenges — the biggest supplier or partner challenge right now

MAKE VS BUY RULE: If the business leader mentions a significant outsourced function, flag it early: "That make vs buy question is often bigger than it looks — worth coming back to at the end." Before emitting READY_TO_SUMMARISE, if make vs buy has surfaced, say: "Before we wrap up — you mentioned [capability]. That make vs buy question is worth thinking through properly. I'd suggest taking it to Decision Guru for a structured analysis — I'll note it in your output so you don't lose it." Then emit READY_TO_SUMMARISE.

STRATEGIC PARTNERSHIPS RULE: Keep Beat 5 light. For any complex partnership structure, say: "That sounds like it could be valuable — but the structure is complex enough that it's worth a proper analysis in Decision Guru rather than designing it here."

After 6–8 exchanges covering all beats, emit READY_TO_SUMMARISE on its own line.

Open with: "If your three most important external partners stopped trading this week — what would you have to replace, and how quickly could you replace it?"`;
    }
    if (hasFutureState) {
      return `You are GuruOne reviewing an AI-generated future supplier and partner strategy with the business leader.

AI-GENERATED FUTURE STATE:
Partnership strategy: ${futureData?.partner_strategy || '(no specific suggestion yet)'}
Insource vs outsource direction: ${futureData?.insource_outsource || '(no specific suggestion yet)'}

Walk through each element. For each: present what was suggested, ask whether it matches their thinking. Probe whether switching costs, capability build time, and relationship implications have been accounted for.

If make vs buy was flagged in current state (${currentData?.make_vs_buy_flagged || 'none recorded'}), raise it: "I also noted a potential make vs buy decision around ${currentData?.make_vs_buy_flagged || '[the flagged item]'} — is that something you want to take to Decision Guru?"

After reviewing all elements, emit READY_TO_SUMMARISE on its own line.

Open with: "I've put together a suggested future state for your supplier and partner model. Let me walk you through it — starting with partnership strategy: ${futureData?.partner_strategy || '(no specific suggestion yet)'}. Does that match your thinking?"`;
    }
    return `You are GuruOne helping a business leader design their future supplier and partner model.

CURRENT STATE CONTEXT:
Key partners: ${currentData?.key_partners || 'not captured'}
Outsource proportion: ${currentData?.outsource_pct ?? 'not captured'}%
Dependency risk: ${currentData?.dependency_risk || 'not captured'}
Make vs buy flagged: ${currentData?.make_vs_buy_flagged || 'none identified'}

Cover three areas in order:
1. Partnership strategy — which relationships to deepen (strategic value), which to exit or reduce (cost, risk, or no longer fit), which to build new
2. Insource vs outsource direction — frame as economics first: "Does the margin at your current scale justify building this capability? What would the fully-loaded cost be?" Then strategic: "Does owning this become a differentiator?" Then risk: "How exposed are you if this partner changes terms?"
3. If make vs buy was flagged — resurface: "You mentioned ${currentData?.make_vs_buy_flagged || '[the flagged item]'} earlier. That's a decision worth taking to Decision Guru for a structured analysis rather than deciding here."

For any complex strategic partnership: "That sounds valuable — but the structure is complex enough that it's worth a proper analysis in Decision Guru rather than designing it here."

After all three areas covered, emit READY_TO_SUMMARISE on its own line.

Open with: "Let's think about your supplier and partner model going forward. Are there relationships you want to deepen because they're genuinely strategic — not just useful? And are there dependencies you want to reduce because they're too risky or too costly?"`;
  }

  function buildFollowupPrompt() {
    if (!isFutureMode) {
      return `You are GuruOne mapping a business leader's supplier and partner picture. You have asked ${questionCount.current} questions.${directionContextBlock}

Continue covering remaining beats:
- Dependency depth — for each critical partner: documented, terms, leverage
- Outsourcing proportion — deliberate or default
- Cost and concentration risk
- Strategic partnerships — referral, complementary service, white-label (surface only)
- Current challenges

MAKE VS BUY: If a significant outsourced function has come up and not yet been flagged, flag it: "That make vs buy question is worth thinking through properly — I'll note it for Decision Guru."

Before emitting READY_TO_SUMMARISE, if make vs buy has surfaced during the conversation: "Before we wrap up — you mentioned [capability]. That make vs buy question is worth a proper structured analysis. I'd suggest taking it to Decision Guru — I'll note it in your output."

Then emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn.`;
    }
    return `You are GuruOne helping a business leader design their future supplier and partner model. You have asked ${questionCount.current} questions.

Continue covering any of the three areas not yet fully addressed:
1. Partnership strategy — relationships to deepen / exit / build
2. Insource vs outsource direction — economics, strategic, risk lenses
3. Make vs buy decisions previously flagged — resurface and offer Decision Guru handoff

For complex strategic partnerships, route to Decision Guru rather than designing here.

After all areas covered, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn.`;
  }

  function buildExtractPrompt() {
    if (!isFutureMode) {
      return `Based on this conversation, extract supplier and partner data.

Return ONLY valid JSON starting with {:
{
  "key_partners": "description of critical external partners — who they are and what the business depends on them for",
  "outsource_pct": number between 0 and 100 representing estimated percentage of delivery dependent on external partners,
  "dependency_risk": "one of exactly: 'Low' | 'Medium' | 'High'",
  "contract_status": "one of exactly: 'Documented and current' | 'Partially documented' | 'Undocumented or at risk'",
  "strategic_partnerships": "existing or potential strategic partnership opportunities — referral, complementary service, white-label. Omit if none discussed.",
  "make_vs_buy_flagged": "significant make vs buy decisions identified for Decision Guru. Omit if none surfaced.",
  "supplier_challenges": "biggest supplier or partner challenge in the business leader's own words"
}
Omit any field not clearly evidenced by the conversation.`;
    }
    return `Based on this conversation, extract the agreed future state for suppliers and partners.

Return ONLY valid JSON starting with {:
{
  "partner_strategy": "which relationships to deepen, exit, or build — and the strategic rationale",
  "insource_outsource": "one of exactly: 'Bring more in-house' | 'Outsource more' | 'Stay the same'"
}
Omit any field not clearly evidenced by the conversation.`;
  }

  // ── Conversation lifecycle ───────────────────────────────────────
  async function startConversation(future) {
    setIsFutureMode(future);
    setMessages([]);
    setInput('');
    setEditedData(null);
    questionCount.current = 0;
    setLoading(true);
    setPhase('chatting');
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: [{ role: 'user', content: 'Begin the dialogue.' }],
        context: buildOpeningPrompt(future),
      });
      setMessages([{ role: 'assistant', content: res.data.reply }]);
      questionCount.current = 1;
    } catch {
      setMessages([{ role: 'assistant', content: future
        ? "Let's think about your supplier and partner model going forward. Are there relationships worth deepening, and dependencies worth reducing?"
        : "If your three most important external partners stopped trading this week — what would you have to replace, and how quickly could you replace it?" }]);
      questionCount.current = 1;
    }
    setLoading(false);
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);
    try {
      questionCount.current += 1;
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: newMessages,
        context: buildFollowupPrompt() + DIALOGUE_RULES_BLOCK,
      });
      const reply = res.data.reply || '';
      const isReady = reply.includes('READY_TO_SUMMARISE');
      const deferredQuestion = isReady ? maybeDeferSummariseForPendingQuestion(reply, questionCount.current, 9) : null;
      if (deferredQuestion) {
        setMessages(prev => [...prev, { role: 'assistant', content: deferredQuestion }]);
      } else if (isReady || questionCount.current >= 9) {
        const clean = reply.replace(/READY_TO_SUMMARISE/g, '').trim();
        const convo = clean ? [...newMessages, { role: 'assistant', content: clean }] : newMessages;
        if (clean) setMessages(prev => [...prev, { role: 'assistant', content: clean }]);
        await extractData(convo);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong — please try again.' }]);
    }
    setLoading(false);
  }

  async function extractData(convo) {
    setMessages(prev => [...prev, { role: 'assistant', content: "Good — I have enough to work with. Let me synthesise what you've shared. You'll review and edit everything before saving." }]);
    setPhase('extracting');
    const founderAnswers = convo.filter(m => m.role === 'user').map((m, i) => `Answer ${i + 1}: ${m.content}`).join('\n\n');
    const condensed = [{ role: 'user', content: `Here are the business leader's answers:\n\n${founderAnswers}\n\nExtract the structured data now.` }];
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, { messages: condensed, context: buildExtractPrompt() });
      const raw = res.data.reply || '';
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1 && e !== -1) {
        const parsed = JSON.parse(raw.slice(s, e + 1));
        setEditedData(parsed);
        setPhase('reviewing');
        return;
      }
    } catch { /* fall through */ }
    setPhase('intro');
    setMessages(prev => [...prev, { role: 'assistant', content: "I wasn't able to extract automatically this time. Use Quick entry to fill in the fields directly." }]);
  }

  function handleSave() {
    if (!editedData) return;
    const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
    if (isFutureMode) {
      onFutureChange({ ...futureData, ...editedData, [TRANSCRIPT_KEY_FUTURE]: transcript });
      setSavedMakeVsBuy(null);
      setPhase('saved');
      return;
    }
    evalFlow.runEvaluation({ fields: editedData, transcript });
    return;
  }

  // ── Render ───────────────────────────────────────────────────────
  const colour = SUPPLIERS_DIALOGUE_COLOUR;
  const bg = SUPPLIERS_DIALOGUE_BG;
  const border = SUPPLIERS_DIALOGUE_BORDER;

  if (phase === 'intro') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1.25rem 1.5rem', marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.75rem' }}>
          Suppliers &amp; partners dialogue
        </div>
        <p style={{ fontSize: '0.82rem', color: '#333', lineHeight: 1.6, margin: '0 0 1rem' }}>
          We&apos;ll surface your critical dependencies, the real outsourcing picture, where concentration risk lives and any strategic partnership opportunities worth exploring. If a significant make vs buy decision comes up, we&apos;ll flag it for a proper analysis in Decision Guru rather than deciding it here.
        </p>
        <div style={{ fontSize: '0.68rem', color: '#888', lineHeight: 1.5, marginBottom: '1.25rem', borderTop: `1px solid ${border}`, paddingTop: '0.75rem' }}>
          <strong>How it works:</strong> GuruOne asks one question at a time and probes for specifics. At the end you review and edit a structured summary before saving. Typically 6–8 exchanges.
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button onClick={() => startConversation(false)}
            style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Explore current state →
          </button>
          {hasFutureState && (
            <button onClick={() => startConversation(true)}
              style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: 'white', color: colour, border: `1.5px solid ${colour}`, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Review future state →
            </button>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'extracting') {
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {messages.slice(-2).map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '0.6rem 0.9rem', borderRadius: 10, background: m.role === 'user' ? colour : bg, color: m.role === 'user' ? 'white' : '#0F6E56', fontSize: '0.8rem', lineHeight: 1.5 }}>{m.content}</div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: colour, fontSize: '0.8rem' }}>
          <div style={{ width: 14, height: 14, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          Synthesising your answers…
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      </div>
    );
  }

  if (phase === 'reviewing' && editedData) {
    const fieldLabels = isFutureMode ? SUPPLIERS_FIELD_LABELS_FUTURE : SUPPLIERS_FIELD_LABELS_CURRENT;
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <DialogueEvalModal
          sectionMeta={sectionMeta}
          evalFlow={evalFlow}
          onCommitWithEval={(evaluation) => {
            const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
            const payload = { ...currentData, ...editedData, [TRANSCRIPT_KEY_CURRENT]: transcript };
            if (evaluation) payload._evaluation = evaluation;
            const flagged = typeof editedData.make_vs_buy_flagged === 'string' && editedData.make_vs_buy_flagged.trim().length > 0
              ? editedData.make_vs_buy_flagged.trim() : null;
            setSavedMakeVsBuy(flagged);
            onChange(payload);
            setPhase('saved');
          }}
          onRefine={() => setPhase('chatting')}
        />
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colour, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Review &amp; edit — {isFutureMode ? 'future state' : 'current state'}
        </div>
        <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: '0.85rem', lineHeight: 1.5 }}>
          AI-drafted from your conversation — edit any field until it precisely matches reality before saving.
        </div>
        {Object.entries(fieldLabels).map(([key, label]) => {
          const rawValue = editedData[key];
          const chipOpts = SUPPLIERS_CHIP_OPTIONS[key];
          if (chipOpts) {
            const value = rawValue || '';
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>{label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {chipOpts.map(opt => {
                    const active = value === opt;
                    return (
                      <button key={opt} onClick={() => setEditedData(p => ({ ...p, [key]: active ? '' : opt }))}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? bg : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          if (key === 'outsource_pct') {
            const numericValue = typeof rawValue === 'number' ? rawValue : 0;
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input type="range" min="0" max="100" step="5"
                    value={numericValue}
                    onChange={e => setEditedData(p => ({ ...p, [key]: Number(e.target.value) }))}
                    style={{ flex: 1, cursor: 'pointer', accentColor: colour }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                    <input type="number" min="0" max="100"
                      value={numericValue}
                      onChange={e => setEditedData(p => ({ ...p, [key]: Math.min(100, Math.max(0, Number(e.target.value))) }))}
                      style={{ width: 52, fontSize: '0.82rem', padding: '0.3rem 0.4rem', borderRadius: 6, border: `1px solid ${border}`, fontFamily: 'inherit', textAlign: 'center', boxSizing: 'border-box' }} />
                    <span style={{ fontSize: '0.82rem', color: '#555' }}>%</span>
                  </div>
                </div>
              </div>
            );
          }
          const value = rawValue || '';
          return (
            <div key={key} style={{ marginBottom: '0.85rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
              <textarea value={value} onChange={e => setEditedData(p => ({ ...p, [key]: e.target.value }))} rows={3}
                style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: `1px solid ${border}`, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55 }} />
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.6rem 1.25rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save
          </button>
          <button onClick={() => setPhase('chatting')}
            style={{ padding: '0.6rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${border}`, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Continue conversation
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'saved') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>
            {isFutureMode ? 'Future state captured' : 'Current state captured'}
          </div>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#444', margin: '0 0 0.85rem', lineHeight: 1.55 }}>
          Your answers are saved. {isFutureMode
            ? 'Switch to the Future State tab to see them, or edit any field there in Quick mode.'
            : 'You can edit any field in Quick mode below, or run another conversation to refine.'}
        </p>
        {savedMakeVsBuy && (
          <div style={{ marginTop: '0.5rem', padding: '0.75rem 1rem', background: 'white', border: `1px solid ${border}`, borderRadius: 8 }}>
            <div style={{ fontSize: '0.72rem', color: '#555', marginBottom: '0.5rem', lineHeight: 1.5 }}>
              You flagged a make vs buy decision worth a structured analysis:
            </div>
            <div style={{ fontSize: '0.82rem', color: '#1a1a2e', fontStyle: 'italic', marginBottom: '0.75rem', lineHeight: 1.5 }}>
              &ldquo;{savedMakeVsBuy}&rdquo;
            </div>
            <button onClick={() => openDecisionGuruWithTopic(savedMakeVsBuy)}
              style={{ padding: '0.55rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Take this to Decision Guru →
            </button>
          </div>
        )}
        <button onClick={() => setPhase('intro')}
          style={{ background: 'none', border: 'none', color: colour, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', padding: '0.5rem 0', width: 'auto', marginTop: '0.5rem' }}>
          Run another conversation →
        </button>
      </div>
    );
  }

  // Chatting phase
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.5rem' }}>
        {isFutureMode ? 'Future state — design / review' : 'Current state — explore'}
      </div>
      <div ref={chatContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '0.85rem', maxHeight: 320, overflowY: 'auto', paddingRight: '0.2rem' }}>
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          const showDgButton = !isUser && typeof m.content === 'string' && m.content.includes('Decision Guru');
          return (
            <div key={i} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '88%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '0.6rem 0.85rem', borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px', background: isUser ? colour : bg, color: isUser ? 'white' : '#0F6E56', fontSize: '0.82rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                {m.content}
              </div>
              {showDgButton && (
                <button onClick={openDecisionGuruInline}
                  style={{ marginTop: '0.5rem', padding: '0.4rem 1rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto', alignSelf: 'flex-start' }}>
                  Take to Decision Guru →
                </button>
              )}
            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: colour, fontStyle: 'italic' }}>
            <div style={{ width: 11, height: 11, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            GuruOne is thinking…
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type your answer… (Enter to send)"
          disabled={loading} rows={2}
          style={{ flex: 1, fontSize: '0.82rem', padding: '0.6rem 0.8rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          style={{ padding: '0.6rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
          Send
        </button>
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

// ── LocationDialogue ──────────────────────────────────────────────
// Standalone dialogue for the location_infrastructure section.
// Mirrors PeopleDialogue: phase machine, /api/canvas/chat endpoint
// (which auto-injects PRINCIPLES_PROMPT), READY_TO_SUMMARISE marker.
// Unlike PeopleDialogue (one mode per mount), this component handles
// BOTH current and future state from a single mount on the current
// tab — the user picks the mode on the intro screen via two buttons.
//
// Props:
//   currentData      — sectionData.current (read for context, written via onChange)
//   futureData       — sectionData.future  (read for context, written via onFutureChange)
//   onChange         — write back to sectionData.current
//   onFutureChange   — write back to sectionData.future
//   hasFutureState   — true when futureData has target_locations or property_strategy;
//                      gates the second intro button (review the suggested future state)

const LOCATION_DIALOGUE_COLOUR = '#9B6B3A';
const LOCATION_DIALOGUE_BG = '#F8F2EC';
const LOCATION_DIALOGUE_BORDER = '#DCC3A5';

const LOCATION_CHIP_OPTIONS = {
  working_arrangement: ['Office-based','Hybrid','Remote','Multi-site'],
  property_strategy: ['Expand current','New office','More remote','Co-working','None needed'],
};

const LOCATION_FIELD_LABELS_CURRENT = {
  working_arrangement: 'Working arrangement',
  site_locations: 'Sites and working arrangements',
  customer_locations: 'Customer locations and proximity',
  infrastructure_constraints: 'Infrastructure constraints',
  location_challenges: 'Current challenges',
};

const LOCATION_FIELD_LABELS_FUTURE = {
  target_locations: 'Target locations',
  property_strategy: 'Property strategy',
  tech_infrastructure_requirements: 'Technical infrastructure requirements',
};

function LocationDialogue({ currentData, futureData, onChange, onFutureChange, hasFutureState, sectionKey }) {
  const TRANSCRIPT_KEY_CURRENT = '_location_current_transcript';
  const TRANSCRIPT_KEY_FUTURE = '_location_future_transcript';

  const [phase, setPhase] = useState('intro');
  useEffect(() => {
    if (phase === 'extracting') track('design_conversation_completed', { section: sectionKey });
  }, [phase, sectionKey]);
  const [isFutureMode, setIsFutureMode] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const chatContainerRef = useRef(null);
  const questionCount = useRef(0);
  // Cross-card context — Direction snapshot. Injected into opening
  // and followup prompts so this dialogue doesn't re-ask the leader
  // anything already captured upstream (stage, revenue model,
  // customer segments, value proposition, etc.).
  const directionContextBlock = buildDirectionContextBlock(readDirectionCanvas());

  // Evaluation flow — kicks off /evaluate-section after the leader
  // confirms the extracted data. The DialogueEvalModal wrapper at
  // the top of the reviewing-phase return handles the modal lifecycle.
  const sectionMeta = OM_SECTIONS.find(s => s.key === sectionKey);
  const evalFlow = useEvaluationFlow({
    sectionKey,
    sectionTitle: sectionMeta?.title || sectionKey,
    sectionFields: sectionMeta?.current_fields || [],
    repeatable: false,
  });

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // ── Prompt builders ────────────────────────────────────────────
  // Note: the backend /chat endpoint injects PRINCIPLES_PROMPT into the
  // system prompt automatically — we don't need to repeat it here.

  function buildOpeningPrompt(future) {
    if (!future) {
      return `You are GuruOne Design Guru, helping a business leader map their location and infrastructure picture.${directionContextBlock}

WHAT TO COVER IN ORDER:
1. Full geographic footprint — every site, depot, hub, or location and what happens at each
2. Working patterns — the actual arrangement per site or team, not the stated policy. For multi-site businesses probe each location separately.
3. Customer locations — where customers are, whether physical proximity matters to the commercial relationship, whether any deals have been lost or relationships are harder because of current location
4. Constraints — what the current footprint limits: hiring, delivery, customer access, or growth
5. Decisions coming up — any property or location decisions in the next 12–18 months
6. Current challenges — the business leader's biggest location or infrastructure challenge right now

RULES:
- One question per turn
- Do not accept a single-line answer for a multi-site business — probe each location separately
- After 5–7 exchanges covering all beats, emit READY_TO_SUMMARISE on its own line

Open with: "Walk me through everywhere your business physically operates — every office, depot, hub, or site — and for each one, tell me what happens there and how many people work from it."`;
    }
    if (hasFutureState) {
      return `You are GuruOne reviewing an AI-generated future location plan with the business leader.

AI-GENERATED FUTURE STATE:
Target locations: ${futureData?.target_locations || '(no specific suggestion yet)'}
Property strategy: ${futureData?.property_strategy || '(no specific suggestion yet)'}
Technical requirements: ${futureData?.tech_infrastructure_requirements || '(no specific suggestion yet)'}

Walk through each element. For each: present what was suggested, ask whether it matches their thinking.
If customer proximity creates tension with employee location stability, raise it if not already discussed.
After reviewing all elements, emit READY_TO_SUMMARISE on its own line.

Open with: "I've put together a suggested future state for your location and infrastructure. Let me walk you through it — starting with where I've suggested the business operates: ${futureData?.target_locations || '(no specific suggestion yet)'}. Does that match your thinking?"`;
    }
    return `You are GuruOne helping a business leader design their future location and infrastructure.

CURRENT STATE CONTEXT:
Sites and arrangements: ${currentData?.site_locations || 'not yet captured'}
Customer locations: ${currentData?.customer_locations || 'not yet captured'}
Constraints: ${currentData?.infrastructure_constraints || 'not yet captured'}

Cover three areas in order:
1. Target locations — where the business needs to operate to serve its direction and its customers
2. Property strategy — what changes to the physical estate are needed and why
3. Technical infrastructure requirements — for tech-dependent or data-sensitive businesses, what any new or expanded location needs to support in terms of connectivity, security, or proprietary systems. Keep this brief — detail sits in the Apps & Technology and Data, Flow & Security sections.

Where moving closer to customers would mean moving away from where current employees chose to work, name that tension directly: "Moving closer to your customers might mean moving away from where your current team is based — how are you thinking about that trade-off?"

After covering all three areas, emit READY_TO_SUMMARISE on its own line.

Open with: "Given where the business is heading — the customers you want to reach, the people you need to hire — does your current footprint set you up for that, or does something need to change?"`;
  }

  function buildFollowupPrompt() {
    if (!isFutureMode) {
      return `You are GuruOne mapping a business leader's location and infrastructure picture. You have asked ${questionCount.current} questions so far.${directionContextBlock}

Continue covering any beats not yet addressed:
- Working patterns per site or team — the real pattern, not the stated policy
- Customer locations and whether proximity affects commercial relationships
- What the current footprint limits — hiring, delivery, customer access
- Upcoming property decisions (lease renewals, new sites, depot changes)
- Biggest current challenge

After all beats are covered, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn. For multi-site businesses, probe per location.`;
    }
    return `You are GuruOne helping a business leader design their future location and infrastructure. You have asked ${questionCount.current} questions so far.

${hasFutureState
  ? `Continue walking through the suggested future state. For each of these still outstanding, present what was suggested then probe for the business leader's view:
- Target locations
- Property strategy
- Technical infrastructure requirements`
  : `Continue covering any of these areas not yet addressed:
- Target locations and rationale
- Property strategy
- Technical infrastructure requirements (brief — detail sits in other sections)`}

After all areas are covered, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn. Keep responses concise.`;
  }

  function buildExtractPrompt() {
    if (!isFutureMode) {
      return `Based on this conversation, extract location and infrastructure data.

Return ONLY valid JSON starting with {:
{
  "working_arrangement": "one of exactly: 'Office-based' | 'Hybrid' | 'Remote' | 'Multi-site'",
  "site_locations": "full geographic footprint — all sites, depots, hubs, and the working arrangement at each",
  "customer_locations": "where customers are located and whether physical proximity matters to the business model or commercial relationships",
  "infrastructure_constraints": "what the current footprint limits — hiring pool, delivery capability, customer access, or growth",
  "location_challenges": "the business leader's biggest location or infrastructure challenge in their own words"
}
Omit any field not clearly evidenced by the conversation.`;
    }
    return `Based on this conversation, extract the agreed future state for location and infrastructure.

Return ONLY valid JSON starting with {:
{
  "target_locations": "where the business plans to operate and the rationale",
  "property_strategy": "one of exactly: 'Expand current' | 'New office' | 'More remote' | 'Co-working' | 'None needed'",
  "tech_infrastructure_requirements": "specific technical requirements for future locations — connectivity, security, proprietary systems. Brief — detail sits in other sections."
}
Omit any field not clearly evidenced by the conversation.`;
  }

  // ── Conversation lifecycle ────────────────────────────────────
  async function startConversation(future) {
    setIsFutureMode(future);
    setMessages([]);
    setInput('');
    setEditedData(null);
    questionCount.current = 0;
    setLoading(true);
    setPhase('chatting');
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: [{ role: 'user', content: 'Begin the dialogue.' }],
        context: buildOpeningPrompt(future),
      });
      setMessages([{ role: 'assistant', content: res.data.reply }]);
      questionCount.current = 1;
    } catch {
      setMessages([{ role: 'assistant', content: future
        ? "Let's design the future state for your location and infrastructure. Given where the business is heading, does your current footprint set you up for that?"
        : "Walk me through everywhere your business physically operates — every office, depot, hub, or site." }]);
      questionCount.current = 1;
    }
    setLoading(false);
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);
    try {
      questionCount.current += 1;
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: newMessages,
        context: buildFollowupPrompt() + DIALOGUE_RULES_BLOCK,
      });
      const reply = res.data.reply || '';
      const isReady = reply.includes('READY_TO_SUMMARISE');
      const deferredQuestion = isReady ? maybeDeferSummariseForPendingQuestion(reply, questionCount.current, 9) : null;
      if (deferredQuestion) {
        setMessages(prev => [...prev, { role: 'assistant', content: deferredQuestion }]);
      } else if (isReady || questionCount.current >= 9) {
        const clean = reply.replace(/READY_TO_SUMMARISE/g, '').trim();
        const convo = clean ? [...newMessages, { role: 'assistant', content: clean }] : newMessages;
        if (clean) setMessages(prev => [...prev, { role: 'assistant', content: clean }]);
        await extractData(convo);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong — please try again.' }]);
    }
    setLoading(false);
  }

  async function extractData(convo) {
    setMessages(prev => [...prev, { role: 'assistant', content: "Good — I have enough to work with. Let me synthesise what you've shared. You'll review and edit everything before saving." }]);
    setPhase('extracting');
    const founderAnswers = convo.filter(m => m.role === 'user').map((m, i) => `Answer ${i + 1}: ${m.content}`).join('\n\n');
    const condensed = [{ role: 'user', content: `Here are the business leader's answers:\n\n${founderAnswers}\n\nExtract the structured data now.` }];
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, { messages: condensed, context: buildExtractPrompt() });
      const raw = res.data.reply || '';
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1 && e !== -1) {
        const parsed = JSON.parse(raw.slice(s, e + 1));
        setEditedData(parsed);
        setPhase('reviewing');
        return;
      }
    } catch { /* fall through */ }
    setPhase('intro');
    setMessages(prev => [...prev, { role: 'assistant', content: "I wasn't able to extract automatically this time. Use Quick entry to fill in the fields directly." }]);
  }

  function handleSave() {
    if (!editedData) return;
    const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
    if (isFutureMode) {
      onFutureChange({ ...futureData, ...editedData, [TRANSCRIPT_KEY_FUTURE]: transcript });
      setPhase('saved');
      return;
    }
    evalFlow.runEvaluation({ fields: editedData, transcript });
  }

  // ── Render ────────────────────────────────────────────────────
  const colour = LOCATION_DIALOGUE_COLOUR;
  const bg = LOCATION_DIALOGUE_BG;
  const border = LOCATION_DIALOGUE_BORDER;

  if (phase === 'intro') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1.25rem 1.5rem', marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.75rem' }}>
          Location &amp; infrastructure dialogue
        </div>
        <p style={{ fontSize: '0.82rem', color: '#333', lineHeight: 1.6, margin: '0 0 1rem' }}>
          We&apos;ll map every place the business operates from — sites, depots, hubs, home-working clusters — the working arrangements at each, where your customers are and whether proximity matters, and what the current footprint limits. We&apos;ll also surface any property decisions on the horizon and your single biggest location or infrastructure challenge.
        </p>
        <div style={{ fontSize: '0.68rem', color: '#888', lineHeight: 1.5, marginBottom: '1.25rem', borderTop: `1px solid ${border}`, paddingTop: '0.75rem' }}>
          <strong>How it works:</strong> GuruOne asks one question at a time and probes for specifics. At the end you review and edit a structured summary before saving. Typically 5–7 exchanges.
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button onClick={() => startConversation(false)}
            style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Explore current state →
          </button>
          {hasFutureState && (
            <button onClick={() => startConversation(true)}
              style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: 'white', color: colour, border: `1.5px solid ${colour}`, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Review future state →
            </button>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'extracting') {
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {messages.slice(-2).map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '0.6rem 0.9rem', borderRadius: 10, background: m.role === 'user' ? colour : bg, color: m.role === 'user' ? 'white' : '#5C3A1A', fontSize: '0.8rem', lineHeight: 1.5 }}>{m.content}</div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: colour, fontSize: '0.8rem' }}>
          <div style={{ width: 14, height: 14, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          Synthesising your answers…
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      </div>
    );
  }

  if (phase === 'reviewing' && editedData) {
    const fieldLabels = isFutureMode ? LOCATION_FIELD_LABELS_FUTURE : LOCATION_FIELD_LABELS_CURRENT;
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <DialogueEvalModal
          sectionMeta={sectionMeta}
          evalFlow={evalFlow}
          onCommitWithEval={(evaluation) => {
            const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
            const payload = { ...currentData, ...editedData, [TRANSCRIPT_KEY_CURRENT]: transcript };
            if (evaluation) payload._evaluation = evaluation;
            onChange(payload);
            setPhase('saved');
          }}
          onRefine={() => setPhase('chatting')}
        />
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colour, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Review &amp; edit — {isFutureMode ? 'future state' : 'current state'}
        </div>
        <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: '0.85rem', lineHeight: 1.5 }}>
          AI-drafted from your conversation — edit any field until it precisely matches reality before saving.
        </div>
        {Object.entries(fieldLabels).map(([key, label]) => {
          const value = editedData[key] || '';
          const chipOpts = LOCATION_CHIP_OPTIONS[key];
          if (chipOpts) {
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>{label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {chipOpts.map(opt => {
                    const active = value === opt;
                    return (
                      <button key={opt} onClick={() => setEditedData(p => ({ ...p, [key]: active ? '' : opt }))}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? bg : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          return (
            <div key={key} style={{ marginBottom: '0.85rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
              <textarea value={value} onChange={e => setEditedData(p => ({ ...p, [key]: e.target.value }))} rows={3}
                style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: `1px solid ${border}`, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55 }} />
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.6rem 1.25rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save
          </button>
          <button onClick={() => setPhase('chatting')}
            style={{ padding: '0.6rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${border}`, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Continue conversation
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'saved') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>
            {isFutureMode ? 'Future state captured' : 'Current state captured'}
          </div>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#444', margin: '0 0 0.85rem', lineHeight: 1.55 }}>
          Your answers are saved. {isFutureMode
            ? 'Switch to the Future State tab to see them, or edit any field there in Quick mode.'
            : 'You can edit any field in Quick mode below, or run another conversation to refine.'}
        </p>
        <button onClick={() => setPhase('intro')}
          style={{ background: 'none', border: 'none', color: colour, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', padding: '0.5rem 0', width: 'auto' }}>
          Run another conversation →
        </button>
      </div>
    );
  }

  // Chatting phase
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.5rem' }}>
        {isFutureMode ? 'Future state — design / review' : 'Current state — explore'}
      </div>
      <div ref={chatContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '0.85rem', maxHeight: 320, overflowY: 'auto', paddingRight: '0.2rem' }}>
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          return (
            <div key={i} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '88%', padding: '0.6rem 0.85rem', borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px', background: isUser ? colour : bg, color: isUser ? 'white' : '#5C3A1A', fontSize: '0.82rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              {m.content}
            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: colour, fontStyle: 'italic' }}>
            <div style={{ width: 11, height: 11, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            GuruOne is thinking…
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type your answer… (Enter to send)"
          disabled={loading} rows={2}
          style={{ flex: 1, fontSize: '0.82rem', padding: '0.6rem 0.8rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          style={{ padding: '0.6rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
          Send
        </button>
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

// ── RisksEditor ───────────────────────────────────────────────────
// Per-card forward-looking risks. Distinct from current_challenges
// (today) — these are things that COULD happen in the next 12-24
// months. Future-state risks are handled by Delivery Guru.
//
// Storage shape: current.key_risks is an array of
//   { id: string, text: string, source: 'ai' | 'user' }
// Empty / missing array means no risks captured yet.
//
// The component is intentionally self-contained so we can later wrap
// it in a RisksDialogue (similar to PeopleDialogue) without changing
// the data shape.

function RisksEditor({ section, current, onChange }) {
  const navigate = useNavigate();
  const risks = Array.isArray(current?.key_risks) ? current.key_risks : [];
  const [newRiskText, setNewRiskText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  // Generated risks now land in a pending list, not the saved canvas.
  // The leader chooses Accept or Delete on each before they become
  // part of the section.
  const [pendingRisks, setPendingRisks] = useState([]);

  const update = (next) => onChange({ ...current, key_risks: next });

  // Route a risk into Decision Guru as a structured challenge. The
  // challenge framing converts the risk into a forward-looking
  // question Decision Guru can analyse with its lens machinery.
  // strategyContext gives Decision Guru the Direction canvas summary
  // so it doesn't ask the leader to re-state context.
  function exploreRiskInDecisionGuru(risk) {
    let strategyContext = '';
    try {
      const raw = localStorage.getItem(STORAGE_KEY_STRATEGY);
      if (raw) {
        const all = JSON.parse(raw);
        const data = Object.values(all).find(v => typeof v === 'object' && v !== null) || {};
        strategyContext = formatStrategyContext(data) || '';
      }
    } catch { /* non-fatal */ }
    const challenge = `Risk on ${section.title}: ${risk.text}\n\nHelp me think through how to address this — likelihood, impact, mitigation options, and what to do next.`;
    track('risk_to_decision_guru', { section: section.key, riskId: risk.id });
    navigate('/decision', {
      state: {
        challenge,
        sectionKey: section.key,
        sectionTitle: section.title,
        strategyContext,
      },
    });
  }

  const newId = () => `r_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

  const handleAdd = () => {
    const trimmed = newRiskText.trim();
    if (!trimmed) return;
    update([...risks, { id: newId(), text: trimmed, source: 'user' }]);
    setNewRiskText('');
  };

  const handleRemove = (id) => update(risks.filter(r => r.id !== id));

  const startEdit = (risk) => { setEditingId(risk.id); setEditingText(risk.text); };

  const cancelEdit = () => { setEditingId(null); setEditingText(''); };

  const saveEdit = () => {
    const trimmed = editingText.trim();
    if (!trimmed) { cancelEdit(); return; }
    update(risks.map(r => r.id === editingId ? { ...r, text: trimmed } : r));
    cancelEdit();
  };

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    try {
      const strategyContext = (() => {
        try {
          const raw = localStorage.getItem(STORAGE_KEY_STRATEGY);
          if (!raw) return null;
          const all = JSON.parse(raw);
          const data = Object.values(all).find(v => typeof v === 'object' && v !== null) || {};
          return formatStrategyContext(data);
        } catch { return null; }
      })();

      const prompt = buildRisksPrompt(section, current, strategyContext, risks);
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: [{ role: 'user', content: 'Generate forward-looking risks for this section.' }],
        context: prompt,
      });
      const raw = res.data.reply || '';
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s === -1 || e === -1) throw new Error('No JSON in reply');
      const parsed = JSON.parse(raw.slice(s, e + 1));
      const generated = Array.isArray(parsed.risks) ? parsed.risks : [];
      const additions = generated
        .filter(t => typeof t === 'string' && t.trim())
        .map(t => ({ id: newId(), text: t.trim(), source: 'ai' }));
      if (additions.length === 0) throw new Error('No risks returned');
      // Append to pending — they won't hit the saved canvas until the
      // leader explicitly accepts each one.
      setPendingRisks(prev => [...prev, ...additions]);
    } catch (err) {
      setError("Couldn't generate risks just now — try again, or add one manually.");
    }
    setGenerating(false);
  }

  const acceptPending = (id) => {
    const risk = pendingRisks.find(r => r.id === id);
    if (!risk) return;
    setPendingRisks(prev => prev.filter(r => r.id !== id));
    update([...risks, risk]);
  };
  const deletePending = (id) => {
    setPendingRisks(prev => prev.filter(r => r.id !== id));
  };
  const acceptAllPending = () => {
    if (pendingRisks.length === 0) return;
    update([...risks, ...pendingRisks]);
    setPendingRisks([]);
  };
  const dismissAllPending = () => setPendingRisks([]);

  const colour = section.colour;
  const tintBg = colour + '0a';
  const tintBorder = colour + '33';

  return (
    <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', marginTop: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.4rem', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: colour }}>
            Key risks
          </div>
          <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 2, lineHeight: 1.5 }}>
            What could go wrong in the next 12–24 months — not what&apos;s happening now.
          </div>
        </div>
        <button onClick={handleGenerate} disabled={generating}
          style={{ padding: '0.45rem 0.9rem', borderRadius: 8, background: generating ? '#eee' : colour + '15', color: generating ? '#aaa' : colour, border: `1px solid ${tintBorder}`, fontSize: '0.78rem', fontWeight: 600, cursor: generating ? 'not-allowed' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0, width: 'auto' }}>
          {generating ? 'Generating…' : '✨ Generate common risks'}
        </button>
      </div>

      {error && (
        <div style={{ fontSize: '0.72rem', color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '0.4rem 0.65rem', marginBottom: '0.6rem' }}>
          {error}
        </div>
      )}

      {pendingRisks.length > 0 && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 10, padding: '0.7rem 0.85rem', marginTop: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#92400E' }}>
                Suggestions — review before saving
              </div>
              <div style={{ fontSize: '0.72rem', color: '#7B4A0F', marginTop: 2, lineHeight: 1.45 }}>
                Accept the ones that ring true; delete the ones that don&apos;t. Nothing saves to your canvas until you&apos;ve picked.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
              <button onClick={dismissAllPending}
                style={{ padding: '0.3rem 0.7rem', borderRadius: 6, background: 'white', color: '#7B4A0F', border: '1px solid #FCD34D', fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto', whiteSpace: 'nowrap' }}>
                Delete all
              </button>
              <button onClick={acceptAllPending}
                style={{ padding: '0.3rem 0.7rem', borderRadius: 6, background: '#D97706', color: 'white', border: 'none', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto', whiteSpace: 'nowrap' }}>
                Accept all
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
            {pendingRisks.map(risk => (
              <div key={risk.id} style={{ background: 'white', border: '1px solid #FDE68A', borderRadius: 7, padding: '0.5rem 0.7rem', display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#7B4A0F', background: '#FEF3C7', padding: '1px 6px', borderRadius: 10, marginRight: '0.5rem' }}>
                    Suggested
                  </span>
                  <span style={{ fontSize: '0.82rem', color: '#1a1a2e', lineHeight: 1.55 }}>{risk.text}</span>
                </div>
                <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0 }}>
                  <button onClick={() => acceptPending(risk.id)} title="Accept"
                    style={{ background: '#D97706', color: 'white', border: 'none', padding: '0.25rem 0.6rem', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
                    Accept
                  </button>
                  <button onClick={() => deletePending(risk.id)} title="Delete"
                    style={{ background: 'white', color: '#888', border: '1px solid #ddd', padding: '0.25rem 0.6rem', borderRadius: 6, fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
        {risks.length === 0 && pendingRisks.length === 0 && !generating && (
          <div style={{ fontSize: '0.78rem', color: '#999', fontStyle: 'italic', padding: '0.6rem 0' }}>
            No risks captured yet. Generate a few to start, or add one below.
          </div>
        )}
        {risks.map(risk => {
          const isEditing = editingId === risk.id;
          return (
            <div key={risk.id} style={{ background: tintBg, border: `1px solid ${tintBorder}`, borderRadius: 8, padding: '0.6rem 0.8rem' }}>
              {isEditing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <textarea autoFocus value={editingText} onChange={e => setEditingText(e.target.value)} rows={2}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveEdit(); }}
                    style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.65rem', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }} />
                  <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                    <button onClick={cancelEdit}
                      style={{ padding: '0.35rem 0.75rem', borderRadius: 6, background: 'white', color: '#666', border: '1px solid #ddd', fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
                      Cancel
                    </button>
                    <button onClick={saveEdit}
                      style={{ padding: '0.35rem 0.75rem', borderRadius: 6, background: colour, color: 'white', border: 'none', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: risk.source === 'ai' ? colour : '#777', background: risk.source === 'ai' ? colour + '22' : '#f0f0f0', padding: '1px 6px', borderRadius: 10, marginRight: '0.5rem' }}>
                        {risk.source === 'ai' ? 'AI' : 'You'}
                      </span>
                      <span style={{ fontSize: '0.82rem', color: '#1a1a2e', lineHeight: 1.55 }}>
                        {risk.text}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
                      <button onClick={() => startEdit(risk)} title="Edit"
                        style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.85rem', cursor: 'pointer', padding: '2px 6px', width: 'auto', fontFamily: 'inherit' }}>
                        ✎
                      </button>
                      <button onClick={() => handleRemove(risk.id)} title="Remove"
                        style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.85rem', cursor: 'pointer', padding: '2px 6px', width: 'auto', fontFamily: 'inherit' }}>
                        ×
                      </button>
                    </div>
                  </div>
                  {/* Per-risk Decision Guru handoff. The risk becomes a
                      structured challenge; Decision Guru's lens model
                      handles the analysis from there. */}
                  <div>
                    <button
                      type="button"
                      onClick={() => exploreRiskInDecisionGuru(risk)}
                      title="Take this risk into Decision Guru for structured analysis"
                      style={{
                        background: 'white', color: colour,
                        border: `1px solid ${colour}`,
                        borderRadius: 14, padding: '0.25rem 0.7rem',
                        fontSize: '0.7rem', fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                      }}>
                      → Explore in Decision Guru
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginTop: '1rem', paddingTop: '0.85rem', borderTop: '1px solid #f0f0f0' }}>
        <textarea value={newRiskText} onChange={e => setNewRiskText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAdd(); } }}
          placeholder="Add a risk you've identified… (Enter to add)"
          rows={2}
          style={{ flex: 1, fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box', lineHeight: 1.5 }} />
        <button onClick={handleAdd} disabled={!newRiskText.trim()}
          style={{ padding: '0.55rem 1rem', borderRadius: 8, background: newRiskText.trim() ? colour : '#e5e5e5', color: newRiskText.trim() ? 'white' : '#aaa', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: newRiskText.trim() ? 'pointer' : 'not-allowed', fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
          + Add
        </button>
      </div>
    </div>
  );
}

function buildRisksPrompt(section, current, strategyContext, existingRisks) {
  const sectionData = Object.entries(current || {})
    .filter(([k, v]) => !k.startsWith('_') && k !== 'key_risks' && v != null)
    .filter(([, v]) => Array.isArray(v) ? v.length > 0 : (typeof v === 'object' ? true : String(v).trim().length > 0))
    .map(([k, v]) => `- ${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join('\n') || '(not yet captured)';

  const existing = existingRisks.length > 0
    ? `Risks ALREADY identified — DO NOT repeat or rephrase these, generate net new ones:\n${existingRisks.map(r => `- ${r.text}`).join('\n')}`
    : '';

  return `You are GuruOne Design Guru, helping a business leader surface forward-looking risks for one section of their operating model.

SECTION: ${section.title}
WHAT IT COVERS: ${section.description}

CURRENT STATE OF THIS SECTION:
${sectionData}

${strategyContext ? `BUSINESS STRATEGY CONTEXT:\n${strategyContext}\n` : ''}
${existing}

TASK
Surface 3–5 plausible forward-looking risks specific to THIS business leader, this section, this business. These are things that could happen in the next 12–24 months that would meaningfully harm the business — distinct from current challenges (which are happening now).

RULES
- Specific and grounded in the data above. Not "supply chain risk" but a concrete scenario tied to what you can see (e.g. "single-supplier dependency on the named partner could halt delivery for weeks if they exit the contract").
- 1–2 sentences each. The first sentence names the risk concretely; the second names the consequence or trigger.
- No prescription ("you should…"). Just the risk.
- Mix where the section allows: structural, commercial, operational, people-related.
- If section data is sparse, generalise from the section's purpose plus any strategy context. Don't refuse to answer.

Return ONLY valid JSON starting with {:
{
  "risks": ["risk 1 text", "risk 2 text", ...]
}`;
}

// ── MarketingDialogue ─────────────────────────────────────────────
// Standalone dialogue for the marketing_brand section.
// Branches deeply on business type (B2B/B2C × product/service) and
// competitive position (price vs quality/expertise). Channel guidance
// is calibrated per quadrant — generic marketing advice is explicitly
// avoided. Lead generation is in scope; lead conversion is BD's
// territory and never asked here.

const MARKETING_DIALOGUE_COLOUR = '#9D174D';
const MARKETING_DIALOGUE_BG = '#FCE7F3';
const MARKETING_DIALOGUE_BORDER = '#F9A8D4';

const MARKETING_CHIP_OPTIONS = {
  business_marketing_context: ['B2B product', 'B2C product', 'B2B service', 'B2C service', 'Mixed'],
  competitive_position: ['Primarily competing on price', 'Primarily competing on quality or expertise', 'Mixed'],
  brand_consistency: ['Consistent and distinctive across all touchpoints', 'Broadly consistent', 'Inconsistent or generic'],
  brand_alignment: ['Strong — reflects where the business is going', 'Partial', 'Weak — left behind'],
  active_channels: ['Website and SEO', 'Social media', 'Content and thought leadership', 'Email', 'Paid advertising', 'Reviews and social proof', 'Platform and marketplace presence', 'Events and networking', 'PR', 'Partnerships'],
  credibility_signals: ['Strong — case studies, testimonials, awards visible', 'Some', 'Weak or absent'],
  marketing_measurement: ['Measured regularly — clear view of what works', 'Some measurement', 'Not measured'],
  marketing_capability: ['Dedicated internal resource', 'Shared responsibility', 'Founder-led', 'Agency or freelancer', 'Mix'],
  marketing_budget: ['Defined budget', 'Ad hoc spend', 'Minimal or none'],
  bd_handoff: ['Explicit process — leads handled consistently', 'Broadly understood', 'Ad hoc'],
};

const MARKETING_MULTI_FIELDS = new Set(['active_channels']);

function MarketingDialogue({ currentData, futureData, onChange, onFutureChange, hasFutureState, sectionKey }) {
  const [isFutureMode, setIsFutureMode] = useState(false);
  const [phase, setPhase] = useState('intro');
  useEffect(() => {
    if (phase === 'extracting') track('design_conversation_completed', { section: sectionKey });
  }, [phase, sectionKey]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const chatContainerRef = useRef(null);
  const questionCount = useRef(0);
  // Pull what's already on the Direction canvas so this dialogue
  // doesn't re-ask questions the leader has answered upstream
  // (B2B/B2C, product/service, pre-revenue stage, etc.).
  const directionContextBlock = buildDirectionContextBlock(readDirectionCanvas());

  // Evaluation flow — same shape as the other 11 OM dialogues:
  // sectionMeta + evalFlow declared here, modal rendered via
  // DialogueEvalModal inside the reviewing-phase return.
  const sectionMeta = OM_SECTIONS.find(s => s.key === sectionKey);
  const evalFlow = useEvaluationFlow({
    sectionKey,
    sectionTitle: sectionMeta?.title || 'Marketing & Brand',
    sectionFields: sectionMeta?.current_fields || [],
    repeatable: false,
  });

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  function openDecisionGuruInline() {
    window.open('/decision', '_blank', 'noopener');
  }

  function getMarketingContext() {
    const ctx = currentData?.business_marketing_context || '';
    const pos = currentData?.competitive_position || '';
    const isProduct = ctx.toLowerCase().includes('product');
    const isB2C = ctx.toLowerCase().includes('b2c');
    const isB2B = ctx.toLowerCase().includes('b2b');
    const isPrice = pos.toLowerCase().includes('price');
    const isQuality = pos.toLowerCase().includes('quality') || pos.toLowerCase().includes('expertise');
    return { isProduct, isB2C, isB2B, isPrice, isQuality, ctx, pos };
  }

  function buildChannelGuidance({ isProduct, isB2C, isB2B, isPrice, isQuality }) {
    if (isProduct && isPrice) {
      return `CHANNEL GUIDANCE — Product / Price competitor:
Platform and marketplace presence is typically the highest-leverage channel. Being found at the moment of comparison — on Amazon, Google Shopping, comparison sites, or the relevant marketplace for this sector — matters more than brand building. Reviews and ratings are credibility signals that directly influence purchase decisions.
Challenge example: "Businesses competing on price in product categories often find that the return on investment in reviews and search rank dramatically outperforms spend on brand content. Have you built a systematic approach to generating reviews and maintaining search visibility on the platforms where your buyers compare options?"`;
    }
    if (isProduct && isQuality) {
      return `CHANNEL GUIDANCE — Product / Quality competitor:
Brand presentation, social proof, and selective channel presence matter most. Being on the right platforms (not all platforms) with strong visual identity and clear credibility signals — case studies, awards, press coverage, user-generated content — builds the trust that justifies premium pricing.
Challenge example: "Premium product brands like [e.g. Patagonia, Brompton] invest heavily in brand story and values because that's what justifies their price point. Their marketing is as much about what they stand for as what they sell. Is there a story or set of values in your business that is being undersold in how you present yourselves?"`;
    }
    if (isB2B && isQuality) {
      return `CHANNEL GUIDANCE — B2B Service / Quality or expertise competitor:
Credibility before the conversation is the most important marketing investment. Buyers in B2B service categories need to trust the provider before they engage. Case studies, client testimonials, accreditations, and selective thought leadership (where buyers actually read it) build that trust. LinkedIn and industry association presence are typically the right channels.
Challenge example: "Firms like McKinsey or Deloitte built their brand substantially through published thinking and expertise demonstration long before they were household names. At a smaller scale, a consistent point of view on an issue your buyers care about — published regularly — can generate the same effect. Have you thought about what your business's distinctive perspective actually is, and whether you're sharing it?"`;
    }
    if (isB2C) {
      return `CHANNEL GUIDANCE — B2C Service:
Discovery and trust at the point of consideration are the primary levers. Google presence, local search visibility, reviews (Google, sector-specific platforms), and visual channels (Instagram in many categories) drive B2C service purchase decisions. Buyers often decide quickly on the basis of proximity, reviews, and first impression.
Challenge example: "For many B2C service businesses, Google reviews and local search rank are the highest-return marketing investments available — and often the most neglected. A business with 50 recent positive Google reviews and a well-optimised local presence will outperform a competitor with a better website but no review discipline. When did you last actively focus on this?"`;
    }
    return `CHANNEL GUIDANCE — Mixed or unclear model:
Establish the primary buyer journey before advising on channels. The right marketing approach depends heavily on whether buyers discover through search, recommendation, social media, or industry presence — and whether purchase decisions are quick or considered. Ask which channel, if working well, would have the most impact on growth.`;
  }

  // ── Prompt builders ─────────────────────────────────────────────
  function buildOpeningPrompt() {
    if (isFutureMode) {
      const hasFutureData = futureData && Object.values(futureData).some(v => v);
      const { isProduct, isB2C, isB2B, isPrice, isQuality } = getMarketingContext();

      if (hasFutureData) {
        return `You are GuruOne reviewing an AI-generated future marketing strategy with the business leader.

BUSINESS CONTEXT: ${currentData?.business_marketing_context || 'not captured'} — ${currentData?.competitive_position || 'not captured'}

AI-GENERATED FUTURE STATE:
Brand development: ${futureData?.brand_development || '(none yet)'}
Target channel mix: ${futureData?.target_channel_mix || '(none yet)'}
Credibility building plan: ${futureData?.credibility_building_plan || '(none yet)'}
Marketing capability changes: ${futureData?.marketing_capability_changes || '(none yet)'}
Marketing investment: ${futureData?.marketing_investment || '(none yet)'}
AI marketing roadmap: ${futureData?.ai_marketing_roadmap || '(none yet)'}

Walk through each element in order. For each: present what was suggested, ask whether it matches their thinking.

Probe specifically:
- Whether the target channel mix is right for the business type and competitive position — not just what seems like good practice in general
- Whether the credibility building plan is specific enough to actually change what prospects see
- Whether the marketing investment is realistic and sequenced correctly — quick wins first
- Whether the AI roadmap is specific or still aspirational
- For any significant repositioning or major investment, flag as a Decision Guru candidate

After all elements reviewed, emit READY_TO_SUMMARISE on its own line.

Open with: "I've put together a suggested future marketing strategy. Let me start with the brand development direction: ${futureData?.brand_development || '(none generated yet)'}. Given you're [${currentData?.business_marketing_context || 'your type of business'}] competing on [${currentData?.competitive_position || 'your position'}], does that feel like the right priority?"`;
      }

      return `You are GuruOne helping a business leader develop their future marketing strategy.

BUSINESS CONTEXT:
Type: ${currentData?.business_marketing_context || 'not captured'}
Competitive position: ${currentData?.competitive_position || 'not captured'}
Current brand consistency: ${currentData?.brand_consistency || 'not captured'}
Brand alignment: ${currentData?.brand_alignment || 'not captured'}
Active channels: ${Array.isArray(currentData?.active_channels) ? currentData.active_channels.join(', ') : currentData?.active_channels || 'not captured'}
Channel effectiveness: ${currentData?.channel_effectiveness || 'not captured'}
Credibility signals: ${currentData?.credibility_signals || 'not captured'}
Marketing measurement: ${currentData?.marketing_measurement || 'not captured'}
Marketing capability: ${currentData?.marketing_capability || 'not captured'}
AI in marketing: ${currentData?.ai_marketing || 'not captured'}

${buildChannelGuidance({ isProduct, isB2C, isB2B, isPrice, isQuality })}

Cover five areas in order, consistently calibrated to the business type and competitive position:

1. Brand development — what needs to change about how the business presents itself. Be specific: is this a visual refresh, a messaging change, or a deeper position question? Distinguish between a refresh (updating presentation, position stays the same) and repositioning (changing what the business stands for — riskier, more expensive). For repositioning decisions, flag as Decision Guru candidate.

2. Target channel mix — the one or two channels to invest in, chosen for this specific business type. Be explicit about why these channels are right for this buyer and this competitive position. Challenge channels being continued from habit rather than evidence.

3. Credibility building plan — for quality and expertise competitors specifically. What proof points need to exist and how will they be built and displayed? For price competitors, this is about review volume and platform presence rather than long-form credibility.

4. Marketing capability — who does marketing going forward and whether that model is right for the ambition. Surface three options: build internally, use agency/freelancer, or founder-led with AI augmentation.

5. AI marketing roadmap — specific tools or approaches to implement. Push past "we'll look at it" to concrete first steps. Content creation, email, SEO optimisation, and targeting are all viable entry points at SME scale.

If the phrase "Decision Guru" appears in any of your messages, the UI will offer a routing button — use it intentionally.

After all five areas covered, emit READY_TO_SUMMARISE on its own line.

Open with: "Let's think about what marketing needs to look like going forward. Given you're [${currentData?.business_marketing_context || 'your type of business'}] competing primarily on [${currentData?.competitive_position || 'your position'}], the most important marketing investments are going to look different from a business in a different position. Where do you think the biggest gap is right now — brand, channels, credibility, measurement, or capability?"`;
    }

    return `You are GuruOne, a strategic advisor helping a business leader map their marketing and brand approach.${directionContextBlock}

IMPORTANT SCOPE: This section covers brand, awareness building, lead generation, and channel strategy — everything that brings a prospect to the door. Business Development (a separate section) covers what happens once a prospect exists. Never ask about lead conversion — that is BD territory.

USE THE DIRECTION CONTEXT ABOVE:
If business type (product / service, B2B / B2C, price / quality) is evident from the Direction canvas above (revenue model, value proposition, customer segments, business type) — DO NOT open with "are you primarily selling a product or a service?". Confirm what you've inferred and move on. Only ask the basic context question when nothing on the Direction canvas reveals it.

If the leader is pre-revenue (vision stage = Early), frame marketing questions as forward-looking ("how do you intend to acquire your first customers?") rather than current-state ("what's working today?"). Don't ask about retention, CAC, or channel effectiveness for a pre-revenue business.

OBJECTIVE
To map the marketing reality calibrated to the type of business — the brand, the channels being used, what's working, and whether the approach is right for how the business competes.

WHY THIS MATTERS
Most SMEs do marketing without a marketing strategy. The right approach for a product business competing on price is fundamentally different from a service business competing on expertise. Channels that are right for one context are irrelevant for another. And AI is transforming what's possible at SME scale — the businesses experimenting now are building advantages that will be hard to close.

BUSINESS TYPE IS CRITICAL — ESTABLISH FIRST
The conversation MUST start by establishing:
1. Product or service business (or mixed)
2. B2B or B2C (or mixed)
3. Competing on price or quality/expertise (or mixed)

Everything — channels, credibility approach, thought leadership relevance, AI priorities — depends on these three dimensions. Do not give generic marketing advice before establishing this context.

CHANNEL GUIDANCE BY BUSINESS TYPE (use once context is established):

PRODUCT / PRICE COMPETITOR:
Platform and marketplace presence is the primary lever. Being found at the moment of comparison — Amazon, Google Shopping, comparison sites, relevant marketplaces. Reviews and ratings are the most important credibility signal.
Challenge example to use: "For product businesses competing on price, the return on investment in reviews and search rank on the right platforms typically outperforms brand content by a significant margin. Have you built a systematic approach to generating reviews and maintaining search visibility where your buyers compare options?"

PRODUCT / QUALITY COMPETITOR:
Brand story, visual identity, social proof. Being on the right platforms rather than all platforms. User-generated content and community. Premium positioning signals.
Challenge example to use: "Premium product brands invest in brand story because that's what justifies premium pricing. Is there a story or set of values in your business that's being undersold in how you present yourselves publicly?"

B2B SERVICE / QUALITY OR EXPERTISE:
Credibility before the conversation. Case studies, testimonials, accreditations, selective thought leadership where buyers actually read it. LinkedIn and industry association presence.
Challenge example to use: "In B2B service categories, buyers need to trust before they engage. Firms that invest in demonstrating expertise publicly — through published thinking, case studies, speaking — typically find that inbound interest improves and price resistance reduces. What does a potential client see about your thinking before they've spoken to you?"

B2C SERVICE:
Discovery and trust at point of consideration. Google presence, local search, reviews (Google and sector-specific), visual channels.
Challenge example to use: "For B2C service businesses, Google reviews and local search rank are often the highest-return marketing investments available — and the most neglected. A business with strong recent reviews and a well-optimised local presence consistently outperforms competitors with better websites but no review discipline. How actively are you working on this?"

THOUGHT LEADERSHIP CALIBRATION:
Relevant for: B2B businesses where buyers value expertise and have time to read long-form content.
Not relevant for: transactional B2C, product businesses competing on price, or service businesses where buyers decide quickly on recommendation.
Ask whether buyers actually consume it before pushing it.

THEMES TO COVER (in order):
1. Business context — product/service, B2B/B2C, price/quality — FIRST
2. Brand and position — consistency, distinctiveness, alignment with direction
3. Channels — what's being used, what's working, calibrated challenge based on business type
4. Digital presence — website, search, social as prospect would see it
5. Measurement — what's tracked and whether it's sufficient to know what works
6. Capability and spend — who does it, at what cost, right model for ambition
7. AI in marketing — current use and specific opportunity
8. Marketing to BD handoff — explicit or accidental
9. Current challenges

CHALLENGE INSTRUCTION:
After establishing business type, offer at least one specific challenge or example relevant to that context. Use the examples above as a guide. The goal is not to lecture — it is to surface something the business leader may not have considered and make it concrete with a real-world reference.

CRITICAL RULES:
1. Establish business type in the first exchange — everything else depends on it.
2. Tailor channel and content advice specifically to the business type — never give generic "post on social" advice.
3. Thought leadership is not universally relevant — calibrate before pushing it.
4. Never ask about lead conversion — that is BD.
5. Offer at least one specific challenge with a concrete example from a relevant context.

Opening question: "Before we get into what you're doing — a couple of things to understand the right marketing approach for your business. Are you primarily selling a product or a service? And when buyers choose you over a competitor, is price usually a factor in that decision, or are you winning on quality, expertise, or something distinctive about what you offer?"`;
  }

  function buildFollowupPrompt() {
    const { isProduct, isB2C, isB2B, isPrice, isQuality, ctx, pos } = getMarketingContext();

    if (isFutureMode) {
      return `You are GuruOne helping a business leader develop their future marketing strategy. You have asked ${questionCount.current} questions.

BUSINESS CONTEXT: ${ctx} — ${pos}

Continue covering any areas not yet addressed:
1. Brand development — refresh vs reposition distinction
2. Target channel mix — right for this specific business type
3. Credibility building — proof points, display, systematisation
4. Marketing capability — build, agency, or AI-augmented business leader
5. AI marketing roadmap — concrete first steps

For repositioning decisions or major investment, flag as Decision Guru candidate.

After all areas covered, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn.`;
    }

    return `You are GuruOne mapping a business leader's marketing and brand approach. You have asked ${questionCount.current} questions.${directionContextBlock}

BUSINESS CONTEXT ESTABLISHED: ${ctx || 'not yet established'} — ${pos || 'not yet established'}

IMPORTANT: This covers awareness and lead generation — NOT lead conversion (that is Business Development).

${buildChannelGuidance({ isProduct, isB2C, isB2B, isPrice, isQuality })}

THEMES TO COVER
1. Business context — if not yet established, ask first
2. Brand — consistency, distinctiveness, whether it reflects the direction
3. Channels — calibrated to business type; challenge generic or habitual choices
4. Digital presence — as a prospect would see it
5. Measurement — what's tracked; name absence of measurement as a gap
6. Capability and spend — who does it, model right for ambition
7. AI in marketing — current use and opportunity
8. BD handoff — explicit or accidental
9. Current challenges

CHALLENGE INSTRUCTION:
Use the channel guidance above to offer at least one specific challenge with a concrete example relevant to this business type. Make it specific — not "have you thought about content?" but the context-appropriate version.

THOUGHT LEADERSHIP CALIBRATION:
Only relevant if B2B and buyers consume long-form content. Do not push for other business types.

COVERAGE CHECK — note which themes addressed. Ask about uncovered themes before probing depth.

When every theme has a substantive answer, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn. 2–4 sentences plus question.`;
  }

  function buildExtractPrompt() {
    if (isFutureMode) {
      return `Based on this conversation, extract the agreed future marketing strategy.

Return ONLY valid JSON starting with {:
{
  "brand_development": "what needs to change about how the business presents itself — distinguish refresh from repositioning",
  "target_channel_mix": "the one or two channels to invest in and why — specific to the business type and competitive position",
  "credibility_building_plan": "how the business will build and display credibility signals. Omit if not relevant to business type.",
  "marketing_capability_changes": "who does marketing going forward and whether the model changes",
  "marketing_investment": "budget and resource needed to execute — sequenced from quick wins to structural investment",
  "ai_marketing_roadmap": "specific AI tools or approaches to implement — concrete, not aspirational"
}
Omit any field not clearly evidenced by the conversation.`;
    }
    return `Based on this conversation, extract the marketing and brand picture.

Return ONLY valid JSON starting with {:
{
  "business_marketing_context": "one of exactly: 'B2B product' | 'B2C product' | 'B2B service' | 'B2C service' | 'Mixed'",
  "competitive_position": "one of exactly: 'Primarily competing on price' | 'Primarily competing on quality or expertise' | 'Mixed'",
  "brand_description": "how the business presents itself, what it stands for, and whether that is distinctive",
  "brand_consistency": "one of exactly: 'Consistent and distinctive across all touchpoints' | 'Broadly consistent' | 'Inconsistent or generic'",
  "brand_alignment": "one of exactly: 'Strong — reflects where the business is going' | 'Partial' | 'Weak — left behind'",
  "active_channels": array of zero or more from exactly: ["Website and SEO", "Social media", "Content and thought leadership", "Email", "Paid advertising", "Reviews and social proof", "Platform and marketplace presence", "Events and networking", "PR", "Partnerships"],
  "channel_effectiveness": "what is working and what is not — and on what basis",
  "credibility_signals": "one of exactly: 'Strong — case studies, testimonials, awards visible' | 'Some' | 'Weak or absent'",
  "marketing_measurement": "one of exactly: 'Measured regularly — clear view of what works' | 'Some measurement' | 'Not measured'",
  "marketing_capability": "one of exactly: 'Dedicated internal resource' | 'Shared responsibility' | 'Founder-led' | 'Agency or freelancer' | 'Mix'",
  "marketing_budget": "one of exactly: 'Defined budget' | 'Ad hoc spend' | 'Minimal or none'",
  "ai_marketing": "AI tools currently in use or specific opportunities identified. Omit if none discussed.",
  "bd_handoff": "one of exactly: 'Explicit process — leads handled consistently' | 'Broadly understood' | 'Ad hoc'",
  "marketing_challenges": "biggest marketing or brand challenge in the business leader's own words"
}
Omit any field not clearly evidenced by the conversation.`;
  }

  // ── Conversation lifecycle ──────────────────────────────────────
  async function startConversation(future) {
    setIsFutureMode(future);
    setMessages([]);
    setInput('');
    setEditedData(null);
    questionCount.current = 0;
    setLoading(true);
    setPhase('chatting');
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: [{ role: 'user', content: future ? 'Start the future marketing strategy conversation.' : 'Start the marketing and brand conversation.' }],
        context: buildOpeningPrompt(),
      });
      setMessages([{ role: 'assistant', content: res.data.reply }]);
      questionCount.current = 1;
    } catch {
      setMessages([{ role: 'assistant', content: future
        ? "Let's think about what marketing needs to look like going forward. Where do you think the biggest gap is right now — brand, channels, credibility, measurement, or capability?"
        : "Before we get into what you're doing — are you primarily selling a product or a service? And when buyers choose you over a competitor, is price usually a factor, or are you winning on quality, expertise, or something distinctive?" }]);
      questionCount.current = 1;
    }
    setLoading(false);
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      questionCount.current += 1;
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: newMessages,
        context: buildFollowupPrompt() + DIALOGUE_RULES_BLOCK,
      });
      const reply = res.data.reply || '';
      const isReady = reply.includes('READY_TO_SUMMARISE');

      const deferredQuestion = isReady ? maybeDeferSummariseForPendingQuestion(reply, questionCount.current, 12) : null;

      if (deferredQuestion) {

        setMessages(prev => [...prev, { role: 'assistant', content: deferredQuestion }]);

      } else if (isReady || questionCount.current >= 12) {
        const clean = reply.replace(/READY_TO_SUMMARISE/g, '').trim();
        const showRoutingButton = clean.includes('Decision Guru');
        if (clean) setMessages(prev => [...prev, { role: 'assistant', content: clean, showDecisionGuruButton: showRoutingButton }]);
        const convo = clean ? [...newMessages, { role: 'assistant', content: clean }] : newMessages;
        await extractData(convo);
      } else {
        const showRoutingButton = reply.includes('Decision Guru');
        setMessages(prev => [...prev, { role: 'assistant', content: reply, showDecisionGuruButton: showRoutingButton }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong — please try again.' }]);
    }
    setLoading(false);
  }

  async function extractData(msgs) {
    setMessages(prev => [...prev, { role: 'assistant', content: "Good — I have enough to work with. Let me synthesise what you've shared. You'll review and edit everything before saving." }]);
    setPhase('extracting');
    const founderAnswers = msgs.filter(m => m.role === 'user').map((m, i) => `Answer ${i + 1}: ${m.content}`).join('\n\n');
    const condensed = [{ role: 'user', content: `Here are the business leader's answers:\n\n${founderAnswers}\n\nExtract the structured data now.` }];
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, { messages: condensed, context: buildExtractPrompt() });
      const raw = res.data.reply || '';
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1 && e !== -1) {
        const parsed = JSON.parse(raw.slice(s, e + 1));
        setEditedData(parsed);
        setPhase('reviewing');
        return;
      }
    } catch { /* fall through */ }
    setPhase('intro');
    setMessages(prev => [...prev, { role: 'assistant', content: "I wasn't able to extract automatically this time. Use Quick entry to fill in the fields directly." }]);
  }

  function handleSave() {
    if (!editedData) return;
    if (isFutureMode) {
      onFutureChange({ ...futureData, ...editedData });
      setPhase('saved');
      return;
    }
    const transcript = { messages, completedAt: new Date().toISOString() };
    evalFlow.runEvaluation({ fields: editedData, transcript });
  }

  // ── Render ───────────────────────────────────────────────────────
  const colour = MARKETING_DIALOGUE_COLOUR;
  const bg = MARKETING_DIALOGUE_BG;
  const border = MARKETING_DIALOGUE_BORDER;

  if (phase === 'intro') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1.25rem 1.5rem', marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.75rem' }}>
          Marketing &amp; brand dialogue
        </div>
        <p style={{ fontSize: '0.82rem', color: '#333', lineHeight: 1.6, margin: '0 0 1rem' }}>
          We&apos;ll map your brand, channels, credibility signals and measurement — calibrated specifically to whether you&apos;re a product or service business, B2B or B2C, and whether you compete on price or quality. The right marketing for one of those quadrants is the wrong marketing for another, and we&apos;ll work to your context — not generic best practice.
        </p>
        <div style={{ fontSize: '0.68rem', color: '#888', lineHeight: 1.5, marginBottom: '1.25rem', borderTop: `1px solid ${border}`, paddingTop: '0.75rem' }}>
          <strong>How it works:</strong> GuruOne asks one question at a time. We establish business type first, then probe everything else through that lens. Typically 8–12 exchanges.
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button onClick={() => startConversation(false)}
            style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Explore current state →
          </button>
          {hasFutureState && (
            <button onClick={() => startConversation(true)}
              style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: 'white', color: colour, border: `1.5px solid ${colour}`, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Review future state →
            </button>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'extracting') {
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {messages.slice(-2).map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '0.6rem 0.9rem', borderRadius: 10, background: m.role === 'user' ? colour : bg, color: m.role === 'user' ? 'white' : '#831843', fontSize: '0.8rem', lineHeight: 1.5 }}>{m.content}</div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: colour, fontSize: '0.8rem' }}>
          <div style={{ width: 14, height: 14, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          Synthesising your answers…
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      </div>
    );
  }

  if (phase === 'reviewing' && editedData) {
    const fieldDefs = isFutureMode ? [
      { key: 'brand_development', label: 'Brand development' },
      { key: 'target_channel_mix', label: 'Target channel mix' },
      { key: 'credibility_building_plan', label: 'Credibility building plan' },
      { key: 'marketing_capability_changes', label: 'Marketing capability changes' },
      { key: 'marketing_investment', label: 'Marketing investment' },
      { key: 'ai_marketing_roadmap', label: 'AI marketing roadmap' },
    ] : [
      { key: 'business_marketing_context', label: 'Business type' },
      { key: 'competitive_position', label: 'Competitive position' },
      { key: 'brand_description', label: 'Brand and position' },
      { key: 'brand_consistency', label: 'Brand consistency' },
      { key: 'brand_alignment', label: 'Brand alignment with direction' },
      { key: 'active_channels', label: 'Active channels' },
      { key: 'channel_effectiveness', label: 'Channel effectiveness' },
      { key: 'credibility_signals', label: 'Credibility signals' },
      { key: 'marketing_measurement', label: 'Marketing measurement' },
      { key: 'marketing_capability', label: 'Marketing capability' },
      { key: 'marketing_budget', label: 'Marketing budget' },
      { key: 'ai_marketing', label: 'AI in marketing' },
      { key: 'bd_handoff', label: 'Marketing to BD handoff' },
      { key: 'marketing_challenges', label: 'Current challenges' },
    ];
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <DialogueEvalModal
          sectionMeta={sectionMeta}
          evalFlow={evalFlow}
          onCommitWithEval={(evaluation) => {
            const payload = { ...currentData, ...editedData };
            if (evaluation) payload._evaluation = evaluation;
            onChange(payload);
            setPhase('saved');
          }}
          onRefine={() => setPhase('chatting')}
        />
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colour, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Review &amp; edit — {isFutureMode ? 'future state' : 'current state'}
        </div>
        <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: '0.85rem', lineHeight: 1.5 }}>
          AI-drafted from your conversation — edit any field until it precisely matches reality before saving.
        </div>
        {fieldDefs.map(({ key, label }) => {
          const rawValue = editedData[key];
          const chipOpts = MARKETING_CHIP_OPTIONS[key];
          const isMulti = MARKETING_MULTI_FIELDS.has(key);
          if (chipOpts && isMulti) {
            const selected = Array.isArray(rawValue) ? rawValue : [];
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>{label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {chipOpts.map(opt => {
                    const active = selected.includes(opt);
                    return (
                      <button key={opt} onClick={() => setEditedData(p => {
                        const cur = Array.isArray(p[key]) ? p[key] : [];
                        const next = active ? cur.filter(v => v !== opt) : [...cur, opt];
                        return { ...p, [key]: next };
                      })}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? bg : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          if (chipOpts) {
            const value = rawValue || '';
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>{label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {chipOpts.map(opt => {
                    const active = value === opt;
                    return (
                      <button key={opt} onClick={() => setEditedData(p => ({ ...p, [key]: active ? '' : opt }))}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? bg : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          const value = rawValue || '';
          return (
            <div key={key} style={{ marginBottom: '0.85rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
              <textarea value={value} onChange={e => setEditedData(p => ({ ...p, [key]: e.target.value }))} rows={3}
                style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: `1px solid ${border}`, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55 }} />
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.6rem 1.25rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save
          </button>
          <button onClick={() => setPhase('chatting')}
            style={{ padding: '0.6rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${border}`, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Continue conversation
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'saved') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>
            {isFutureMode ? 'Future state captured' : 'Current state captured'}
          </div>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#444', margin: '0 0 0.85rem', lineHeight: 1.55 }}>
          Your answers are saved. {isFutureMode
            ? 'Switch to the Future State tab to see them, or edit any field there in Quick mode.'
            : 'You can edit any field in Quick mode below, or run another conversation to refine.'}
        </p>
        <button onClick={() => setPhase('intro')}
          style={{ background: 'none', border: 'none', color: colour, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', padding: '0.5rem 0', width: 'auto' }}>
          Run another conversation →
        </button>
      </div>
    );
  }

  // Chatting phase
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.5rem' }}>
        {isFutureMode ? 'Future state — design / review' : 'Current state — explore'}
      </div>
      <div ref={chatContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '0.85rem', maxHeight: 320, overflowY: 'auto', paddingRight: '0.2rem' }}>
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          const showDgButton = !isUser && m.showDecisionGuruButton === true;
          return (
            <div key={i} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '88%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '0.6rem 0.85rem', borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px', background: isUser ? colour : bg, color: isUser ? 'white' : '#831843', fontSize: '0.82rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                {m.content}
              </div>
              {showDgButton && (
                <button onClick={openDecisionGuruInline}
                  style={{ marginTop: '0.5rem', padding: '0.4rem 1rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto', alignSelf: 'flex-start' }}>
                  Take to Decision Guru →
                </button>
              )}
            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: colour, fontStyle: 'italic' }}>
            <div style={{ width: 11, height: 11, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            GuruOne is thinking…
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type your answer… (Enter to send)"
          disabled={loading} rows={2}
          style={{ flex: 1, fontSize: '0.82rem', padding: '0.6rem 0.8rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          style={{ padding: '0.6rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
          Send
        </button>
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

// ── BDDialogue ────────────────────────────────────────────────────
// Standalone dialogue for the business_development section.
// Branches on sales model (transactional / consultative / mixed) — the
// system prompts adapt to whichever path the business leader is on. Scope is
// CONVERSION (qualification, pipeline, pricing, referral activation,
// expansion); lead generation is explicitly out of scope and belongs
// to a future Marketing section.

const BD_DIALOGUE_COLOUR = '#DC2626';
const BD_DIALOGUE_BG = '#FEE2E2';
const BD_DIALOGUE_BORDER = '#FCA5A5';

const BD_CHIP_OPTIONS = {
  sales_model: ['Transactional — short cycle, set pricing', 'Consultative — longer cycle, bespoke proposals', 'Mixed'],
  repeat_purchase_approach: ['Systematic — loyalty or incentive programme in place', 'Occasional reminders or follow-up', 'Passive — customer-led'],
  pipeline_definition: ['Defined pipeline with clear stages', 'Informal pipeline', 'No defined pipeline'],
  qualification_discipline: ['Strong — clear criteria for what to pursue', 'Some qualification', 'Most opportunities are pursued'],
  pricing_commercial: ['Consistent approach — held under pressure', 'Varies by opportunity', 'Often discounted'],
  existing_client_expansion: ['Systematic — actively pursued', 'Occasional', 'Reactive — client-led'],
  referral_activation: ['Systematic — asked for, tracked, and incentivised', 'Occasional', 'Passive — happens when it happens'],
  founder_dependency_bd: ['Founder does most BD', 'Split between founder and team', 'Team-led with founder support'],
  crm_pipeline_mgmt: ['CRM used consistently', 'CRM used inconsistently', 'No CRM — managed manually'],
};

function BDDialogue({ currentData, futureData, onChange, onFutureChange, hasFutureState, sectionKey }) {
  const [isFutureMode, setIsFutureMode] = useState(false);
  const [phase, setPhase] = useState('intro');
  useEffect(() => {
    if (phase === 'extracting') track('design_conversation_completed', { section: sectionKey });
  }, [phase, sectionKey]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const chatContainerRef = useRef(null);
  const questionCount = useRef(0);
  // Cross-card context — Direction snapshot. Injected into opening
  // and followup prompts so this dialogue doesn't re-ask the leader
  // anything already captured upstream (stage, revenue model,
  // customer segments, value proposition, etc.).
  const directionContextBlock = buildDirectionContextBlock(readDirectionCanvas());

  // Evaluation flow — kicks off /evaluate-section after the leader
  // confirms the extracted data. The DialogueEvalModal wrapper at
  // the top of the reviewing-phase return handles the modal lifecycle.
  const sectionMeta = OM_SECTIONS.find(s => s.key === sectionKey);
  const evalFlow = useEvaluationFlow({
    sectionKey,
    sectionTitle: sectionMeta?.title || sectionKey,
    sectionFields: sectionMeta?.current_fields || [],
    repeatable: false,
  });

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  function openDecisionGuruInline() {
    window.open('/decision', '_blank', 'noopener');
  }

  // ── Prompt builders ─────────────────────────────────────────────
  function buildOpeningPrompt() {
    if (isFutureMode) {
      const hasFutureData = futureData && Object.values(futureData).some(v => v);
      if (hasFutureData) {
        return `You are GuruOne reviewing an AI-generated future BD model with the business leader.

AI-GENERATED FUTURE STATE:
Target BD model: ${futureData?.target_bd_model || '(none yet)'}
Process improvements: ${futureData?.bd_process_improvements || '(none yet)'}
Team and capability: ${futureData?.bd_team_capability || '(none yet)'}
Referral and expansion approach: ${futureData?.referral_expansion_approach || '(none yet)'}

Walk through each element in order. For each: present what was suggested, ask whether it matches their thinking.

Probe specifically:
- Whether the target BD model is realistic given the current stage and financial position
- Whether process improvements are specific enough to change behaviour — "be more disciplined" is not a process improvement
- Whether the first BD hire plan (if proposed) is sequenced correctly — hire into a documented process, not an undefined role
- Whether referral and expansion approaches are systematic enough to actually generate results
- For any significant BD hire decision, flag as a Decision Guru candidate

After all elements reviewed, emit READY_TO_SUMMARISE on its own line.

Open with: "I've put together a suggested future BD model. Let me start with the overall direction: ${futureData?.target_bd_model || '(none generated yet)'}. Does that match what you're trying to build?"`;
      }

      const salesModel = currentData?.sales_model || 'not captured';
      const isTransactional = salesModel.toLowerCase().includes('transactional');

      return `You are GuruOne helping a business leader develop their future BD approach.

CURRENT STATE CONTEXT:
Sales model: ${salesModel}
${isTransactional ? `Conversion rate: ${currentData?.conversion_rate || 'not captured'}
Average transaction value: ${currentData?.avg_transaction_value || 'not captured'}
Repeat purchase approach: ${currentData?.repeat_purchase_approach || 'not captured'}` :
`Pipeline definition: ${currentData?.pipeline_definition || 'not captured'}
Qualification discipline: ${currentData?.qualification_discipline || 'not captured'}
Pricing in commercial conversation: ${currentData?.pricing_commercial || 'not captured'}
Win rate: ${currentData?.win_rate || 'not captured'}`}
Existing client expansion: ${currentData?.existing_client_expansion || 'not captured'}
Referral activation: ${currentData?.referral_activation || 'not captured'}
Business leader dependency: ${currentData?.founder_dependency_bd || 'not captured'}
CRM: ${currentData?.crm_pipeline_mgmt || 'not captured'}

Cover four areas in order, calibrated to the sales model:

${isTransactional ? `1. Target BD model — conversion improvements, average transaction value growth, repeat purchase mechanisms. What does the business need to do differently at the point of sale and in the period after purchase?

2. Process improvements — specific to transactional model: clearer conversion triggers, better follow-up mechanics, loyalty or retention programme design.` :
`1. Target BD model — what the pipeline looks like when working properly. Defined stages, qualification criteria documented, commercial conversation consistent. Push for specifics: "What does a well-qualified opportunity look like for this business — what criteria would it meet?"

2. Process improvements — specific disciplines that need to change. Be concrete: "A standing win/loss review every month" not "be more systematic." For business leader dependency: "The sequence that works is systematise first, then hire into a documented process. What would it take to write down exactly what you do in a BD conversation?"`}

3. Referral and expansion approach — systematic approaches to both. For referrals: asking process, tracking, incentivisation. For expansion: account review cadence, proactive identification of expansion opportunities. Push past "we'll be more proactive" to specific mechanisms.

4. Team and capability — ${isTransactional ? 'who owns BD, what tools are needed, how performance is tracked.' : 'first BD hire timing and profile, transition from founder-led selling, commission or incentive structure. Flag as Decision Guru candidate if a hire decision is imminent.'}

If the phrase "Decision Guru" appears in any of your messages, the UI will offer a routing button — use it intentionally.

After all four areas covered, emit READY_TO_SUMMARISE on its own line.

Open with: "Let's think about what the BD function needs to look like going forward. ${isTransactional ? "Given you're working with a transactional model — where's the biggest opportunity: improving conversion at point of sale, increasing what customers spend, or getting more of them to come back?" : "One of the most important things to get clear is what 'good' looks like in your pipeline — what does a well-qualified opportunity actually look like for this business? Let's start there."}"`;
    }

    return `You are GuruOne, a strategic advisor helping a business leader map their business development approach.${directionContextBlock}

IMPORTANT SCOPE: This section covers how the business CONVERTS opportunities into income — qualification, pipeline, the commercial conversation, pricing discipline in the sales context, win rate, referral activation, and existing client expansion. It does NOT cover how leads are generated — that is Marketing. Start from the point where a prospect exists.

OBJECTIVE
To map the BD reality — how opportunities get converted, what the process looks like, how warm channels (referrals, existing clients) are being used, and whether BD capability is built to scale or dependent on the business leader.

WHY THIS MATTERS
Poor BD process is one of the most consistent and costly weaknesses in SMEs. The warmest pipeline most businesses have — existing clients who could expand, happy clients who could refer — often goes largely unactivated. And business leader dependency in BD is almost universal but always a scaling constraint.

SALES MODEL DISTINCTION — CRITICAL
The conversation must first establish whether the business has a transactional or consultative sales model — this determines the depth and direction of everything that follows.

TRANSACTIONAL (short cycle, set pricing, volume-based, quick customer decisions):
Focus on: conversion rate, average transaction value, repeat purchase mechanics, referral and loyalty programmes.
Do NOT push pipeline management and proposal discipline — these are not relevant.
Keep the conversation shorter and more focused.

CONSULTATIVE (longer cycle, bespoke proposals, relationship-dependent, high average value):
Focus on: pipeline discipline, qualification rigour, pricing in negotiation, win rate, business leader dependency, CRM discipline.
This warrants a deeper conversation — push past rehearsed answers.

THEMES TO COVER — establish sales model first, then follow the relevant path:

ALL BUSINESSES:
1. Sales model — transactional or consultative (or mixed)
2. Existing client expansion — systematic or passive
3. Referral activation — systematic or passive
4. Business leader dependency in BD
5. Current challenges

TRANSACTIONAL ADDITIONAL:
- Conversion rate at point of sale
- Average transaction value
- Repeat purchase approach

CONSULTATIVE ADDITIONAL:
- Pipeline stages and discipline
- Qualification — criteria and cost of poor qualification
- Commercial conversation — pricing consistency and discounting patterns
- Win rate — known figure and whether reviewed

SURFACE-LEVEL ANSWERS TO PROBE DEEPER ON
- "We get most of our work through referrals" — probe whether referrals are actively cultivated or passively received
- Win rate "we win most of what we go for" — push for a number; not knowing means you can't improve it
- "We pursue most opportunities" — probe the cost: time and resource per unconverted proposal
- Pricing "it depends" — probe whether decisions are consistent or drift under pressure
- Existing clients as a growth source without a systematic approach — the warmest pipeline is a client with a new need
- Business leader as the closer — probe what happens to BD if the business leader steps back

CRITICAL RULES
1. Establish sales model in the first exchange — everything else follows from this.
2. For transactional businesses, keep the conversation focused and shorter. Don't push pipeline discipline.
3. For consultative businesses, push past the rehearsed answer on qualification and win rate.
4. Never ask about lead generation — that is Marketing's territory.
5. On referrals and expansion — probe whether these are passive or systematic; most business leaders have never thought about the distinction.

Opening question: "When a new client or customer comes to you — what typically happens next? Is it usually a fairly quick decision on their part, or is there a longer process of conversations, proposals, and sign-off before they commit?"`;
  }

  function buildFollowupPrompt() {
    if (isFutureMode) {
      return `You are GuruOne helping a business leader develop their future BD approach. You have asked ${questionCount.current} questions.

Continue covering any areas not yet addressed:
1. Target BD model — specific and realistic
2. Process improvements — concrete disciplines, not aspirations
3. Referral and expansion — systematic mechanisms
4. Team and capability — hire sequencing, transition from founder-led

For any significant BD hire decision, flag as Decision Guru candidate.

After all areas covered, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn.`;
    }
    return `You are GuruOne mapping a business leader's business development approach. You have asked ${questionCount.current} questions.${directionContextBlock}

IMPORTANT: This covers CONVERSION of opportunities — not lead generation (that is Marketing).

Assess from the conversation whether this is a TRANSACTIONAL or CONSULTATIVE sales model and calibrate accordingly.

TRANSACTIONAL — focus on: conversion rate, transaction value, repeat purchase, referral and loyalty mechanics. Keep focused and shorter.

CONSULTATIVE — focus on: pipeline stages, qualification (and cost of poor qualification), pricing discipline in commercial conversation, win rate and review process, business leader dependency, CRM.

ALL BUSINESSES — also cover: existing client expansion (systematic or passive), referral activation (systematic or passive), business leader dependency, current challenges.

THINGS WORTH PROBING DEEPER ON
- Referrals as passive receipt vs active cultivation — most business leaders have never thought about the distinction
- Existing clients as growth source without a systematic approach — probe whether there's a process for identifying and pursuing expansion
- "We win most of what we go for" — push for a number
- "We pursue most things" — probe the cost of unconverted proposals
- Pricing "it depends" — probe whether it drifts under pressure

COVERAGE CHECK — note which themes are addressed. Ask about uncovered themes before probing depth.

When every relevant theme has a substantive answer, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn. 2–4 sentences plus question.`;
  }

  function buildExtractPrompt() {
    if (isFutureMode) {
      return `Based on this conversation, extract the agreed future BD approach.

Return ONLY valid JSON starting with {:
{
  "target_bd_model": "what the BD function looks like when working properly — specific to the sales model type",
  "bd_process_improvements": "specific disciplines that need to change — concrete, not aspirational",
  "bd_team_capability": "first BD hire plan, transition from founder-led selling, incentive approach. Omit if not discussed.",
  "referral_expansion_approach": "systematic approaches to referral activation and existing client expansion"
}
Omit any field not clearly evidenced by the conversation.`;
    }
    return `Based on this conversation, extract the business development picture.

Return ONLY valid JSON starting with {:
{
  "sales_model": "one of exactly: 'Transactional — short cycle, set pricing' | 'Consultative — longer cycle, bespoke proposals' | 'Mixed'",
  "conversion_rate": "proportion of prospects who buy — known figure or estimate. Include only if transactional model.",
  "avg_transaction_value": "typical transaction value and whether it varies. Include only if transactional model.",
  "repeat_purchase_approach": "one of exactly: 'Systematic — loyalty or incentive programme in place' | 'Occasional reminders or follow-up' | 'Passive — customer-led'. Include only if transactional model.",
  "pipeline_definition": "one of exactly: 'Defined pipeline with clear stages' | 'Informal pipeline' | 'No defined pipeline'. Include only if consultative model.",
  "qualification_discipline": "one of exactly: 'Strong — clear criteria for what to pursue' | 'Some qualification' | 'Most opportunities are pursued'. Include only if consultative model.",
  "pricing_commercial": "one of exactly: 'Consistent approach — held under pressure' | 'Varies by opportunity' | 'Often discounted'. Include only if consultative model.",
  "win_rate": "proportion of pursued opportunities that convert and whether wins and losses are reviewed. Include only if consultative model.",
  "existing_client_expansion": "one of exactly: 'Systematic — actively pursued' | 'Occasional' | 'Reactive — client-led'",
  "referral_activation": "one of exactly: 'Systematic — asked for, tracked, and incentivised' | 'Occasional' | 'Passive — happens when it happens'",
  "founder_dependency_bd": "one of exactly: 'Founder does most BD' | 'Split between founder and team' | 'Team-led with founder support'",
  "crm_pipeline_mgmt": "one of exactly: 'CRM used consistently' | 'CRM used inconsistently' | 'No CRM — managed manually'",
  "bd_challenges": "biggest BD or income generation challenge in the business leader's own words"
}
Omit any field not clearly evidenced by the conversation.`;
  }

  // ── Conversation lifecycle ──────────────────────────────────────
  async function startConversation(future) {
    setIsFutureMode(future);
    setMessages([]);
    setInput('');
    setEditedData(null);
    questionCount.current = 0;
    setLoading(true);
    setPhase('chatting');
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: [{ role: 'user', content: future ? 'Start the future BD conversation.' : 'Start the business development conversation.' }],
        context: buildOpeningPrompt(),
      });
      setMessages([{ role: 'assistant', content: res.data.reply }]);
      questionCount.current = 1;
    } catch {
      setMessages([{ role: 'assistant', content: future
        ? "Let's think about what the BD function needs to look like going forward. What's the biggest opportunity to improve how the business converts interest into income?"
        : "When a new client or customer comes to you — what typically happens next? Is it usually a fairly quick decision on their part, or is there a longer process of conversations, proposals, and sign-off before they commit?" }]);
      questionCount.current = 1;
    }
    setLoading(false);
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      questionCount.current += 1;
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: newMessages,
        context: buildFollowupPrompt() + DIALOGUE_RULES_BLOCK,
      });
      const reply = res.data.reply || '';
      const isReady = reply.includes('READY_TO_SUMMARISE');

      const deferredQuestion = isReady ? maybeDeferSummariseForPendingQuestion(reply, questionCount.current, 11) : null;

      if (deferredQuestion) {

        setMessages(prev => [...prev, { role: 'assistant', content: deferredQuestion }]);

      } else if (isReady || questionCount.current >= 11) {
        const clean = reply.replace(/READY_TO_SUMMARISE/g, '').trim();
        const showRoutingButton = clean.includes('Decision Guru');
        if (clean) setMessages(prev => [...prev, { role: 'assistant', content: clean, showDecisionGuruButton: showRoutingButton }]);
        const convo = clean ? [...newMessages, { role: 'assistant', content: clean }] : newMessages;
        await extractData(convo);
      } else {
        const showRoutingButton = reply.includes('Decision Guru');
        setMessages(prev => [...prev, { role: 'assistant', content: reply, showDecisionGuruButton: showRoutingButton }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong — please try again.' }]);
    }
    setLoading(false);
  }

  async function extractData(msgs) {
    setMessages(prev => [...prev, { role: 'assistant', content: "Good — I have enough to work with. Let me synthesise what you've shared. You'll review and edit everything before saving." }]);
    setPhase('extracting');
    const founderAnswers = msgs.filter(m => m.role === 'user').map((m, i) => `Answer ${i + 1}: ${m.content}`).join('\n\n');
    const condensed = [{ role: 'user', content: `Here are the business leader's answers:\n\n${founderAnswers}\n\nExtract the structured data now.` }];
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, { messages: condensed, context: buildExtractPrompt() });
      const raw = res.data.reply || '';
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1 && e !== -1) {
        const parsed = JSON.parse(raw.slice(s, e + 1));
        setEditedData(parsed);
        setPhase('reviewing');
        return;
      }
    } catch { /* fall through */ }
    setPhase('intro');
    setMessages(prev => [...prev, { role: 'assistant', content: "I wasn't able to extract automatically this time. Use Quick entry to fill in the fields directly." }]);
  }

  function handleSave() {
    if (!editedData) return;
    if (isFutureMode) {
      onFutureChange({ ...futureData, ...editedData });
      setPhase('saved');
      return;
    }
    const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
    evalFlow.runEvaluation({ fields: editedData, transcript });
  }

  // ── Render ───────────────────────────────────────────────────────
  const colour = BD_DIALOGUE_COLOUR;
  const bg = BD_DIALOGUE_BG;
  const border = BD_DIALOGUE_BORDER;

  if (phase === 'intro') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1.25rem 1.5rem', marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.75rem' }}>
          Business development dialogue
        </div>
        <p style={{ fontSize: '0.82rem', color: '#333', lineHeight: 1.6, margin: '0 0 1rem' }}>
          We&apos;ll map how the business converts opportunities into income — qualification, pipeline discipline, pricing in the commercial conversation, win rate, and how systematically you activate referrals and existing clients. Lead generation is out of scope here — that&apos;s Marketing&apos;s territory. We&apos;ll start from the point a prospect exists.
        </p>
        <div style={{ fontSize: '0.68rem', color: '#888', lineHeight: 1.5, marginBottom: '1.25rem', borderTop: `1px solid ${border}`, paddingTop: '0.75rem' }}>
          <strong>How it works:</strong> GuruOne asks one question at a time and adapts depth to your sales model. Transactional gets a focused conversation; consultative goes deeper. Typically 7–11 exchanges.
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button onClick={() => startConversation(false)}
            style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Explore current state →
          </button>
          {hasFutureState && (
            <button onClick={() => startConversation(true)}
              style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: 'white', color: colour, border: `1.5px solid ${colour}`, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Review future state →
            </button>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'extracting') {
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {messages.slice(-2).map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '0.6rem 0.9rem', borderRadius: 10, background: m.role === 'user' ? colour : bg, color: m.role === 'user' ? 'white' : '#7F1D1D', fontSize: '0.8rem', lineHeight: 1.5 }}>{m.content}</div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: colour, fontSize: '0.8rem' }}>
          <div style={{ width: 14, height: 14, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          Synthesising your answers…
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      </div>
    );
  }

  if (phase === 'reviewing' && editedData) {
    // Filter the field list to only include fields that have a value (or
    // are always-relevant). The transactional/consultative paths produce
    // different subsets — show what was captured rather than every field.
    const ALL_CURRENT = [
      { key: 'sales_model', label: 'Sales model' },
      { key: 'conversion_rate', label: 'Conversion rate' },
      { key: 'avg_transaction_value', label: 'Average transaction value' },
      { key: 'repeat_purchase_approach', label: 'Repeat purchase approach' },
      { key: 'pipeline_definition', label: 'Pipeline definition' },
      { key: 'qualification_discipline', label: 'Qualification discipline' },
      { key: 'pricing_commercial', label: 'Pricing in commercial conversation' },
      { key: 'win_rate', label: 'Win rate' },
      { key: 'existing_client_expansion', label: 'Existing client expansion' },
      { key: 'referral_activation', label: 'Referral activation' },
      { key: 'founder_dependency_bd', label: 'Business leader dependency in BD' },
      { key: 'crm_pipeline_mgmt', label: 'CRM and pipeline management' },
      { key: 'bd_challenges', label: 'Current challenges' },
    ];
    const fieldDefs = isFutureMode ? [
      { key: 'target_bd_model', label: 'Target BD model' },
      { key: 'bd_process_improvements', label: 'Process improvements' },
      { key: 'bd_team_capability', label: 'Team and capability' },
      { key: 'referral_expansion_approach', label: 'Referral and expansion approach' },
    ] : ALL_CURRENT.filter(f => editedData[f.key] !== undefined && editedData[f.key] !== null && editedData[f.key] !== '');
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <DialogueEvalModal
          sectionMeta={sectionMeta}
          evalFlow={evalFlow}
          onCommitWithEval={(evaluation) => {
            const payload = { ...currentData, ...editedData };
            if (evaluation) payload._evaluation = evaluation;
            onChange(payload);
            setPhase('saved');
          }}
          onRefine={() => setPhase('chatting')}
        />
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colour, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Review &amp; edit — {isFutureMode ? 'future state' : 'current state'}
        </div>
        <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: '0.85rem', lineHeight: 1.5 }}>
          AI-drafted from your conversation — edit any field until it precisely matches reality before saving.
        </div>
        {fieldDefs.map(({ key, label }) => {
          const value = editedData[key] || '';
          const chipOpts = BD_CHIP_OPTIONS[key];
          if (chipOpts) {
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>{label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {chipOpts.map(opt => {
                    const active = value === opt;
                    return (
                      <button key={opt} onClick={() => setEditedData(p => ({ ...p, [key]: active ? '' : opt }))}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? bg : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          return (
            <div key={key} style={{ marginBottom: '0.85rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
              <textarea value={value} onChange={e => setEditedData(p => ({ ...p, [key]: e.target.value }))} rows={3}
                style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: `1px solid ${border}`, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55 }} />
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.6rem 1.25rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save
          </button>
          <button onClick={() => setPhase('chatting')}
            style={{ padding: '0.6rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${border}`, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Continue conversation
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'saved') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>
            {isFutureMode ? 'Future state captured' : 'Current state captured'}
          </div>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#444', margin: '0 0 0.85rem', lineHeight: 1.55 }}>
          Your answers are saved. {isFutureMode
            ? 'Switch to the Future State tab to see them, or edit any field there in Quick mode.'
            : 'You can edit any field in Quick mode below, or run another conversation to refine.'}
        </p>
        <button onClick={() => setPhase('intro')}
          style={{ background: 'none', border: 'none', color: colour, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', padding: '0.5rem 0', width: 'auto' }}>
          Run another conversation →
        </button>
      </div>
    );
  }

  // Chatting phase
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.5rem' }}>
        {isFutureMode ? 'Future state — design / review' : 'Current state — explore'}
      </div>
      <div ref={chatContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '0.85rem', maxHeight: 320, overflowY: 'auto', paddingRight: '0.2rem' }}>
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          const showDgButton = !isUser && m.showDecisionGuruButton === true;
          return (
            <div key={i} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '88%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '0.6rem 0.85rem', borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px', background: isUser ? colour : bg, color: isUser ? 'white' : '#7F1D1D', fontSize: '0.82rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                {m.content}
              </div>
              {showDgButton && (
                <button onClick={openDecisionGuruInline}
                  style={{ marginTop: '0.5rem', padding: '0.4rem 1rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto', alignSelf: 'flex-start' }}>
                  Take to Decision Guru →
                </button>
              )}
            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: colour, fontStyle: 'italic' }}>
            <div style={{ width: 11, height: 11, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            GuruOne is thinking…
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type your answer… (Enter to send)"
          disabled={loading} rows={2}
          style={{ flex: 1, fontSize: '0.82rem', padding: '0.6rem 0.8rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          style={{ padding: '0.6rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
          Send
        </button>
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

// ── PortfolioDialogue ─────────────────────────────────────────────
// Standalone dialogue for the portfolio section. Same phase machine
// as the other dialogues. Strategic-direction framing (deeper vs
// broader) is the central question; surface it early and probe the
// rehearsed answer before accepting it.

const PORTFOLIO_DIALOGUE_COLOUR = '#B45309';
const PORTFOLIO_DIALOGUE_BG = '#FEF3C7';
const PORTFOLIO_DIALOGUE_BORDER = '#FCD34D';

const PORTFOLIO_CHIP_OPTIONS = {
  portfolio_size: ['Single core offering', 'Small portfolio — 2 to 5 offerings', 'Broader portfolio — 6 or more'],
  portfolio_health: ['Strong across the board', 'Mixed — some performing well, some under pressure', 'Under pressure overall'],
  margin_profile: ['High margin offerings dominant', 'Mixed margin profile', 'Low margin — volume dependent'],
  portfolio_focus: ['Tightly focused — clear specialism', 'Moderately focused', 'Broad — wide range of offerings'],
  offering_development_model: ['Structured and deliberate', 'Ad hoc — as opportunities arise', 'Reactive to client requests'],
  strategic_direction: ['Go deeper — strengthen the specialism', 'Broaden deliberately — add adjacent offerings', 'Both — different parts of the portfolio need different approaches'],
  build_buy_partner: ['Build internally', 'Acquire', 'Partner with another business', 'Mix of routes'],
};

function PortfolioDialogue({ currentData, futureData, onChange, onFutureChange, hasFutureState, sectionKey }) {
  const [isFutureMode, setIsFutureMode] = useState(false);
  const [phase, setPhase] = useState('intro');
  useEffect(() => {
    if (phase === 'extracting') track('design_conversation_completed', { section: sectionKey });
  }, [phase, sectionKey]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const chatContainerRef = useRef(null);
  const questionCount = useRef(0);
  // Cross-card context — Direction snapshot. Injected into opening
  // and followup prompts so this dialogue doesn't re-ask the leader
  // anything already captured upstream (stage, revenue model,
  // customer segments, value proposition, etc.).
  const directionContextBlock = buildDirectionContextBlock(readDirectionCanvas());

  // Evaluation flow — kicks off /evaluate-section after the leader
  // confirms the extracted data. The DialogueEvalModal wrapper at
  // the top of the reviewing-phase return handles the modal lifecycle.
  const sectionMeta = OM_SECTIONS.find(s => s.key === sectionKey);
  const evalFlow = useEvaluationFlow({
    sectionKey,
    sectionTitle: sectionMeta?.title || sectionKey,
    sectionFields: sectionMeta?.current_fields || [],
    repeatable: false,
  });

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  function openDecisionGuruInline() {
    window.open('/decision', '_blank', 'noopener');
  }

  // ── Prompt builders ─────────────────────────────────────────────
  function buildOpeningPrompt() {
    if (isFutureMode) {
      const hasFutureData = futureData && Object.values(futureData).some(v => v);
      if (hasFutureData) {
        return `You are GuruOne reviewing an AI-generated future portfolio strategy with the business leader.

AI-GENERATED FUTURE STATE:
Strategic direction: ${futureData?.strategic_direction || '(none yet)'}
New offerings planned: ${futureData?.new_offerings || '(none yet)'}
Portfolio rationalisation: ${futureData?.portfolio_rationalisation || '(none yet)'}
Adjacencies: ${futureData?.portfolio_adjacencies || '(none yet)'}
Route to filling gaps: ${futureData?.build_buy_partner || '(none yet)'}
Future portfolio vision: ${futureData?.future_portfolio_vision || '(none yet)'}

Walk through each element in order. For each: present what was suggested, ask whether it matches their thinking.

Probe specifically:
- Whether the strategic direction (deeper vs broader) has been chosen deliberately or defaulted to
- Whether new offerings are realistic given current team capability and the time to build credibility
- Whether portfolio rationalisation is specific enough — "we'll review it" is not a plan
- Whether the route to filling gaps (build/buy/partner) has been genuinely considered or just assumed
- For any build vs acquire decision, flag as a Decision Guru candidate — these are among the highest-stakes choices a business leader makes

After all elements reviewed, emit READY_TO_SUMMARISE on its own line.

Open with: "I've put together a suggested future portfolio strategy. Let me start with the strategic direction: ${futureData?.strategic_direction || '(none generated yet)'}. Does that match where you want to take the portfolio?"`;
      }
      return `You are GuruOne helping a business leader develop their future portfolio strategy.

CURRENT STATE CONTEXT:
Current portfolio: ${currentData?.portfolio_description || 'not captured'}
Portfolio size: ${currentData?.portfolio_size || 'not captured'}
Portfolio health: ${currentData?.portfolio_health || 'not captured'}
Margin profile: ${currentData?.margin_profile || 'not captured'}
Portfolio focus: ${currentData?.portfolio_focus || 'not captured'}
Development model: ${currentData?.offering_development_model || 'not captured'}
Offerings to retire: ${currentData?.offerings_to_retire || 'not captured'}

Cover five areas in order:

1. Strategic direction — niche deeper or broaden deliberately. This is the most important framing question. Don't assume: ask which stage the business is actually at. The characteristics that suggest going deeper: win rate declining, marketing hard to articulate, delivery inconsistent, margins varying. The characteristics that suggest broadening: core is profitable and well-delivered, clients are actively pulling for adjacent services, genuine capability overlap. If the business leader says they've saturated their niche, probe whether they've genuinely reached the ceiling or whether harder-to-reach clients in the same space remain.

2. New offerings — what's in development or planned and why. Probe whether demand is being pulled by clients or pushed by the business. Client pull is a much stronger signal than internal conviction.

3. Portfolio rationalisation — what to exit or wind down. Be direct: "Given what you've said about [underperforming offering], is there a version of this where you stop doing it entirely? What would have to be true for that to be the right call?"

4. Adjacencies — natural extensions the business is well placed to develop. Surface two or three realistic adjacencies based on what's been described. For each: "Is that something clients are asking for, or something you think they should want?"

5. Build, buy or partner — how portfolio gaps get filled. Surface the three routes explicitly: build (slower, lower financial cost, preserves culture), acquire (faster, higher cost, integration risk), partner (lowest cost, creates dependency). For any significant build vs acquire decision, flag as a Decision Guru candidate.

If the phrase "Decision Guru" appears in any of your messages, the UI will offer a routing button — use it intentionally.

After all five areas covered, emit READY_TO_SUMMARISE on its own line.

Open with: "Let's think about where the portfolio needs to go. Before we get into specific offerings — is the instinct to go deeper into what you already do best, or to broaden deliberately? Because those lead to very different decisions, and it's worth being clear which stage the business is actually at."`;
    }

    return `You are GuruOne, a strategic advisor helping a business leader map their product and service portfolio.${directionContextBlock}

This is about the CURRENT STATE — what the business actually offers and how it's performing. Be specific and grounded. Push back on rehearsed answers and ask for the real picture.

OBJECTIVE
To map what the business offers, how each part of the portfolio performs commercially, whether the portfolio is the right shape for the direction, and whether the business is at the stage to go deeper into its specialism or to broaden deliberately.

WHY THIS MATTERS
Most businesses have portfolios that evolved rather than ones that were designed. The instinct to add is almost always stronger than the instinct to cut — adding feels like growth, cutting feels like retreat. But a portfolio broad enough to confuse buyers, too thin to compete with specialists, and too complex to deliver consistently is one of the most common and most expensive strategic mistakes a growing business makes.

WHAT A GOOD ANSWER LOOKS LIKE
A clear description of each offering — who it's for, how it performs, and what it costs to deliver. An honest view of which parts are genuinely driving the business and which are being carried. A clear answer to whether the portfolio is the right shape — not what the business leader wishes it were, but what the evidence suggests.

SURFACE-LEVEL ANSWERS TO PROBE DEEPER ON
- "We do X, Y, and Z" without any view on relative performance — push for which is growing, which is under pressure, which is making money
- Portfolio described as broad because "clients need different things" — probe whether the business is genuinely executing well across all of it, or whether breadth is masking an inability to compete as a specialist
- "We've saturated our niche" — probe whether they've genuinely reached the ceiling or whether harder-to-reach clients in the same space remain
- "Everything has a role" when asked what to retire — this is almost always the rehearsed answer; probe for the real one
- Performance described without reference to margin — a large offering that's barely profitable is a different conversation from a small one that makes excellent returns

THEMES TO COVER (in order)
1. The full portfolio — every offering, who it's for, what it involves
2. Performance — which are growing, which are under pressure
3. Commercial reality — which generate the best return relative to cost and effort
4. Niche or broaden — whether the portfolio has been shaped by deliberate choice or accumulated decisions
5. How new offerings are developed — structured or reactive
6. What should stop — the honest answer about what would be retired if starting again
7. Current challenges — the biggest portfolio challenge right now. Many leaders struggle to name a portfolio-specific challenge because they default to adjacent issues (positioning, pricing, sales). Help by listing portfolio-specific challenge types when probing:
   - Cannibalisation — newer offering eating the older one's revenue
   - Underperformer — an offering kept for the wrong reasons (loyalty, legacy, fear)
   - Margin distortion — one offering's economics dragging the overall margin
   - Internal capability stretch — portfolio breadth outrunning team capability or process
   - Buyer confusion — too many offerings making the business hard to describe
   - Saturation in one area — flat performance in the core offering without an obvious next step
   - Adjacency gap — known client need with no offering ready to meet it
   - Build-vs-buy stuck — a known portfolio gap where the route to fill it isn't clear

CRITICAL RULES
1. Ask ONE warm, direct opening question.
2. Wait for the answer before asking the next.
3. On the niche vs broaden question — don't accept the first answer. Most business leaders have a rehearsed version and a real one.
4. On retiring offerings — "everything has a role" is not a real answer. Probe once more.
5. Keep the conversation strategic — this is not a product feature discussion.
6. On current challenges — if the leader names a challenge that's actually go-to-market, positioning, or value proposition (a different canvas section), acknowledge it and gently redirect: "That's worth capturing in the [GTM/Marketing/Value Proposition] section. For the portfolio specifically, is there something about the SHAPE of what you offer that's giving you trouble?"

Opening question: "Walk me through everything the business currently offers — products, services, anything a client or customer pays for. Not the marketing version — just what it actually is and who it's for."`;
  }

  function buildFollowupPrompt() {
    if (isFutureMode) {
      return `You are GuruOne helping a business leader develop their portfolio strategy. You have asked ${questionCount.current} questions.

Continue covering any areas not yet fully addressed:
1. Strategic direction — deeper vs broader, with evidence
2. New offerings — client pull vs internal conviction
3. Portfolio rationalisation — specific, not aspirational
4. Adjacencies — realistic, not wishful
5. Build, buy or partner — flag significant decisions as Decision Guru candidates

After all areas covered, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn.`;
    }
    return `You are GuruOne mapping a business leader's product and service portfolio. You have asked ${questionCount.current} questions.${directionContextBlock}

IMPORTANT: This is about the CURRENT STATE. Push past rehearsed answers to the real picture.

THEMES TO COVER
1. The full portfolio — every offering, who it's for
2. Performance — growing, under pressure, unknown
3. Commercial reality — best and worst return relative to effort
4. Niche or broaden — deliberate choice or accumulated decisions
5. How new offerings are developed — structured or reactive
6. What should stop — the honest answer
7. Current challenges

THINGS WORTH PROBING DEEPER ON
- "We do X, Y, and Z" — push for relative performance of each
- Breadth justified by "client need" — probe whether genuinely executing well across all of it
- "Saturated our niche" — probe whether ceiling is real or assumed
- "Everything has a role" — probe once more for the real answer
- Performance described without margin — a large offering barely profitable is a different conversation

COVERAGE CHECK — note which themes are addressed. Ask about uncovered themes before probing depth.

When every theme has a substantive answer, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn. 2–4 sentences plus question.`;
  }

  function buildExtractPrompt() {
    if (isFutureMode) {
      return `Based on this conversation, extract the agreed future portfolio strategy.

Return ONLY valid JSON starting with {:
{
  "strategic_direction": "one of exactly: 'Go deeper — strengthen the specialism' | 'Broaden deliberately — add adjacent offerings' | 'Both — different parts of the portfolio need different approaches'",
  "new_offerings": "what is being developed or planned and why — including whether demand is client-led or internally driven. Omit if none.",
  "portfolio_rationalisation": "what to exit or wind down and why. Omit if none.",
  "portfolio_adjacencies": "natural extensions the business is well placed to develop. Omit if none identified.",
  "build_buy_partner": "one of exactly: 'Build internally' | 'Acquire' | 'Partner with another business' | 'Mix of routes'",
  "future_portfolio_vision": "what the overall portfolio looks like at target scale — the shape and focus of what the business offers"
}
Omit any field not clearly evidenced by the conversation.`;
    }
    return `Based on this conversation, extract the product and service portfolio picture.

Return ONLY valid JSON starting with {:
{
  "portfolio_description": "what the business offers — each offering, who it is for, and what it involves",
  "portfolio_size": "one of exactly: 'Single core offering' | 'Small portfolio — 2 to 5 offerings' | 'Broader portfolio — 6 or more'",
  "portfolio_health": "one of exactly: 'Strong across the board' | 'Mixed — some performing well, some under pressure' | 'Under pressure overall'",
  "margin_profile": "one of exactly: 'High margin offerings dominant' | 'Mixed margin profile' | 'Low margin — volume dependent'",
  "portfolio_focus": "one of exactly: 'Tightly focused — clear specialism' | 'Moderately focused' | 'Broad — wide range of offerings'",
  "offering_development_model": "one of exactly: 'Structured and deliberate' | 'Ad hoc — as opportunities arise' | 'Reactive to client requests'",
  "offerings_to_retire": "offerings the business leader acknowledged should be retired or wound down. Omit if none surfaced.",
  "portfolio_challenges": "biggest portfolio challenge in the business leader's own words"
}
Omit any field not clearly evidenced by the conversation.`;
  }

  // ── Conversation lifecycle ──────────────────────────────────────
  async function startConversation(future) {
    setIsFutureMode(future);
    setMessages([]);
    setInput('');
    setEditedData(null);
    questionCount.current = 0;
    setLoading(true);
    setPhase('chatting');
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: [{ role: 'user', content: future ? 'Start the future portfolio strategy conversation.' : 'Start the product and service portfolio conversation.' }],
        context: buildOpeningPrompt(),
      });
      setMessages([{ role: 'assistant', content: res.data.reply }]);
      questionCount.current = 1;
    } catch {
      setMessages([{ role: 'assistant', content: future
        ? "Let's think about where the portfolio needs to go. Before we get into specific offerings — is the instinct to go deeper into what you already do best, or to broaden deliberately?"
        : "Walk me through everything the business currently offers — products, services, anything a client or customer pays for. Not the marketing version — just what it actually is and who it's for." }]);
      questionCount.current = 1;
    }
    setLoading(false);
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      questionCount.current += 1;
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: newMessages,
        context: buildFollowupPrompt() + DIALOGUE_RULES_BLOCK,
      });
      const reply = res.data.reply || '';
      const isReady = reply.includes('READY_TO_SUMMARISE');

      const deferredQuestion = isReady ? maybeDeferSummariseForPendingQuestion(reply, questionCount.current, 10) : null;

      if (deferredQuestion) {

        setMessages(prev => [...prev, { role: 'assistant', content: deferredQuestion }]);

      } else if (isReady || questionCount.current >= 10) {
        const clean = reply.replace(/READY_TO_SUMMARISE/g, '').trim();
        const showRoutingButton = clean.includes('Decision Guru');
        if (clean) setMessages(prev => [...prev, { role: 'assistant', content: clean, showDecisionGuruButton: showRoutingButton }]);
        const convo = clean ? [...newMessages, { role: 'assistant', content: clean }] : newMessages;
        await extractData(convo);
      } else {
        const showRoutingButton = reply.includes('Decision Guru');
        setMessages(prev => [...prev, { role: 'assistant', content: reply, showDecisionGuruButton: showRoutingButton }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong — please try again.' }]);
    }
    setLoading(false);
  }

  async function extractData(msgs) {
    setMessages(prev => [...prev, { role: 'assistant', content: "Good — I have enough to work with. Let me synthesise what you've shared. You'll review and edit everything before saving." }]);
    setPhase('extracting');
    const founderAnswers = msgs.filter(m => m.role === 'user').map((m, i) => `Answer ${i + 1}: ${m.content}`).join('\n\n');
    const condensed = [{ role: 'user', content: `Here are the business leader's answers:\n\n${founderAnswers}\n\nExtract the structured data now.` }];
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, { messages: condensed, context: buildExtractPrompt() });
      const raw = res.data.reply || '';
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1 && e !== -1) {
        const parsed = JSON.parse(raw.slice(s, e + 1));
        setEditedData(parsed);
        setPhase('reviewing');
        return;
      }
    } catch { /* fall through */ }
    setPhase('intro');
    setMessages(prev => [...prev, { role: 'assistant', content: "I wasn't able to extract automatically this time. Use Quick entry to fill in the fields directly." }]);
  }

  function handleSave() {
    if (!editedData) return;
    if (isFutureMode) {
      onFutureChange({ ...futureData, ...editedData });
      setPhase('saved');
      return;
    }
    const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
    evalFlow.runEvaluation({ fields: editedData, transcript });
  }

  // ── Render ───────────────────────────────────────────────────────
  const colour = PORTFOLIO_DIALOGUE_COLOUR;
  const bg = PORTFOLIO_DIALOGUE_BG;
  const border = PORTFOLIO_DIALOGUE_BORDER;

  if (phase === 'intro') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1.25rem 1.5rem', marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.75rem' }}>
          Product &amp; service portfolio dialogue
        </div>
        <p style={{ fontSize: '0.82rem', color: '#333', lineHeight: 1.6, margin: '0 0 1rem' }}>
          We&apos;ll map what the business actually offers and how each part performs — not the marketing version. The most important question we&apos;ll get to: is this the moment to go deeper into your specialism, or to broaden deliberately? Those are very different decisions and most business leaders have a rehearsed answer and a real one.
        </p>
        <div style={{ fontSize: '0.68rem', color: '#888', lineHeight: 1.5, marginBottom: '1.25rem', borderTop: `1px solid ${border}`, paddingTop: '0.75rem' }}>
          <strong>How it works:</strong> GuruOne asks one question at a time and probes for the real picture. At the end you review and edit a structured summary before saving. Typically 7–10 exchanges.
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button onClick={() => startConversation(false)}
            style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Explore current state →
          </button>
          {hasFutureState && (
            <button onClick={() => startConversation(true)}
              style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: 'white', color: colour, border: `1.5px solid ${colour}`, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Review future state →
            </button>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'extracting') {
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {messages.slice(-2).map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '0.6rem 0.9rem', borderRadius: 10, background: m.role === 'user' ? colour : bg, color: m.role === 'user' ? 'white' : '#78350F', fontSize: '0.8rem', lineHeight: 1.5 }}>{m.content}</div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: colour, fontSize: '0.8rem' }}>
          <div style={{ width: 14, height: 14, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          Synthesising your answers…
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      </div>
    );
  }

  if (phase === 'reviewing' && editedData) {
    const fieldDefs = isFutureMode ? [
      { key: 'strategic_direction', label: 'Strategic direction' },
      { key: 'new_offerings', label: 'New offerings planned' },
      { key: 'portfolio_rationalisation', label: 'Portfolio rationalisation' },
      { key: 'portfolio_adjacencies', label: 'Portfolio adjacencies' },
      { key: 'build_buy_partner', label: 'Route to filling gaps' },
      { key: 'future_portfolio_vision', label: 'Future portfolio vision' },
    ] : [
      { key: 'portfolio_description', label: 'What the business offers' },
      { key: 'portfolio_size', label: 'Portfolio size' },
      { key: 'portfolio_health', label: 'Portfolio health' },
      { key: 'margin_profile', label: 'Margin profile' },
      { key: 'portfolio_focus', label: 'Portfolio focus' },
      { key: 'offering_development_model', label: 'How new offerings are developed' },
      { key: 'offerings_to_retire', label: 'Offerings to consider retiring' },
      { key: 'portfolio_challenges', label: 'Current challenges' },
    ];
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <DialogueEvalModal
          sectionMeta={sectionMeta}
          evalFlow={evalFlow}
          onCommitWithEval={(evaluation) => {
            const payload = { ...currentData, ...editedData };
            if (evaluation) payload._evaluation = evaluation;
            onChange(payload);
            setPhase('saved');
          }}
          onRefine={() => setPhase('chatting')}
        />
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colour, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Review &amp; edit — {isFutureMode ? 'future state' : 'current state'}
        </div>
        <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: '0.85rem', lineHeight: 1.5 }}>
          AI-drafted from your conversation — edit any field until it precisely matches reality before saving.
        </div>
        {fieldDefs.map(({ key, label }) => {
          const value = editedData[key] || '';
          const chipOpts = PORTFOLIO_CHIP_OPTIONS[key];
          if (chipOpts) {
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>{label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {chipOpts.map(opt => {
                    const active = value === opt;
                    return (
                      <button key={opt} onClick={() => setEditedData(p => ({ ...p, [key]: active ? '' : opt }))}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? bg : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          return (
            <div key={key} style={{ marginBottom: '0.85rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
              <textarea value={value} onChange={e => setEditedData(p => ({ ...p, [key]: e.target.value }))} rows={3}
                style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: `1px solid ${border}`, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55 }} />
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.6rem 1.25rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save
          </button>
          <button onClick={() => setPhase('chatting')}
            style={{ padding: '0.6rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${border}`, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Continue conversation
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'saved') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>
            {isFutureMode ? 'Future state captured' : 'Current state captured'}
          </div>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#444', margin: '0 0 0.85rem', lineHeight: 1.55 }}>
          Your answers are saved. {isFutureMode
            ? 'Switch to the Future State tab to see them, or edit any field there in Quick mode.'
            : 'You can edit any field in Quick mode below, or run another conversation to refine.'}
        </p>
        <button onClick={() => setPhase('intro')}
          style={{ background: 'none', border: 'none', color: colour, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', padding: '0.5rem 0', width: 'auto' }}>
          Run another conversation →
        </button>
      </div>
    );
  }

  // Chatting phase
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.5rem' }}>
        {isFutureMode ? 'Future state — design / review' : 'Current state — explore'}
      </div>
      <div ref={chatContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '0.85rem', maxHeight: 320, overflowY: 'auto', paddingRight: '0.2rem' }}>
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          const showDgButton = !isUser && m.showDecisionGuruButton === true;
          return (
            <div key={i} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '88%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '0.6rem 0.85rem', borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px', background: isUser ? colour : bg, color: isUser ? 'white' : '#78350F', fontSize: '0.82rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                {m.content}
              </div>
              {showDgButton && (
                <button onClick={openDecisionGuruInline}
                  style={{ marginTop: '0.5rem', padding: '0.4rem 1rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto', alignSelf: 'flex-start' }}>
                  Take to Decision Guru →
                </button>
              )}
            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: colour, fontStyle: 'italic' }}>
            <div style={{ width: 11, height: 11, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            GuruOne is thinking…
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type your answer… (Enter to send)"
          disabled={loading} rows={2}
          style={{ flex: 1, fontSize: '0.82rem', padding: '0.6rem 0.8rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          style={{ padding: '0.6rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
          Send
        </button>
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

// ── ITDialogue ────────────────────────────────────────────────────
// Standalone dialogue for the it_data_security section (consolidated
// from the previous apps_technology + data_flow_security pair).
// Same phase machine as the other dialogues. Plain-language rules
// enforced for GDPR / encryption / 2FA explanations.

const IT_DIALOGUE_COLOUR = '#0F766E';
const IT_DIALOGUE_BG = '#F0FDFA';
const IT_DIALOGUE_BORDER = '#5EEAD4';

const IT_CHIP_OPTIONS = {
  ai_data_risk: ['Aware and managed', 'Not yet considered', 'No AI tools in use'],
  file_organisation: ['Consistent structure — anyone could find things', 'Partially organised', 'Each person has their own system'],
  data_categories: ['Customer contact data', 'Payment or financial data', 'Employee records', 'Commercially sensitive client data', 'Health or special category data', 'Operational data'],
  gdpr_posture: ['Current privacy policy and consent in place', 'Partially addressed', 'Not yet addressed'],
  security_basics: ['Strong controls — devices protected, access managed', 'Basic controls in place', 'Limited controls'],
  backup_recovery: ['Tested and reliable', 'In place but untested', 'Not in place'],
  horizon_scanning: ['Active — deliberate scanning and experimentation', 'Occasional', 'Reactive only'],
};

const IT_MULTI_FIELDS = new Set(['data_categories']);

function ITDialogue({ currentData, futureData, onChange, onFutureChange, hasFutureState, sectionKey }) {
  const [isFutureMode, setIsFutureMode] = useState(false);
  const [phase, setPhase] = useState('intro');
  useEffect(() => {
    if (phase === 'extracting') track('design_conversation_completed', { section: sectionKey });
  }, [phase, sectionKey]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const chatContainerRef = useRef(null);
  const questionCount = useRef(0);
  // Cross-card context — Direction snapshot. Injected into opening
  // and followup prompts so this dialogue doesn't re-ask the leader
  // anything already captured upstream (stage, revenue model,
  // customer segments, value proposition, etc.).
  const directionContextBlock = buildDirectionContextBlock(readDirectionCanvas());

  // Evaluation flow — kicks off /evaluate-section after the leader
  // confirms the extracted data. The DialogueEvalModal wrapper at
  // the top of the reviewing-phase return handles the modal lifecycle.
  const sectionMeta = OM_SECTIONS.find(s => s.key === sectionKey);
  const evalFlow = useEvaluationFlow({
    sectionKey,
    sectionTitle: sectionMeta?.title || sectionKey,
    sectionFields: sectionMeta?.current_fields || [],
    repeatable: false,
  });

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  function openDecisionGuruInline() {
    window.open('/decision', '_blank', 'noopener');
  }

  // ── Prompt builders ─────────────────────────────────────────────
  function buildOpeningPrompt() {
    if (isFutureMode) {
      const hasFutureData = futureData && Object.values(futureData).some(v => v);
      if (hasFutureData) {
        return `You are GuruOne reviewing an AI-generated future IT and data plan with the business leader.

AI-GENERATED FUTURE STATE:
Compliance and security priorities: ${futureData?.compliance_security_priorities || '(none yet)'}
Bespoke solution remediation: ${futureData?.bespoke_remediation || '(none yet)'}
Data organisation improvements: ${futureData?.data_organisation_improvements || '(none yet)'}
Technology opportunities: ${futureData?.technology_opportunities || '(none yet)'}

Walk through each element in order. For each: present what was suggested, ask whether it matches their thinking.

Probe specifically:
- Whether compliance and security priorities are sequenced correctly — quick wins first, then structural changes
- Whether bespoke solution remediation is realistic given the team's capacity
- Whether technology opportunities are specific enough to act on, or still aspirational
- For any significant investment, flag as a Decision Guru candidate

After all elements reviewed, emit READY_TO_SUMMARISE on its own line.

Open with: "I've put together a suggested plan for IT and data. Let me start with the compliance and security priorities: ${futureData?.compliance_security_priorities || '(none generated yet)'}. Does that reflect where you think the focus should be?"`;
      }
      return `You are GuruOne helping a business leader improve their IT and data posture.

CURRENT STATE CONTEXT:
Tech estate: ${currentData?.tech_estate || 'not captured'}
Bespoke solutions: ${currentData?.bespoke_solutions || 'not captured'}
AI data risk: ${currentData?.ai_data_risk || 'not captured'}
File organisation: ${currentData?.file_organisation || 'not captured'}
Data categories: ${Array.isArray(currentData?.data_categories) ? currentData.data_categories.join(', ') : 'not captured'}
GDPR posture: ${currentData?.gdpr_posture || 'not captured'}
Security basics: ${currentData?.security_basics || 'not captured'}
Backup and recovery: ${currentData?.backup_recovery || 'not captured'}
Horizon scanning: ${currentData?.horizon_scanning || 'not captured'}

Cover four areas in order:
1. Compliance and security priorities — what needs to be addressed first. Sequence quick wins (strong passwords, two-factor authentication, encrypted devices) before structural changes. Be practical and specific.
2. Bespoke solution remediation — for any informal tools identified, explore whether to document and share the knowledge, replace with a proper tool, or consciously accept the risk.
3. Data and file organisation improvements — what needs to change so the business isn't dependent on individuals knowing where things are.
4. Technology opportunities — tools or capabilities on the radar that could create competitive advantage. Push beyond passive awareness toward active experimentation: "What would it take to run a small pilot on that in the next three months?"

For any significant technology investment, flag as a Decision Guru candidate.

If the phrase "Decision Guru" appears in any of your messages, the UI will offer a routing button — use it intentionally.

After all four areas covered, emit READY_TO_SUMMARISE on its own line.

Open with: "Let's think about where your IT and data posture needs to get to. Given what you've described, what's the priority — reducing your exposure, getting more organised, replacing informal solutions, or actively using technology as a competitive lever?"`;
    }

    return `You are GuruOne, a strategic advisor helping a business leader map their IT and data posture.${directionContextBlock}

This is about the CURRENT STATE — what the business actually uses, holds, and how it manages it today. Be specific and grounded. Push back on vague answers.

OBJECTIVE
To map the technology the business relies on, where bespoke or informal solutions create hidden risk, how data and files are organised, whether basic compliance obligations are being met, and whether the business is actively scanning for technology that could give it an edge.

WHY THIS MATTERS
Most SMEs hold more sensitive data than they realise, rely on more informal technology than they'd admit, and have never asked whether their data and file management would survive a key person leaving. But this is also about opportunity — businesses that actively scan for and experiment with new technology are building structural advantages over those that treat IT as a maintenance question.

WHAT A GOOD ANSWER LOOKS LIKE
A clear picture of the main systems the business relies on and where informal solutions have filled gaps. An honest view of how data and files are organised. A basic compliance posture the business leader can describe. Security basics in place and tested. A genuine view on how actively the business looks for technology that could give it an edge.

LANGUAGE RULES
Use plain language throughout. Specifically:
- Explain "GDPR" briefly if needed: "the rules around how businesses handle personal data"
- Explain "subject access request" as "a legal right for anyone to ask what information you hold about them"
- Don't say "encryption" without explaining it: "whether files and devices are protected so that if lost, the data can't be read"
- Don't say "two-factor authentication" without explaining it: "a second check when logging in — like a code sent to your phone"
- Be matter-of-fact about risks — inform, don't alarm

SURFACE-LEVEL ANSWERS TO PROBE DEEPER ON
- Bespoke solutions without acknowledging key-person risk — push for who else understands how they work
- "AI tools process files I give them" treated as obviously fine — gently surface the question of whether sensitive client data is going through consumer AI tools
- File organisation described as "everyone knows where things are" — probe whether a new person could find things on day one
- Backups described as "automatic" — push for where they go and whether recovery has been tested
- "We keep an eye on new tools" — probe what that actually looks like in practice

THEMES TO COVER (in order)
1. The tech estate — main systems beyond delivery tools
2. Bespoke solutions and workarounds — spreadsheets, automations, informal tools, and who understands them
3. AI tools and data risk — whether client or sensitive data is going through consumer AI tools
4. Data and file organisation — whether the business has a coherent structure anyone could navigate
5. What data the business holds — categories and sensitivity
6. GDPR and compliance basics — privacy policy, consent, ability to respond to data requests
7. Security basics — device protection, access controls, backups
8. Technology horizon scanning — deliberate or reactive
9. Current challenges — biggest concern or gap

CRITICAL RULES
1. Ask ONE warm, direct opening question.
2. Wait for the answer before asking the next.
3. Be matter-of-fact about risks — the goal is honest mapping, not a compliance lecture.
4. Keep language plain throughout.
5. On AI data risk — raise it as a question worth thinking about, not an accusation.

Opening question: "Beyond the tools you use to actually deliver your work — what are the main systems that run the business day to day? Things like finance software, file storage, email, CRM, or anything else the business genuinely depends on."`;
  }

  function buildFollowupPrompt() {
    if (isFutureMode) {
      return `You are GuruOne helping a business leader improve their IT and data posture. You have asked ${questionCount.current} questions.

Continue covering any areas not yet addressed:
1. Compliance and security priorities — quick wins first, then structural changes
2. Bespoke solution remediation — document, replace, or consciously accept
3. Data and file organisation improvements
4. Technology opportunities — push toward active experimentation, not just awareness

For any significant investment, flag as Decision Guru candidate.

After all areas covered, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn.`;
    }
    return `You are GuruOne mapping a business leader's IT and data posture. You have asked ${questionCount.current} questions.${directionContextBlock}

IMPORTANT: This is about the CURRENT STATE. Use plain language — no unexplained technical jargon.

THEMES TO COVER
1. Tech estate — main systems beyond delivery tools
2. Bespoke solutions — spreadsheets, automations, informal tools, key-person dependency
3. AI tools and data risk — client or sensitive data going through consumer AI tools
4. Data and file organisation — coherent structure or individual systems
5. Data categories held — types and sensitivity
6. GDPR and compliance — privacy policy, consent, data request readiness
7. Security basics — device protection, access controls, backups
8. Horizon scanning — deliberate or reactive
9. Current challenges

THINGS WORTH PROBING DEEPER ON
- Bespoke solutions without naming who maintains them — push for key-person dependency
- "AI tools" used without considering what data goes through them — raise gently
- "Everyone knows where things are" — probe whether a new person could find things
- "Automatic" backups — where, how often, ever tested?
- "We keep an eye on things" — what does that actually look like?

COVERAGE CHECK — note which themes are addressed. Ask about uncovered themes before probing depth.

When every theme has a substantive answer, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn. 2–4 sentences plus question.`;
  }

  function buildExtractPrompt() {
    if (isFutureMode) {
      return `Based on this conversation, extract the agreed future IT and data plan.

Return ONLY valid JSON starting with {:
{
  "compliance_security_priorities": "what needs to be addressed first — sequenced from quick wins to structural changes",
  "bespoke_remediation": "informal tools to document, replace, or consciously accept — with rationale",
  "data_organisation_improvements": "what needs to change about how data and files are managed",
  "technology_opportunities": "specific tools or capabilities to explore that could create competitive advantage"
}
Omit any field not clearly evidenced by the conversation.`;
    }
    return `Based on this conversation, extract the IT and data posture of the business.

Return ONLY valid JSON starting with {:
{
  "tech_estate": "main systems beyond delivery tools — finance, HR, communication, file storage, CRM",
  "bespoke_solutions": "spreadsheets, automations, or informal tools the business depends on and who understands them. Omit if none.",
  "ai_data_risk": "one of exactly: 'Aware and managed' | 'Not yet considered' | 'No AI tools in use'",
  "file_organisation": "one of exactly: 'Consistent structure — anyone could find things' | 'Partially organised' | 'Each person has their own system'",
  "data_categories": array of zero or more of exactly: ["Customer contact data", "Payment or financial data", "Employee records", "Commercially sensitive client data", "Health or special category data", "Operational data"],
  "gdpr_posture": "one of exactly: 'Current privacy policy and consent in place' | 'Partially addressed' | 'Not yet addressed'",
  "security_basics": "one of exactly: 'Strong controls — devices protected, access managed' | 'Basic controls in place' | 'Limited controls'",
  "backup_recovery": "one of exactly: 'Tested and reliable' | 'In place but untested' | 'Not in place'",
  "horizon_scanning": "one of exactly: 'Active — deliberate scanning and experimentation' | 'Occasional' | 'Reactive only'",
  "it_challenges": "biggest concern around data, technology, or security in the business leader's own words"
}
Omit any field not clearly evidenced by the conversation.`;
  }

  // ── Conversation lifecycle ──────────────────────────────────────
  async function startConversation(future) {
    setIsFutureMode(future);
    setMessages([]);
    setInput('');
    setEditedData(null);
    questionCount.current = 0;
    setLoading(true);
    setPhase('chatting');
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: [{ role: 'user', content: future ? 'Start the future state IT conversation.' : 'Start the IT and data security conversation.' }],
        context: buildOpeningPrompt(),
      });
      setMessages([{ role: 'assistant', content: res.data.reply }]);
      questionCount.current = 1;
    } catch {
      setMessages([{ role: 'assistant', content: future
        ? "Let's think about where your IT and data posture needs to get to. What's the priority — reducing exposure, getting organised, replacing informal solutions, or using technology as a competitive lever?"
        : "Beyond the tools you use to actually deliver your work — what are the main systems that run the business day to day? Things like finance software, file storage, email, CRM, or anything else the business genuinely depends on." }]);
      questionCount.current = 1;
    }
    setLoading(false);
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      questionCount.current += 1;
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: newMessages,
        context: buildFollowupPrompt() + DIALOGUE_RULES_BLOCK,
      });
      const reply = res.data.reply || '';
      const isReady = reply.includes('READY_TO_SUMMARISE');

      const deferredQuestion = isReady ? maybeDeferSummariseForPendingQuestion(reply, questionCount.current, 11) : null;

      if (deferredQuestion) {

        setMessages(prev => [...prev, { role: 'assistant', content: deferredQuestion }]);

      } else if (isReady || questionCount.current >= 11) {
        const clean = reply.replace(/READY_TO_SUMMARISE/g, '').trim();
        const showRoutingButton = clean.includes('Decision Guru');
        if (clean) setMessages(prev => [...prev, { role: 'assistant', content: clean, showDecisionGuruButton: showRoutingButton }]);
        const convo = clean ? [...newMessages, { role: 'assistant', content: clean }] : newMessages;
        await extractData(convo);
      } else {
        const showRoutingButton = reply.includes('Decision Guru');
        setMessages(prev => [...prev, { role: 'assistant', content: reply, showDecisionGuruButton: showRoutingButton }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong — please try again.' }]);
    }
    setLoading(false);
  }

  async function extractData(msgs) {
    setMessages(prev => [...prev, { role: 'assistant', content: "Good — I have enough to work with. Let me synthesise what you've shared. You'll review and edit everything before saving." }]);
    setPhase('extracting');
    const founderAnswers = msgs.filter(m => m.role === 'user').map((m, i) => `Answer ${i + 1}: ${m.content}`).join('\n\n');
    const condensed = [{ role: 'user', content: `Here are the business leader's answers:\n\n${founderAnswers}\n\nExtract the structured data now.` }];
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, { messages: condensed, context: buildExtractPrompt() });
      const raw = res.data.reply || '';
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1 && e !== -1) {
        const parsed = JSON.parse(raw.slice(s, e + 1));
        setEditedData(parsed);
        setPhase('reviewing');
        return;
      }
    } catch { /* fall through */ }
    setPhase('intro');
    setMessages(prev => [...prev, { role: 'assistant', content: "I wasn't able to extract automatically this time. Use Quick entry to fill in the fields directly." }]);
  }

  function handleSave() {
    if (!editedData) return;
    if (isFutureMode) {
      onFutureChange({ ...futureData, ...editedData });
      setPhase('saved');
      return;
    }
    const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
    evalFlow.runEvaluation({ fields: editedData, transcript });
  }

  // ── Render ───────────────────────────────────────────────────────
  const colour = IT_DIALOGUE_COLOUR;
  const bg = IT_DIALOGUE_BG;
  const border = IT_DIALOGUE_BORDER;

  if (phase === 'intro') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1.25rem 1.5rem', marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.75rem' }}>
          IT &amp; data security dialogue
        </div>
        <p style={{ fontSize: '0.82rem', color: '#333', lineHeight: 1.6, margin: '0 0 1rem' }}>
          We&apos;ll map the technology you actually run on, where informal solutions have filled gaps, how data and files are organised, your basic compliance posture, and how actively you&apos;re looking at technology as a competitive lever. Plain language throughout — no jargon required.
        </p>
        <div style={{ fontSize: '0.68rem', color: '#888', lineHeight: 1.5, marginBottom: '1.25rem', borderTop: `1px solid ${border}`, paddingTop: '0.75rem' }}>
          <strong>How it works:</strong> GuruOne asks one question at a time and probes for what actually happens. At the end you review and edit a structured summary before saving. Typically 8–11 exchanges.
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button onClick={() => startConversation(false)}
            style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Explore current state →
          </button>
          {hasFutureState && (
            <button onClick={() => startConversation(true)}
              style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: 'white', color: colour, border: `1.5px solid ${colour}`, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Review future state →
            </button>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'extracting') {
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {messages.slice(-2).map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '0.6rem 0.9rem', borderRadius: 10, background: m.role === 'user' ? colour : bg, color: m.role === 'user' ? 'white' : '#115E59', fontSize: '0.8rem', lineHeight: 1.5 }}>{m.content}</div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: colour, fontSize: '0.8rem' }}>
          <div style={{ width: 14, height: 14, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          Synthesising your answers…
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      </div>
    );
  }

  if (phase === 'reviewing' && editedData) {
    const fieldDefs = isFutureMode ? [
      { key: 'compliance_security_priorities', label: 'Compliance and security priorities' },
      { key: 'bespoke_remediation', label: 'Bespoke solution remediation' },
      { key: 'data_organisation_improvements', label: 'Data and file organisation improvements' },
      { key: 'technology_opportunities', label: 'Technology opportunities to explore' },
    ] : [
      { key: 'tech_estate', label: 'Main systems and tools' },
      { key: 'bespoke_solutions', label: 'Bespoke solutions and workarounds' },
      { key: 'ai_data_risk', label: 'AI tool data risk' },
      { key: 'file_organisation', label: 'Data and file organisation' },
      { key: 'data_categories', label: 'Data the business holds' },
      { key: 'gdpr_posture', label: 'GDPR and compliance posture' },
      { key: 'security_basics', label: 'Security basics' },
      { key: 'backup_recovery', label: 'Backup and recovery' },
      { key: 'horizon_scanning', label: 'Technology horizon scanning' },
      { key: 'it_challenges', label: 'Current challenges' },
    ];
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <DialogueEvalModal
          sectionMeta={sectionMeta}
          evalFlow={evalFlow}
          onCommitWithEval={(evaluation) => {
            const payload = { ...currentData, ...editedData };
            if (evaluation) payload._evaluation = evaluation;
            onChange(payload);
            setPhase('saved');
          }}
          onRefine={() => setPhase('chatting')}
        />
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colour, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Review &amp; edit — {isFutureMode ? 'future state' : 'current state'}
        </div>
        <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: '0.85rem', lineHeight: 1.5 }}>
          AI-drafted from your conversation — edit any field until it precisely matches reality before saving.
        </div>
        {fieldDefs.map(({ key, label }) => {
          const rawValue = editedData[key];
          const chipOpts = IT_CHIP_OPTIONS[key];
          const isMulti = IT_MULTI_FIELDS.has(key);
          if (chipOpts && isMulti) {
            const selected = Array.isArray(rawValue) ? rawValue : [];
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>{label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {chipOpts.map(opt => {
                    const active = selected.includes(opt);
                    return (
                      <button key={opt} onClick={() => setEditedData(p => {
                        const cur = Array.isArray(p[key]) ? p[key] : [];
                        const next = active ? cur.filter(v => v !== opt) : [...cur, opt];
                        return { ...p, [key]: next };
                      })}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? bg : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          if (chipOpts) {
            const value = rawValue || '';
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>{label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {chipOpts.map(opt => {
                    const active = value === opt;
                    return (
                      <button key={opt} onClick={() => setEditedData(p => ({ ...p, [key]: active ? '' : opt }))}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? bg : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          const value = rawValue || '';
          return (
            <div key={key} style={{ marginBottom: '0.85rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
              <textarea value={value} onChange={e => setEditedData(p => ({ ...p, [key]: e.target.value }))} rows={3}
                style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: `1px solid ${border}`, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55 }} />
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.6rem 1.25rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save
          </button>
          <button onClick={() => setPhase('chatting')}
            style={{ padding: '0.6rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${border}`, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Continue conversation
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'saved') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>
            {isFutureMode ? 'Future state captured' : 'Current state captured'}
          </div>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#444', margin: '0 0 0.85rem', lineHeight: 1.55 }}>
          Your answers are saved. {isFutureMode
            ? 'Switch to the Future State tab to see them, or edit any field there in Quick mode.'
            : 'You can edit any field in Quick mode below, or run another conversation to refine.'}
        </p>
        <button onClick={() => setPhase('intro')}
          style={{ background: 'none', border: 'none', color: colour, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', padding: '0.5rem 0', width: 'auto' }}>
          Run another conversation →
        </button>
      </div>
    );
  }

  // Chatting phase
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.5rem' }}>
        {isFutureMode ? 'Future state — design / review' : 'Current state — explore'}
      </div>
      <div ref={chatContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '0.85rem', maxHeight: 320, overflowY: 'auto', paddingRight: '0.2rem' }}>
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          const showDgButton = !isUser && m.showDecisionGuruButton === true;
          return (
            <div key={i} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '88%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '0.6rem 0.85rem', borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px', background: isUser ? colour : bg, color: isUser ? 'white' : '#115E59', fontSize: '0.82rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                {m.content}
              </div>
              {showDgButton && (
                <button onClick={openDecisionGuruInline}
                  style={{ marginTop: '0.5rem', padding: '0.4rem 1rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto', alignSelf: 'flex-start' }}>
                  Take to Decision Guru →
                </button>
              )}
            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: colour, fontStyle: 'italic' }}>
            <div style={{ width: 11, height: 11, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            GuruOne is thinking…
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type your answer… (Enter to send)"
          disabled={loading} rows={2}
          style={{ flex: 1, fontSize: '0.82rem', padding: '0.6rem 0.8rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          style={{ padding: '0.6rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
          Send
        </button>
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

// ── GovernanceDialogue ────────────────────────────────────────────
// Standalone dialogue for the governance_controls section.
// Renamed in this iteration: "Governance & Controls" → "Governance &
// Review Rhythm". Same phase machine as the other dialogues.

const GOVERNANCE_DIALOGUE_COLOUR = '#4F46E5';
const GOVERNANCE_DIALOGUE_BG = '#EEF2FF';
const GOVERNANCE_DIALOGUE_BORDER = '#A5B4FC';

const GOVERNANCE_CHIP_OPTIONS = {
  meeting_quality: ['Structured agenda — decisions and actions focused', 'Mix of updates and decisions', 'Mostly updates', 'Ad hoc'],
  action_capture: ['Captured and reviewed every meeting', 'Captured but not always reviewed', 'Not formally captured'],
  financial_review_frequency: ['Weekly', 'Monthly', 'Quarterly', 'Ad hoc'],
  decision_making: ['Formal leadership forum', 'Informal group', 'Founder only'],
  external_oversight: ['Active board or advisor — genuinely challenging', 'Available but largely passive', 'None'],
};

function GovernanceDialogue({ currentData, futureData, onChange, onFutureChange, hasFutureState, sectionKey }) {
  const [isFutureMode, setIsFutureMode] = useState(false);
  const [phase, setPhase] = useState('intro');
  useEffect(() => {
    if (phase === 'extracting') track('design_conversation_completed', { section: sectionKey });
  }, [phase, sectionKey]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const chatContainerRef = useRef(null);
  const questionCount = useRef(0);
  // Cross-card context — Direction snapshot. Injected into opening
  // and followup prompts so this dialogue doesn't re-ask the leader
  // anything already captured upstream (stage, revenue model,
  // customer segments, value proposition, etc.).
  const directionContextBlock = buildDirectionContextBlock(readDirectionCanvas());

  // Evaluation flow — kicks off /evaluate-section after the leader
  // confirms the extracted data. The DialogueEvalModal wrapper at
  // the top of the reviewing-phase return handles the modal lifecycle.
  const sectionMeta = OM_SECTIONS.find(s => s.key === sectionKey);
  const evalFlow = useEvaluationFlow({
    sectionKey,
    sectionTitle: sectionMeta?.title || sectionKey,
    sectionFields: sectionMeta?.current_fields || [],
    repeatable: false,
  });

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  function openDecisionGuruInline() {
    window.open('/decision', '_blank', 'noopener');
  }

  // ── Prompt builders ─────────────────────────────────────────────
  function buildOpeningPrompt() {
    if (isFutureMode) {
      const hasFutureData = futureData && Object.values(futureData).some(v => v);
      if (hasFutureData) {
        return `You are GuruOne reviewing an AI-generated future governance and review rhythm with the business leader.

AI-GENERATED FUTURE STATE:
Target meeting rhythm: ${futureData?.target_meeting_rhythm || '(none yet)'}
Discipline improvements: ${futureData?.discipline_improvements || '(none yet)'}
External oversight changes: ${futureData?.external_oversight_changes || '(none yet)'}

Walk through each element in order. For each: present what was suggested, ask whether it matches their thinking.

Probe specifically:
- Whether proposed meeting changes add structure or just add meetings — structure almost always has more impact
- Whether discipline improvements are specific enough to actually change behaviour
- Whether the external oversight proposal reflects what the business leader actually needs — sounding board, expertise, or accountability
- For any significant governance change involving equity or investors, flag as a Decision Guru candidate

After all elements reviewed, emit READY_TO_SUMMARISE on its own line.

Open with: "I've put together a suggested future rhythm for how the business runs itself. Let me walk you through it — starting with the meeting structure: ${futureData?.target_meeting_rhythm || '(none generated yet)'}. Does that feel like the right direction?"`;
      }
      return `You are GuruOne helping a business leader improve how their business runs itself.

CURRENT STATE CONTEXT:
Meeting cadence: ${currentData?.meeting_cadence || 'not captured'}
Meeting quality: ${currentData?.meeting_quality || 'not captured'}
Action capture: ${currentData?.action_capture || 'not captured'}
Financial review frequency: ${currentData?.financial_review_frequency || 'not captured'}
Decision-making: ${currentData?.decision_making || 'not captured'}
External oversight: ${currentData?.external_oversight || 'not captured'}

Cover three areas in order:
1. Target meeting rhythm — what needs to meet, how often, and critically what the focus of each forum should be. Push the distinction between meetings that are updates and meetings that make decisions and create accountability.
2. Discipline improvements — the specific habits or structures that need to change. This might be as simple as a standing agenda, a shared action log, or a monthly financial review date that doesn't move. Practical and specific.
3. External oversight — what external challenge or accountability the business needs as it grows. Surface three options if relevant: advisory board (structured, defined remit), fractional or non-executive appointment (specific expertise with accountability), or peer network (less formal but still creates challenge).

For any significant governance change involving equity or investor relations, flag as a Decision Guru candidate.

If the phrase "Decision Guru" appears in any of your messages, the UI will offer a routing button — use it intentionally.

After all three areas covered, emit READY_TO_SUMMARISE on its own line.

Open with: "Let's think about how the business needs to run itself going forward. Given what you've described, what's the priority — is it getting more structure into how meetings work, making financial review more regular, improving how decisions get made and followed through, or getting proper external challenge in place?"`;
    }

    return `You are GuruOne, a strategic advisor helping a business leader map how their business runs itself.${directionContextBlock}

This is about the CURRENT STATE — the actual discipline and rhythm of the business today. Be specific and grounded. Push back on aspirational descriptions and ask what actually happens.

OBJECTIVE
To map whether the right conversations are happening at the right frequency, whether meetings are focused on decisions and actions rather than just updates, whether commitments are captured and followed through, and whether there is enough external perspective to challenge the business leader's thinking.

WHY THIS MATTERS
The difference between a well-run and a poorly-run business of the same size is often not strategy — it's discipline. Whether actions from last week's meeting get reviewed this week. Whether the financial picture is looked at regularly enough to act on it. Whether a leadership conversation ends with clear next steps or just a sense that something was discussed. These habits compound.

WHAT A GOOD ANSWER LOOKS LIKE
A clear picture of what meetings actually happen and how they run — including whether they have a purpose, a defined focus, and a follow-through mechanism. Financial performance reviewed regularly enough to inform decisions. At least one external perspective that genuinely challenges the business leader's thinking rather than just affirming it.

SURFACE-LEVEL ANSWERS TO PROBE DEEPER ON
- Meetings that exist but drift — push for whether there's an agenda, whether actions get captured, whether the same topics recur without resolution
- "We're quite informal" — informality is fine; the question is whether commitments still get made and followed through
- Meetings described as mostly updates — a briefing is not a management meeting; probe whether decisions and actions come out of it
- "We trust each other to follow through" — push for what happens when something falls through the gap
- Advisor described as available — passive availability is not external challenge; when did they last change how the business leader was thinking?

THEMES TO COVER (in order)
1. What actually meets — the meetings that reliably happen, their frequency and who attends
2. How meetings run — agenda or not, updates versus decisions and actions, whether the conversation has structure and output
3. Action capture and follow-through — whether commitments get recorded and whether they come back up at the next meeting
4. Financial review — how often, and whether it's timely enough to act on
5. How significant decisions get made — structured forum, informal group, or business leader alone
6. External perspective — board, advisors, or peers who genuinely challenge thinking
7. Current challenges — the biggest gap in how the business runs itself

CRITICAL RULES
1. Ask ONE warm, direct opening question.
2. Wait for the answer before asking the next.
3. If an answer is vague, probe for what actually happens — not what is intended.
4. Keep responses to 2–3 sentences of framing then the question.
5. Don't make the business leader feel judged for informality — the goal is honest description, not a compliance audit.

Opening question: "Walk me through what meetings reliably happen in the business — not what you think should happen, but what actually occurs week to week or month to month. Who's in the room and what's each one for?"`;
  }

  function buildFollowupPrompt() {
    if (isFutureMode) {
      return `You are GuruOne helping a business leader improve how their business runs itself. You have asked ${questionCount.current} questions.${directionContextBlock}

Continue covering any areas not yet addressed:
1. Target meeting rhythm — what meets, how often, decisions and actions focused
2. Discipline improvements — specific, practical habits or structures
3. External oversight — advisory board, fractional appointment, or peer network

For any significant governance change involving equity or investors, flag as Decision Guru candidate.

After all areas covered, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn.`;
    }
    return `You are GuruOne mapping how a business runs itself. You have asked ${questionCount.current} questions.

IMPORTANT: This is about the CURRENT STATE — what actually happens, not what is intended.

THEMES TO COVER
1. What actually meets — frequency, attendance, purpose
2. How meetings run — agenda, updates versus decisions and actions, structure and output
3. Action capture and follow-through — recorded and reviewed, or trust-based
4. Financial review — frequency and whether timely enough to act on
5. How significant decisions get made — forum, group, or business leader alone
6. External perspective — genuine challenge or passive availability
7. Current challenges

THINGS WORTH PROBING DEEPER ON
- Meetings that happen but drift — same topics recurring without resolution
- "Mostly updates" — push for whether decisions and actions come out of it
- "We trust each other" — what happens when something falls through the gap
- Advisor described as available — when did they last genuinely change how the business leader was thinking?
- "Ad hoc" financial review — probe what triggers it and whether it's often too late to act

COVERAGE CHECK — note which themes have been addressed. Ask about uncovered themes before probing depth.

When every theme has a substantive answer, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn. 2–4 sentences plus question.`;
  }

  function buildExtractPrompt() {
    if (isFutureMode) {
      return `Based on this conversation, extract the agreed future governance and review rhythm.

Return ONLY valid JSON starting with {:
{
  "target_meeting_rhythm": "what needs to meet, how often, and what each forum should focus on",
  "discipline_improvements": "specific practical habits or structures that need to change — action logs, standing agendas, fixed review dates",
  "external_oversight_changes": "what external challenge or accountability the business needs as it grows — advisory board, fractional appointment, or peer network"
}
Omit any field not clearly evidenced by the conversation.`;
    }
    return `Based on this conversation, extract how the business currently runs itself.

Return ONLY valid JSON starting with {:
{
  "meeting_cadence": "what meetings happen, how often, who attends, and what each is for",
  "meeting_quality": "one of exactly: 'Structured agenda — decisions and actions focused' | 'Mix of updates and decisions' | 'Mostly updates' | 'Ad hoc'",
  "action_capture": "one of exactly: 'Captured and reviewed every meeting' | 'Captured but not always reviewed' | 'Not formally captured'",
  "financial_review_frequency": "one of exactly: 'Weekly' | 'Monthly' | 'Quarterly' | 'Ad hoc'",
  "decision_making": "one of exactly: 'Formal leadership forum' | 'Informal group' | 'Founder only'",
  "external_oversight": "one of exactly: 'Active board or advisor — genuinely challenging' | 'Available but largely passive' | 'None'",
  "governance_challenges": "biggest gap in how the business runs itself, in the business leader's own words"
}
Omit any field not clearly evidenced by the conversation.`;
  }

  // ── Conversation lifecycle ──────────────────────────────────────
  async function startConversation(future) {
    setIsFutureMode(future);
    setMessages([]);
    setInput('');
    setEditedData(null);
    questionCount.current = 0;
    setLoading(true);
    setPhase('chatting');
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: [{ role: 'user', content: future ? 'Start the future state governance conversation.' : 'Start the governance and review rhythm conversation.' }],
        context: buildOpeningPrompt(),
      });
      setMessages([{ role: 'assistant', content: res.data.reply }]);
      questionCount.current = 1;
    } catch {
      setMessages([{ role: 'assistant', content: future
        ? "Let's think about how the business needs to run itself going forward. What's the priority — meeting structure, financial review, decision-making, or external challenge?"
        : "Walk me through what meetings reliably happen in the business — not what you think should happen, but what actually occurs week to week or month to month. Who's in the room and what's each one for?" }]);
      questionCount.current = 1;
    }
    setLoading(false);
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      questionCount.current += 1;
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: newMessages,
        context: buildFollowupPrompt() + DIALOGUE_RULES_BLOCK,
      });
      const reply = res.data.reply || '';
      const isReady = reply.includes('READY_TO_SUMMARISE');

      const deferredQuestion = isReady ? maybeDeferSummariseForPendingQuestion(reply, questionCount.current, 9) : null;

      if (deferredQuestion) {

        setMessages(prev => [...prev, { role: 'assistant', content: deferredQuestion }]);

      } else if (isReady || questionCount.current >= 9) {
        const clean = reply.replace(/READY_TO_SUMMARISE/g, '').trim();
        const showRoutingButton = clean.includes('Decision Guru');
        if (clean) setMessages(prev => [...prev, { role: 'assistant', content: clean, showDecisionGuruButton: showRoutingButton }]);
        const convo = clean ? [...newMessages, { role: 'assistant', content: clean }] : newMessages;
        await extractData(convo);
      } else {
        const showRoutingButton = reply.includes('Decision Guru');
        setMessages(prev => [...prev, { role: 'assistant', content: reply, showDecisionGuruButton: showRoutingButton }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong — please try again.' }]);
    }
    setLoading(false);
  }

  async function extractData(msgs) {
    setMessages(prev => [...prev, { role: 'assistant', content: "Good — I have enough to work with. Let me synthesise what you've shared. You'll review and edit everything before saving." }]);
    setPhase('extracting');
    const founderAnswers = msgs.filter(m => m.role === 'user').map((m, i) => `Answer ${i + 1}: ${m.content}`).join('\n\n');
    const condensed = [{ role: 'user', content: `Here are the business leader's answers:\n\n${founderAnswers}\n\nExtract the structured data now.` }];
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, { messages: condensed, context: buildExtractPrompt() });
      const raw = res.data.reply || '';
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1 && e !== -1) {
        const parsed = JSON.parse(raw.slice(s, e + 1));
        setEditedData(parsed);
        setPhase('reviewing');
        return;
      }
    } catch { /* fall through */ }
    setPhase('intro');
    setMessages(prev => [...prev, { role: 'assistant', content: "I wasn't able to extract automatically this time. Use Quick entry to fill in the fields directly." }]);
  }

  function handleSave() {
    if (!editedData) return;
    if (isFutureMode) {
      onFutureChange({ ...futureData, ...editedData });
      setPhase('saved');
      return;
    }
    const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
    evalFlow.runEvaluation({ fields: editedData, transcript });
  }

  // ── Render ───────────────────────────────────────────────────────
  const colour = GOVERNANCE_DIALOGUE_COLOUR;
  const bg = GOVERNANCE_DIALOGUE_BG;
  const border = GOVERNANCE_DIALOGUE_BORDER;

  if (phase === 'intro') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1.25rem 1.5rem', marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.75rem' }}>
          Governance &amp; review rhythm dialogue
        </div>
        <p style={{ fontSize: '0.82rem', color: '#333', lineHeight: 1.6, margin: '0 0 1rem' }}>
          We&apos;ll map how the business runs itself today — what meetings actually happen, how decisions get made, whether commitments get followed through, and whether there&apos;s enough external challenge to your thinking. Honest answers preferred over aspirational ones.
        </p>
        <div style={{ fontSize: '0.68rem', color: '#888', lineHeight: 1.5, marginBottom: '1.25rem', borderTop: `1px solid ${border}`, paddingTop: '0.75rem' }}>
          <strong>How it works:</strong> GuruOne asks one question at a time and probes for what actually happens. At the end you review and edit a structured summary before saving. Typically 6–9 exchanges.
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button onClick={() => startConversation(false)}
            style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Explore current state →
          </button>
          {hasFutureState && (
            <button onClick={() => startConversation(true)}
              style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: 'white', color: colour, border: `1.5px solid ${colour}`, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Review future state →
            </button>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'extracting') {
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {messages.slice(-2).map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '0.6rem 0.9rem', borderRadius: 10, background: m.role === 'user' ? colour : bg, color: m.role === 'user' ? 'white' : '#312E81', fontSize: '0.8rem', lineHeight: 1.5 }}>{m.content}</div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: colour, fontSize: '0.8rem' }}>
          <div style={{ width: 14, height: 14, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          Synthesising your answers…
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      </div>
    );
  }

  if (phase === 'reviewing' && editedData) {
    const fieldDefs = isFutureMode ? [
      { key: 'target_meeting_rhythm', label: 'Target meeting rhythm' },
      { key: 'discipline_improvements', label: 'Discipline improvements' },
      { key: 'external_oversight_changes', label: 'External oversight changes' },
    ] : [
      { key: 'meeting_cadence', label: 'What meets and how often' },
      { key: 'meeting_quality', label: 'How meetings run' },
      { key: 'action_capture', label: 'Action capture and follow-through' },
      { key: 'financial_review_frequency', label: 'Financial review frequency' },
      { key: 'decision_making', label: 'How significant decisions get made' },
      { key: 'external_oversight', label: 'External oversight and challenge' },
      { key: 'governance_challenges', label: 'Current challenges' },
    ];
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <DialogueEvalModal
          sectionMeta={sectionMeta}
          evalFlow={evalFlow}
          onCommitWithEval={(evaluation) => {
            const payload = { ...currentData, ...editedData };
            if (evaluation) payload._evaluation = evaluation;
            onChange(payload);
            setPhase('saved');
          }}
          onRefine={() => setPhase('chatting')}
        />
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colour, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Review &amp; edit — {isFutureMode ? 'future state' : 'current state'}
        </div>
        <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: '0.85rem', lineHeight: 1.5 }}>
          AI-drafted from your conversation — edit any field until it precisely matches reality before saving.
        </div>
        {fieldDefs.map(({ key, label }) => {
          const value = editedData[key] || '';
          const chipOpts = GOVERNANCE_CHIP_OPTIONS[key];
          if (chipOpts) {
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>{label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {chipOpts.map(opt => {
                    const active = value === opt;
                    return (
                      <button key={opt} onClick={() => setEditedData(p => ({ ...p, [key]: active ? '' : opt }))}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? bg : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          return (
            <div key={key} style={{ marginBottom: '0.85rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
              <textarea value={value} onChange={e => setEditedData(p => ({ ...p, [key]: e.target.value }))} rows={3}
                style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: `1px solid ${border}`, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55 }} />
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.6rem 1.25rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save
          </button>
          <button onClick={() => setPhase('chatting')}
            style={{ padding: '0.6rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${border}`, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Continue conversation
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'saved') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>
            {isFutureMode ? 'Future state captured' : 'Current state captured'}
          </div>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#444', margin: '0 0 0.85rem', lineHeight: 1.55 }}>
          Your answers are saved. {isFutureMode
            ? 'Switch to the Future State tab to see them, or edit any field there in Quick mode.'
            : 'You can edit any field in Quick mode below, or run another conversation to refine.'}
        </p>
        <button onClick={() => setPhase('intro')}
          style={{ background: 'none', border: 'none', color: colour, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', padding: '0.5rem 0', width: 'auto' }}>
          Run another conversation →
        </button>
      </div>
    );
  }

  // Chatting phase
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.5rem' }}>
        {isFutureMode ? 'Future state — design / review' : 'Current state — explore'}
      </div>
      <div ref={chatContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '0.85rem', maxHeight: 320, overflowY: 'auto', paddingRight: '0.2rem' }}>
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          const showDgButton = !isUser && m.showDecisionGuruButton === true;
          return (
            <div key={i} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '88%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '0.6rem 0.85rem', borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px', background: isUser ? colour : bg, color: isUser ? 'white' : '#312E81', fontSize: '0.82rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                {m.content}
              </div>
              {showDgButton && (
                <button onClick={openDecisionGuruInline}
                  style={{ marginTop: '0.5rem', padding: '0.4rem 1rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto', alignSelf: 'flex-start' }}>
                  Take to Decision Guru →
                </button>
              )}
            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: colour, fontStyle: 'italic' }}>
            <div style={{ width: 11, height: 11, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            GuruOne is thinking…
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type your answer… (Enter to send)"
          disabled={loading} rows={2}
          style={{ flex: 1, fontSize: '0.82rem', padding: '0.6rem 0.8rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          style={{ padding: '0.6rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
          Send
        </button>
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

// ── FinanceDialogue ───────────────────────────────────────────────
// Standalone dialogue for the finance_commercial section.
// Same phase machine as the other dialogues. Plain language enforced
// in the system prompt — no "working capital", "EBITDA", "net margin"
// jargon unless the business leader uses it first.

const FINANCE_DIALOGUE_COLOUR = '#059669';
const FINANCE_DIALOGUE_BG = '#ECFDF5';
const FINANCE_DIALOGUE_BORDER = '#86EFAC';

const FINANCE_CHIP_OPTIONS = {
  revenue_model: ['Recurring retainer', 'Project-based', 'Transactional', 'Mixed'],
  revenue_concentration: ['Well spread', 'Some concentration', 'High concentration — one source >30%'],
  pricing_approach: ['Based on our costs', 'Based on market rates', 'Based on the value we deliver', 'Mixed'],
  cash_position: ['Generally comfortable', 'Manageable', 'Often tight — waiting on invoices'],
  financial_decision_data: ['Regular management accounts', 'Periodic review', 'Mainly experience-based'],
};

function FinanceDialogue({ currentData, futureData, onChange, onFutureChange, hasFutureState, sectionKey }) {
  const [isFutureMode, setIsFutureMode] = useState(false);
  const [phase, setPhase] = useState('intro');
  useEffect(() => {
    if (phase === 'extracting') track('design_conversation_completed', { section: sectionKey });
  }, [phase, sectionKey]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const chatContainerRef = useRef(null);
  const questionCount = useRef(0);
  // Cross-card context — Direction snapshot. Injected into opening
  // and followup prompts so this dialogue doesn't re-ask the leader
  // anything already captured upstream (stage, revenue model,
  // customer segments, value proposition, etc.).
  const directionContextBlock = buildDirectionContextBlock(readDirectionCanvas());

  // Evaluation flow — kicks off /evaluate-section after the leader
  // confirms the extracted data. The DialogueEvalModal wrapper at
  // the top of the reviewing-phase return handles the modal lifecycle.
  const sectionMeta = OM_SECTIONS.find(s => s.key === sectionKey);
  const evalFlow = useEvaluationFlow({
    sectionKey,
    sectionTitle: sectionMeta?.title || sectionKey,
    sectionFields: sectionMeta?.current_fields || [],
    repeatable: false,
  });

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  function openDecisionGuruInline() {
    window.open('/decision', '_blank', 'noopener');
  }

  // ── Prompt builders ─────────────────────────────────────────────
  function buildOpeningPrompt() {
    if (isFutureMode) {
      const hasFutureData = futureData && Object.values(futureData).some(v => v);
      if (hasFutureData) {
        return `You are GuruOne reviewing an AI-generated future commercial model with the business leader.

AI-GENERATED FUTURE STATE:
Commercial model changes: ${futureData?.commercial_model_changes || '(none yet)'}
Ways to keep more from each pound of revenue: ${futureData?.margin_improvements || '(none yet)'}
Income diversification plan: ${futureData?.income_diversification || '(none yet)'}

Walk through each element in order. For each: present what was suggested, ask whether it matches their thinking.

Probe specifically:
- Whether proposed changes address the actual financial constraint or a symptom of it
- Whether margin improvements are realistic given the current cost structure and market position
- Whether income diversification is genuinely strategic or just complexity for its own sake
- For any significant investment or restructure implied, flag as a Decision Guru candidate

After all elements reviewed, emit READY_TO_SUMMARISE on its own line.

Open with: "I've put together a suggested future commercial model. Let me walk you through it — starting with the main changes proposed: ${futureData?.commercial_model_changes || '(none generated yet)'}. Does that match your thinking?"`;
      }
      return `You are GuruOne helping a business leader improve their commercial model.

CURRENT STATE CONTEXT:
Revenue sources: ${currentData?.revenue_streams || 'not captured'}
Revenue model: ${currentData?.revenue_model || 'not captured'}
Revenue concentration: ${currentData?.revenue_concentration || 'not captured'}
Gross margin: ${currentData?.gross_margin ?? 'not captured'}%
Main costs: ${currentData?.main_costs || 'not captured'}
Cash position: ${currentData?.cash_position || 'not captured'}
Financial decision data: ${currentData?.financial_decision_data || 'not captured'}

Cover three areas in order:
1. Commercial model changes — does the way the business makes money need to change, or does the existing model need to work better? Surface the most important shift.
2. Keeping more from each pound of revenue — revenue-side (explored further in Business Development), cost-side (what costs could reduce without damaging delivery), or model-side (moving from one-off to recurring income, for example). Frame these as practical levers.
3. Income diversification — only if concentration risk was identified. Is diversification realistic, and what would it actually take?

Use plain language throughout. Avoid "working capital", "unit economics", "EBITDA". Say "how much the business keeps" not "gross margin". Say "whether cash is available when you need it" not "working capital position".

For any significant commercial model change, flag as a Decision Guru candidate.

If the phrase "Decision Guru" appears in any of your messages, the UI will offer a routing button — use it intentionally.

After all three areas covered, emit READY_TO_SUMMARISE on its own line.

Open with: "Let's think about where the financial model needs to go. Given what you've described, where's the biggest opportunity — is it about keeping more from each pound of revenue, reducing what it costs to run, spreading income across more sources, or changing the type of work you do?"`;
    }

    return `You are GuruOne, a strategic advisor helping a business leader map the financial shape of their business.${directionContextBlock}

This is about the CURRENT STATE — the financial reality today. Be specific and grounded. Push back on vague answers and ask what is actually happening.

OBJECTIVE
To map where revenue comes from, what the business costs to run, how much it keeps from each pound of revenue, and whether cash is generally available when it's needed.

WHY THIS MATTERS
Many business leaders manage by feel rather than by numbers. That makes good decisions harder — you can't optimise what you haven't measured. This conversation maps the financial picture clearly enough to make better commercial choices.

WHAT A GOOD ANSWER LOOKS LIKE
A clear view of where income comes from and roughly how much each source contributes. The main costs named. An honest sense of what the business keeps from each pound of revenue after paying for delivery. A clear picture of whether cash is generally available or constantly chased.

LANGUAGE RULES — CRITICAL
Use plain language throughout. Specifically:
- Never say "working capital" — say "whether cash is available when you need it" or "the cash position day to day"
- Never say "gross margin" without explaining it — say "what's left after paying for the direct cost of delivery" or "what the business keeps from each pound of revenue"
- Never say "net margin" — say "what's left after paying for everything"
- Never say "unit economics" — say "the financial picture for each job or client"
- Never say "EBITDA" or "P&L" — if referencing accounts, say "your regular financial numbers" or "management accounts"
- If a business leader uses these terms themselves, that's fine — reflect their language back

SURFACE-LEVEL ANSWERS TO PROBE DEEPER ON
- "We're doing fine" or "profitable" without specifics — push for what that actually means in numbers
- Cash described as "fine" — push for whether the business generally has cash available or is waiting on invoices
- Revenue described as spread when one source is actually dominant — probe proportions
- Financial decisions described as experience-based — push for what numbers actually inform significant calls

THEMES TO COVER (in order)
1. Where revenue comes from — sources, relative contribution, stability and predictability
2. What the business keeps from each pound of revenue after paying for direct delivery costs
3. The main costs — the three or four largest, and whether they stay fixed or move with revenue
4. Cash day to day — whether money is generally available when needed, or constantly chased
5. How financial decisions get made — what numbers feed significant calls
6. Current challenges — the biggest financial or commercial challenge right now

CRITICAL RULES
1. Ask ONE warm, direct opening question.
2. Wait for the answer before asking the next.
3. Probe for numbers. If a business leader doesn't know their margin, name that explicitly and constructively — it's useful information.
4. Keep language plain throughout. No financial jargon without explanation.
5. This is not an accounting exercise — keep the conversation strategic and practical.

Opening question: "Walk me through how the business makes money — what are the different ways revenue comes in, and roughly how much does each contribute?"`;
  }

  function buildFollowupPrompt() {
    if (isFutureMode) {
      return `You are GuruOne helping a business leader improve their commercial model. You have asked ${questionCount.current} questions.${directionContextBlock}

Continue covering any areas not yet fully addressed:
1. Commercial model changes — does the way the business makes money need to change?
2. Keeping more from each pound of revenue — revenue-side, cost-side, or model-side levers
3. Income diversification — only if concentration risk was identified

Use plain language. No financial jargon. For any significant change, flag as a Decision Guru candidate.

After all areas covered, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn.`;
    }
    return `You are GuruOne mapping the financial shape of a business. You have asked ${questionCount.current} questions.

IMPORTANT: This is about the CURRENT STATE. Use plain language throughout — no financial jargon without explanation.

LANGUAGE RULES
- "What the business keeps from each pound of revenue" not "gross margin"
- "Whether cash is available when you need it" not "working capital"
- "What's left after paying for everything" not "net margin"

THEMES TO COVER
1. Where revenue comes from — sources, contribution, stability
2. What the business keeps after direct delivery costs — push for a number
3. Main costs — the three or four largest, fixed versus variable
4. Cash day to day — available or constantly chased
5. How financial decisions get made — numbers versus feel
6. Current challenges

THINGS WORTH PROBING DEEPER ON
- "We're doing fine" — push for what that means in numbers
- Cash described as "fine" — push for whether it's structurally available or invoice-dependent
- Revenue described as spread — probe whether one source is actually dominant
- No regular financial review — name this as a gap, constructively

COVERAGE CHECK — note which themes have been addressed. Ask about uncovered themes before probing depth.

When every theme has a substantive answer, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn. 2–4 sentences plus question.`;
  }

  function buildExtractPrompt() {
    if (isFutureMode) {
      return `Based on this conversation, extract the agreed future commercial model.

Return ONLY valid JSON starting with {:
{
  "commercial_model_changes": "what needs to change about how the business makes money — specific and actionable",
  "margin_improvements": "how to keep more from each pound of revenue — revenue-side, cost-side, or model changes",
  "income_diversification": "how to spread income across more sources if concentration was identified as a risk. Omit if not discussed."
}
Omit any field not clearly evidenced by the conversation.`;
    }
    return `Based on this conversation, extract the financial and commercial picture of the business.

Return ONLY valid JSON starting with {:
{
  "revenue_streams": "where revenue comes from and approximate relative contribution of each source",
  "revenue_model": "one of exactly: 'Recurring retainer' | 'Project-based' | 'Transactional' | 'Mixed'",
  "revenue_concentration": "one of exactly: 'Well spread' | 'Some concentration' | 'High concentration — one source >30%'",
  "gross_margin": number between 0 and 100 representing estimated percentage kept after direct delivery costs,
  "pricing_approach": "one of exactly: 'Based on our costs' | 'Based on market rates' | 'Based on the value we deliver' | 'Mixed'",
  "main_costs": "the three or four largest costs and whether they are fixed or variable",
  "cash_position": "one of exactly: 'Generally comfortable' | 'Manageable' | 'Often tight — waiting on invoices'",
  "financial_decision_data": "one of exactly: 'Regular management accounts' | 'Periodic review' | 'Mainly experience-based'",
  "finance_challenges": "biggest financial or commercial challenge in the business leader's own words"
}
Omit any field not clearly evidenced by the conversation.`;
  }

  // ── Conversation lifecycle ──────────────────────────────────────
  async function startConversation(future) {
    setIsFutureMode(future);
    setMessages([]);
    setInput('');
    setEditedData(null);
    questionCount.current = 0;
    setLoading(true);
    setPhase('chatting');
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: [{ role: 'user', content: future ? 'Start the future state finance conversation.' : 'Start the finance and commercial conversation.' }],
        context: buildOpeningPrompt(),
      });
      setMessages([{ role: 'assistant', content: res.data.reply }]);
      questionCount.current = 1;
    } catch {
      setMessages([{ role: 'assistant', content: future
        ? "Let's think about where the financial model needs to go. Where's the biggest opportunity — keeping more from each pound, reducing costs, spreading income, or changing the type of work?"
        : "Walk me through how the business makes money — what are the different ways revenue comes in, and roughly how much does each contribute?" }]);
      questionCount.current = 1;
    }
    setLoading(false);
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      questionCount.current += 1;
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: newMessages,
        context: buildFollowupPrompt() + DIALOGUE_RULES_BLOCK,
      });
      const reply = res.data.reply || '';
      const isReady = reply.includes('READY_TO_SUMMARISE');

      const deferredQuestion = isReady ? maybeDeferSummariseForPendingQuestion(reply, questionCount.current, 9) : null;

      if (deferredQuestion) {

        setMessages(prev => [...prev, { role: 'assistant', content: deferredQuestion }]);

      } else if (isReady || questionCount.current >= 9) {
        const clean = reply.replace(/READY_TO_SUMMARISE/g, '').trim();
        const showRoutingButton = clean.includes('Decision Guru');
        if (clean) setMessages(prev => [...prev, { role: 'assistant', content: clean, showDecisionGuruButton: showRoutingButton }]);
        const convo = clean ? [...newMessages, { role: 'assistant', content: clean }] : newMessages;
        await extractData(convo);
      } else {
        const showRoutingButton = reply.includes('Decision Guru');
        setMessages(prev => [...prev, { role: 'assistant', content: reply, showDecisionGuruButton: showRoutingButton }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong — please try again.' }]);
    }
    setLoading(false);
  }

  async function extractData(msgs) {
    setMessages(prev => [...prev, { role: 'assistant', content: "Good — I have enough to work with. Let me synthesise what you've shared. You'll review and edit everything before saving." }]);
    setPhase('extracting');
    const founderAnswers = msgs.filter(m => m.role === 'user').map((m, i) => `Answer ${i + 1}: ${m.content}`).join('\n\n');
    const condensed = [{ role: 'user', content: `Here are the business leader's answers:\n\n${founderAnswers}\n\nExtract the structured data now.` }];
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, { messages: condensed, context: buildExtractPrompt() });
      const raw = res.data.reply || '';
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1 && e !== -1) {
        const parsed = JSON.parse(raw.slice(s, e + 1));
        setEditedData(parsed);
        setPhase('reviewing');
        return;
      }
    } catch { /* fall through */ }
    setPhase('intro');
    setMessages(prev => [...prev, { role: 'assistant', content: "I wasn't able to extract automatically this time. Use Quick entry to fill in the fields directly." }]);
  }

  function handleSave() {
    if (!editedData) return;
    if (isFutureMode) {
      onFutureChange({ ...futureData, ...editedData });
      setPhase('saved');
      return;
    }
    const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
    evalFlow.runEvaluation({ fields: editedData, transcript });
  }

  // ── Render ───────────────────────────────────────────────────────
  const colour = FINANCE_DIALOGUE_COLOUR;
  const bg = FINANCE_DIALOGUE_BG;
  const border = FINANCE_DIALOGUE_BORDER;

  if (phase === 'intro') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1.25rem 1.5rem', marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.75rem' }}>
          Finance &amp; commercial dialogue
        </div>
        <p style={{ fontSize: '0.82rem', color: '#333', lineHeight: 1.6, margin: '0 0 1rem' }}>
          We&apos;ll map the financial shape of the business — where income comes from, what it costs to run, what&apos;s kept from each pound of revenue, and whether cash is generally available when needed. Plain language throughout — no jargon required.
        </p>
        <div style={{ fontSize: '0.68rem', color: '#888', lineHeight: 1.5, marginBottom: '1.25rem', borderTop: `1px solid ${border}`, paddingTop: '0.75rem' }}>
          <strong>How it works:</strong> GuruOne asks one question at a time and probes for specifics. At the end you review and edit a structured summary before saving. Typically 6–9 exchanges.
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button onClick={() => startConversation(false)}
            style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Explore current state →
          </button>
          {hasFutureState && (
            <button onClick={() => startConversation(true)}
              style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: 'white', color: colour, border: `1.5px solid ${colour}`, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Review future state →
            </button>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'extracting') {
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {messages.slice(-2).map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '0.6rem 0.9rem', borderRadius: 10, background: m.role === 'user' ? colour : bg, color: m.role === 'user' ? 'white' : '#065F46', fontSize: '0.8rem', lineHeight: 1.5 }}>{m.content}</div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: colour, fontSize: '0.8rem' }}>
          <div style={{ width: 14, height: 14, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          Synthesising your answers…
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      </div>
    );
  }

  if (phase === 'reviewing' && editedData) {
    const fieldDefs = isFutureMode ? [
      { key: 'commercial_model_changes', label: 'Commercial model changes' },
      { key: 'margin_improvements', label: 'Ways to keep more from each pound of revenue' },
      { key: 'income_diversification', label: 'Income diversification plan' },
    ] : [
      { key: 'revenue_streams', label: 'Where revenue comes from' },
      { key: 'revenue_model', label: 'Revenue model' },
      { key: 'revenue_concentration', label: 'Revenue concentration' },
      { key: 'gross_margin', label: 'Gross margin (% kept after direct delivery costs)' },
      { key: 'pricing_approach', label: 'Pricing approach' },
      { key: 'main_costs', label: 'Main costs' },
      { key: 'cash_position', label: 'Cash position day to day' },
      { key: 'financial_decision_data', label: 'How financial decisions get made' },
      { key: 'finance_challenges', label: 'Current challenges' },
    ];
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <DialogueEvalModal
          sectionMeta={sectionMeta}
          evalFlow={evalFlow}
          onCommitWithEval={(evaluation) => {
            const payload = { ...currentData, ...editedData };
            if (evaluation) payload._evaluation = evaluation;
            onChange(payload);
            setPhase('saved');
          }}
          onRefine={() => setPhase('chatting')}
        />
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colour, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Review &amp; edit — {isFutureMode ? 'future state' : 'current state'}
        </div>
        <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: '0.85rem', lineHeight: 1.5 }}>
          AI-drafted from your conversation — edit any field until it precisely matches reality before saving.
        </div>
        {fieldDefs.map(({ key, label }) => {
          const rawValue = editedData[key];
          const chipOpts = FINANCE_CHIP_OPTIONS[key];
          if (chipOpts) {
            const value = rawValue || '';
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>{label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {chipOpts.map(opt => {
                    const active = value === opt;
                    return (
                      <button key={opt} onClick={() => setEditedData(p => ({ ...p, [key]: active ? '' : opt }))}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? bg : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          if (key === 'gross_margin') {
            const numericValue = typeof rawValue === 'number' ? rawValue : 0;
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input type="range" min="0" max="100" step="5"
                    value={numericValue}
                    onChange={e => setEditedData(p => ({ ...p, [key]: Number(e.target.value) }))}
                    style={{ flex: 1, cursor: 'pointer', accentColor: colour }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                    <input type="number" min="0" max="100"
                      value={numericValue}
                      onChange={e => setEditedData(p => ({ ...p, [key]: Math.min(100, Math.max(0, Number(e.target.value))) }))}
                      style={{ width: 52, fontSize: '0.82rem', padding: '0.3rem 0.4rem', borderRadius: 6, border: `1px solid ${border}`, fontFamily: 'inherit', textAlign: 'center', boxSizing: 'border-box' }} />
                    <span style={{ fontSize: '0.82rem', color: '#555' }}>%</span>
                  </div>
                </div>
              </div>
            );
          }
          const value = rawValue || '';
          return (
            <div key={key} style={{ marginBottom: '0.85rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
              <textarea value={value} onChange={e => setEditedData(p => ({ ...p, [key]: e.target.value }))} rows={3}
                style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: `1px solid ${border}`, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55 }} />
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.6rem 1.25rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save
          </button>
          <button onClick={() => setPhase('chatting')}
            style={{ padding: '0.6rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${border}`, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Continue conversation
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'saved') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>
            {isFutureMode ? 'Future state captured' : 'Current state captured'}
          </div>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#444', margin: '0 0 0.85rem', lineHeight: 1.55 }}>
          Your answers are saved. {isFutureMode
            ? 'Switch to the Future State tab to see them, or edit any field there in Quick mode.'
            : 'You can edit any field in Quick mode below, or run another conversation to refine.'}
        </p>
        <button onClick={() => setPhase('intro')}
          style={{ background: 'none', border: 'none', color: colour, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', padding: '0.5rem 0', width: 'auto' }}>
          Run another conversation →
        </button>
      </div>
    );
  }

  // Chatting phase
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.5rem' }}>
        {isFutureMode ? 'Future state — design / review' : 'Current state — explore'}
      </div>
      <div ref={chatContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '0.85rem', maxHeight: 320, overflowY: 'auto', paddingRight: '0.2rem' }}>
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          const showDgButton = !isUser && m.showDecisionGuruButton === true;
          return (
            <div key={i} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '88%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '0.6rem 0.85rem', borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px', background: isUser ? colour : bg, color: isUser ? 'white' : '#065F46', fontSize: '0.82rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                {m.content}
              </div>
              {showDgButton && (
                <button onClick={openDecisionGuruInline}
                  style={{ marginTop: '0.5rem', padding: '0.4rem 1rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto', alignSelf: 'flex-start' }}>
                  Take to Decision Guru →
                </button>
              )}
            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: colour, fontStyle: 'italic' }}>
            <div style={{ width: 11, height: 11, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            GuruOne is thinking…
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type your answer… (Enter to send)"
          disabled={loading} rows={2}
          style={{ flex: 1, fontSize: '0.82rem', padding: '0.6rem 0.8rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          style={{ padding: '0.6rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
          Send
        </button>
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

// ── ProcessDialogue ───────────────────────────────────────────────
// Standalone dialogue for the process_ways_of_work section.
// Same phase machine as the other dialogues (intro → chatting →
// extracting → reviewing → saved). Handles both current and future
// state from a single mount via isFutureMode.
//
// Decision Guru routing: spec uses an explicit per-message
// showDecisionGuruButton flag (set when the assistant reply contains
// "Decision Guru"), unlike the other dialogues which check the message
// substring at render time. The button itself uses
// window.open('/decision', '_blank', 'noopener') to preserve dialogue
// state — same as the other dialogues.

const PROCESS_DIALOGUE_COLOUR = '#0891B2';
const PROCESS_DIALOGUE_BG = '#E0F2FE';
const PROCESS_DIALOGUE_BORDER = '#7DD3FC';

const PROCESS_CHIP_OPTIONS = {
  documentation_status: ['Fully documented', 'Partially documented', 'Mostly undocumented'],
  step_ownership_clarity: ['Explicit — each step has a named owner', 'Broadly understood but informal', 'Unclear or overlapping'],
  systems_integration: ['Well integrated', 'Partial integration — some manual handoffs', 'Largely manual — systems don\'t talk to each other'],
  skill_judgment_dependency: ['High — most steps require experience', 'Mixed', 'Low — largely procedural'],
  quality_control_method: ['Formal QA process', 'Senior review on every job', 'Spot-check', 'Customer feedback only', 'None'],
};

function ProcessDialogue({ currentData, futureData, onChange, onFutureChange, hasFutureState, sectionKey }) {
  const [isFutureMode, setIsFutureMode] = useState(false);
  const [phase, setPhase] = useState('intro');
  useEffect(() => {
    if (phase === 'extracting') track('design_conversation_completed', { section: sectionKey });
  }, [phase, sectionKey]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const chatContainerRef = useRef(null);
  const questionCount = useRef(0);
  // Cross-card context — Direction snapshot. Injected into opening
  // and followup prompts so this dialogue doesn't re-ask the leader
  // anything already captured upstream (stage, revenue model,
  // customer segments, value proposition, etc.).
  const directionContextBlock = buildDirectionContextBlock(readDirectionCanvas());

  // Evaluation flow — kicks off /evaluate-section after the leader
  // confirms the extracted data. The DialogueEvalModal wrapper at
  // the top of the reviewing-phase return handles the modal lifecycle.
  const sectionMeta = OM_SECTIONS.find(s => s.key === sectionKey);
  const evalFlow = useEvaluationFlow({
    sectionKey,
    sectionTitle: sectionMeta?.title || sectionKey,
    sectionFields: sectionMeta?.current_fields || [],
    repeatable: false,
  });

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  function openDecisionGuruInline() {
    window.open('/decision', '_blank', 'noopener');
  }

  // ── Prompt builders ─────────────────────────────────────────────
  function buildOpeningPrompt() {
    if (isFutureMode) {
      const hasFutureData = futureData && Object.values(futureData).some(v => v);
      if (hasFutureData) {
        return `You are GuruOne reviewing an AI-generated future process and delivery model with the business leader.

AI-GENERATED FUTURE STATE:
Priority process improvements: ${futureData?.priority_process_improvements || '(none yet)'}
Target delivery model: ${futureData?.target_delivery_model || '(none yet)'}
Systems and integration changes: ${futureData?.systems_integration_changes || '(none yet)'}
AI and automation roadmap: ${futureData?.ai_automation_roadmap || '(none yet)'}

Walk through each element in order. For each: present what was suggested, ask whether it matches their thinking.

Probe specifically:
- Whether proposed process improvements address the actual bottleneck or a symptom of it
- Whether the target delivery model is genuinely scalable or a tidier version of what already exists
- Whether the AI roadmap is specific enough to be actionable or still aspirational
- For any significant investment implied, flag as a Decision Guru candidate

After all elements reviewed, emit READY_TO_SUMMARISE on its own line.

Open with: "I've put together a suggested future state for your process and delivery model. Let me walk you through it — starting with the priority improvements: ${futureData?.priority_process_improvements || '(none generated yet)'}. Does that match what you think needs to change most?"`;
      }
      return `You are GuruOne, helping a business leader design their future process and delivery model.

CURRENT STATE CONTEXT:
Delivery process: ${currentData?.delivery_process_description || 'not captured'}
Documentation: ${currentData?.documentation_status || 'not captured'}
Step ownership: ${currentData?.step_ownership_clarity || 'not captured'}
Key systems: ${currentData?.key_delivery_systems || 'not captured'}
Systems integration: ${currentData?.systems_integration || 'not captured'}
Key bottlenecks: ${currentData?.key_bottlenecks || 'not captured'}
Quality control: ${currentData?.quality_control_method || 'not captured'}
AI/automation in use: ${currentData?.ai_automation_in_use || 'not captured'}

Cover three areas in order:
1. Priority process improvements — documentation, ownership clarity, bottleneck removal, quality control changes
2. Target delivery model — what delivery looks like at scale; what must be true for the business to grow without the business leader in every job
3. Systems and AI roadmap — integration changes needed, AI tools under consideration, what could change the economics of delivery

On AI specifically: push beyond vague intent. Ask: "Is there a version of your delivery model in two years that's structurally more efficient because of AI — and if so, what does that require you to do in the next six months?"

For any significant process or systems investment, flag it as a Decision Guru candidate.

If the phrase "Decision Guru" appears in any of your messages, the UI will offer a routing button — use it intentionally.

After all three areas covered, emit READY_TO_SUMMARISE on its own line.

Open with: "Let's think about where your delivery model needs to get to. Given what you've described, what's the most important change — is it about getting things documented and owned clearly, removing a specific bottleneck, improving your systems, or changing how AI fits into how you deliver?"`;
    }

    return `You are GuruOne, a strategic advisor helping a business leader map their process and ways of work.${directionContextBlock}

This is about the CURRENT STATE — how the business actually delivers today. Be specific and grounded. Push back on aspirational or intended answers and ask what is actually happening now.

OBJECTIVE
To map how the business delivers — process repeatability, step ownership, supporting systems, capability gaps, bottlenecks, quality control, and where AI could change the economics of delivery.

WHY THIS MATTERS
Most SMEs have delivery models that work at current scale but weren't designed to scale. Processes that rely on specific individuals, undocumented steps, or heroic effort are fragile. The systems picture compounds this: most small businesses operate with tools that don't integrate, data that moves manually between them, and AI capability they haven't looked at seriously.

WHAT A GOOD ANSWER LOOKS LIKE
A clear description of how delivery actually happens — the key steps, who owns each, and where the handoffs are. The systems that support delivery named and their integration picture understood honestly. Bottlenecks named specifically. A clear-eyed view of where AI is already changing what's possible in this type of work.

SURFACE-LEVEL ANSWERS TO PROBE DEEPER ON
- Processes described as intended rather than actual — press for what actually happens
- Ownership described as shared or collective — push for who specifically
- "We export to a spreadsheet" as the integration story — this is a manual workaround, not an integration
- AI dismissed as not relevant without examining specific steps — follow up: "Where in your delivery does someone do the same thing repeatedly, manually, at volume?"
- Quality control described as "everyone knows the standard" without explaining how it's actually checked

THEMES TO COVER (in order)
1. The delivery sequence — key steps end to end, who owns each, consistency across jobs
2. Documentation and transferability — what's written down versus in people's heads; how long before a new person executes consistently without supervision
3. Systems and integration — tools that touch delivery, how they connect, where manual workarounds exist because systems don't talk to each other
4. Skill and judgment dependency — which steps need genuine experience versus which are procedural and could be systematised
5. Bottlenecks — the specific step, person, or handoff that creates constraint
6. Quality control — the actual check, not the intended one
7. AI and automation — where repetitive manual work exists, what's already being used, what's being considered
8. Current challenges — the single biggest process or delivery challenge right now

CRITICAL RULES
1. Ask ONE warm, direct opening question — specific, not generic.
2. Wait for the answer before asking the next question.
3. If an answer is vague, probe for a concrete picture before moving on.
4. Be concise — 2–3 sentences of framing then the question.
5. Keep all questions grounded in current reality, not aspirations.
6. On AI — do not accept "not applicable" without asking specifically where repetitive manual work exists in the delivery process.

Opening question: "Walk me through how a piece of work gets delivered — from the moment a client says yes to the moment it's done and invoiced. What are the key steps and who owns each one?"`;
  }

  function buildFollowupPrompt() {
    if (isFutureMode) {
      return `You are GuruOne helping a business leader design their future process and delivery model. You have asked ${questionCount.current} questions.

Continue covering any of the three areas not yet fully addressed:
1. Priority process improvements — documentation, ownership, bottleneck removal, quality
2. Target delivery model — what delivery looks like at scale, business leader-independent
3. Systems and AI roadmap — integration changes, AI tools, economics of delivery

On AI: if not yet explored, ask: "Is there a version of your delivery model in two years that's structurally more efficient because of AI — and what does that require you to do now?"

For any significant investment implied, flag as a Decision Guru candidate.

After all three areas covered, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn.`;
    }
    return `You are GuruOne mapping a business leader's process and ways of work. You have asked ${questionCount.current} questions.${directionContextBlock}

IMPORTANT: This is about the CURRENT STATE. Keep all questions grounded in what is actually happening now.

THEMES TO COVER
1. Delivery sequence — steps, ownership, consistency
2. Documentation and transferability — written down versus in heads; new person onboarding time
3. Systems and integration — delivery tools, integration picture, manual workarounds
4. Skill and judgment dependency — experience-dependent versus procedural steps
5. Bottlenecks — specific step, person, or handoff
6. Quality control — the actual check
7. AI and automation — repetitive manual work, tools in use or considered
8. Current challenges

THINGS WORTH PROBING DEEPER ON
- Ownership described as shared — push for who specifically is accountable when something goes wrong
- "We export to spreadsheet" — this is a manual data transfer, not an integration; probe the operational cost of that
- AI dismissed as not applicable — ask where in the process the same action is done repeatedly, manually, at volume
- Bottlenecks attributed to volume or busyness — push for the specific constraint

COVERAGE CHECK — before every response, note which themes have been addressed. Ask about uncovered themes before probing depth.

When every theme has a substantive answer, emit READY_TO_SUMMARISE on its own line.
Rules: one question per turn. Two only when genuinely independent. 2–4 sentences plus question.`;
  }

  function buildExtractPrompt() {
    if (isFutureMode) {
      return `Based on this conversation, extract the agreed future process and delivery model.

Return ONLY valid JSON starting with {:
{
  "priority_process_improvements": "the one or two process changes with highest impact — specific and actionable",
  "target_delivery_model": "what delivery looks like when operating at scale — key changes from current state",
  "systems_integration_changes": "integration improvements or new tools needed to support the delivery model",
  "ai_automation_roadmap": "specific AI or automation changes planned or under active consideration"
}
Omit any field not clearly evidenced by the conversation.`;
    }
    return `Based on this conversation, extract process and ways of work data.

Return ONLY valid JSON starting with {:
{
  "delivery_process_description": "clear description of how delivery actually works end to end — the key steps and who owns each",
  "documentation_status": "one of exactly: 'Fully documented' | 'Partially documented' | 'Mostly undocumented'",
  "step_ownership_clarity": "one of exactly: 'Explicit — each step has a named owner' | 'Broadly understood but informal' | 'Unclear or overlapping'",
  "key_delivery_systems": "the tools that touch the delivery process and how they connect — or don't",
  "systems_integration": "one of exactly: 'Well integrated' | 'Partial integration — some manual handoffs' | 'Largely manual — systems don\\'t talk to each other'",
  "skill_judgment_dependency": "one of exactly: 'High — most steps require experience' | 'Mixed' | 'Low — largely procedural'",
  "key_bottlenecks": "the specific steps, people, or handoffs that create constraint in delivery",
  "quality_control_method": "one of exactly: 'Formal QA process' | 'Senior review on every job' | 'Spot-check' | 'Customer feedback only' | 'None'",
  "ai_automation_in_use": "AI or automation tools already being used in delivery, or specific opportunities identified. Omit if none discussed.",
  "process_challenges": "biggest process or delivery challenge in the business leader's own words"
}
Omit any field not clearly evidenced by the conversation.`;
  }

  // ── Conversation lifecycle ──────────────────────────────────────
  async function startConversation(future) {
    setIsFutureMode(future);
    setMessages([]);
    setInput('');
    setEditedData(null);
    questionCount.current = 0;
    setLoading(true);
    setPhase('chatting');
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: [{ role: 'user', content: future ? 'Start the future state process conversation.' : 'Start the process and ways of work conversation.' }],
        context: buildOpeningPrompt(),
      });
      setMessages([{ role: 'assistant', content: res.data.reply }]);
      questionCount.current = 1;
    } catch {
      setMessages([{ role: 'assistant', content: future
        ? "Let's think about where your delivery model needs to get to. What's the most important change — documentation, ownership, removing a bottleneck, or how AI fits in?"
        : "Walk me through how a piece of work gets delivered — from the moment a client says yes to the moment it's done and invoiced. What are the key steps and who owns each one?" }]);
      questionCount.current = 1;
    }
    setLoading(false);
  }

  async function sendMessage() {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      questionCount.current += 1;
      const res = await axios.post(`${CANVAS_API}/chat`, {
        messages: newMessages,
        context: buildFollowupPrompt() + DIALOGUE_RULES_BLOCK,
      });
      const reply = res.data.reply || '';
      const isReady = reply.includes('READY_TO_SUMMARISE');

      const deferredQuestion = isReady ? maybeDeferSummariseForPendingQuestion(reply, questionCount.current, 10) : null;

      if (deferredQuestion) {

        setMessages(prev => [...prev, { role: 'assistant', content: deferredQuestion }]);

      } else if (isReady || questionCount.current >= 10) {
        const clean = reply.replace(/READY_TO_SUMMARISE/g, '').trim();
        const showRoutingButton = clean.includes('Decision Guru');
        if (clean) setMessages(prev => [...prev, { role: 'assistant', content: clean, showDecisionGuruButton: showRoutingButton }]);
        const convo = clean ? [...newMessages, { role: 'assistant', content: clean }] : newMessages;
        await extractData(convo);
      } else {
        const showRoutingButton = reply.includes('Decision Guru');
        setMessages(prev => [...prev, { role: 'assistant', content: reply, showDecisionGuruButton: showRoutingButton }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong — please try again.' }]);
    }
    setLoading(false);
  }

  async function extractData(msgs) {
    setMessages(prev => [...prev, { role: 'assistant', content: "Good — I have enough to work with. Let me synthesise what you've shared. You'll review and edit everything before saving." }]);
    setPhase('extracting');
    const founderAnswers = msgs.filter(m => m.role === 'user').map((m, i) => `Answer ${i + 1}: ${m.content}`).join('\n\n');
    const condensed = [{ role: 'user', content: `Here are the business leader's answers:\n\n${founderAnswers}\n\nExtract the structured data now.` }];
    try {
      const res = await axios.post(`${CANVAS_API}/chat`, { messages: condensed, context: buildExtractPrompt() });
      const raw = res.data.reply || '';
      const s = raw.indexOf('{'); const e = raw.lastIndexOf('}');
      if (s !== -1 && e !== -1) {
        const parsed = JSON.parse(raw.slice(s, e + 1));
        setEditedData(parsed);
        setPhase('reviewing');
        return;
      }
    } catch { /* fall through */ }
    setPhase('intro');
    setMessages(prev => [...prev, { role: 'assistant', content: "I wasn't able to extract automatically this time. Use Quick entry to fill in the fields directly." }]);
  }

  function handleSave() {
    if (!editedData) return;
    if (isFutureMode) {
      onFutureChange({ ...futureData, ...editedData });
      setPhase('saved');
      return;
    }
    const transcript = { messages, questionCount: questionCount.current, completedAt: new Date().toISOString() };
    evalFlow.runEvaluation({ fields: editedData, transcript });
  }

  // ── Render ───────────────────────────────────────────────────────
  const colour = PROCESS_DIALOGUE_COLOUR;
  const bg = PROCESS_DIALOGUE_BG;
  const border = PROCESS_DIALOGUE_BORDER;

  if (phase === 'intro') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1.25rem 1.5rem', marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.75rem' }}>
          Process &amp; ways of work dialogue
        </div>
        <p style={{ fontSize: '0.82rem', color: '#333', lineHeight: 1.6, margin: '0 0 1rem' }}>
          We&apos;ll map how delivery actually happens — the steps, the owners, the systems that support it, where the bottlenecks really sit, and where AI could meaningfully change the economics. Honest answers preferred over aspirational ones.
        </p>
        <div style={{ fontSize: '0.68rem', color: '#888', lineHeight: 1.5, marginBottom: '1.25rem', borderTop: `1px solid ${border}`, paddingTop: '0.75rem' }}>
          <strong>How it works:</strong> GuruOne asks one question at a time and probes for specifics. At the end you review and edit a structured summary before saving. Typically 7–10 exchanges.
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button onClick={() => startConversation(false)}
            style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Explore current state →
          </button>
          {hasFutureState && (
            <button onClick={() => startConversation(true)}
              style={{ padding: '0.65rem 1.4rem', borderRadius: 8, background: 'white', color: colour, border: `1.5px solid ${colour}`, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Review future state →
            </button>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'extracting') {
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {messages.slice(-2).map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '0.6rem 0.9rem', borderRadius: 10, background: m.role === 'user' ? colour : bg, color: m.role === 'user' ? 'white' : '#0E5E7A', fontSize: '0.8rem', lineHeight: 1.5 }}>{m.content}</div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: colour, fontSize: '0.8rem' }}>
          <div style={{ width: 14, height: 14, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
          Synthesising your answers…
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      </div>
    );
  }

  if (phase === 'reviewing' && editedData) {
    const fieldDefs = isFutureMode ? [
      { key: 'priority_process_improvements', label: 'Priority process improvements' },
      { key: 'target_delivery_model', label: 'Target delivery model' },
      { key: 'systems_integration_changes', label: 'Systems and integration changes' },
      { key: 'ai_automation_roadmap', label: 'AI and automation roadmap' },
    ] : [
      { key: 'delivery_process_description', label: 'Delivery process' },
      { key: 'documentation_status', label: 'Documentation status' },
      { key: 'step_ownership_clarity', label: 'Step ownership' },
      { key: 'key_delivery_systems', label: 'Key delivery systems' },
      { key: 'systems_integration', label: 'Systems integration' },
      { key: 'skill_judgment_dependency', label: 'Skill and judgment dependency' },
      { key: 'key_bottlenecks', label: 'Key bottlenecks' },
      { key: 'quality_control_method', label: 'Quality control' },
      { key: 'ai_automation_in_use', label: 'AI and automation in use' },
      { key: 'process_challenges', label: 'Current challenges' },
    ];
    return (
      <div style={{ marginTop: '1.25rem' }}>
        <DialogueEvalModal
          sectionMeta={sectionMeta}
          evalFlow={evalFlow}
          onCommitWithEval={(evaluation) => {
            const payload = { ...currentData, ...editedData };
            if (evaluation) payload._evaluation = evaluation;
            onChange(payload);
            setPhase('saved');
          }}
          onRefine={() => setPhase('chatting')}
        />
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colour, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Review &amp; edit — {isFutureMode ? 'future state' : 'current state'}
        </div>
        <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: '0.85rem', lineHeight: 1.5 }}>
          AI-drafted from your conversation — edit any field until it precisely matches reality before saving.
        </div>
        {fieldDefs.map(({ key, label }) => {
          const value = editedData[key] || '';
          const chipOpts = PROCESS_CHIP_OPTIONS[key];
          if (chipOpts) {
            return (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>{label}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                  {chipOpts.map(opt => {
                    const active = value === opt;
                    return (
                      <button key={opt} onClick={() => setEditedData(p => ({ ...p, [key]: active ? '' : opt }))}
                        style={{ padding: '0.25rem 0.7rem', borderRadius: 20, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${colour}` : '1.5px solid #e0e0e0', background: active ? bg : 'white', color: active ? colour : '#666', fontWeight: active ? 600 : 400, width: 'auto' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          return (
            <div key={key} style={{ marginBottom: '0.85rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
              <textarea value={value} onChange={e => setEditedData(p => ({ ...p, [key]: e.target.value }))} rows={3}
                style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: `1px solid ${border}`, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55 }} />
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.6rem 1.25rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save
          </button>
          <button onClick={() => setPhase('chatting')}
            style={{ padding: '0.6rem 1rem', borderRadius: 8, background: 'white', color: colour, border: `1px solid ${border}`, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Continue conversation
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'saved') {
    return (
      <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1D9E75', display: 'inline-block' }} />
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>
            {isFutureMode ? 'Future state captured' : 'Current state captured'}
          </div>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#444', margin: '0 0 0.85rem', lineHeight: 1.55 }}>
          Your answers are saved. {isFutureMode
            ? 'Switch to the Future State tab to see them, or edit any field there in Quick mode.'
            : 'You can edit any field in Quick mode below, or run another conversation to refine.'}
        </p>
        <button onClick={() => setPhase('intro')}
          style={{ background: 'none', border: 'none', color: colour, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', padding: '0.5rem 0', width: 'auto' }}>
          Run another conversation →
        </button>
      </div>
    );
  }

  // Chatting phase
  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: colour, marginBottom: '0.5rem' }}>
        {isFutureMode ? 'Future state — design / review' : 'Current state — explore'}
      </div>
      <div ref={chatContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '0.85rem', maxHeight: 320, overflowY: 'auto', paddingRight: '0.2rem' }}>
        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          const showDgButton = !isUser && m.showDecisionGuruButton === true;
          return (
            <div key={i} style={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '88%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '0.6rem 0.85rem', borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px', background: isUser ? colour : bg, color: isUser ? 'white' : '#0E5E7A', fontSize: '0.82rem', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                {m.content}
              </div>
              {showDgButton && (
                <button onClick={openDecisionGuruInline}
                  style={{ marginTop: '0.5rem', padding: '0.4rem 1rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto', alignSelf: 'flex-start' }}>
                  Take to Decision Guru →
                </button>
              )}
            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', color: colour, fontStyle: 'italic' }}>
            <div style={{ width: 11, height: 11, border: `1.5px solid ${border}`, borderTop: `1.5px solid ${colour}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
            GuruOne is thinking…
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type your answer… (Enter to send)"
          disabled={loading} rows={2}
          style={{ flex: 1, fontSize: '0.82rem', padding: '0.6rem 0.8rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
        <button onClick={sendMessage} disabled={loading || !input.trim()}
          style={{ padding: '0.6rem 1rem', borderRadius: 8, background: colour, color: 'white', border: 'none', fontSize: '0.82rem', fontWeight: 600, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
          Send
        </button>
      </div>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

// ── Section detail screen ─────────────────────────────────────────
function SectionDetail({ section, sectionData, onChange, onBack, futureUnlocked, mode }) {
  const [tab, setTab] = useState('current');
  // Apply legacy field migrations at read time so users with old field
  // names see their data surfaced in the new fields.
  const migratedSectionData = section.type === 'location_dialogue'
    ? migrateLocationData(sectionData)
    : section.type === 'suppliers_dialogue'
      ? migrateSuppliersData(sectionData)
      : section.type === 'customer_dialogue'
        ? migrateCustomerData(sectionData)
        : section.type === 'process_dialogue'
          ? migrateProcessData(sectionData)
          : section.type === 'finance_dialogue'
            ? migrateFinanceData(sectionData)
            : section.type === 'org_builder'
              ? migratePeopleRolesData(sectionData)
              : sectionData;
  const current = migratedSectionData?.current || {};
  const future = migratedSectionData?.future || {};

  // Determine effective mode: use prop, fallback to section type default
  const effectiveMode = mode || (section.type === 'culture_dialogue' ? 'explore' : 'quick');

  // Render current-state quick-entry fields (chips + textareas)
  function renderQuickFields(data, onChangeData, fields) {
    return (
      <div>
        {fields.map(f => {
          const value = data[f.key] || '';
          const update = (v) => onChangeData({ ...data, [f.key]: v });
          if (f.type === 'chips') {
            const vals = Array.isArray(value) ? value : (value ? [value] : []);
            return (
              <div key={f.key} style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: f.explainer ? 2 : 6 }}>{f.label}</div>
                {f.explainer && (
                  <div style={{ fontSize: '0.68rem', color: '#888', lineHeight: 1.5, marginBottom: 6, background: '#f7f7f9', borderRadius: 6, padding: '0.4rem 0.6rem', borderLeft: '2px solid #185FA5' }}>
                    {f.explainer}
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {f.options.map(opt => {
                    const active = f.multi ? vals.includes(opt) : value === opt;
                    return (
                      <button key={opt} onClick={() => update(f.multi ? (active ? vals.filter(v => v !== opt) : [...vals, opt]) : (active ? '' : opt))}
                        style={{ padding: '0.3rem 0.75rem', borderRadius: 20, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', border: active ? `1.5px solid ${section.colour}` : '1.5px solid #e0e0e0', background: active ? section.colour + '15' : 'white', color: active ? section.colour : '#555', width: 'auto', fontWeight: active ? 600 : 400, transition: 'all 0.12s' }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }
          if (f.type === 'percentage_slider') return (
            <div key={f.key} style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{f.label}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <input type="range" min="0" max="100" step="5"
                  value={value || 0}
                  onChange={e => update(Number(e.target.value))}
                  style={{ flex: 1, cursor: 'pointer', accentColor: '#1D9E75' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                  <input type="number" min="0" max="100"
                    value={value || 0}
                    onChange={e => update(Math.min(100, Math.max(0, Number(e.target.value))))}
                    style={{ width: 52, fontSize: '0.82rem', padding: '0.3rem 0.4rem', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', textAlign: 'center', boxSizing: 'border-box' }} />
                  <span style={{ fontSize: '0.82rem', color: '#555' }}>%</span>
                </div>
              </div>
            </div>
          );
          if (f.type === 'textarea') return (
            <div key={f.key} style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{f.label}</div>
              <textarea value={value} onChange={e => update(e.target.value)} placeholder={f.placeholder || ''} rows={3}
                style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
          );
          return (
            <div key={f.key} style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{f.label}</div>
              <input type="text" value={value} onChange={e => update(e.target.value)} placeholder={f.placeholder || ''}
                style={{ width: '100%', fontSize: '0.82rem', padding: '0.5rem 0.7rem', borderRadius: 8, border: '1px solid #ddd', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 780, margin: '0 auto' }}>
      <button onClick={onBack}
        style={{ background: 'none', border: 'none', color: '#1D9E75', fontSize: '0.82rem', cursor: 'pointer', width: 'auto', padding: 0, marginBottom: '1.5rem', fontFamily: 'inherit' }}>
        ← Back to Operating Model Canvas
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '1.25rem' }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: section.colour + '18', border: `1.5px solid ${section.colour}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', flexShrink: 0 }}>
          {section.icon}
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#1a1a2e' }}>{section.title}</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>{section.description}</p>
        </div>
      </div>

      {/* Current / Future tab bar — always accessible */}
      <div style={{ display: 'flex', border: '1px solid #f0f0f0', borderRadius: 10, overflow: 'hidden', marginBottom: '1.25rem' }}>
        <button onClick={() => setTab('current')}
          style={{ flex: 1, padding: '0.65rem', fontSize: '0.85rem', fontWeight: 600, border: 'none', background: tab === 'current' ? section.colour : 'white', color: tab === 'current' ? 'white' : '#888', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
          Current State
        </button>
        <button onClick={() => setTab('future')}
          style={{ flex: 1, padding: '0.65rem', fontSize: '0.85rem', fontWeight: 600, border: 'none', borderLeft: '1px solid #f0f0f0', background: tab === 'future' ? '#185FA5' : 'white', color: tab === 'future' ? 'white' : '#888', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
          Future State
        </button>
      </div>

      {/* ── CURRENT STATE ──────────────────────────────────────────── */}
      {tab === 'current' && (
        <div>
          <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: section.colour, marginBottom: '1.25rem' }}>
              Where you are now
            </div>

            {/* Org builder */}
            {section.type === 'org_builder' && (
              <div style={{ marginBottom: '1.5rem' }}>
                <OrgBuilder value={current.org} onChange={org => onChange({ ...sectionData, current: { ...current, org } })} label="Org structure — current state" />
              </div>
            )}

            {/* Culture dialogue — explore mode */}
            {section.type === 'culture_dialogue' && effectiveMode === 'explore' && (
              <CultureDialogue currentData={current} onChange={next => onChange({ ...sectionData, current: next })} isFutureState={false} sectionKey={section.key} />
            )}

            {/* Business leader time slider — always shown for org_builder, both modes */}
            {section.type === 'org_builder' && (
              <div style={{ marginBottom: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #f0f0f0' }}>
                <FounderTimeSlider value={current.founder_time_splits || { active: ['delivery'], splits: { delivery: 100 } }} onChange={v => onChange({ ...sectionData, current: { ...current, founder_time_splits: v } })} />
              </div>
            )}

            {/* People dialogue — explore mode for org_builder. Replaces quick fields + key interactions. */}
            {section.type === 'org_builder' && effectiveMode === 'explore' && (
              <PeopleDialogue
                currentData={current}
                onChange={next => onChange({ ...sectionData, current: next })}
                isFutureState={false}
                hasFutureState={!!(future?.hiring_priorities || future?.founder_role || future?.structural_changes)}
                sectionKey={section.key}
              />
            )}

            {/* Location & infrastructure dialogue — explore mode. Handles both current and future state. */}
            {section.type === 'location_dialogue' && effectiveMode === 'explore' && (
              <LocationDialogue
                currentData={current}
                futureData={future}
                onChange={next => onChange({ ...sectionData, current: next })}
                onFutureChange={next => onChange({ ...sectionData, future: next })}
                hasFutureState={!!(future?.target_locations || future?.property_strategy)}
                sectionKey={section.key}
              />
            )}

            {/* Suppliers & partners dialogue — explore mode. Handles both current and future state. */}
            {section.type === 'suppliers_dialogue' && effectiveMode === 'explore' && (
              <SuppliersDialogue
                currentData={current}
                futureData={future}
                onChange={next => onChange({ ...sectionData, current: next })}
                onFutureChange={next => onChange({ ...sectionData, future: next })}
                hasFutureState={!!(future?.partner_strategy || future?.insource_outsource)}
                sectionKey={section.key}
              />
            )}

            {/* Customer interactions dialogue — explore mode. Handles both current and future state. */}
            {section.type === 'customer_dialogue' && effectiveMode === 'explore' && (
              <CustomerDialogue
                currentData={current}
                futureData={future}
                onChange={next => onChange({ ...sectionData, current: next })}
                onFutureChange={next => onChange({ ...sectionData, future: next })}
                hasFutureState={!!(future?.cx_improvements || future?.target_relationship)}
                sectionKey={section.key}
              />
            )}

            {/* Process & ways of work dialogue — explore mode. Handles both current and future state. */}
            {section.type === 'process_dialogue' && effectiveMode === 'explore' && (
              <ProcessDialogue
                currentData={current}
                futureData={future}
                onChange={next => onChange({ ...sectionData, current: next })}
                onFutureChange={next => onChange({ ...sectionData, future: next })}
                hasFutureState={!!(future?.priority_process_improvements || future?.target_delivery_model || future?.systems_integration_changes || future?.ai_automation_roadmap)}
                sectionKey={section.key}
              />
            )}

            {/* Finance & commercial dialogue — explore mode. Handles both current and future state. */}
            {section.type === 'finance_dialogue' && effectiveMode === 'explore' && (
              <FinanceDialogue
                currentData={current}
                futureData={future}
                onChange={next => onChange({ ...sectionData, current: next })}
                onFutureChange={next => onChange({ ...sectionData, future: next })}
                hasFutureState={!!(future?.commercial_model_changes || future?.margin_improvements || future?.income_diversification)}
                sectionKey={section.key}
              />
            )}

            {/* Governance & review rhythm dialogue — explore mode. Handles both current and future state. */}
            {section.type === 'governance_dialogue' && effectiveMode === 'explore' && (
              <GovernanceDialogue
                currentData={current}
                futureData={future}
                onChange={next => onChange({ ...sectionData, current: next })}
                onFutureChange={next => onChange({ ...sectionData, future: next })}
                hasFutureState={!!(future?.target_meeting_rhythm || future?.discipline_improvements || future?.external_oversight_changes)}
                sectionKey={section.key}
              />
            )}

            {/* IT & data security dialogue — explore mode. Handles both current and future state. */}
            {section.type === 'it_dialogue' && effectiveMode === 'explore' && (
              <ITDialogue
                currentData={current}
                futureData={future}
                onChange={next => onChange({ ...sectionData, current: next })}
                onFutureChange={next => onChange({ ...sectionData, future: next })}
                hasFutureState={!!(future?.compliance_security_priorities || future?.bespoke_remediation || future?.data_organisation_improvements || future?.technology_opportunities)}
                sectionKey={section.key}
              />
            )}

            {/* Product & service portfolio dialogue — explore mode. Handles both current and future state. */}
            {section.type === 'portfolio_dialogue' && effectiveMode === 'explore' && (
              <PortfolioDialogue
                currentData={current}
                futureData={future}
                onChange={next => onChange({ ...sectionData, current: next })}
                onFutureChange={next => onChange({ ...sectionData, future: next })}
                hasFutureState={!!(future?.strategic_direction || future?.new_offerings || future?.portfolio_rationalisation || future?.portfolio_adjacencies || future?.build_buy_partner || future?.future_portfolio_vision)}
                sectionKey={section.key}
              />
            )}

            {/* Business development dialogue — explore mode. Handles both current and future state. */}
            {section.type === 'bd_dialogue' && effectiveMode === 'explore' && (
              <BDDialogue
                currentData={current}
                futureData={future}
                onChange={next => onChange({ ...sectionData, current: next })}
                onFutureChange={next => onChange({ ...sectionData, future: next })}
                hasFutureState={!!(future?.target_bd_model || future?.bd_process_improvements || future?.bd_team_capability || future?.referral_expansion_approach)}
                sectionKey={section.key}
              />
            )}

            {/* Marketing & brand dialogue — explore mode. Handles both current and future state. */}
            {section.type === 'marketing_dialogue' && effectiveMode === 'explore' && (
              <MarketingDialogue
                currentData={current}
                futureData={future}
                onChange={next => onChange({ ...sectionData, current: next })}
                onFutureChange={next => onChange({ ...sectionData, future: next })}
                hasFutureState={!!(future?.brand_development || future?.target_channel_mix || future?.credibility_building_plan || future?.marketing_capability_changes || future?.marketing_investment || future?.ai_marketing_roadmap)}
                sectionKey={section.key}
              />
            )}

            {/* Quick entry — all non-explore modes, or non-dialogue sections */}
            {(effectiveMode === 'quick' || section.type !== 'culture_dialogue') && !(section.type === 'org_builder' && effectiveMode === 'explore') && !(section.type === 'location_dialogue' && effectiveMode === 'explore') && !(section.type === 'suppliers_dialogue' && effectiveMode === 'explore') && !(section.type === 'customer_dialogue' && effectiveMode === 'explore') && !(section.type === 'process_dialogue' && effectiveMode === 'explore') && !(section.type === 'finance_dialogue' && effectiveMode === 'explore') && !(section.type === 'governance_dialogue' && effectiveMode === 'explore') && !(section.type === 'it_dialogue' && effectiveMode === 'explore') && !(section.type === 'portfolio_dialogue' && effectiveMode === 'explore') && !(section.type === 'bd_dialogue' && effectiveMode === 'explore') && !(section.type === 'marketing_dialogue' && effectiveMode === 'explore') && (
              <div>
                {section.type === 'culture_dialogue' ? (
                  <div>
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#7C3AED', marginBottom: '0.5rem' }}>Culture</div>
                      {renderQuickFields(current, next => onChange({ ...sectionData, current: next }),
                        section.current_fields.filter(f => ['decision_making','transparency','team_empowerment','what_celebrated','what_not_tolerated','culture_characterisation','culture_evidence','culture_challenges'].includes(f.key)))}
                    </div>
                    <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '1.25rem' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#185FA5', marginBottom: '0.5rem' }}>Leadership</div>
                      {renderQuickFields(current, next => onChange({ ...sectionData, current: next }),
                        section.current_fields.filter(f => ['leadership_visibility','empowerment_style','communication_pattern','leadership_development','leadership_characterisation','leadership_evidence'].includes(f.key)))}
                    </div>
                  </div>
                ) : (
                  renderQuickFields(current, next => onChange({ ...sectionData, current: next }), section.current_fields)
                )}
                {section.type === 'org_builder' && (() => {
                  const org = current.org;
                  if (!org) return null;
                  const allRoles = [];
                  ORG_LAYERS.forEach(l => {
                    (org.functions || []).forEach((_, fi) => {
                      (org[l.key]?.[fi] || []).filter(r => r && r.trim()).forEach(r => allRoles.push(r));
                    });
                  });
                  if (allRoles.length < 2) return null;
                  const interactions = current.key_interactions || {};
                  return (
                    <div style={{ marginTop: '1.25rem', borderTop: '1px solid #f0f0f0', paddingTop: '1.25rem' }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>Key interactions</div>
                      <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: 8, lineHeight: 1.4 }}>For each role, note their most important working relationship.</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {allRoles.map((role, ri) => {
                          const others = allRoles.filter((_, i) => i !== ri);
                          const selected = interactions[role] || '';
                          return (
                            <div key={ri} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                              <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1a1a2e', minWidth: 140, flexShrink: 0 }}>{role}</span>
                              <span style={{ fontSize: '0.68rem', color: '#aaa', flexShrink: 0 }}>works closest with</span>
                              <select value={selected}
                                onChange={e => onChange({ ...sectionData, current: { ...current, key_interactions: { ...interactions, [role]: e.target.value } } })}
                                style={{ fontSize: '0.72rem', padding: '0.2rem 0.4rem', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', flex: 1, color: selected ? '#1a1a2e' : '#aaa' }}>
                                <option value="">Select…</option>
                                {others.map((o, oi) => <option key={oi} value={o}>{o}</option>)}
                                <option value="External clients">External clients</option>
                                <option value="External suppliers">External suppliers</option>
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Key risks — forward-looking, distinct from current_challenges. */}
          {/* Always visible on the current-state tab regardless of mode. */}
          <RisksEditor
            section={section}
            current={current}
            onChange={next => onChange({ ...sectionData, current: next })}
          />
        </div>
      )}

      {/* ── FUTURE STATE ───────────────────────────────────────────── */}
      {tab === 'future' && (
        <div>
          {/* Generate future state button */}
          <div style={{ marginBottom: '1.25rem', padding: '1rem 1.25rem', borderRadius: 10, border: `1px solid ${futureUnlocked ? '#C4B5FD' : '#e0e0e0'}`, background: futureUnlocked ? '#FAFAFA' : '#fafafa', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: futureUnlocked ? '#4C1D95' : '#aaa', marginBottom: 2 }}>
                {futureUnlocked ? '✨ Generate future state from Strategy Canvas' : '🔒 Generate future state from Strategy Canvas'}
              </div>
              <div style={{ fontSize: '0.68rem', color: '#aaa', lineHeight: 1.4 }}>
                {futureUnlocked
                  ? 'Use your completed strategy to auto-draft this section\'s future state — you review and edit before saving.'
                  : 'Complete the key Strategy Canvas sections (Vision, Customers, Go to Market, Revenue Model, Cost Model) to unlock.'}
              </div>
            </div>
            <button disabled={!futureUnlocked}
              style={{ padding: '0.5rem 1rem', borderRadius: 8, background: futureUnlocked ? '#7C3AED' : '#f0f0f0', color: futureUnlocked ? 'white' : '#bbb', border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: futureUnlocked ? 'pointer' : 'not-allowed', fontFamily: 'inherit', width: 'auto', flexShrink: 0 }}>
              Generate →
            </button>
          </div>

          <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#185FA5', marginBottom: '0.5rem' }}>
              Where you are going
            </div>
            <p style={{ fontSize: '0.78rem', color: '#888', margin: '0 0 1.25rem', lineHeight: 1.5 }}>
              Describe what this dimension needs to look like to deliver your strategy — or use the generate button above to draft it from your Strategy Canvas.
            </p>
            {section.type === 'org_builder' && (
              <div style={{ marginBottom: '1.5rem' }}>
                <OrgBuilder value={future.org} onChange={org => onChange({ ...sectionData, future: { ...future, org } })} currentOrg={current.org} label="Org structure — future state" />
              </div>
            )}
            {section.type === 'org_builder' && effectiveMode === 'explore' && (
              <PeopleDialogue
                currentData={future}
                onChange={next => onChange({ ...sectionData, future: next })}
                isFutureState={true}
                hasFutureState={!!(future?.hiring_priorities || future?.founder_role || future?.structural_changes)}
                sectionKey={section.key}
              />
            )}
            {section.type === 'culture_dialogue' ? (
              <CultureDialogue currentData={future} onChange={next => onChange({ ...sectionData, future: next })} isFutureState={true} sectionKey={section.key} />
            ) : (
              <div style={section.type === 'org_builder' ? { borderTop: '1px solid #f0f0f0', paddingTop: '1.25rem' } : {}}>
                <FieldGroup fields={section.future_fields} data={future} onChange={next => onChange({ ...sectionData, future: next })} />
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: '1rem', padding: '0.75rem 0', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onBack}
          style={{ padding: '0.6rem 1.5rem', borderRadius: 8, background: section.colour, color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
          ← Back to section options
        </button>
      </div>
    </div>
  );
}


// ── Design Guru mode selector ─────────────────────────────────────
// Mirrors Direction Canvas ModeSelector — shown when a card is clicked,
// before entering the section. Offers Explore (for dialogue sections),
// Quick entry, and Upload.
function BOModeSelector({ section, onSelect, onBack, existing }) {
  const hasExplore = section.type === 'culture_dialogue' || section.type === 'org_builder' || section.type === 'location_dialogue' || section.type === 'suppliers_dialogue' || section.type === 'customer_dialogue' || section.type === 'process_dialogue' || section.type === 'finance_dialogue' || section.type === 'governance_dialogue' || section.type === 'it_dialogue' || section.type === 'portfolio_dialogue' || section.type === 'bd_dialogue' || section.type === 'marketing_dialogue';
  const colour = section.colour;

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <button onClick={onBack}
        style={{ background: 'none', border: 'none', color: '#185FA5', fontSize: '0.82rem', cursor: 'pointer', width: 'auto', padding: 0, marginBottom: '1.5rem', fontFamily: 'inherit' }}>
        ← Back to canvas
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: colour + '18', border: `1.5px solid ${colour}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>
          {section.icon}
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#1a1a2e' }}>{section.title}</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>{section.description}</p>
        </div>
      </div>

      <div style={{ height: 1, background: '#f0f0f0', margin: '1.25rem 0' }} />

      {existing && (
        <div style={{ background: '#f0faf6', border: '1px solid #b8e4d4', borderRadius: 8, padding: '0.55rem 1rem', marginBottom: '1.25rem', fontSize: '0.78rem', color: '#0F6E56' }}>
          ✓ You have existing data for this section — any journey will update it.
        </div>
      )}

      {/* Explore — for sections that have a deep-dive dialogue (culture or org_builder) */}
      {hasExplore && (
        <button onClick={() => { track('design_deep_dive_started', { section: section.key }); onSelect('explore'); }}
          style={{ width: '100%', textAlign: 'left', padding: '1.1rem 1.4rem', borderRadius: 12, border: `2px solid ${colour}`, background: colour + '08', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '0.85rem', transition: 'all 0.15s', display: 'block' }}
          onMouseEnter={e => { e.currentTarget.style.background = colour + '15'; }}
          onMouseLeave={e => { e.currentTarget.style.background = colour + '08'; }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <span style={{ fontSize: '1.2rem' }}>◎</span>
              <span style={{ fontSize: '0.95rem', fontWeight: 700, color: colour }}>Deep dive</span>
              <span style={{ fontSize: '0.62rem', color: colour, fontWeight: 700, padding: '2px 7px', background: colour + '15', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Recommended</span>
            </div>
            <span style={{ fontSize: '0.62rem', color: '#aaa', fontWeight: 600, padding: '2px 7px', background: '#f5f5f5', borderRadius: 20 }}>~20 min</span>
          </div>
          <div style={{ fontSize: '0.8rem', color: '#333', lineHeight: 1.55, maxWidth: '90%' }}>
            A structured dialogue that deepens understanding through specific questions and evidence — not labels. Produces a draft you review before saving.
          </div>
        </button>
      )}

      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>
        {hasExplore ? 'Or — enter directly' : 'How would you like to enter this section?'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        {/* Quick entry */}
        <button onClick={() => { track('design_quick_select_started', { section: section.key }); onSelect('quick'); }}
          style={{ textAlign: 'left', padding: '1rem 1.1rem', borderRadius: 10, border: `1.5px solid ${colour}33`, background: 'white', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}
          onMouseEnter={e => { e.currentTarget.style.background = colour + '08'; e.currentTarget.style.borderColor = colour; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = colour + '33'; }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '1rem', color: colour }}>⚡</span>
            <span style={{ fontSize: '0.62rem', color: '#aaa', fontWeight: 600, padding: '2px 6px', background: '#f5f5f5', borderRadius: 20 }}>~2 min</span>
          </div>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>Quick entry</div>
          <div style={{ fontSize: '0.75rem', color: '#777', lineHeight: 1.5 }}>
            Select from pre-defined options and fill in the key fields. Fast and structured.
          </div>
        </button>

        {/* Upload */}
        <button onClick={() => onSelect('upload')}
          style={{ textAlign: 'left', padding: '1rem 1.1rem', borderRadius: 10, border: '1.5px solid #9B6B3A33', background: 'white', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#9B6B3A08'; e.currentTarget.style.borderColor = '#9B6B3A'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#9B6B3A33'; }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '1rem', color: '#9B6B3A' }}>↑</span>
            <span style={{ fontSize: '0.62rem', color: '#aaa', fontWeight: 600, padding: '2px 6px', background: '#f5f5f5', borderRadius: 20 }}>~5 min</span>
          </div>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e' }}>Upload / website</div>
          <div style={{ fontSize: '0.75rem', color: '#777', lineHeight: 1.5 }}>
            Upload a document or paste your website URL. AI reads it and suggests values — you review before saving.
          </div>
        </button>
      </div>
    </div>
  );
}

// ── Section upload editor ──────────────────────────────────────────
// Handles the upload mode for an individual section — extracts just
// that section's data from files/URL.
function SectionUploadEditor({ section, sectionData, onSave, onBack }) {
  const [files, setFiles] = useState([]);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [accepted, setAccepted] = useState({});
  const fileInputRef = useRef(null);
  const ACCEPTED_TYPES = '.pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.gif,.webp';
  const hasInput = files.length > 0 || websiteUrl.trim().length > 0;

  const handleFiles = (e) => {
    const incoming = Array.from(e.target.files);
    setFiles(prev => { const n = new Set(prev.map(f => f.name)); return [...prev, ...incoming.filter(f => !n.has(f.name))]; });
    e.target.value = '';
  };
  const handleDrop = (e) => { e.preventDefault(); const incoming = Array.from(e.dataTransfer.files); setFiles(prev => { const n = new Set(prev.map(f => f.name)); return [...prev, ...incoming.filter(f => !n.has(f.name))]; }); };

  const handleUpload = async () => {
    if (!hasInput) return;
    setUploading(true); setResult(null);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      if (websiteUrl.trim()) formData.append('url', websiteUrl.trim());
      formData.append('sectionKey', section.key);
      const res = await axios.post(`${UPLOAD_API}/om-document`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      const sectionResult = res.data.sections?.[section.key];
      if (sectionResult) {
        setResult({ data: sectionResult, confidence: res.data.confidence?.[section.key] || 'medium' });
        setAccepted({ current: true });
      } else {
        alert("We couldn't pull anything for " + section.title + " from what you shared. This can happen with a scanned/image-only PDF we couldn't read, a password-protected file, or a file with little relevant detail. Try a clearer or more detailed file, paste your website URL, or email Glenn at glenn@guruone.ai.");
      }
    } catch (e) { alert(e.response?.data?.error || 'Upload failed. Please try again.'); }
    setUploading(false);
  };

  const handleSave = () => {
    if (!result || !accepted.current) return;
    const existing = sectionData || {};
    onSave({ ...existing, current: { ...(existing.current || {}), ...result.data } });
  };

  const CONF_STYLE = {
    high:   { label: 'High confidence',   bg: '#f0fdf8', border: '#a7f3d0', color: '#065f46' },
    medium: { label: 'Medium confidence', bg: '#fffbeb', border: '#fde68a', color: '#78350f' },
    low:    { label: 'Low confidence',    bg: '#fef2f2', border: '#fecaca', color: '#991b1b' },
  };

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <button onClick={onBack}
        style={{ background: 'none', border: 'none', color: '#185FA5', fontSize: '0.82rem', cursor: 'pointer', width: 'auto', padding: 0, marginBottom: '1.5rem', fontFamily: 'inherit' }}>
        ← Back
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: section.colour + '18', border: `1.5px solid ${section.colour}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>
          {section.icon}
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#1a1a2e' }}>{section.title}</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>Upload &amp; infer — AI reads your documents and suggests values</p>
        </div>
      </div>

      {/* Upload form */}
      {!result && (
        <div>
          <div onDragOver={e => e.preventDefault()} onDrop={handleDrop}
            style={{ border: '1.5px solid #e0e0e0', borderRadius: 10, padding: '1rem 1.25rem', background: '#f9fffe', marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#555', marginBottom: '0.6rem' }}>
              Add files or a website URL — AI extracts information relevant to {section.title}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.72rem', color: '#888', flexShrink: 0 }}>🌐 Website</span>
              <input type="url" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)}
                placeholder="https://yourwebsite.com"
                style={{ width: 220, fontSize: '0.78rem', padding: '0.35rem 0.6rem', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit' }} />
              <span style={{ color: '#ddd' }}>|</span>
              <span style={{ fontSize: '0.72rem', color: '#888', flexShrink: 0 }}>📄 Documents</span>
              <input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES} multiple onChange={handleFiles} style={{ display: 'none' }} />
              <button onClick={() => fileInputRef.current?.click()}
                style={{ padding: '0.35rem 0.75rem', borderRadius: 6, border: '1px solid #185FA5', background: 'white', color: '#185FA5', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', width: 'auto', fontFamily: 'inherit' }}>
                + Add files
              </button>
              {hasInput && (
                <button onClick={handleUpload} disabled={uploading}
                  style={{ marginLeft: 'auto', padding: '0.35rem 0.9rem', borderRadius: 6, background: uploading ? '#ccc' : '#185FA5', border: 'none', color: 'white', fontSize: '0.75rem', fontWeight: 700, cursor: uploading ? 'not-allowed' : 'pointer', width: 'auto', fontFamily: 'inherit' }}>
                  {uploading ? 'Analysing…' : 'Analyse & suggest values →'}
                </button>
              )}
            </div>
            {files.length > 0 && (
              <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                {files.map((f, i) => (
                  <span key={i} style={{ fontSize: '0.68rem', background: '#E1F5EE', color: '#0F6E56', padding: '2px 6px 2px 8px', borderRadius: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                    {f.name}
                    <button onClick={() => setFiles(p => p.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#0F6E56', cursor: 'pointer', padding: 0, fontSize: '0.75rem', lineHeight: 1, width: 'auto', opacity: 0.7 }}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Review extracted values */}
      {result && (
        <div>
          <div style={{ background: '#f0faf6', border: '1.5px solid #1D9E75', borderRadius: '12px 12px 0 0', padding: '0.9rem 1.25rem', borderBottom: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#0F6E56', flex: 1 }}>Values extracted for {section.title}</div>
              <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 8px', borderRadius: 10, background: (CONF_STYLE[result.confidence] || CONF_STYLE.medium).bg, border: `1px solid ${(CONF_STYLE[result.confidence] || CONF_STYLE.medium).border}`, color: (CONF_STYLE[result.confidence] || CONF_STYLE.medium).color }}>
                {(CONF_STYLE[result.confidence] || CONF_STYLE.medium).label}
              </span>
            </div>
            <div style={{ fontSize: '0.72rem', color: '#1D9E75', marginTop: '0.2rem' }}>Review before saving — edit after to refine</div>
          </div>
          <div style={{ border: '1.5px solid #1D9E75', borderTop: 'none', borderRadius: '0 0 12px 12px', padding: '1rem 1.25rem', marginBottom: '1rem' }}>
            {Object.entries(result.data).filter(([k]) => !k.startsWith('_')).map(([key, val]) => {
              const fieldDef = section.current_fields?.find(f => f.key === key);
              const label = fieldDef?.label || key.replace(/_/g, ' ');
              const display = Array.isArray(val) ? val.join(', ') : typeof val === 'object' ? JSON.stringify(val).slice(0, 80) : String(val).slice(0, 120);
              return (
                <div key={key} style={{ padding: '0.4rem 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.72rem', color: '#888', minWidth: 160, flexShrink: 0 }}>{label}</span>
                  <span style={{ fontSize: '0.78rem', color: '#1a1a2e', flex: 1 }}>{display}</span>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={handleSave}
              style={{ padding: '0.6rem 1.25rem', borderRadius: 8, background: '#185FA5', color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Save to section →
            </button>
            <button onClick={() => { setResult(null); setFiles([]); setWebsiteUrl(''); }}
              style={{ padding: '0.6rem 1rem', borderRadius: 8, background: 'white', color: '#888', border: '1px solid #ddd', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
              Try again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Design Guru global upload panel ───────────────────────────────
// One upload, both canvases. Hits /api/upload/combined so a leader
// landing on the OM dashboard first still gets Direction populated
// (and vice versa). Mirrors StrategyCanvas's GlobalUploadPanel — the
// save handler writes Design via the parent onSectionsLoaded prop
// and writes Direction straight to localStorage 'guruone_strategy'
// in the user-scoped { user_X: {...sections} } shape that
// StrategyCanvas reads on mount.
function OMGlobalUploadPanel({ onSectionsLoaded }) {
  const [files, setFiles] = useState([]);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [urlEditable, setUrlEditable] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [helpOpen, setHelpOpen] = useState(false);
  const [scope, setScope] = useState('both');
  const fileInputRef = useRef(null);
  const ACCEPTED_TYPES = '.pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.gif,.webp';
  const hasInput = files.length > 0 || websiteUrl.trim().length > 0;

  // Seed the URL from the saved user profile and lock the input by
  // default if one is present — user can click "Edit" to unlock.
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('guruone_user'));
      if (user?.websiteUrl) {
        setWebsiteUrl(user.websiteUrl);
        setUrlEditable(false);
      }
    } catch { /* non-fatal */ }
  }, []);

  const STRAT_LABELS = {
    founder_purpose: 'Founder Purpose', values: 'Values', mission: 'Mission', vision: 'Vision',
    value_proposition: 'Value Proposition', customers: 'Customers', competitors: 'Competitors',
    differentiators: 'Differentiators', go_to_market: 'Go to Market', revenue_model: 'Revenue Model',
    cost_model: 'Cost Model', current_challenges: 'Current Challenges',
  };
  const STRAT_ICONS = {
    founder_purpose: '🌱', values: '🤝', mission: '🎯', vision: '🔭',
    value_proposition: '💎', customers: '👥', competitors: '⚔️',
    differentiators: '✨', go_to_market: '🚀', revenue_model: '💰',
    cost_model: '📊', current_challenges: '🚧',
  };
  const OM_LABELS = {
    people_roles: 'People, Roles & Structure', culture_leadership: 'Culture & Leadership',
    location_infrastructure: 'Location & Infrastructure', suppliers_partners: 'Suppliers & Partners',
    customer_interactions: 'Customer Interactions', process_ways_of_work: 'Process & Ways of Work',
    governance_controls: 'Governance & Review Rhythm', finance_commercial: 'Finance & Commercial',
    it_data_security: 'IT & Data Security', portfolio: 'Product & Service Portfolio',
    business_development: 'Business Development', marketing_brand: 'Marketing & Brand',
  };
  const OM_ICONS = {
    people_roles: '👥', culture_leadership: '🌱', location_infrastructure: '🏢',
    suppliers_partners: '🤝', customer_interactions: '💬', process_ways_of_work: '⚙️',
    governance_controls: '🔄', finance_commercial: '💰', it_data_security: '🔒',
    portfolio: '🗂️', business_development: '🤝', marketing_brand: '📣',
  };

  const UPLOAD_HINTS = [
    { doc: 'Business plan or vision deck', unlocks: 'Founder purpose, values, mission, vision, value proposition' },
    { doc: 'Pitch deck or sales collateral', unlocks: 'Value proposition, competitors, differentiators, go-to-market' },
    { doc: 'P&L / management accounts / cost breakdown', unlocks: 'Revenue model, cost model, finance & commercial' },
    { doc: 'Customer list, case studies or testimonials', unlocks: 'Customers, customer interactions, differentiators' },
    { doc: 'Org chart or role descriptions', unlocks: 'People & roles, culture, governance' },
    { doc: 'Process docs, SOPs or playbooks', unlocks: 'Process & ways of work, suppliers, quality' },
    { doc: 'Marketing collateral or website', unlocks: 'Marketing & brand, GTM, value proposition' },
    { doc: 'IT estate diagram or systems list', unlocks: 'IT & data security, portfolio' },
  ];

  const CONF_STYLE = {
    high:   { label: 'High confidence',   bg: '#f0fdf8', border: '#a7f3d0', color: '#065f46' },
    medium: { label: 'Medium confidence', bg: '#fffbeb', border: '#fde68a', color: '#78350f' },
    low:    { label: 'Low confidence',    bg: '#fef2f2', border: '#fecaca', color: '#991b1b' },
  };

  const handleFiles = (e) => {
    const incoming = Array.from(e.target.files);
    setFiles(prev => { const n = new Set(prev.map(f => f.name)); return [...prev, ...incoming.filter(f => !n.has(f.name))]; });
    setResult(null); e.target.value = '';
  };
  const handleDrop = (e) => { e.preventDefault(); const incoming = Array.from(e.dataTransfer.files); setFiles(prev => { const n = new Set(prev.map(f => f.name)); return [...prev, ...incoming.filter(f => !n.has(f.name))]; }); setResult(null); };

  const handleUpload = async () => {
    if (!hasInput) return;
    setUploading(true); setResult(null);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      if (websiteUrl.trim()) formData.append('url', websiteUrl.trim());
      formData.append('scope', scope);
      const res = await axios.post(`${UPLOAD_API}/combined`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      const stratSecs = res.data.strategy?.sections || {};
      const omSecs = res.data.operating_model?.sections || {};
      if (Object.keys(stratSecs).length === 0 && Object.keys(omSecs).length === 0) {
        // Honest feedback instead of an empty review screen.
        alert("We couldn't pull anything usable from what you shared. This can happen with a scanned/image-only PDF we couldn't read, a password-protected file, or a file with very little text. Try a clearer or text-based file, paste your website URL, or email Glenn at glenn@guruone.ai and he'll take a look.");
        setUploading(false);
        return;
      }
      setResult(res.data);
      const init = {};
      Object.keys(stratSecs).forEach(k => { init[`strat_${k}`] = true; });
      Object.keys(omSecs).forEach(k => { init[`om_${k}`] = true; });
      setAccepted(init);
    } catch (e) { alert(e.response?.data?.error || 'Upload failed. Please try again.'); }
    setUploading(false);
  };

  const handleSave = () => {
    const omToSave = {};
    const stratToSave = {};
    Object.keys(accepted).forEach(k => {
      if (!accepted[k]) return;
      if (k.startsWith('strat_')) {
        const key = k.slice(6);
        if (result?.strategy?.sections?.[key]) stratToSave[key] = result.strategy.sections[key];
      } else if (k.startsWith('om_')) {
        const key = k.slice(3);
        if (result?.operating_model?.sections?.[key]) omToSave[key] = { current: result.operating_model.sections[key] };
      }
    });

    // Read existing canvas state once so the merge helper can
    // preserve narratives / transcripts / evaluations on both sides.
    let existingOM = {};
    let existingStratFull = null; // user_X-keyed map
    let existingStrat = {};
    try {
      const oRaw = localStorage.getItem('guruone_operating_model');
      existingOM = oRaw ? JSON.parse(oRaw) : {};
      const user = JSON.parse(localStorage.getItem('guruone_user') || '{}');
      const userKey = user?.id ? `user_${user.id}` : 'default';
      const sRaw = localStorage.getItem('guruone_strategy');
      existingStratFull = sRaw ? JSON.parse(sRaw) : {};
      existingStrat = (existingStratFull[userKey] && typeof existingStratFull[userKey] === 'object') ? existingStratFull[userKey] : {};
      // Stash userKey on the snapshot so we can write back below.
      existingStratFull.__userKey = userKey;
    } catch { /* non-fatal */ }

    // Design — merge then send merged values up to the parent. The
    // parent's handleBulkSave does next[key] = data which fully
    // replaces, so we need to send already-merged payloads.
    if (Object.keys(omToSave).length > 0) {
      const merged = {};
      Object.entries(omToSave).forEach(([key, incoming]) => {
        merged[key] = mergeSectionPreservingMeta(existingOM[key], incoming);
      });
      onSectionsLoaded(merged);
    }

    // Direction — write straight to the user-scoped strategy
    // localStorage, merging the incoming fields with what's already
    // there so narratives + transcripts survive.
    if (Object.keys(stratToSave).length > 0 && existingStratFull) {
      try {
        const userKey = existingStratFull.__userKey || 'default';
        delete existingStratFull.__userKey;
        const mergedStrat = {};
        Object.entries(stratToSave).forEach(([key, incoming]) => {
          mergedStrat[key] = mergeSectionPreservingMeta(existingStrat[key], incoming);
        });
        existingStratFull[userKey] = {
          ...existingStrat,
          ...mergedStrat,
          lastUpdated: new Date().toISOString(),
        };
        localStorage.setItem('guruone_strategy', JSON.stringify(existingStratFull));
        track('upload_combined_save', { from: 'design', strategy: Object.keys(stratToSave).length, om: Object.keys(omToSave).length, scope });
      } catch { /* localStorage write — non-fatal */ }
    }

    setResult(null); setFiles([]); setWebsiteUrl('');
  };

  const ORDER = ['high','medium','low'];
  const getConf = (key, conf) => {
    if (conf[key]) return conf[key];
    const hits = Object.entries(conf).filter(([k]) => k.startsWith(key)).map(([,v]) => v);
    if (!hits.length) return 'medium';
    return hits.sort((a,b) => ORDER.indexOf(b) - ORDER.indexOf(a))[0];
  };

  const buildPreview = (data) => {
    if (!data || typeof data !== 'object') return String(data).slice(0, 80);
    if (data.entries) return `${data.entries.length} entr${data.entries.length === 1 ? 'y' : 'ies'} found`;
    const vals = Object.entries(data)
      .filter(([,v]) => v && !String(v).includes('unclear'))
      .slice(0, 3)
      .map(([k, v]) => {
        const display = Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v).slice(0,40) : String(v).slice(0,60);
        return `${k.replace(/_/g,' ')}: ${display}`;
      });
    return vals.join(' · ') || 'Values extracted';
  };

  const renderRow = (key, data, conf, icon, label, canvas, isLast) => {
    const cs = CONF_STYLE[conf] || CONF_STYLE.medium;
    const acceptKey = `${canvas}_${key}`;
    return (
      <div key={acceptKey} style={{ padding: '0.75rem 1.25rem', borderBottom: isLast ? 'none' : '1px solid #f0f0f0', background: accepted[acceptKey] ? 'white' : '#fafafa' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e', flex: 1, minWidth: 120 }}>
            {icon && <span style={{ marginRight: '0.35rem' }}>{icon}</span>}
            {label}
          </span>
          <span style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 10, background: cs.bg, border: `1px solid ${cs.border}`, color: cs.color, whiteSpace: 'nowrap' }}>
            {cs.label}
          </span>
          <button onClick={() => setAccepted(prev => ({ ...prev, [acceptKey]: !prev[acceptKey] }))}
            style={{ padding: '0.25rem 0.65rem', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600, border: accepted[acceptKey] ? '1.5px solid #1D9E75' : '1.5px solid #ddd', background: accepted[acceptKey] ? '#E1F5EE' : 'white', color: accepted[acceptKey] ? '#0F6E56' : '#888', cursor: 'pointer', fontFamily: 'inherit', width: 'auto', whiteSpace: 'nowrap' }}>
            {accepted[acceptKey] ? '✓ Accepted' : 'Accept'}
          </button>
        </div>
        <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '0.3rem', lineHeight: 1.4 }}>{buildPreview(data)}</div>
      </div>
    );
  };

  if (result && (Object.keys(result.strategy?.sections || {}).length > 0 || Object.keys(result.operating_model?.sections || {}).length > 0)) {
    const stratSections = result.strategy?.sections || {};
    const stratConfidence = result.strategy?.confidence || {};
    const omSections = result.operating_model?.sections || {};
    const omConfidence = result.operating_model?.confidence || {};
    const stratKeys = Object.keys(stratSections);
    const omKeys = Object.keys(omSections);
    const acceptedCount = Object.entries(accepted)
      .filter(([k, v]) => v)
      .filter(([k]) => {
        if (k.startsWith('strat_')) return !!stratSections[k.slice(6)];
        if (k.startsWith('om_')) return !!omSections[k.slice(3)];
        return false;
      }).length;

    // Count protected deep-dive sections across both canvases so the
    // header can reassure the leader.
    let existingStrategySnapshot = {};
    let existingOMSnapshot = {};
    try {
      const sUser = JSON.parse(localStorage.getItem('guruone_user') || '{}');
      const userKey = sUser?.id ? `user_${sUser.id}` : 'default';
      const sRaw = localStorage.getItem('guruone_strategy');
      const sAll = sRaw ? JSON.parse(sRaw) : {};
      existingStrategySnapshot = (sAll[userKey] && typeof sAll[userKey] === 'object') ? sAll[userKey] : {};
      const oRaw = localStorage.getItem('guruone_operating_model');
      existingOMSnapshot = oRaw ? JSON.parse(oRaw) : {};
    } catch { /* non-fatal */ }
    const protectedDeepDives = countDeepDiveSections(existingStrategySnapshot) + countDeepDiveSections(existingOMSnapshot);

    return (
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ background: '#f0faf6', border: '1.5px solid #1D9E75', borderRadius: '12px 12px 0 0', padding: '1rem 1.25rem', borderBottom: 'none' }}>
          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#0F6E56' }}>
            Found information across {stratKeys.length + omKeys.length} section{(stratKeys.length + omKeys.length) !== 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#1D9E75', marginTop: '0.2rem' }}>
            Review each section — accept or reject before saving. Direction sections land on your Direction canvas; Design sections land on your Design canvas.
          </div>
          {protectedDeepDives > 0 && (
            <div style={{ marginTop: '0.6rem', padding: '0.55rem 0.8rem', background: 'white', border: '1px solid #A6DCC8', borderRadius: 6, fontSize: '0.7rem', color: '#0F6E56', lineHeight: 1.5 }}>
              <strong>Your deep-dive work is safe.</strong> {protectedDeepDives} section{protectedDeepDives !== 1 ? 's have' : ' has'} a narrative or transcript captured — those will not be overwritten. Accepted suggestions will only update chip selections and short text fields.
            </div>
          )}
        </div>

        {omKeys.length > 0 && (
          <>
            <div style={{ background: '#185FA5', color: 'white', padding: '0.5rem 1.25rem', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', borderLeft: '1.5px solid #1D9E75', borderRight: '1.5px solid #1D9E75' }}>
              Design canvas — {omKeys.length} section{omKeys.length !== 1 ? 's' : ''}
            </div>
            <div style={{ borderLeft: '1.5px solid #1D9E75', borderRight: '1.5px solid #1D9E75' }}>
              {omKeys.map((key, i) => renderRow(
                key,
                omSections[key],
                getConf(key, omConfidence),
                OM_ICONS[key],
                OM_LABELS[key] || key,
                'om',
                i === omKeys.length - 1 && stratKeys.length === 0,
              ))}
            </div>
          </>
        )}

        {stratKeys.length > 0 && (
          <>
            <div style={{ background: '#1a1a2e', color: 'white', padding: '0.5rem 1.25rem', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', borderLeft: '1.5px solid #1D9E75', borderRight: '1.5px solid #1D9E75' }}>
              Direction canvas — {stratKeys.length} section{stratKeys.length !== 1 ? 's' : ''}
            </div>
            <div style={{ borderLeft: '1.5px solid #1D9E75', borderRight: '1.5px solid #1D9E75' }}>
              {stratKeys.map((key, i) => renderRow(
                key,
                stratSections[key],
                getConf(key, stratConfidence),
                STRAT_ICONS[key],
                STRAT_LABELS[key] || key,
                'strat',
                i === stratKeys.length - 1,
              ))}
            </div>
          </>
        )}

        <div style={{ border: '1.5px solid #1D9E75', borderTop: 'none', borderRadius: '0 0 12px 12px', height: 1 }} />

        {result.errors?.length > 0 && <div style={{ fontSize: '0.72rem', color: '#BA7517', margin: '0.5rem 0' }}>⚠ {result.errors.join(' · ')}</div>}
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.85rem' }}>
          <button onClick={handleSave}
            style={{ padding: '0.55rem 1.25rem', borderRadius: 8, background: '#1D9E75', color: 'white', border: 'none', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Save {acceptedCount} section{acceptedCount !== 1 ? 's' : ''} to canvases →
          </button>
          <button onClick={() => { setResult(null); setFiles([]); setWebsiteUrl(''); }}
            style={{ padding: '0.55rem 1rem', borderRadius: 8, background: 'white', color: '#888', border: '1px solid #ddd', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit', width: 'auto' }}>
            Discard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div onDragOver={e => e.preventDefault()} onDrop={handleDrop}
        style={{ border: '1.5px solid #e0e0e0', borderRadius: 10, padding: '0.75rem 1rem', background: '#f9fffe' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '0.25rem' }}>
          Pre-fill both canvases from existing materials
        </div>
        <div style={{ fontSize: '0.72rem', color: '#666', lineHeight: 1.5, marginBottom: '0.4rem' }}>
          Upload once — we&apos;ll suggest values across <strong>both your Direction canvas and your Design canvas</strong> for you to review before saving. The more relevant material you include, the more we can populate.
        </div>

        <button
          type="button"
          onClick={() => setHelpOpen(o => !o)}
          aria-expanded={helpOpen}
          style={{
            background: 'none', border: 'none', color: '#185FA5', cursor: 'pointer',
            padding: 0, fontSize: '0.72rem', fontWeight: 600, fontFamily: 'inherit',
            textDecoration: 'underline', textUnderlineOffset: 2, marginBottom: '0.55rem', width: 'auto',
          }}>
          {helpOpen ? 'Hide what helps and why' : 'What helps and why →'}
        </button>
        {helpOpen && (
          <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: 8, padding: '0.7rem 0.9rem', marginBottom: '0.6rem' }}>
            <div style={{ fontSize: '0.7rem', color: '#666', lineHeight: 1.5, marginBottom: '0.45rem' }}>
              Different documents unlock different parts of the canvas. Include anything you have for the areas that matter most.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.3rem' }}>
              {UPLOAD_HINTS.map(({ doc, unlocks }) => (
                <div key={doc} style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', fontSize: '0.72rem', lineHeight: 1.4 }}>
                  <span style={{ color: '#185FA5', flexShrink: 0 }}>·</span>
                  <span style={{ fontWeight: 600, color: '#1a1a2e', flexShrink: 0 }}>{doc}</span>
                  <span style={{ color: '#888' }}>→ {unlocks}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scope selector — populate one or both canvases. Narratives
            are always preserved regardless of scope. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap', marginBottom: '0.55rem' }}>
          <span style={{ fontSize: '0.7rem', color: '#666', fontWeight: 600 }}>Populate:</span>
          {[
            { key: 'both', label: 'Both canvases' },
            { key: 'strategy', label: 'Direction only' },
            { key: 'operating_model', label: 'Design only' },
          ].map(opt => (
            <button key={opt.key} type="button" onClick={() => setScope(opt.key)}
              style={{
                padding: '0.25rem 0.7rem', borderRadius: 14, fontSize: '0.7rem', fontWeight: 600,
                border: scope === opt.key ? '1.5px solid #185FA5' : '1px solid #d1d5db',
                background: scope === opt.key ? '#DBEAFE' : 'white',
                color: scope === opt.key ? '#1E40AF' : '#666',
                cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
              }}>
              {opt.label}
            </button>
          ))}
          <span style={{ fontSize: '0.66rem', color: '#888', fontStyle: 'italic' }}>
            Narratives + deep-dive content on existing sections are always preserved.
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.72rem', color: '#888', flexShrink: 0 }}>🌐 Website</span>
          {urlEditable ? (
            <input type="url" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://yourwebsite.com"
              style={{ width: 220, height: 32, fontSize: '0.78rem', padding: '0 0.6rem', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }} />
          ) : (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              height: 32, padding: '0 0.7rem', borderRadius: 6,
              background: '#E1F5EE', border: '1px solid #A5D6C3',
              fontSize: '0.75rem', color: '#0F6E56', fontWeight: 600,
              boxSizing: 'border-box',
            }}>
              <span style={{ color: '#1D9E75', fontWeight: 800 }}>✓</span>
              {websiteUrl}
              <button onClick={() => setUrlEditable(true)}
                style={{ background: 'none', border: 'none', color: '#0F6E56', fontSize: '0.7rem', cursor: 'pointer', padding: 0, marginLeft: '0.3rem', textDecoration: 'underline', fontFamily: 'inherit' }}>
                Edit
              </button>
            </span>
          )}
          <span style={{ color: '#ddd' }}>|</span>
          <span style={{ fontSize: '0.72rem', color: '#888', flexShrink: 0 }}>📄 Documents</span>
          <input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES} multiple onChange={handleFiles} style={{ display: 'none' }} />
          <button onClick={() => fileInputRef.current?.click()}
            style={{ height: 32, padding: '0 0.75rem', borderRadius: 6, border: '1px solid #185FA5', background: 'white', color: '#185FA5', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', width: 'auto', fontFamily: 'inherit', boxSizing: 'border-box' }}>
            + Add files
          </button>
          <span style={{ fontSize: '0.68rem', color: '#ccc', flexShrink: 0 }}>PDF, Word, images, text</span>
          {hasInput && (
            <button onClick={handleUpload} disabled={uploading}
              style={{ marginLeft: 'auto', height: 32, padding: '0 0.9rem', borderRadius: 6, background: uploading ? '#ccc' : '#185FA5', border: 'none', color: 'white', fontSize: '0.75rem', fontWeight: 700, cursor: uploading ? 'not-allowed' : 'pointer', width: 'auto', fontFamily: 'inherit', boxSizing: 'border-box' }}>
              {uploading ? 'Analysing…' : 'Analyse & pre-fill →'}
            </button>
          )}
        </div>
        {files.length > 0 && (
          <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
            {files.map((f, i) => (
              <span key={i} style={{ fontSize: '0.68rem', background: '#E1F5EE', color: '#0F6E56', padding: '2px 6px 2px 8px', borderRadius: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                {f.name}
                <button onClick={() => setFiles(p => p.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#0F6E56', cursor: 'pointer', padding: 0, fontSize: '0.75rem', lineHeight: 1, width: 'auto', opacity: 0.7 }}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Design journey track (4-stage indicator) ─────────────────────
// Mirrors StrategyCanvas's DirectionJourneyTrack visually. Stages
// reference actual section keys (people_roles, etc — no _structure
// suffix and no future_ prefix). Track field distinguishes current vs
// future, and the "filled" check uses the matching .current / .future
// sub-object on each section's data with the section's own
// current_fields / future_fields. Note: customer_interactions sits in
// OM_SECTIONS but does not appear in any stage below — that mirrors
// the spec exactly.
// ── Stars rating ──────────────────────────────────────────────────
// Mirrors the Stars component used on Direction Canvas for visual
// consistency across overview cards.
// Stars render: filled to `score` when numeric; all hollow + dimmed
// when score is null (the "not yet rated" placeholder per Glenn's
// signed-off design — empty 5-star skeleton with hover hint).
function Stars({ score, max = 5, title }) {
  const isRated = typeof score === 'number';
  const fillCount = isRated ? score : 0;
  return (
    <div
      style={{ display: 'flex', gap: 2, cursor: title ? 'help' : 'default', opacity: isRated ? 1 : 0.55 }}
      title={title || undefined}
    >
      {Array.from({ length: max }, (_, i) => (
        <span key={i} style={{ color: i < fillCount ? '#FFB300' : '#e0e0e0', fontSize: '0.75rem' }}>★</span>
      ))}
    </div>
  );
}

// OM quality score — strict LLM evaluation. Returns null when no
// _evaluation exists on the section (read from sectionData.current
// or sectionData top level). Stars render empty when null.
function getOMSectionQuality(sectionData) {
  const e = sectionData?.current?._evaluation || sectionData?._evaluation;
  if (e && typeof e.score === 'number') {
    return Math.max(0, Math.min(5, Math.round(e.score)));
  }
  return null;
}

// Three-step depth indicator alongside the star score. Mirrors the
// Direction Guru TierPill so both canvases read as one product.
// level 0 = nothing rendered, 1 = Started, 2 = Captured, 3 = Detailed.
// Four-state status badge — mirrors StrategyCanvas's StatusBadge so
// Direction and Design read as the same product. Replaces both the
// old "FILL pip" tier pill and the duplicate "In progress / Complete"
// pill that used to sit in the card header.
//   0 → Not started   (no fields filled)
//   1 → In progress   (some fields filled, below the all-fields gate)
//   2 → Basic         (all fields filled via Quick Select)
//   3 → Detailed      (all fields + a deep-dive dialogue captured)
function OMStatusBadge({ tier, compact = false }) {
  if (!tier) return null;
  // Same labels as Direction's StatusBadge — self-explanatory at both
  // densities, no leader hovering required to make sense of "Basic".
  const config = [
    { label: 'Not started',     short: 'Not started',     bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' },
    { label: 'In progress',     short: 'In progress',     bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' },
    { label: 'Fields captured', short: 'Fields captured', bg: '#DBEAFE', color: '#1E40AF', border: '#93C5FD' },
    { label: 'Deep-dive done',  short: 'Deep-dive done',  bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7' },
  ][tier.level || 0];
  const tooltip = [
    'No fields filled yet.',
    'Some fields filled — keep going to complete the section.',
    'All fields captured via Quick Select. Run a deep-dive dialogue for a quality review.',
    'Deep-dive captured — all fields plus a narrative from the dialogue.',
  ][tier.level || 0];
  return (
    <span title={tooltip}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '0.22rem 0.6rem', borderRadius: 14,
        background: config.bg, color: config.color,
        border: `1px solid ${config.border}`,
        fontSize: '0.62rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
        cursor: 'help',
      }}>
      {compact ? config.short : config.label}
    </span>
  );
}

function omStarsTooltip(sectionData) {
  const e = sectionData?.current?._evaluation || sectionData?._evaluation;
  if (e && typeof e.score === 'number') {
    const lines = [`${e.score}/5 — ${e.headline || 'Section reviewed.'}`];
    if (Array.isArray(e.improvements) && e.improvements.length > 0) {
      lines.push('To improve: ' + e.improvements[0]);
    }
    return lines.join('\n');
  }
  return 'Not yet rated — complete the deep-dive dialogue to get a quality review.';
}

// isOMFieldFilled — used by getOMSectionTier to figure out "started".
// The old getOMSectionCompleteness mechanical fallback was removed
// once stars switched to quality-only; tier-level handles the
// "is started" signal now.
function isOMFieldFilled(v) {
  if (v == null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return String(v).trim().length > 0;
}

// 3-step tier label keyed on completeness ratio + narrative presence.
function getOMSectionTier(section, sectionData) {
  if (!sectionData) return { level: 0, label: '' };
  const current = sectionData.current;
  if (!current) return { level: 0, label: '' };
  const keys = (section.current_fields || []).map(f => f.key);
  if (keys.length === 0) return { level: 0, label: '' };
  const filled = keys.filter(k => isOMFieldFilled(current[k])).length;
  const ratio = filled / keys.length;
  const hasNarrative = !!(sectionData._narrative || sectionData._explore_transcript || current._narrative);
  // Tier thresholds match Direction Guru's getSectionTier — 90% is the
  // "all fields" threshold; 100% + narrative graduates to deep-dive.
  if (ratio === 0) return { level: 0, label: '' };
  if (ratio < 0.9) return { level: 1, label: 'some fields' };
  if (!hasNarrative) return { level: 2, label: 'all fields' };
  return { level: 3, label: 'deep-dive' };
}

// ── Canvas groups — replaces DESIGN_STAGES ────────────────────────
const OM_GROUPS = [
  {
    key: 'people_portfolio',
    title: 'People & Portfolio',
    description: 'Who the business is and what it offers',
    sectionKeys: ['people_roles', 'portfolio'],
  },
  {
    key: 'revenue_generation',
    title: 'Revenue Generation',
    description: 'How the business generates and grows income',
    sectionKeys: ['business_development', 'marketing_brand'],
  },
  {
    key: 'operations',
    title: 'Operations',
    description: 'How the business delivers and manages itself commercially',
    sectionKeys: ['process_ways_of_work', 'finance_commercial', 'customer_interactions', 'suppliers_partners'],
  },
  {
    key: 'foundations',
    title: 'Foundations',
    description: 'The culture, governance, technology, and infrastructure the business runs on',
    sectionKeys: ['culture_leadership', 'governance_controls', 'it_data_security', 'location_infrastructure'],
  },
];

// ── DesignJourneyTrack ────────────────────────────────────────────
// 4-group tracker. Each group dot is split horizontally:
// left half = current state completion, right half = future state completion.
// Grey (0%) → amber (partial) → green (complete).
function DesignJourneyTrack({ omData }) {
  function groupCompletion(group) {
    const sections = group.sectionKeys.map(k => OM_SECTIONS.find(s => s.key === k)).filter(Boolean);
    const currentFilled = sections.filter(s => isSectionFilled(omData[s.key]?.current, s.current_fields)).length;
    const futureFilled = sections.filter(s => isSectionFilled(omData[s.key]?.future, s.future_fields)).length;
    const total = sections.length;
    return {
      currentPct: total > 0 ? currentFilled / total : 0,
      futurePct: total > 0 ? futureFilled / total : 0,
      currentFilled,
      futureFilled,
      total,
    };
  }

  function dotColour(pct) {
    if (pct === 0) return '#E5E7EB';
    if (pct >= 1) return '#1D9E75';
    return '#D97706';
  }

  function handleGroupClick(group) {
    const firstKey = group.sectionKeys[0];
    if (!firstKey) return;
    const el = document.getElementById(`design-section-${firstKey}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', margin: '0 0 1.75rem', padding: '0.75rem 0.25rem 0' }}>
      {OM_GROUPS.map((group, i) => {
        const { currentPct, futurePct, currentFilled, futureFilled, total } = groupCompletion(group);
        const isFullyCurrent = currentPct >= 1;
        const isFullyFuture = futurePct >= 1;
        const isFullyComplete = isFullyCurrent && isFullyFuture;

        return (
          <div key={group.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', minWidth: 0 }}>
            {/* Connector line */}
            {i < OM_GROUPS.length - 1 && (
              <div style={{
                position: 'absolute', top: 12,
                left: 'calc(50% + 14px)', right: 'calc(-50% + 14px)',
                height: '1.5px',
                background: isFullyComplete ? '#1D9E75' : '#E5E7EB',
                zIndex: 0,
              }} />
            )}

            <button
              onClick={() => handleGroupClick(group)}
              title={`${group.title} — Current: ${currentFilled}/${total}, Future: ${futureFilled}/${total}`}
              style={{
                background: 'none', border: 'none', padding: 0,
                cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                position: 'relative', zIndex: 1,
              }}
            >
              {/* Split dot — left = current, right = future */}
              <div style={{ width: 26, height: 26, borderRadius: '50%', position: 'relative', marginBottom: 6, flexShrink: 0, overflow: 'hidden', border: `2px solid ${isFullyComplete ? '#1D9E75' : currentPct > 0 || futurePct > 0 ? '#D97706' : '#E5E7EB'}` }}>
                {/* Left half — current state */}
                <div style={{
                  position: 'absolute', left: 0, top: 0, width: '50%', height: '100%',
                  background: dotColour(currentPct),
                  transition: 'background 0.2s',
                }} />
                {/* Right half — future state */}
                <div style={{
                  position: 'absolute', right: 0, top: 0, width: '50%', height: '100%',
                  background: dotColour(futurePct),
                  transition: 'background 0.2s',
                }} />
                {/* Complete tick */}
                {isFullyComplete && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                    <svg viewBox="0 0 10 10" width="11" height="11">
                      <polyline points="1.5,5 4,7.5 8.5,2" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
              </div>

              <div style={{ fontSize: 12, fontWeight: 700, color: currentFilled > 0 || futureFilled > 0 ? '#1a1a2e' : '#666', textAlign: 'center', lineHeight: 1.2 }}>
                {group.title}
              </div>
              <div style={{ fontSize: 10.5, color: '#999', textAlign: 'center', marginTop: 3, lineHeight: 1.35, padding: '0 0.4rem' }}>
                {group.description}
              </div>
              <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 700, marginTop: 4, letterSpacing: '0.04em' }}>
                {currentFilled}/{total} · {futureFilled}/{total}
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ── Journey track legend ──────────────────────────────────────────
function JourneyTrackLegend() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.25rem', marginTop: '-1.25rem', marginBottom: '0.5rem' }}>
      {[
        { label: 'Current state', isLeft: true },
        { label: 'Future state', isLeft: false },
      ].map(({ label, isLeft }) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <div style={{ width: 12, height: 12, borderRadius: '50%', overflow: 'hidden', border: '1.5px solid #D97706', flexShrink: 0, position: 'relative' }}>
            <div style={{ position: 'absolute', left: 0, top: 0, width: '50%', height: '100%', background: isLeft ? '#D97706' : '#E5E7EB' }} />
            <div style={{ position: 'absolute', right: 0, top: 0, width: '50%', height: '100%', background: isLeft ? '#E5E7EB' : '#D97706' }} />
          </div>
          <span style={{ fontSize: 10, color: '#9CA3AF' }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

// ── How to use Design Guru — slide-in panel ──────────────────────
// Shared "How to use" section block — icon + bigger title + body
function HowToSection({ Icon, colour, title, children, isLast }) {
  return (
    <div style={{ paddingBottom: isLast ? 0 : '1rem', marginBottom: isLast ? 0 : '1rem', borderBottom: isLast ? 'none' : '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginBottom: '0.45rem' }}>
        <Icon colour={colour} />
        <h3 style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1a1a2e', margin: 0, letterSpacing: '-0.01em' }}>{title}</h3>
      </div>
      <div style={{ fontSize: '0.82rem', color: '#444', lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function HowToUseDesignPanel({ onClose }) {
  const accent = '#185FA5';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 150, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
      <div style={{ width: '100%', maxWidth: 380, height: '100vh', background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '1.1rem 1.4rem 0.9rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa' }}>Help</div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a2e', marginTop: 2 }}>How to use Design Guru</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', color: '#aaa', padding: '0.2rem 0.4rem', lineHeight: 1, fontFamily: 'inherit' }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '1.1rem 1.4rem' }}>
          <HowToSection Icon={PurposeIcon} colour={accent} title="What Design Guru is for">
            <p style={{ margin: 0 }}>
              Design Guru is where you map how the business operates today and design the operating model your direction demands. It follows Direction Guru — once you know where you are heading, this is where you translate that direction into structure: people, process, technology, finance and how customers experience the business. The honest current-state picture matters more than people expect; it is the gap between today and the future state that the Delivery Guru will turn into action.
            </p>
          </HowToSection>

          <HowToSection Icon={FlowIcon} colour={accent} title="How to work through it">
            <p style={{ margin: 0 }}>
              The canvas is split into four stages: <strong>Current State: People</strong>, <strong>Current State: Operations</strong>, <strong>Future State: People</strong> and <strong>Future State: Operations</strong>. Always start with the current state — you cannot design a future you have not first described. Future-state sections unlock once Direction Guru is far enough along to steer them. Each section has a Capture mode for chips and dropdowns when you already know the answer, and an Explore mode for an AI-guided conversation when you want to think it through.
            </p>
          </HowToSection>

          <HowToSection Icon={OutcomeIcon} colour={accent} title="What you will get out of it">
            <p style={{ margin: 0 }}>
              A completed Operating Model Canvas describing today honestly and tomorrow ambitiously. A one-page business design for sharing with the team and advisors. And the structural foundation that Delivery Guru needs to generate a meaningful, sequenced plan of action — without it the plan would be generic.
            </p>
          </HowToSection>

          <HowToSection Icon={TipsIcon} colour={accent} title="Tips for getting the most from it" isLast>
            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
              <li style={{ marginBottom: '0.4rem' }}>Be honest about the current state rather than aspirational — the gap is the value.</li>
              <li style={{ marginBottom: '0.4rem' }}>Future-state sections unlock once you have made progress on Direction Guru — finish that first.</li>
              <li style={{ marginBottom: '0.4rem' }}>Small teams often complete this canvas faster than they expect — do not overthink each box.</li>
              <li style={{ marginBottom: '0.4rem' }}>Treat the canvas as a living document — revisit and revise it as the business changes.</li>
            </ul>
          </HowToSection>
        </div>

        <div style={{ padding: '0.9rem 1.4rem', borderTop: '1px solid #f0f0f0', flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{ width: '100%', padding: '0.65rem', borderRadius: 8, background: 'white', border: '1.5px solid #e0e0e0', color: '#555', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Operating Model Canvas ────────────────────────────────────────
export default function OperatingModelCanvas() {
  // Seed from localStorage for fast first paint. Server fetch below
  // overrides this if the server has more recent data.
  const [omData, setOmData] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY_OM)) || {};
      return migrateITConsolidation(raw);
    } catch { return {}; }
  });
  const [activeSection, setActiveSection] = useState(null);
  const [activeMode, setActiveMode] = useState(null); // null | 'quick' | 'explore' | 'upload'
  const [helpOpen, setHelpOpen] = useState(false);
  const [prefillOpen, setPrefillOpen] = useState(false);
  // Dashboard "View review" modal state — sectionKey of the section
  // whose saved _evaluation is being read. Null when no modal open.
  const [reviewOpenSectionKey, setReviewOpenSectionKey] = useState(null);
  // Server-load gating — same shape as StrategyCanvas. serverLoaded
  // gates the debounced save (so initial state doesn't echo back to
  // the server) and drives the loading skeleton.
  const [serverLoaded, setServerLoaded] = useState(false);
  const [serverLoadError, setServerLoadError] = useState(null);
  const skipNextSaveRef = useRef(false);
  const saveTimerRef = useRef(null);

  // ── Server load on mount ──────────────────────────────────────
  // Server is source of truth. If server has data, apply it. If
  // server is empty but localStorage has data (pre-server-persistence
  // users), do a one-time migration upload. If both are empty, the
  // leader gets a fresh canvas.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('guruone_token');
        if (!token) { if (!cancelled) setServerLoaded(true); return; }
        const base = CANVAS_API.replace('/canvas', '');
        const res = await axios.get(`${base}/canvas-state/operating_model`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const serverData = res.data?.data || {};
        const serverHasData = res.data?.version > 0 && Object.keys(serverData).length > 0;
        const localRaw = (() => {
          try { return JSON.parse(localStorage.getItem(STORAGE_KEY_OM)) || {}; }
          catch { return {}; }
        })();
        const localData = migrateITConsolidation(localRaw);
        const localHasData = Object.keys(localData).length > 0;

        if (serverHasData) {
          skipNextSaveRef.current = true;
          setOmData(serverData);
        } else if (localHasData) {
          try {
            await axios.put(
              `${base}/canvas-state/operating_model`,
              { data: localData, origin: 'localstorage_migration' },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            track('canvas_localstorage_migrated', { canvas: 'operating_model', sections: Object.keys(localData).length });
          } catch (migErr) {
            console.warn('OM localStorage migration failed:', migErr.message);
          }
        }
        setServerLoaded(true);
      } catch (err) {
        if (cancelled) return;
        console.warn('OM server load failed — falling back to localStorage:', err.message);
        setServerLoadError(err.message || 'load_failed');
        setServerLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // Runs once on mount; auth state is stable for this component's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Debounced server save ──────────────────────────────────────
  // Coalesces flurries of edits into one save 1.5s after the last
  // mutation. localStorage continues to be written synchronously in
  // updateSection / handleBulkSave below — server is belt, local is
  // braces. skipNextSaveRef short-circuits the save triggered by
  // applying server data during initial load.
  useEffect(() => {
    if (!serverLoaded) return;
    if (skipNextSaveRef.current) { skipNextSaveRef.current = false; return; }
    if (Object.keys(omData).length === 0) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('guruone_token');
        if (!token) return;
        const base = CANVAS_API.replace('/canvas', '');
        await axios.put(
          `${base}/canvas-state/operating_model`,
          { data: omData, origin: 'om_save' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.warn('OM server save failed (localStorage cache still good):', err.message);
      }
    }, 1500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [omData, serverLoaded]);

  const handleDownloadOnePage = () => {
    // Placeholder — one-page business design generation lives in a follow-up.
    alert('Coming soon — one-page business design download is in development.');
  };

  const handleGenerateFutureState = () => {
    // Placeholder — generate future state from Direction canvas lives in a follow-up.
    alert('Coming soon — generate future state from your Direction canvas is in development.');
  };

  const { unlocked } = checkStrategyUnlock();
  const directionPct = calcDirectionPct();
  const futureStateUnlocked = directionPct >= 50;

  const updateSection = (key, data) => {
    const next = { ...omData, [key]: data };
    setOmData(next);
    localStorage.setItem(STORAGE_KEY_OM, JSON.stringify(next));
  };

  const handleBulkSave = (sections) => {
    const next = { ...omData };
    Object.entries(sections).forEach(([key, data]) => { next[key] = data; });
    setOmData(next);
    localStorage.setItem(STORAGE_KEY_OM, JSON.stringify(next));
  };

  const handleBack = () => {
    if (activeMode) { setActiveMode(null); } // mode → mode selector
    else { setActiveSection(null); }          // mode selector → overview
  };

  const activeSecDef = activeSection ? OM_SECTIONS.find(s => s.key === activeSection) : null;

  const renderContent = () => {
    // ── Overview ──────────────────────────────────────────────────
    if (!activeSection) {
      return (
        <>
          {/* Journey indicator — sits above the masthead per spec, mirroring DecisionGuru */}
          <DesignJourneyTrack omData={omData} />
          <JourneyTrackLegend />

          {/* Inline guru title — uses #48B650 for the "Guru" word */}
          <div style={{ textAlign: 'center', margin: '0 0 1.5rem' }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 300, color: '#1a1a2e', letterSpacing: '-0.02em', lineHeight: 1 }}>
              Design
              <strong style={{ color: '#185FA5', fontWeight: 700 }}> Guru</strong>
            </div>
            <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '0.4rem', letterSpacing: '0.04em' }}>
              Map the current. Design the future.
            </div>
          </div>

          {/* Dark guru toolbar — left group (logo + How to use + canvas label), right group (Generate future state / Pre-fill / Download) */}
          {(() => {
            const toolbarBtn = {
              fontSize: '0.74rem', padding: '0.4rem 0.85rem', borderRadius: 18,
              border: '1px solid rgba(255,255,255,0.25)', background: 'transparent',
              color: 'white', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit',
              width: 'auto', whiteSpace: 'nowrap',
            };
            return (
              <div style={{
                background: '#1a1a2e', color: 'white', borderRadius: 10,
                padding: '0.55rem 0.85rem', marginBottom: '1.25rem',
                display: 'flex', alignItems: 'center', gap: '0.75rem',
              }}>
                {/* Left group */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flex: 1 }}>
                  <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <img src="/Logo_emblem_only.png" alt="GuruOne" style={{ height: 22, width: 22, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                  </div>
                  <button onClick={() => setHelpOpen(true)} aria-label="How to use Design Guru" style={toolbarBtn}>
                    How to use
                  </button>
                  <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>
                    Your Design Canvas
                  </span>
                </div>
                {/* Right group */}
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={handleGenerateFutureState}
                    disabled={!futureStateUnlocked}
                    title={futureStateUnlocked ? 'Generate future state from your Direction canvas' : `Complete your Direction Canvas to 50% first (currently ${directionPct}%)`}
                    style={{
                      ...toolbarBtn,
                      background: futureStateUnlocked ? '#D97706' : 'rgba(255,255,255,0.06)',
                      color: futureStateUnlocked ? 'white' : 'rgba(255,255,255,0.3)',
                      border: futureStateUnlocked ? '1px solid #D97706' : '1px solid rgba(255,255,255,0.12)',
                      cursor: futureStateUnlocked ? 'pointer' : 'not-allowed',
                    }}
                  >
                    ✨ Generate future state
                  </button>
                  <button onClick={() => setPrefillOpen(true)} style={toolbarBtn}>
                    ✨ Pre-fill canvas
                  </button>
                  <button
                    onClick={handleDownloadOnePage}
                    style={{ ...toolbarBtn, borderColor: 'rgba(167,227,167,0.55)', color: '#A7E3A7' }}
                  >
                    ⬇ Download 1-pager
                  </button>
                </div>
              </div>
            );
          })()}

          {OM_GROUPS.map(group => {
            const groupSections = OM_SECTIONS.filter(s => group.sectionKeys.includes(s.key));
            return (
              <div key={group.key} style={{ marginBottom: '1.75rem' }} id={`design-group-${group.key}`}>
                {/* Group header — same shape as Direction's group rows (vertical
                    colour bar + uppercase title + sublabel) for visual parity. */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{ width: 4, height: 18, background: '#9CA3AF', borderRadius: 2, flexShrink: 0, alignSelf: 'center' }} />
                  <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{group.title}</span>
                  <span style={{ fontSize: '0.75rem', color: '#aaa' }}>{group.description}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '0.75rem' }}>
                  {groupSections.map(section => {
                    const sectionData = omData[section.key] || {};
                    const tier = getOMSectionTier(section, sectionData);
                    // "started" comes from the structural tier (any
                    // field filled) — stars no longer drive this
                    // because they now reflect quality only.
                    const started = (tier?.level || 0) >= 1;
                    const sectionEval = sectionData?.current?._evaluation || sectionData?._evaluation;
                    const hasEval = !!(sectionEval && typeof sectionEval.score === 'number');
                    const futureFilled = futureStateUnlocked && isSectionFilled(sectionData.future, section.future_fields);
                    return (
                      <div key={section.key} id={`design-section-${section.key}`} style={{ gridColumn: `span ${section.colSpan}` }}>
                        {/* Card markup mirrors StrategyCanvas's SectionCard so
                            Direction and Design read as the same product. */}
                        <div style={{
                          background: 'white', borderRadius: 12, padding: '1.1rem 1.25rem',
                          border: `1.5px solid ${started ? section.colour + '44' : '#f0f0f0'}`,
                          boxShadow: started ? `0 2px 8px ${section.colour}18` : '0 1px 3px rgba(0,0,0,0.06)',
                          display: 'flex', flexDirection: 'column', gap: '0.5rem',
                          height: '100%', boxSizing: 'border-box',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                            <span style={{ fontSize: '1.2rem', color: section.colour }}>{section.icon}</span>
                            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1a1a2e', flex: 1, lineHeight: 1.2 }}>{section.title}</span>
                            <OMStatusBadge tier={tier} compact />
                          </div>
                          <p style={{ fontSize: '0.75rem', color: '#888', margin: 0, lineHeight: 1.4, flex: 1 }}>{section.description}</p>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <Stars score={getOMSectionQuality(sectionData)} title={omStarsTooltip(sectionData)} />
                              {hasEval && (
                                <button onClick={() => setReviewOpenSectionKey(section.key)}
                                  style={{
                                    background: 'none', border: 'none', color: section.colour,
                                    fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                                    fontFamily: 'inherit', padding: '0.15rem 0.35rem', width: 'auto',
                                    textDecoration: 'underline', textUnderlineOffset: 2,
                                  }}>
                                  View review
                                </button>
                              )}
                            </div>
                            {futureStateUnlocked && (
                              <span title={futureFilled ? 'Future state mapped' : 'Future state not yet mapped'} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.6rem', color: '#9CA3AF' }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: futureFilled ? '#1D9E75' : 'transparent', border: `1.5px dashed ${futureFilled ? '#1D9E75' : '#C4C4C4'}`, flexShrink: 0 }} />
                                Future
                              </span>
                            )}
                          </div>
                          <button onClick={() => { setActiveSection(section.key); setActiveMode(null); }}
                            style={{ width: '100%', padding: '0.4rem', fontSize: '0.75rem', borderRadius: 7, border: `1px solid ${section.colour}`, background: started ? section.colour : 'white', color: started ? 'white' : section.colour, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500, marginTop: 'auto' }}>
                            {started ? '✎ Edit' : '+ Start'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      );
    }

    // ── Mode selector ─────────────────────────────────────────────
    if (!activeMode) {
      const hasData = isSectionFilled(omData[activeSection]?.current, activeSecDef.current_fields);
      return (
        <BOModeSelector
          section={activeSecDef}
          onSelect={mode => setActiveMode(mode)}
          onBack={() => setActiveSection(null)}
          existing={hasData}
        />
      );
    }

    // ── Upload mode ───────────────────────────────────────────────
    if (activeMode === 'upload') {
      return (
        <SectionUploadEditor
          section={activeSecDef}
          sectionData={omData[activeSection] || {}}
          onSave={data => { updateSection(activeSection, data); setActiveMode(null); }}
          onBack={handleBack}
        />
      );
    }

    // ── Quick or Explore mode → SectionDetail ─────────────────────
    return (
      <SectionDetail
        section={activeSecDef}
        sectionData={omData[activeSection] || {}}
        onChange={data => { updateSection(activeSection, data); track('design_section_saved', { section: activeSection }); }}
        onBack={handleBack}
        futureUnlocked={unlocked}
        mode={activeMode}
      />
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f7f7f9' }}>
      <Header />
      <main style={{ maxWidth: 1300, margin: '0 auto', padding: '1.5rem' }}>
        <div style={{ background: 'white', borderRadius: 14, padding: '2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
          {!serverLoaded ? (
            <div style={{ padding: '2rem 0', textAlign: 'center', color: '#888', fontSize: '0.9rem' }}>
              Loading your canvas{serverLoadError ? ' (cached copy)' : '…'}
            </div>
          ) : renderContent()}
        </div>
      </main>
      <DeliveryJourneySidebar />
      {helpOpen && <HowToUseDesignPanel onClose={() => setHelpOpen(false)} />}

      {/* Section-card "View review" modal — pops over the dashboard
          in read-only mode. Mirrors Direction's pattern. */}
      {reviewOpenSectionKey && (() => {
        const sec = OM_SECTIONS.find(s => s.key === reviewOpenSectionKey);
        const sd = omData[reviewOpenSectionKey] || {};
        const evaluation = sd?.current?._evaluation || sd?._evaluation;
        if (!sec || !evaluation) return null;
        return (
          <EvaluationModal
            section={{ title: sec.title, icon: sec.icon, colour: sec.colour }}
            evalState={{ stage: 'done', evaluation }}
            readOnly
            onCancel={() => setReviewOpenSectionKey(null)}
            onRefine={() => {
              // "Refine" from a saved review on OM: open the section in
              // explore mode so the leader can re-run the deep-dive.
              setReviewOpenSectionKey(null);
              setActiveSection(reviewOpenSectionKey);
              setActiveMode('explore');
            }}
            refineLabel="Refine — back to dialogue"
          />
        );
      })()}

      {/* Pre-fill slide-in — wraps OMGlobalUploadPanel; closes after a successful save */}
      {prefillOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
          <div style={{ width: '100%', maxWidth: 380, height: '100vh', background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '1.1rem 1.4rem 0.9rem', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#aaa' }}>Pre-fill</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a2e', marginTop: 2 }}>Pre-fill your Design canvas</div>
                <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '0.35rem', lineHeight: 1.5 }}>
                  Upload your business plan, org chart, process documentation or financial materials and we'll suggest values across all sections for you to review before saving.
                </div>
                <div style={{ fontSize: '0.68rem', color: '#aaa', marginTop: '0.4rem' }}>
                  Accepted: PDF, Word (.docx), PowerPoint (.pptx)
                </div>
              </div>
              <button onClick={() => setPrefillOpen(false)} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', color: '#aaa', padding: '0.2rem 0.4rem', lineHeight: 1, fontFamily: 'inherit' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.1rem 1.4rem' }}>
              <OMGlobalUploadPanel onSectionsLoaded={(sections) => { handleBulkSave(sections); setPrefillOpen(false); }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
